import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import institutionMcpEn from './i18n-mcp/en.json';
import institutionMcpFr from './i18n-mcp/fr.json';
import { networkStrings } from './i18n-network';
import { institutionOverlay } from './i18n-institution';
import { learnerDashboardLocales } from './i18n-learner-dashboard-locales';
import { useLanguageStore, resolveInitialLanguage, type Language } from '@/stores/language.store';
import { applyDocumentLocale } from './locale-direction';
import { videosStudioLocales } from './i18n-videos-studio-locales';
import { eduflowBlockEditorsFr } from './i18n-eduflow-block-editors-fr';
import { eduflowBlockEditorsEn } from './i18n-eduflow-block-editors-en';
import { playerVisualParallelLocales } from './i18n-player-visual-parallel-locales';
import { webhooksLocales } from './i18n-webhooks';
import { educationSegmentLocales } from './i18n-education-segment';
import { classesEvaluationsLocales } from './i18n-classes-evaluations-locales';
import { schoolExperienceLocales } from './i18n-school-experience-locales';
import { rhReportsLocales } from './i18n-rh-reports-locales';
import { studentLoginLocales } from './i18n-student-login-locales';

function withInstitution(
  base: Record<string, string>,
  code: string,
  /** Surcouche établissement d'une langue chargée à la demande (sinon lue dans `institutionOverlay`). */
  lazyInstitution?: Record<string, string>,
): Record<string, string> {
  return {
    ...base,
    ...(lazyInstitution ?? institutionOverlay[code] ?? {}),
    ...networkStrings.en,
    ...(networkStrings[code] ?? {}),
    ...(learnerDashboardLocales[code] ?? {}),
  };
}

/** Remplit les clés manquantes des bundles partiels (de, it, es, …) avec l’anglais avant la langue cible. */
function withEnglishFallback(overrides: Record<string, string>): Record<string, string> {
  return { ...enTranslations, ...overrides };
}

const frTranslations = {
  // Navigation
  'nav.dashboard': 'Tableau de bord',
  'nav.projects': 'Projets',
  'nav.eduflow': 'EduFlow',
  'nav.audiobooks': 'Livres audio',
  'nav.audio_studio': 'Studio Audio',
  'nav.analytics': 'Analytiques',
  'nav.users': 'Utilisateurs',
  'nav.settings': 'Paramètres',
  'nav.profile': 'Profil',
  'profile.institution': 'Établissement',
  'profile.institutionHint':
    'Votre compte LyrraStudio est rattaché à cet établissement. Ce rattachement est géré par votre administrateur.',
  'profile.institutionUnknownName': 'Établissement (nom indisponible)',
  'nav.notifications': 'Notifications',
  'nav.help': 'Aide',
  'nav.community': 'Communauté',
  'nav.library': 'Bibliothèque',
  'nav.certificates': 'Certificats',
  'nav.institution': 'Établissement',
  'nav.groups': 'Groupes',
  'nav.supportGroups': 'Groupes de soutien',
  'help.carousel.counter': "{{current}} / {{total}}",
  'help.carousel.roledescription': "carrousel",
  'help.carousel.label': "Captures d’écran",
  'help.carousel.unavailable': "Capture indisponible pour le moment.",
  'help.carousel.zoom': "Agrandir la capture",
  'help.carousel.zoomShort': "Agrandir",
  'help.carousel.previous': "Capture précédente",
  'help.carousel.next': "Capture suivante",
  'help.carousel.seeShot': "Voir la capture",
  'profile.page.emailLocked': "L’e-mail sert à vous connecter : contactez la direction pour le modifier.",
  'profile.page.currentPassword': "Mot de passe actuel",
  'profile.page.newPassword': "Nouveau mot de passe",
  'profile.page.confirmPassword': "Confirmer le nouveau mot de passe",
  'profile.page.changePassword': "Modifier le mot de passe",
  'profile.page.languageRegion': "Langue et région",
  'profile.page.notificationsTitle': "Notifications",
  'profile.page.prefEmail': "Notifications par e-mail",
  'profile.page.prefEmailHint': "Recevoir un e-mail pour les événements importants",
  'profile.page.prefPush': "Notifications du navigateur",
  'profile.page.prefPushHint': "Recevoir des notifications dans le navigateur",
  'profile.page.prefWeekly': "Rapport hebdomadaire",
  'profile.page.prefWeeklyHint': "Recevoir chaque semaine un résumé de votre activité",
  'profile.page.prefLanguage': "Langue de l’interface",
  'profile.page.prefTimezone': "Fuseau horaire",
  'profile.page.saving': "Enregistrement…",
  'profile.page.savePreferences': "Enregistrer les préférences",
  'nav.networkGroup': 'Groupe',
  'nav.learners': 'Apprenants',
  'nav.myLearning': 'Mon apprentissage',
  'nav.reports': 'Rapports',
  'nav.qualiopi': 'Qualiopi',
  'nav.training_plan': 'Plan de formation',
  'nav.catalog': 'Catalogue',
  'nav.competencies': 'Compétences',
  'nav.budget': 'Budget',
  'nav.campaigns': 'Campagnes',
  'nav.codir': 'CODIR',
  'nav.home': 'Accueil',
  'nav.producerAssignments': 'Missions de production',
  'nav.videos': 'Vidéos',
  'nav.sounds': 'Sons',
  'nav.images': 'Images',
  'pages.images.title': 'Images',
  'pages.images.pasteHint': 'Ou collez une capture d’écran (Ctrl+V / Cmd+V)',
  'pages.images.emptyHint':
    'Glisser-déposer, cliquer pour importer, ou coller une capture (Ctrl+V / Cmd+V).',
  'pages.images.viewLarge': 'Voir en grand',
  'pages.images.lightboxTitle': 'Aperçu de l’image',
  'pages.images.lightboxClose': 'Fermer',
  'pages.images.lightboxBackdropClose': 'Fermer l’aperçu',
  'pages.images.lightboxPrev': 'Image précédente',
  'pages.images.lightboxNext': 'Image suivante',
  'nav.documents': 'Documents',
  'nav.podcasts': 'Podcasts',
  'nav.webinars': 'Webinaires',
  'nav.webinarsEnterprise': 'Webinaires (entreprise)',
  'webinar.room.capacityFull':
    'Cette salle a atteint sa capacité maximale ({{max}} personnes connectées en même temps). Réessayez plus tard.',
  'webinar.room.participants': 'Participants',
  'webinar.room.chat': 'Chat',
  'webinar.room.speechTab': 'Parole',
  'webinar.room.speechPanelHint':
    'Texte reconnu depuis le micro des animateurs (reconnaissance vocale du navigateur). Le nom affiché est celui de la personne qui parle.',
  'webinar.room.speechEmpty':
    'Quand un animateur parle avec le micro ouvert, le texte apparaît ici avec son nom. La ponctuation suit celle proposée par le navigateur.',
  'webinar.room.speechUnknownSpeaker': 'Animateur',
  'webinar.room.speechPresenterActive':
    'Votre voix est reconnue par le navigateur et envoyée aux participants (onglet Parole).',
  'webinar.room.speechUnsupportedBrowser':
    'La transcription automatique des animateurs n’est pas disponible sur ce navigateur (essayez Chrome ou Edge).',
  'webinar.room.sidePanelTabs':
    'Participants, parole des animateurs, sondage et chat',
  'webinar.room.you': 'vous',
  'webinar.room.showParticipantsPanel': 'Afficher le panneau Participants',
  'webinar.room.hideParticipantsPanel': 'Réduire le panneau',
  'webinar.room.leave': 'Quitter la salle',
  'webinar.room.contentFocusExpand': 'Contenu sur tout l’écran',
  'webinar.room.contentFocusCollapse': 'Afficher décor et intervenants',
  'webinar.room.contentFocusAriaExpand':
    'Agrandir la formation, le document partagé ou le partage d’écran sur tout l’espace sans fond ni intervenants',
  'webinar.room.liveDocumentLabel': 'Fichier (PDF ou PowerPoint)',
  'webinar.room.liveDocumentUploadChoice': 'Choisir un fichier à afficher',
  'webinar.room.liveDocumentUploading': 'Envoi…',
  'webinar.room.liveDocumentUploadError':
    'Impossible d’envoyer le fichier. Vérifiez le format (PDF ou PowerPoint) et la taille (max. 40 Mo).',
  'webinar.room.liveDocumentClose': 'Retirer le document',
  'webinar.room.liveDocumentBehindScreen':
    'Le document reste disponible — arrêtez le partage d’écran pour le réafficher.',
  'webinar.room.waitingSharedContentExtended':
    'En attente du contenu partagé par l’animateur : formation catalogue, fichier (PDF ou PowerPoint) ou partage d’écran.',
  'webinar.room.liveDocumentOfficeHint':
    'Si l’aperçu ne charge pas (HTTP local ou fichier trop lourd), ouvrez le lien ci-dessus.',
  'webinar.room.liveDocumentOpenOrDownload': 'Ouvrir ou télécharger le fichier',
  'webinar.room.liveDocumentNoInlinePreview':
    'Ce fichier ne peut pas être affiché ici automatiquement. Ouvrez-le dans un nouvel onglet.',
  'webinar.room.liveDocumentOpenTab': 'Ouvrir',
  'webinar.room.pollTab': 'Sondage',
  'webinar.room.poll.regionTitle': 'Sondage du webinaire',
  'webinar.room.poll.presenterHint':
    'Créez une question et 2 à 8 réponses. Le sondage s’affiche ici pour toute la salle ; seuls les animateurs voient les résultats en direct.',
  'webinar.room.poll.questionLabel': 'Question',
  'webinar.room.poll.questionPlaceholder': 'Ex. : Quel thème préférez-vous pour la suite ?',
  'webinar.room.poll.optionsLabel': 'Réponses possibles',
  'webinar.room.poll.optionNumber': 'Réponse',
  'webinar.room.poll.choiceLabel': 'Choix',
  'webinar.room.poll.addOption': 'Ajouter une réponse',
  'webinar.room.poll.removeLastOption': 'Retirer la dernière',
  'webinar.room.poll.launch': 'Lancer le sondage',
  'webinar.room.poll.waitingParticipant': 'Aucun sondage en cours. L’animateur peut en lancer un.',
  'webinar.room.poll.voteHint':
    'Une réponse par personne sur le serveur : vous pouvez changer de choix en cliquant une autre option.',
  'webinar.room.poll.yourVote': 'votre choix',
  'webinar.room.poll.resultsHeading': 'Résultats (animateurs)',
  'webinar.room.poll.noVotesYet': 'Aucun vote pour l’instant.',
  'webinar.room.poll.totalVotes': 'Nombre de votes : {{n}}',
  'webinar.room.poll.closePoll': 'Fermer le sondage',
  'webinar.room.contentFocusAriaCollapse': 'Réafficher le décor de la scène et la colonne des intervenants',
  'webinar.room.exitDirectTitle': 'Quitter le webinaire ?',
  'webinar.room.exitDirectMessage':
    'Vous avez ouvert cette salle depuis un lien partagé (sans passer par le tableau de bord). Vous pouvez fermer cet onglet. Si le navigateur refuse la fermeture, vous serez renvoyé vers votre espace.',
  'webinar.room.exitDirectConfirm': 'Fermer l’onglet',
  'webinar.room.publicGateTitle': 'Inscription au webinaire',
  'webinar.room.publicGateHint':
    'L’organisateur a activé l’inscription publique : indiquez votre e-mail, choisissez un mot de passe (8 caractères min.) et complétez les champs demandés.',
  'webinar.room.publicGateEmail': 'E-mail',
  'webinar.room.publicGateLogin': 'Identifiant',
  'webinar.room.publicGatePassword': 'Mot de passe',
  'webinar.room.publicGatePasswordHintParticipant':
    'Première visite : inscription. Visites suivantes : même e-mail et mot de passe. Minimum 8 caractères.',
  'webinar.room.publicGateSubmit': 'Entrer dans la salle',
  'webinar.room.publicGateSubmitting': 'Vérification…',
  'webinar.room.publicGateDenied': 'E-mail ou mot de passe incorrect.',
  'webinar.room.socketGateDenied':
    'Impossible de rejoindre la salle en temps réel : session d’inscription publique manquante ou expirée. Rechargez la page et reconnectez-vous avec le même e-mail et mot de passe.',
  'nav.parcours': 'Parcours',
  'nav.eduflowList': 'Formations',
  'nav.section.pilotage': 'Pilotage',
  'nav.section.creation': 'Création',
  'nav.section.medias': 'Médias',
  'nav.section.aide': 'Aide',
  'sidebar.brandTagline': 'Formation',
  'sidebar.logoInstitutionAlt': "Logo de l'établissement",
  'sidebar.expand': 'Agrandir le menu',
  'sidebar.collapse': 'Réduire le menu',
  'sidebar.expandAria': 'Agrandir la barre latérale',
  'sidebar.collapseAria': 'Réduire la barre latérale',
  'dashboard.kindPodcast': 'Podcast',
  'pages.learners.title': 'Apprenants',
  'pages.learners.description': 'Suivez la progression de vos apprenants',
  'pages.learners.enrolledFormations': 'Formations inscrites',
  'pages.learners.notStarted': 'Non commencé',
  'pages.learners.startedOn': 'Début {{date}}',
  'pages.learners.detailedTraces': 'Traces détaillées · {{flowTitle}}',
  'pages.learners.statsEvents': 'Événements : {{count}}',
  'pages.learners.statsResponses': 'Réponses : {{count}}',
  'pages.learners.statsBlockSessions': 'Sessions blocs : {{count}}',
  'pages.learners.timePerBlock': 'Temps passé par bloc',
  'pages.learners.lastEvents': 'Derniers événements',
  'pages.learners.blockSessionInProgress': '(en cours)',
  'pages.learners.blockTimeOnBlock': '({{duration}} sur ce bloc)',
  'pages.learners.trackingEvent.unknown': 'Événement',
  'pages.learners.trackingEvent.training_enter': 'Ouverture de la formation',
  'pages.learners.trackingEvent.training_exit': 'Fermeture ou sortie de la formation',
  'pages.learners.trackingEvent.training_completed': 'Formation terminée',
  'pages.learners.trackingEvent.block_enter': 'Entrée dans un bloc',
  'pages.learners.trackingEvent.block_exit': 'Sortie d’un bloc',
  'pages.learners.trackingEvent.response_submitted': 'Réponse ou validation envoyée',
  'pages.learners.trackingEvent.progress_sync': 'Synchronisation de la progression',
  'pages.learners.refresh': 'Actualiser',
  'pages.learners.exportCsv': 'Exporter CSV',
  'pages.learners.statTotal': 'Total apprenants',
  'pages.learners.statInProgress': 'En cours',
  'pages.learners.statCompleted': 'Terminé',
  'pages.learners.statAvgProgress': 'Progression moy.',
  'pages.learners.searchPlaceholder': 'Rechercher par nom ou email…',
  'pages.learners.filterAllFlows': 'Toutes les formations',
  'pages.learners.sortRecent': 'Plus récents',
  'pages.learners.sortName': 'Nom A-Z',
  'pages.learners.sortProgress': 'Progression',
  'pages.learners.colLearner': 'Apprenant',
  'pages.learners.colFormations': 'Formations',
  'pages.learners.colProgress': 'Progression',
  'pages.learners.colCompleted': 'Complétées',
  'pages.learners.colTime': 'Temps',
  'pages.learners.colLastAccess': 'Dernier accès',
  'pages.learners.colActions': 'Actions',
  'pages.learners.openProgressDashboard': 'Tableau de bord d’avancement',
  'pages.learners.dashboardTitle': 'Avancement de l’apprenant',
  'pages.learners.dashboardBack': 'Retour à la liste',
  'pages.learners.dashboardInvalidId': 'Identifiant apprenant invalide',
  'pages.learners.dashboardNotFound': 'Apprenant introuvable ou accès refusé',
  'pages.learners.dashboardOverviewHint':
    'Vue consolidée : formations EduFlow auxquelles ce participant est inscrit, parcours et podcasts assignés au compte Lyrra qui partage le même e-mail.',
  'pages.learners.tabOverview': 'Synthèse',
  'pages.learners.tabFormations': 'Formations',
  'pages.learners.tabParcours': 'Parcours',
  'pages.learners.tabPodcasts': 'Podcasts',
  'pages.learners.sectionFormations': 'Formations',
  'pages.learners.sectionParcours': 'Parcours',
  'pages.learners.sectionPodcasts': 'Podcasts',
  'pages.learners.emptyParcours': 'Aucun parcours assigné à un compte Lyrra avec cet e-mail.',
  'pages.learners.emptyPodcasts': 'Aucun podcast assigné à un compte Lyrra avec cet e-mail.',
  'pages.learners.linkedAccountHint':
    'Les parcours et podcasts apparaissent lorsqu’un utilisateur Lyrra existe avec le même e-mail que ce participant.',
  'pages.learners.kpiEduflows': 'Formations',
  'pages.learners.kpiAvgProgress': 'Progression moy.',
  'pages.learners.kpiPaths': 'Parcours',
  'pages.learners.kpiPodcasts': 'Podcasts',
  'pages.learners.kpiCompletedFormations': '{{count}} terminée(s)',
  'pages.learners.pathProgressLabel': '{{done}} / {{total}} activités',
  'pages.learners.pathAssignedOn': 'Ajout le {{date}}',
  'pages.learners.podcastCompleted': 'Terminé',
  'pages.learners.podcastInProgress': 'En cours',
  'pages.learners.pathStatusPublished': 'Publié',
  'pages.learners.pathStatusDraft': 'Brouillon',
  'pages.learners.openInDesigner': 'Ouvrir dans le concepteur',
  'pages.learners.openParcoursEditor': 'Ouvrir le parcours',
  'pages.learners.viewPodcasts': 'Voir les podcasts',
  'pages.learners.loading': 'Chargement des apprenants…',
  'pages.learners.loadError': 'Impossible de charger les apprenants',
  'pages.learners.retry': 'Réessayer',
  'pages.learners.emptyFiltered': 'Aucun apprenant trouvé',
  'pages.learners.emptyFilteredHint': 'Essayez de modifier vos critères de recherche',
  'pages.learners.emptyNone': 'Aucun apprenant',
  'pages.learners.emptyNoneHint':
    'Les élèves de vos classes et les inscrits à vos formations apparaîtront ici',
  'pages.learners.pagination':
    'Page {{page}} sur {{totalPages}} · {{total}} apprenant(s)',
  'pages.learners.prev': 'Précédent',
  'pages.learners.filterAllClasses': 'Toutes mes classes',
  'pages.learners.noFormation': 'Aucune formation assignée',
  'pages.learners.csvClasses': 'Classes',
  'pages.learners.next': 'Suivant',
  'pages.learners.csvLastName': 'Nom',
  'pages.learners.csvFirstName': 'Prénom',
  'pages.learners.csvEmail': 'Email',
  'pages.learners.csvFormations': 'Formations',
  'pages.learners.csvAvgProgress': 'Progression moyenne',
  'pages.learners.csvTotalTime': 'Temps total',
  'pages.learners.csvLastActivity': 'Dernière activité',
  'eduflowList.status.draft': 'Brouillon',
  'eduflowList.status.published': 'Publié',
  'eduflowList.status.archived': 'Archivé',
  'eduflowList.badge.enterpriseShell': 'Besoin entreprise',
  'eduflowList.badge.enterpriseLinked': 'Entreprise',
  'eduflowList.badge.toProduce': 'À produire',
  'eduflow.list.blocksCount': '{{count}} blocs',
  'eduflow.list.noContent': 'Sans contenu',
  'eduflow.list.minutesShort': '{{count}} min',
  'eduflow.list.tooltip.openMissions': 'Ouvrir Mes missions',
  'eduflow.list.tooltip.edit': 'Modifier',
  'eduflow.list.shellHint':
    'Fiche définie par votre organisation — créez l’EduFlow depuis « Mes missions ».',
  'breadcrumb.label': "Fil d'Ariane",
  'breadcrumb.home': 'Accueil',
  'breadcrumb.dashboard': 'Tableau de bord',
  'breadcrumb.projects': 'Projets',
  'breadcrumb.eduflow': 'Formations',
  'breadcrumb.learners': 'Apprenants',
  'breadcrumb.analytics': 'Analytiques',
  'breadcrumb.videos': 'Vidéos',
  'breadcrumb.sounds': 'Sons',
  'breadcrumb.images': 'Images',
  'breadcrumb.documents': 'Documents',
  'breadcrumb.help': 'Aide',
  'breadcrumb.admin': 'Administration',
  'breadcrumb.settings': 'Paramètres',
  'breadcrumb.profile': 'Profil',
  'breadcrumb.groups': 'Groupes',
  'breadcrumb.branding': 'Image de marque',
  'breadcrumb.landingPage': "Page d'accueil",
  'breadcrumb.webhooks': 'Webhooks',
  'breadcrumb.campaigns': 'Campagnes',
  'breadcrumb.competencies': 'Compétences',
  'breadcrumb.compliance': 'Conformité',
  'breadcrumb.reports': 'Rapports',
  'breadcrumb.budget': 'Budget',
  'breadcrumb.catalog': 'Catalogue',
  'breadcrumb.integrations': 'Intégrations',
  'breadcrumb.requests': 'Demandes',
  'breadcrumb.team': 'Équipe',
  'breadcrumb.reviews': 'Évaluations',
  'breadcrumb.training': 'Formations',
  'breadcrumb.skills': 'Compétences',
  'breadcrumb.certificates': 'Certificats',
  'breadcrumb.new': 'Nouveau',
  'breadcrumb.edit': 'Modifier',
  'breadcrumb.podcasts': 'Podcasts',
  'breadcrumb.episodes': 'Épisodes',
  'breadcrumb.parcours': 'Parcours',
  'breadcrumb.producerAssignments': 'Missions de production',
  'header.toggleMenu': 'Ouvrir ou fermer le menu',
  'header.search': 'Rechercher…',
  'header.toggleTheme': 'Changer le thème clair ou sombre',
  'header.profile': 'Profil',
  'header.settings': 'Paramètres',
  'header.backToInstitutionAdmin': 'Administration de l’établissement',
  'header.openTrainerWorkspace': 'Espace formateur et création',
  'header.logout': 'Déconnexion',
  'footer.madeWith': 'Fait avec',
  'footer.forEducation': "pour l'éducation",
  'footer.legal': 'Liens légaux',
  'footer.terms': 'CGU',
  'footer.privacy': 'Confidentialité',
  'footer.accessibility': 'Accessibilité',

  // Common buttons
  'btn.save': 'Enregistrer',
  'btn.cancel': 'Annuler',
  'btn.delete': 'Supprimer',
  'btn.edit': 'Modifier',
  'btn.create': 'Créer',
  'btn.add': 'Ajouter',
  'btn.close': 'Fermer',
  'btn.confirm': 'Confirmer',
  'btn.back': 'Retour',
  'btn.next': 'Suivant',
  'btn.previous': 'Précédent',
  'btn.submit': 'Soumettre',
  'btn.search': 'Rechercher',
  'btn.filter': 'Filtrer',
  'btn.export': 'Exporter',
  'btn.import': 'Importer',
  'btn.download': 'Télécharger',
  'btn.upload': 'Téléverser',
  'btn.refresh': 'Actualiser',
  'btn.retry': 'Réessayer',
  'btn.view': 'Voir',
  'btn.details': 'Détails',
  'btn.publish': 'Publier',
  'btn.unpublish': 'Dépublier',
  'btn.duplicate': 'Dupliquer',
  'btn.archive': 'Archiver',
  'btn.restore': 'Restaurer',
  'btn.send': 'Envoyer',
  'btn.apply': 'Appliquer',
  'btn.reset': 'Réinitialiser',
  'btn.select_all': 'Tout sélectionner',
  'btn.deselect_all': 'Tout désélectionner',
  'btn.load_more': 'Charger plus',
  'btn.show_more': 'Voir plus',
  'btn.show_less': 'Voir moins',
  'btn.copy': 'Copier',
  'btn.share': 'Partager',
  'btn.preview': 'Aperçu',
  'btn.continue': 'Continuer',
  'btn.skip': 'Passer',

  // Form labels
  'form.email': 'Adresse e-mail',
  'form.password': 'Mot de passe',
  'form.password_confirm': 'Confirmer le mot de passe',
  'form.first_name': 'Prénom',
  'form.last_name': 'Nom',
  'form.name': 'Nom',
  'form.phone': 'Téléphone',
  'form.address': 'Adresse',
  'form.city': 'Ville',
  'form.zip': 'Code postal',
  'form.country': 'Pays',
  'form.title': 'Titre',
  'form.description': 'Description',
  'form.category': 'Catégorie',
  'form.tags': 'Tags',
  'form.status': 'Statut',
  'form.role': 'Rôle',
  'form.language': 'Langue',
  'form.date': 'Date',
  'form.start_date': 'Date de début',
  'form.end_date': 'Date de fin',
  'form.duration': 'Durée',
  'form.price': 'Prix',
  'form.avatar': 'Photo de profil',
  'form.required': 'Obligatoire',
  'form.optional': 'Optionnel',
  'form.select_placeholder': 'Sélectionner...',
  'form.search_placeholder': 'Rechercher...',
  'form.no_results': 'Aucun résultat',
  'form.file_drop': 'Glissez-déposez un fichier ici ou cliquez pour parcourir',
  'form.max_size': 'Taille maximale : {{size}}',
  'form.institution': 'Établissement',
  'form.department': 'Département',
  'form.group': 'Groupe',
  'form.level': 'Niveau',
  'form.notes': 'Notes',
  'form.url': 'URL',
  'form.content': 'Contenu',
  'form.thumbnail': 'Vignette',

  // Validation errors
  'error.required': 'Ce champ est obligatoire',
  'error.email_invalid': 'Adresse e-mail invalide',
  'error.password_min': 'Le mot de passe doit contenir au moins {{min}} caractères',
  'error.password_match': 'Les mots de passe ne correspondent pas',
  'error.min_length': 'Minimum {{min}} caractères',
  'error.max_length': 'Maximum {{max}} caractères',
  'error.min_value': 'La valeur minimale est {{min}}',
  'error.max_value': 'La valeur maximale est {{max}}',
  'error.invalid_url': 'URL invalide',
  'error.file_too_large': 'Le fichier est trop volumineux',
  'error.file_type': 'Type de fichier non supporté',
  'error.generic': 'Une erreur est survenue',
  'error.network': 'Erreur de connexion au serveur',
  'error.unauthorized': 'Vous devez être connecté',
  'error.forbidden': 'Accès interdit',
  'error.not_found': 'Ressource introuvable',
  'error.server': 'Erreur serveur interne',
  'error.conflict': 'Cette ressource existe déjà',
  'error.rate_limit': 'Trop de requêtes, veuillez patienter',

  // Auth
  'auth.login': 'Se connecter',
  'auth.logout': 'Se déconnecter',
  'auth.register': 'Créer un compte',
  'auth.forgot_password': 'Mot de passe oublié ?',
  'auth.reset_password': 'Réinitialiser le mot de passe',
  'auth.otp_title': 'Connexion par code',
  'auth.otp_sent': 'Un code de vérification a été envoyé à {{email}}',
  'auth.otp_placeholder': 'Entrez le code à 6 chiffres',
  'auth.google_login': 'Continuer avec Google',
  'auth.remember_me': 'Se souvenir de moi',
  'auth.no_account': 'Pas encore de compte ?',
  'auth.has_account': 'Déjà un compte ?',
  'auth.welcome_back': 'Bon retour, {{name}} !',
  'auth.login_success': 'Connexion réussie',
  'auth.register_success': 'Compte créé avec succès',
  'auth.logout_success': 'Déconnexion réussie',

  // Roles
  'role.teacher': 'Formateur',
  'role.institution': 'Établissement',
  'role.institution_admin': 'Administrateur entreprise',
  'role.admin': 'Administrateur',
  'role.drh': 'DRH',
  'role.rh': 'RH',
  'role.resp_formation': 'Responsable formation',
  'role.manager': 'Manager',
  'role.collaborator': 'Collaborateur',
  'role.learner': 'Apprenant',

  // Dashboard
  'dashboard.title': 'Tableau de bord',
  'dashboard.welcome': 'Bienvenue, {{name}}',
  'dashboard.recent_projects': 'Projets récents',
  'dashboard.recent_activity': 'Activité récente',
  'dashboard.quick_actions': 'Actions rapides',
  'dashboard.stats': 'Statistiques',
  'dashboard.total_projects': 'Total projets',
  'dashboard.total_learners': 'Total apprenants',
  'dashboard.total_eduflows': 'Total formations',
  'dashboard.completion_rate': 'Taux de complétion',
  'dashboard.active_users': 'Utilisateurs actifs',
  'dashboard.new_registrations': 'Nouvelles inscriptions',
  'dashboard.revenue': 'Chiffre d\'affaires',
  'dashboard.subtitle': 'Vue d\'ensemble de votre activité',
  'dashboard.home.greeting': 'Bonjour {{name}}',
  'dashboard.home.greetingSubtitle':
    'Pilotez vos formations, parcours et podcasts depuis un seul espace.',
  'dashboard.home.primaryCta': 'Créer une formation',
  'dashboard.home.continueEditing': 'Reprendre la dernière modification',
  'dashboard.home.publishRate': 'Taux de publication',
  'dashboard.home.totalFormations': 'Formations créées',
  'dashboard.home.uniqueLearners': 'Apprenants touchés',
  'dashboard.home.calendarKicker': 'Activité ce mois-ci',
  'dashboard.home.recentWork': 'Dernières modifications',
  'dashboard.home.viewAll': 'Tout voir',
  'dashboard.home.quickAccess': 'Accès rapides',
  'dashboard.home.loading': 'Chargement du tableau de bord…',
  'dashboard.home.defaultName': 'formateur·rice',
  'dashboard.welcomeSub': 'Voici ce qui se passe sur vos projets.',
  'dashboard.projects': 'Projets',
  'dashboard.eduflows': 'Formations',
  'dashboard.publishedFlows': 'Parcours publiés',
  'dashboard.certificatesIssued': 'Certificats délivrés',
  'dashboard.activityEmpty': 'Aucune activité récente sur vos parcours ou podcasts.',
  'dashboard.loadError': 'Impossible de charger les données du tableau de bord.',
  'dashboard.learners': 'Apprenants',
  'dashboard.newProject': 'Nouveau projet',
  'dashboard.newEduFlow': 'Nouvelle formation',
  'dashboard.uploadMedia': 'Importer un média',
  'dashboard.viewDocs': 'Documentation',
  'dashboard.recentProjects': 'Projets récents',
  'dashboard.noRecentProjects': 'Aucun projet récent',
  'dashboard.activity': 'Activité',
  'dashboard.searchProjects': 'Rechercher des projets...',
  'dashboard.noProjects': 'Aucun projet pour le moment',
  'dashboard.createFirst': 'Créez votre premier projet pour commencer',
  'dashboard.createProject': 'Créer un projet',
  'dashboard.projectTitle': 'Titre du projet',
  'dashboard.recentActivity': 'Activité récente',
  'dashboard.roleSpecific': 'Votre espace est personnalisé selon votre rôle.',
  'dashboard.calendar.mon': 'Lun',
  'dashboard.calendar.wed': 'Mer',
  'dashboard.calendar.fri': 'Ven',
  'dashboard.calendar.less': 'Moins',
  'dashboard.calendar.more': 'Plus',
  'dashboard.calendar.tooltip': '{{count}} activite(s) le {{date}}',
  'dashboard.mock.updated2h': 'Il y a 2 h',
  'dashboard.mock.updated5h': 'Il y a 5 h',
  'dashboard.mock.updatedYesterday': 'Hier',
  'dashboard.home': 'Accueil',
  'dashboard.analytics': 'Analytique',
  'dashboard.settings': 'Paramètres',
  'dashboard.analyticsComingSoon': 'Analytique bientôt disponible',
  'dashboard.settingsComingSoon': 'Paramètres bientôt disponibles',
  'pages.analytics.title': 'Analytiques',
  'pages.analytics.description': 'Tableaux de bord et statistiques',
  'pages.analytics.subtitle':
    'Filtrez les données, réorganisez les graphiques par glisser-déposer et adaptez la disposition à votre besoin.',
  'pages.analytics.filters.period': 'Période',
  'pages.analytics.filters.flow': 'Formation',
  'pages.analytics.filters.allFlows': 'Toutes les formations',
  'pages.analytics.period.7d': '7 derniers jours',
  'pages.analytics.period.30d': '30 derniers jours',
  'pages.analytics.period.90d': '90 derniers jours',
  'pages.analytics.period.365d': '12 derniers mois',
  'pages.analytics.addChart': 'Ajouter un graphique',
  'pages.analytics.resetLayout': 'Réinitialiser la disposition',
  'pages.analytics.dragHint': 'Glisser pour réorganiser',
  'pages.analytics.size.sm': 'S',
  'pages.analytics.size.md': 'M',
  'pages.analytics.size.lg': 'L',
  'pages.analytics.removeChart': 'Retirer ce graphique',
  'pages.analytics.kind.completion_line': 'Complétion dans le temps',
  'pages.analytics.kind.learners_bar': 'Apprenants par module',
  'pages.analytics.kind.status_pie': 'Répartition des statuts',
  'pages.analytics.kind.engagement_area': 'Temps d’engagement',
  'pages.analytics.series.completion': 'Taux de complétion (%)',
  'pages.analytics.series.learners': 'Apprenants',
  'pages.analytics.series.hours': 'Heures',
  'pages.analytics.status.completed': 'Complété',
  'pages.analytics.status.in_progress': 'En cours',
  'pages.analytics.status.dropped': 'Abandonné',
  'pages.analytics.status.not_started': 'Non commencé',
  'pages.analytics.loadError': 'Impossible de charger les données analytiques.',
  'pages.analytics.retry': 'Réessayer',
  'pages.analytics.axis.bucket': 'P. {{n}}',
  'pages.analytics.mock.wd0': 'Lun',
  'pages.analytics.mock.wd1': 'Mar',
  'pages.analytics.mock.wd2': 'Mer',
  'pages.analytics.mock.wd3': 'Jeu',
  'pages.analytics.mock.wd4': 'Ven',
  'pages.analytics.mock.wd5': 'Sam',
  'pages.analytics.mock.wd6': 'Dim',
  'pages.analytics.emptyCharts': 'Ajoutez au moins un graphique pour visualiser les données.',
  'pages.analytics.comingSoonDesc':
    'Visualisez les statistiques de vos formations, le taux de complétion, l\'engagement des apprenants et les performances globales.',
  'pages.help.title': 'Centre d\'aide',
  'pages.help.description': 'Documentation et support',
  'pages.help.hubIntro':
    'Thématiques couvrant l’usage de Lyrra : vous pourrez bientôt lier chaque fiche à votre documentation, tutoriels ou tickets support.',
  'pages.help.searchPlaceholder': 'Rechercher une thématique…',
  'pages.help.noResults': 'Aucun résultat pour cette recherche.',
  'pages.help.configHint':
    'Contenu de démonstration : branchez vos URLs, articles ou base de connaissances quand vous serez prêt.',
  'pages.help.comingSoonDesc':
    'Accédez à la documentation, aux tutoriels et au support technique pour utiliser la plateforme.',
  'pages.help.modal.steps': 'En pratique',
  'pages.help.modal.tips': 'Conseils',
  'pages.help.modal.related': 'Voir aussi',
  'pages.help.openArticle': 'Ouvrir la fiche',
  'pages.help.onboardingFlowMissing': 'Formation introuvable',
  'pages.help.onboardingFlowMissingDesc':
    "Lancez les seeds backend (db:seed:onboarding-eduflow, db:seed:onboarding-navigation-eduflow, db:seed:onboarding-profile-eduflow, db:seed:onboarding-roles-eduflow, db:seed:onboarding-block-types-eduflow, db:seed:onboarding-designer-overview-eduflow, db:seed:onboarding-branches-eduflow, db:seed:onboarding-validation-eduflow, db:seed:onboarding-objectives-eduflow, db:seed:onboarding-ai-designer-eduflow, db:seed:onboarding-publication-status-eduflow, db:seed:onboarding-publication-links-eduflow, db:seed:onboarding-catalog-visibility-eduflow, db:seed:onboarding-enrollments-eduflow, db:seed:onboarding-groups-cohorts-eduflow, db:seed:onboarding-progress-eduflow, db:seed:onboarding-certificates-eduflow et db:seed:onboarding-media-library-eduflow) ou renseignez VITE_ONBOARDING_DASHBOARD_FLOW_ID / VITE_ONBOARDING_NAVIGATION_FLOW_ID / VITE_ONBOARDING_PROFILE_FLOW_ID / VITE_ONBOARDING_ROLES_FLOW_ID / VITE_ONBOARDING_BLOCK_TYPES_FLOW_ID / VITE_ONBOARDING_DESIGNER_OVERVIEW_FLOW_ID / VITE_ONBOARDING_BRANCHES_FLOW_ID / VITE_ONBOARDING_VALIDATION_FLOW_ID / VITE_ONBOARDING_OBJECTIVES_FLOW_ID / VITE_ONBOARDING_AI_DESIGNER_FLOW_ID / VITE_ONBOARDING_PUBLICATION_STATUS_FLOW_ID / VITE_ONBOARDING_PUBLICATION_LINKS_FLOW_ID / VITE_ONBOARDING_CATALOG_VISIBILITY_FLOW_ID / VITE_ONBOARDING_ENROLLMENTS_FLOW_ID / VITE_ONBOARDING_GROUPS_COHORTS_FLOW_ID / VITE_ONBOARDING_PROGRESS_FLOW_ID / VITE_ONBOARDING_CERTIFICATES_FLOW_ID / VITE_ONBOARDING_MEDIA_LIBRARY_FLOW_ID.",
  'pages.help.onboardingDashboardPlayerTitle': 'Formation — Tableau de bord Lyrra (onboarding)',
  'pages.help.onboardingNavigationPlayerTitle':
    'Formation — Navigation & menu latéral Lyrra (onboarding)',
  'pages.help.onboardingProfilePlayerTitle': 'Formation — Profil & préférences Lyrra (onboarding)',
  'pages.help.onboardingRolesPlayerTitle':
    "Formation — Rôles et droits d'accès Lyrra (onboarding)",
  'pages.help.onboardingBlockTypesPlayerTitle':
    'Formation — Types de blocs EduFlow (onboarding)',
  'pages.help.onboardingDesignerOverviewPlayerTitle':
    'Formation — Vue d’ensemble du concepteur EduFlow (onboarding)',
  'pages.help.onboardingBranchesPlayerTitle':
    'Formation — Branches & conditions EduFlow (onboarding)',
  'pages.help.onboardingValidationPlayerTitle':
    'Formation — Validation du parcours EduFlow (onboarding)',
  'pages.help.onboardingObjectivesPlayerTitle':
    'Formation — Objectifs pédagogiques EduFlow (onboarding)',
  'pages.help.onboardingAiDesignerPlayerTitle':
    'Formation — Assistant IA du designer LyrraStudio (onboarding)',
  'pages.help.onboardingPublicationStatusPlayerTitle':
    'Formation — Brouillon, publication et mises à jour LyrraStudio (onboarding)',
  'pages.help.onboardingPublicationLinksPlayerTitle':
    'Formation — Liens d’accès & partage LyrraStudio (onboarding)',
  'pages.help.onboardingCatalogVisibilityPlayerTitle':
    'Formation — Catalogue & visibilité LyrraStudio (onboarding)',
  'pages.help.onboardingEnrollmentsPlayerTitle':
    'Formation — Inscriptions & participants LyrraStudio (onboarding)',
  'pages.help.onboardingGroupsCohortsPlayerTitle':
    'Formation — Groupes & cohortes LyrraStudio (onboarding)',
  'pages.help.onboardingProgressPlayerTitle':
    'Formation — Progression & temps passé LyrraStudio (onboarding)',
  'pages.help.onboardingCertificatesPlayerTitle':
    'Formation — Certificats délivrés LyrraStudio (onboarding)',
  'pages.help.onboardingMediaLibraryPlayerTitle':
    'Formation — Bibliothèque média LyrraStudio (onboarding)',
  'dashboard.period.week': 'Semaine',
  'dashboard.period.month': 'Mois',
  'dashboard.period.quarter': 'Trimestre',
  'dashboard.vsLastPeriod': 'par rapport à la période précédente ({{period}})',
  'dashboard.completionPercent': 'Complétion %',
  'dashboard.activeLearners': 'Apprenants actifs',
  'dashboard.sessions': 'Sessions',
  'dashboard.activityType.completion': 'Terminé',
  'dashboard.activityType.enrollment': 'Inscription',
  'dashboard.activityType.milestone': 'Étape',
  'dashboard.activityType.feedback': 'Feedback',
  'dashboard.activityType.start': 'Démarrage',
  'dashboard.totalLearners': 'Total apprenants',
  'dashboard.activeEduFlows': 'Formations actives',
  'dashboard.publishedTraining': 'Formations publiées',
  'dashboard.activeUsersPeriod': 'Utilisateurs actifs (période)',
  'dashboard.completionRate': 'Taux de complétion',
  'dashboard.npsScore': 'Score NPS',
  'dashboard.topEduFlows': 'Meilleures formations',
  'dashboard.topEduFlowsHint':
    'Taux de complétion des formations les plus suivies (tri par nombre d’inscrits).',
  'dashboard.engagement': 'Engagement des apprenants',
  'dashboard.xp_earned': 'XP gagnés',
  'dashboard.streak': 'Série en cours',
  'dashboard.level': 'Niveau {{level}}',
  'dashboard.badges_earned': 'Badges obtenus',
  'dashboard.next_level': 'Prochain niveau',
  'dashboard.no_projects': 'Aucun projet pour le moment',
  'dashboard.create_first': 'Créez votre premier projet',
  'dashboard.pending_reviews': 'Évaluations en attente',
  'dashboard.upcoming_deadlines': 'Échéances à venir',

  // Podcast — liste & création d’épisodes
  'podcast.episodes.pageTitle': 'Épisodes',
  'podcast.episodes.count': '{{count}} épisode(s)',
  'podcast.episodes.newEpisode': 'Nouvel épisode',
  'podcast.episodes.searchPlaceholder': 'Rechercher par titre…',
  'podcast.episodes.filterAllStatuses': 'Tous les statuts',
  'podcast.episodes.filterAllPodcasts': 'Tous les podcasts',
  'podcast.episodes.filterDraft': 'Brouillon',
  'podcast.episodes.filterPublished': 'Publié',
  'podcast.episodes.unassignedPodcast': 'Sans podcast',
  'podcast.episodes.generateVoiceOff': 'Générer la voix off',
  'podcast.episodes.generateVoiceConfirmTitle': 'Générer la voix off ?',
  'podcast.episodes.generateVoiceConfirmMessage':
    'L’épisode « {{title}} » sera synthétisé ({{count}} segment(s)). Cela peut prendre plusieurs minutes.',
  'podcast.episodes.generateVoiceConfirmButton': 'Générer',
  'podcast.episodes.generateNoBlocks':
    'Aucun texte ni enregistrement micro sur cet épisode — ouvrez l’éditeur pour ajouter du contenu.',
  'podcast.episodes.generateSuccess': 'Voix off générée pour « {{title}} » ({{count}} segment(s)).',
  'podcast.episodes.generateError': 'Échec de la génération de la voix off.',
  'podcast.episodes.publish': 'Publier l’épisode',
  'podcast.episodes.publishConfirmTitle': 'Publier cet épisode ?',
  'podcast.episodes.publishConfirmMessage':
    '« {{title}} » sera visible pour les apprenants assignés au podcast (statut publié).',
  'podcast.episodes.publishConfirmButton': 'Publier',
  'podcast.episodes.publishSuccess': '« {{title}} » est publié.',
  'podcast.episodes.publishError': 'Échec de la publication.',
  'podcast.episodes.emptyTitle': 'Aucun épisode trouvé',
  'podcast.episodes.emptyHint': 'Créez votre premier épisode pour commencer',
  'podcast.episodes.emptyCreateCta': 'Créer un épisode',
  'podcast.episodes.backPodcasts': 'Podcasts',
  'podcast.episodes.paginationTotal': '{{total}} épisode(s) au total',
  'podcast.episodes.pageOf': 'Page {{page}} / {{totalPages}}',
  'podcast.episodes.deleteTitle': 'Supprimer l’épisode ?',
  'podcast.episodes.deleteHint': '« {{title}} » sera supprimé définitivement.',
  'podcast.episodes.deleteCancel': 'Annuler',
  'podcast.episodes.deleteConfirm': 'Supprimer',
  'podcast.episodes.statusPublished': 'Publié',
  'podcast.episodes.statusDraft': 'Brouillon',
  'podcast.episodes.noDescription': 'Aucune description',
  'podcast.episodes.noAudio': 'Pas d’audio',
  'podcast.episodes.audioReady': 'Audio prêt',
  'podcast.episodes.retry': 'Réessayer',
  'podcast.createEpisode.panelTitle': 'Nouvel épisode',
  'podcast.createEpisode.panelSubtitle':
    'Indiquez le titre et, si vous le souhaitez, une description et une image. Après validation, l’épisode est créé et l’éditeur s’ouvre pour concevoir le contenu.',
  'podcast.createEpisode.titleLabel': 'Titre',
  'podcast.createEpisode.descriptionLabel': 'Description',
  'podcast.createEpisode.coverLabel': 'Image de couverture (facultatif)',
  'podcast.createEpisode.coverHint': 'JPEG, PNG, WebP ou GIF — max 5 Mo.',
  'podcast.createEpisode.coverChoose': 'Choisir une image',
  'podcast.createEpisode.coverRemove': 'Retirer l’image',
  'podcast.createEpisode.titlePlaceholder': 'Ex. : Introduction au module sécurité',
  'podcast.createEpisode.descriptionPlaceholder': 'Résumé, objectifs… (facultatif)',
  'podcast.createEpisode.validate': 'Créer et ouvrir l’éditeur',
  'podcast.createEpisode.titleRequired': 'Le titre est obligatoire.',
  'podcast.createEpisode.error': 'Impossible de créer l’épisode.',
  'podcast.createEpisode.uploadCoverError': 'L’épisode a été créé, mais l’envoi de l’image a échoué.',
  'podcast.createEpisode.coverTooLarge': 'L’image ne doit pas dépasser 5 Mo.',
  'podcast.editor.defaultNewTitle': 'Nouvel épisode',

  // EduFlow
  'eduflow.title': 'Éditeur EduFlow',
  'eduflow.new_flow': 'Nouvelle formation',
  'eduflow.list.title': 'Formations',
  'eduflow.list.subtitle': 'Concevez vos parcours pédagogiques interactifs',
  'eduflow.list.searchPlaceholder': 'Rechercher une formation…',
  'eduflow.list.import': 'Importer',
  'eduflow.list.importHint': 'Fichier .lyrra (médias inclus) ou export JSON historique Lyrra V1.',
  'eduflow.list.importSuccess': 'Formation importée. Le designer s’ouvre.',
  'eduflow.list.importError':
    'Import impossible — vérifiez le fichier (.lyrra ou export JSON valide, au moins un bloc).',
  'eduflow.list.filterAll': 'Tous',
  'eduflow.list.filterDraft': 'Brouillons',
  'eduflow.list.filterPublished': 'Publiés',
  'eduflow.list.filterArchived': 'Archivés',
  'eduflow.list.archiveFormation': 'Archiver la formation',
  'eduflow.list.restoreFromArchive': 'Restaurer depuis les archives (brouillon)',
  'eduflow.list.archiveSuccess': 'Formation archivée',
  'eduflow.list.archiveError': 'Impossible d’archiver la formation.',
  'eduflow.list.restoreSuccess': 'Formation restaurée en brouillon',
  'eduflow.list.restoreError': 'Impossible de restaurer la formation.',
  'eduflow.list.viewModeGrid': 'Vue mosaïque',
  'eduflow.list.viewModeList': 'Vue liste',
  'eduflow.list.viewByCategory': 'Vue catégories',
  'eduflow.list.categoryBack': 'Toutes les catégories',
  'eduflow.list.categoryBrowseHint': 'Choisissez une catégorie pour afficher ses formations.',
  'eduflow.list.categoryFormationCount': '{{count}} formation(s)',
  'eduflow.list.categoryEmptyFiltered': 'Aucune formation dans cette vue (filtre ou recherche actifs).',
  'eduflow.list.uncategorizedCardHint': 'Formations sans étiquette de catégorie.',
  'eduflow.list.uncategorized': 'Sans catégorie',
  'eduflow.list.manageCategories': 'Gérer les catégories',
  'eduflow.list.categoriesPanelTitle': 'Catégories de la formation',
  'eduflow.list.noCategoryAvailable': 'Aucune catégorie disponible.',
  'eduflow.list.categoriesSaveError': 'Impossible de sauvegarder les catégories.',
  'eduflow.list.emptyFilteredTitle': 'Aucun résultat trouvé',
  'eduflow.list.emptyFilteredHint': 'Essayez de modifier vos critères de recherche ou de filtre.',
  'eduflow.list.emptyFirstTitle': 'Créez votre première formation',
  'eduflow.list.emptyFirstHint':
    "Concevez des expériences d'apprentissage interactives avec le designer visuel.",
  'eduflow.list.emptyCreateCta': 'Créer ma première formation',
  'eduflow.list.deleteToTrashTitle': 'Mettre la formation en corbeille ?',
  'eduflow.list.deleteToTrashBody':
    '« {{title}} » sera retirée de votre liste (suppression logique). Votre établissement pourra la restaurer depuis la corbeille institution.',
  'eduflow.list.deleteToTrashConfirm': 'Mettre en corbeille',
  'eduflow.createFormation.panelTitle': 'Nouvelle formation',
  'eduflow.createFormation.modeSimple': "Simple",
  'eduflow.createFormation.modeSimpleHint': "Modules, chapitres et blocs vidéo / texte / audio / quiz",
  'eduflow.createFormation.modeAdvanced': "Avancé",
  'eduflow.createFormation.modeAdvancedHint': "Designer EduFlow complet (graphe, branches, 35+ blocs)",
  'eduflow.createFormation.panelSubtitle':
    'Renseignez le titre et la description. Après validation, la formation est créée puis le designer s’ouvre pour composer le flux.',
  'eduflow.createFormation.titleLabel': 'Titre',
  'eduflow.createFormation.descriptionLabel': 'Description',
  'eduflow.createFormation.titlePlaceholder': 'Ex. : Introduction au module sécurité',
  'eduflow.createFormation.descriptionPlaceholder': 'Objectifs, public visé, etc. (facultatif)',
  'eduflow.createFormation.validate': 'Créer et ouvrir le designer',
  'eduflow.createFormation.titleRequired': 'Le titre est obligatoire.',
  'eduflow.createFormation.error': 'Impossible de créer la formation.',
  'eduflow.shareFormation.title': 'Partager la formation',
  'eduflow.shareFormation.publicLinkTitle': 'Lien public (sans connexion)',
  'eduflow.shareFormation.publicLinkCopy': 'Copier le lien',
  'eduflow.shareFormation.publicLinkCopied': 'Copié',
  'eduflow.shareFormation.publishRequired':
    'Publiez la formation pour obtenir un lien accessible sans connexion.',
  'eduflow.shareFormation.embedTitle': 'Bouton intégré et lecteur en modale',
  'eduflow.shareFormation.embedHint':
    'Collez ce bloc sur votre site. Le script ne doit figurer qu’une fois par page ; pour d’autres formations, ajoutez uniquement des boutons en dupliquant la première ligne et en changeant l’URL (data-lyrra-eduflow-url). L’URL du lecteur inclut sidebar=0 pour afficher la formation avec le sommaire replié dans la modale.',
  'eduflow.shareFormation.embedCopy': 'Copier le code HTML',
  'eduflow.shareFormation.embedCopied': 'Copié',
  'eduflow.shareFormation.embedCloseAria': 'Fermer',
  'eduflow.shareFormation.embedButtonLabel': 'Ouvrir : {{title}}',
  'eduflow.shareFormation.embedToggle': 'Intégration site web',
  'eduflow.shareFormation.removeAccessSuccess': "Accès retiré pour l'apprenant.",
  'eduflow.shareFormation.removeAccessError': "Impossible de retirer l'apprenant.",
  'eduflow.shareFormation.assignSuccess': "Formation partagée et email d'invitation envoyé.",
  'eduflow.shareFormation.assignError': "Impossible d'assigner cet apprenant.",
  'eduflow.shareFormation.createAssignSuccess': "Compte créé/lié, formation assignée et email d'invitation envoyé.",
  'eduflow.shareFormation.createLearnerError': "Impossible de créer l'apprenant.",
  'eduflow.shareFormation.selectExistingLearner': 'Sélectionner un apprenant existant',
  'eduflow.shareFormation.searchLearnerPlaceholder': 'Rechercher un apprenant (nom, prénom, email)',
  'eduflow.shareFormation.loadingLearners': 'Chargement des apprenants...',
  'eduflow.shareFormation.noLearnerAvailable': 'Aucun apprenant disponible.',
  'eduflow.shareFormation.hideQuickCreate': 'Masquer la création rapide',
  'eduflow.shareFormation.createNewLearner': 'Créer un nouvel apprenant',
  'eduflow.shareFormation.firstNameRequired': 'Prénom *',
  'eduflow.shareFormation.lastNameRequired': 'Nom *',
  'eduflow.shareFormation.emailRequired': 'Email *',
  'eduflow.shareFormation.createShareInvite': "Créer, partager et envoyer l'invitation",
  'eduflow.shareFormation.assignedLearnersCount_one': '{{count}} apprenant assigné',
  'eduflow.shareFormation.assignedLearnersCount_other': '{{count}} apprenants assignés',
  'eduflow.shareFormation.noAssignedLearner': 'Aucun apprenant assigné pour cette formation.',
  'eduflow.shareFormation.removeAccessTitle': "Retirer l'accès",
  'eduflow.shareFormation.inviteEmailsTitle': 'Inviter par e-mail (compte existant)',
  'eduflow.shareFormation.inviteEmailsHint':
    'Une ou plusieurs adresses : comptes LyrraStudio uniquement (y compris d’une autre institution). Les personnes sans compte ne sont pas ajoutées — utilisez « Créer un nouvel apprenant » pour les inviter.',
  'eduflow.shareFormation.inviteEmailsPlaceholder':
    'Une adresse par ligne ou séparées par des virgules\nexemple@lyrra.fr, autre@entreprise.com',
  'eduflow.shareFormation.inviteEmailsSubmit': 'Ajouter ces comptes à la formation',
  'eduflow.shareFormation.inviteEmailsInvalid': 'Saisissez au moins une adresse e-mail valide.',
  'eduflow.shareFormation.inviteEmailsError': 'Impossible d’ajouter les adresses.',
  'eduflow.shareFormation.inviteEmailsSummaryOk': '{{count}} compte(s) ajouté(s).',
  'eduflow.shareFormation.inviteEmailsSummaryNoAccount': '{{count}} sans compte LyrraStudio.',
  'eduflow.shareFormation.inviteEmailsSummaryAlready': '{{count}} déjà assigné(s).',
  'eduflow.participant.flowTitle': 'Parcours de formation',
  'eduflow.participant.stepWelcome': 'Bienvenue',
  'eduflow.participant.stepModule1': 'Module 1',
  'eduflow.participant.stepEvaluation': 'Évaluation',
  'eduflow.participant.stepContentPlaceholder': "Contenu interactif de l'étape affiché pour le participant.",
  'eduflow.edit_flow': 'Modifier le parcours',
  'eduflow.flow_name': 'Nom du parcours',
  'eduflow.block_palette': 'Palette de blocs',
  'eduflow.canvas': 'Canvas',
  'eduflow.properties': 'Propriétés',
  'eduflow.publish': 'Publier',
  'eduflow.publish_confirm': 'Êtes-vous sûr de vouloir publier ce parcours ?',
  'eduflow.save_draft': 'Sauvegarder le brouillon',
  'eduflow.preview': 'Aperçu',
  'eduflow.versions': 'Versions',
  'eduflow.versionHistory': 'Historique des versions',
  'eduflow.versionHistory.count': '{{count}} version(s)',
  'eduflow.versionHistory.create': 'Créer une version',
  'eduflow.versionHistory.createVersion': 'Créer v{{version}}',
  'eduflow.versionHistory.minor': 'Mineure',
  'eduflow.versionHistory.major': 'Majeure',
  'eduflow.versionHistory.minorBadge': 'Version mineure',
  'eduflow.versionHistory.majorBadge': 'Version majeure',
  'eduflow.versionHistory.latest': 'Dernière',
  'eduflow.versionHistory.public': 'Version publique',
  'eduflow.versionHistory.publicActive': 'En production',
  'eduflow.versionHistory.publish': 'Publier cette version',
  'eduflow.versionHistory.publishCta': 'Publier',
  'eduflow.versionHistory.openPublicPlayer': 'Ouvrir le player public',
  'eduflow.versionHistory.copyPublicPlayer': 'Copier le lien public',
  'eduflow.versionHistory.copyPublicPlayerError': 'Impossible de copier le lien public',
  'eduflow.versionHistory.systemPublished': 'Publication de {{version}}',
  'eduflow.versionHistory.systemSaved': 'Sauvegarde {{version}}',
  'eduflow.versionHistory.systemAutosaveRestore': 'Sauvegarde automatique avant restauration de {{version}}',
  'eduflow.versionHistory.descriptionPlaceholder': 'Description des changements…',
  'eduflow.versionHistory.empty': 'Aucune version créée.',
  'eduflow.versionHistory.emptyHint': 'Sauvegardez une version pour y accéder ici.',
  'eduflow.versionHistory.blocks': '{{count}} blocs',
  'eduflow.versionHistory.restoreTitle': 'Restaurer la version v{{version}} ?',
  'eduflow.versionHistory.restoreWarning': 'Le contenu actuel sera remplacé. Une sauvegarde automatique sera créée avant la restauration.',
  'eduflow.versionHistory.createSuccess': 'Version {{version}} créée',
  'eduflow.versionHistory.createError': 'Erreur lors de la création de la version',
  'eduflow.versionHistory.restoreSuccess': 'Version {{version}} restaurée',
  'eduflow.versionHistory.restoreError': 'Erreur lors de la restauration',
  'eduflow.versionHistory.publishSuccess': 'Version {{version}} publiée',
  'eduflow.versionHistory.publishError': 'Erreur lors de la publication',
  'eduflow.versionHistory.now': 'à l\'instant',
  'eduflow.versionHistory.minutesAgo': 'il y a {{count}} min',
  'eduflow.versionHistory.hoursAgo': 'il y a {{count}} h',
  'eduflow.versionHistory.yesterday': 'hier',
  'eduflow.versionHistory.daysAgo': 'il y a {{count}} j',
  'eduflow.add_block': 'Ajouter un bloc',
  'eduflow.remove_block': 'Supprimer le bloc',
  'eduflow.connect_blocks': 'Connecter les blocs',
  'eduflow.undo': 'Annuler',
  'eduflow.redo': 'Rétablir',
  'eduflow.zoom_in': 'Zoomer',
  'eduflow.zoom_out': 'Dézoomer',
  'eduflow.fit_view': 'Ajuster la vue',
  'eduflow.auto_layout': 'Disposition automatique',
  'eduflow.unsaved_changes': 'Modifications non sauvegardées',
  'eduflow.blocks.quiz_mcq': 'QCM',
  'eduflow.blocks.quiz_open': 'Question ouverte',
  'eduflow.blocks.video': 'Vidéo',
  'eduflow.blocks.audio': 'Audio',
  'eduflow.blocks.text': 'Texte',
  'eduflow.blocks.evaluation': 'Évaluation',
  'eduflow.blocks.form': 'Formulaire',
  'eduflow.blocks.mindmap': 'Carte mentale',
  'eduflow.blocks.poll': 'Sondage',
  'eduflow.blocks.flashcard': 'Flashcard',
  'eduflow.blocks.timeline': 'Frise chronologique',
  'eduflow.blocks.pdf': 'Document PDF',
  'eduflow.blocks.image': 'Image',
  'eduflow.blocks.code': 'Code',
  'eduflow.blocks.whiteboard': 'Tableau blanc',
  'eduflow.blocks.ai_chat': 'Chat IA',
  'eduflow.blocks.certificate': 'Certificat',
  'eduflow.blocks.attendance': 'Émargement',
  'eduflow.blocks.sortable': 'Classement',
  'eduflow.blocks.pair_matching': 'Appariement',
  'eduflow.blocks.drawing': 'Dessin',
  'eduflow.blocks.embed': 'Contenu embarqué',
  'eduflow.blocks.h5p': 'H5P',
  'eduflow.blocks.scorm': 'SCORM',
  'eduflow.blocks.conditional': 'Condition',
  'eduflow.blocks.timer': 'Minuteur',
  'eduflow.blocks.randomizer': 'Aléatoire',
  'eduflow.blocks.grouping': 'Regroupement',
  'eduflow.blocks.debate': 'Débat',
  'eduflow.blocks.roleplay': 'Jeu de rôle',
  'eduflow.blocks.simulation': 'Simulation',
  'eduflow.blocks.scenario': 'Scénario',
  'eduflow.blocks.checklist': 'Checklist',
  'eduflow.blocks.kanban': 'Kanban',
  'eduflow.blocks.peer_review': 'Évaluation par les pairs',

  // EduFlow designer (palette, canvas, barre d’outils)
  'eduflow.designer.paletteCategory.structure': 'Parcours & structure',
  'eduflow.designer.paletteCategory.content': 'Contenu & médias',
  'eduflow.designer.paletteCategory.interactive': 'Activités & évaluation',
  'eduflow.designer.paletteCategory.accessibility': 'Accessibilité (DYS)',
  'eduflow.designer.paletteCategory.enterprise': 'Conformité & avancé',
  'eduflow.designer.flowSettings.toolbarTitle': 'Paramètres formation & lecteur',
  'eduflow.designer.flowSettings.panelTitle': 'Paramètres de la formation',
  'eduflow.designer.flowSettings.intro':
    'Ces informations sont affichées côté lecteur (titre, description, visuel). Le bouton « Précédent » peut être masqué pour un parcours strictement linéaire.',
  'eduflow.designer.flowSettings.titleLabel': 'Titre de la formation',
  'eduflow.designer.flowSettings.descriptionLabel': 'Description de la formation',
  'eduflow.designer.flowSettings.coverLabel': 'Image de couverture',
  'eduflow.designer.flowSettings.coverUpload': 'Importer une image',
  'eduflow.designer.flowSettings.coverUrlPlaceholder': 'Ou coller une URL d’image (https://…)',
  'eduflow.designer.flowSettings.coverRemove': 'Retirer la couverture',
  'eduflow.designer.flowSettings.allowPreviousLabel': 'Bouton « Précédent » dans le lecteur',
  'eduflow.designer.flowSettings.allowPreviousHint':
    'Désactivé : l’apprenant ne peut pas revenir au bloc précédent (ni par raccourci clavier).',
  'eduflow.designer.flowSettings.focusGuard.title': 'Vérification de perte de focus',
  'eduflow.designer.flowSettings.focusGuard.hint':
    'Si l’apprenant quitte l’onglet ou la page, la formation se met en pause (écran grisé, reprise au clic).',
  'eduflow.designer.flowSettings.focusGuard.limitLabel': 'Sorties autorisées',
  'eduflow.designer.flowSettings.focusGuard.unlimited': 'Illimité',
  'eduflow.designer.flowSettings.focusGuard.limited': 'Limité',
  'eduflow.designer.flowSettings.focusGuard.lossesUnit': 'perte(s) de focus autorisée(s)',
  'eduflow.designer.flowSettings.focusGuard.blockWarning':
    'Au-delà de ce nombre, l’apprenant ne peut plus continuer la formation.',
  'eduflow.designer.flowSettings.coverUploadError': "Impossible d'envoyer la couverture.",
  'eduflow.designer.flowSettings.certificate.uploadError': 'Réponse serveur invalide.',
  'eduflow.designer.flowSettings.certificate.uploadFailed': "Impossible d'envoyer le modèle Word.",
  'eduflow.designer.blockLabel.start': 'Départ',
  'eduflow.designer.blockLabel.end': 'Fin',
  'eduflow.designer.blockLabel.summary': 'Récapitulatif',
  'eduflow.summary.editorHint':
    'Liste les notions ou étapes vues dans la formation. Chaque ligne apparaît comme une carte numérotée dans le lecteur.',
  'eduflow.summary.addItem': 'Ajouter un point',
  'eduflow.summary.emptyList': 'Aucun point pour l’instant. Ajoutez-en un.',
  'eduflow.summary.itemPlaceholder': 'Ex. : Les risques incendie en entreprise',
  'eduflow.designer.blockLabel.form': 'Formulaire',
  'eduflow.designer.blockLabel.loop': 'Boucle',
  'eduflow.designer.blockLabel.merge': 'Convergence',
  'eduflow.designer.blockLabel.split': 'Embranchement',
  'eduflow.designer.blockLabel.subflow': 'Sous-formation',
  'eduflow.designer.blockLabel.timer': 'Minuteur',
  'eduflow.designer.blockLabel.email': 'Email automatique',
  'eduflow.designer.blockLabel.text': 'Texte',
  'eduflow.designer.blockLabel.podcast': 'PodCast',
  'eduflow.designer.blockLabel.video': 'Vidéo',
  'eduflow.designer.blockLabel.audio': 'Audio',
  'eduflow.designer.blockLabel.image': 'Image annotée',
  'eduflow.designer.blockLabel.pdf': 'Document PDF',
  'eduflow.designer.blockLabel.browser': 'Page web intégrée',
  'eduflow.designer.blockLabel.presentation': 'Diaporama',
  'eduflow.designer.blockLabel.timeline': 'Frise chronologique',
  'eduflow.designer.blockLabel.chart': 'Graphique interactif',
  'eduflow.designer.blockLabel.mindmap': 'Carte mentale',
  'eduflow.designer.blockLabel.visual_ai': 'Visuel IA',
  'eduflow.designer.blockLabel.quiz_mcq': 'Quiz (QCM / V-F / Ordre)',
  'eduflow.designer.blockLabel.poll': 'Sondage en direct',
  'eduflow.designer.blockLabel.self_assessment': 'Auto-évaluation',
  'eduflow.selfAssessment.panelTitle': 'Auto-évaluation',
  'eduflow.selfAssessment.panelHint':
    'Définissez chaque dimension à noter : intitulé court et, si besoin, une description pour guider l’apprenant.',
  'eduflow.selfAssessment.scaleLabel': 'Échelle de notation',
  'eduflow.selfAssessment.scaleHint': 'De 1 à N (ex. 5 = Likert)',
  'eduflow.selfAssessment.criteriaSection': 'Critères',
  'eduflow.selfAssessment.addCriterion': 'Ajouter',
  'eduflow.selfAssessment.emptyCriteria':
    'Aucun critère. Ajoutez au moins une ligne pour que l’apprenant puisse s’auto-évaluer.',
  'eduflow.selfAssessment.criterionTitle': 'Intitulé',
  'eduflow.selfAssessment.criterionTitlePh': 'Ex. Communication, Autonomie…',
  'eduflow.selfAssessment.criterionDescription': 'Description (optionnelle)',
  'eduflow.selfAssessment.criterionDescriptionPh':
    'Précisez ce que l’apprenant doit observer ou comment se positionner…',
  'eduflow.selfAssessment.moveUp': 'Monter',
  'eduflow.selfAssessment.moveDown': 'Descendre',
  'eduflow.selfAssessment.removeCriterion': 'Supprimer ce critère',
  'eduflow.selfAssessment.responseTypeLabel': 'Type de réponse',
  'eduflow.selfAssessment.typeNumeric': 'Chiffres',
  'eduflow.selfAssessment.typeNumericHint': 'Boutons 1 à N',
  'eduflow.selfAssessment.typeStars': 'Étoiles',
  'eduflow.selfAssessment.typeStarsHint': 'Notation visuelle',
  'eduflow.selfAssessment.typeText': 'Texte libre',
  'eduflow.selfAssessment.typeTextHint': 'Badges (mots) ou texte libre par critère',
  'eduflow.selfAssessment.badgeOptionsLabel': 'Propositions (badges)',
  'eduflow.selfAssessment.badgeOptionsHint':
    'Mots ou courtes expressions : l’apprenant les choisit comme pastilles. Sans proposition → champ texte libre.',
  'eduflow.selfAssessment.removeBadge': 'Retirer',
  'eduflow.selfAssessment.badgeInputPlaceholder': 'Ex. Souvent, Jamais, Parfois…',
  'eduflow.selfAssessment.addBadge': 'Ajouter',
  'eduflow.selfAssessment.starCountLabel': 'Nombre d’étoiles',
  'eduflow.selfAssessment.starCountHint': 'Souvent 5',
  'eduflow.selfAssessment.textModeHint':
    'L’apprenant rédige une réponse pour chaque critère. Le commentaire global reste disponible sous les critères dans le lecteur.',
  'eduflow.designer.blockLabel.evaluation': 'Évaluation multi-questions',
  'eduflow.designer.blockLabel.phrase_reveal': 'Révélation progressive',
  'eduflow.designer.blockLabel.voice_assessment': 'Évaluation vocale',
  'eduflow.designer.blockLabel.dys_reader': 'Lecteur DYS (syllabes + TTS)',
  'eduflow.designer.blockLabel.dys_image_zones': 'Image zones interactives',
  'eduflow.designer.blockLabel.dys_clock': 'Horloge colorée DYS',
  'eduflow.designer.blockLabel.dys_reading_practice': 'Exercice lecture adaptée',
  'eduflow.designer.blockLabel.glossary': 'Glossaire + Quiz intégré',
  'eduflow.designer.blockLabel.html': 'HTML / JavaScript personnalisé',
  'eduflow.designer.blockLabel.certification': 'Certificat (template Word)',
  'eduflow.designer.blockLabel.signature': 'Signature apprenant',
  'eduflow.designer.blockLabel.facial_recognition': 'Reconnaissance faciale',
  'eduflow.designer.blockLabel.attendance': 'Émargement numérique',
  'eduflow.designer.startBlockTitle': 'Départ',
  'eduflow.designer.placeholderUntitled': 'Sans titre',
  'eduflow.designer.placeholderFlowDescription': 'Description de la formation (255 caractères max)',
  'eduflow.designer.saving': 'Enregistrement…',
  'eduflow.designer.unsaved': 'Non enregistré',
  'eduflow.designer.saved': 'Enregistré',
  'eduflow.designer.blocksPanelTitle': 'Blocs',
  'eduflow.designer.paletteSearchLabel': 'Rechercher un bloc',
  'eduflow.designer.paletteSearchPlaceholder': 'Rechercher un bloc…',
  'eduflow.designer.paletteSearchEmpty': 'Aucun bloc ne correspond à votre recherche.',
  'eduflow.designer.autoLayoutShort': 'Auto-layout',
  'eduflow.designer.validate': 'Valider',
  'eduflow.designer.validateTitle': 'Valider le parcours',
  'eduflow.designer.validationBeforePreview': 'Corrigez les erreurs du parcours avant l’aperçu.',
  'eduflow.designer.validationBeforePublish': 'Corrigez les erreurs du parcours avant de publier.',
  'eduflow.designer.aiShort': 'IA',
  'eduflow.designer.aiTitle': 'Assistant IA',
  'eduflow.designer.ragChatbotTitle': 'Chatbot formation',
  'eduflow.ragChatbot.panelTitle': 'Chatbot formation (RAG)',
  'eduflow.ragChatbot.loadError': 'Impossible de charger la configuration du chatbot.',
  'eduflow.ragChatbot.needDoc': 'Ajoutez au moins un document avant d’activer le chatbot.',
  'eduflow.ragChatbot.enabledOk': 'Chatbot activé pour le player.',
  'eduflow.ragChatbot.disabledOk': 'Chatbot désactivé.',
  'eduflow.ragChatbot.toggleError': 'Mise à jour impossible.',
  'eduflow.ragChatbot.uploadOk': 'Document indexé.',
  'eduflow.ragChatbot.uploadSavedNotIndexed':
    'Document enregistré. Il ne sera pas utilisé par le RAG tant que la base vectorielle n’est pas configurée sur le serveur.',
  'eduflow.ragChatbot.ragIndexFailed':
    'Document enregistré, mais non indexé. Vérifiez pgvector (DATABASE_VECTOR_URL) et Mistral, puis réimportez si besoin.',
  'eduflow.ragChatbot.uploadError': 'Échec de l’upload ou de l’indexation.',
  'eduflow.ragChatbot.confirmDelete': 'Supprimer ce document et son index ?',
  'eduflow.ragChatbot.deleted': 'Document supprimé.',
  'eduflow.ragChatbot.deleteError': 'Suppression impossible.',
  'eduflow.ragChatbot.vectorHint':
    'Pour indexer des PDF / TXT / MD : configurez DATABASE_VECTOR_URL ou PGVECTOR_HOST (PostgreSQL + extension pgvector) et Mistral dans Administration → Intégrations plateforme (clé + modèle d’embeddings). Sans cela, l’upload et l’indexation échouent.',
  'eduflow.ragChatbot.chatHint':
    'Les réponses et les embeddings RAG utilisent Mistral. Les documents ajoutés ici sont cherchés via embeddings Mistral + pgvector ; sans document, seul le texte des blocs du parcours sert de contexte.',
  'eduflow.ragChatbot.playerToggle': 'Afficher dans le player',
  'eduflow.ragChatbot.playerToggleHint':
    'Les apprenants posent des questions uniquement sur le contenu de cette formation : texte des blocs du parcours, et extraits des documents indexés si vous en avez ajouté.',
  'eduflow.ragChatbot.addDocument': 'Ajouter un document (PDF, TXT, MD)',
  'eduflow.ragChatbot.docCount': 'Documents indexés : {{count}}',
  'eduflow.ragChatbot.emptyDocs':
    'Aucun document. Le chatbot utilisera uniquement le texte des blocs du parcours.',
  'eduflow.ragChatbot.open': 'Ouvrir l’assistant',
  'eduflow.ragChatbot.dialogTitle': 'Assistant formation',
  'eduflow.ragChatbot.playerDisclaimer':
    'Réponses basées sur les documents et le contenu de cette formation uniquement.',
  'eduflow.ragChatbot.playerWelcome': 'Posez une question sur le cours ou les documents fournis.',
  'eduflow.ragChatbot.playerError': 'Impossible d’obtenir une réponse. Réessayez plus tard.',
  'eduflow.ragChatbot.placeholder': 'Votre question…',
  'eduflow.ragChatbot.send': 'Envoyer',
  'eduflow.designer.aiPanel.welcome':
    'Décrivez ce que vous voulez faire. Le bloc ouvert dans **Propriétés** est la cible par défaut. Utilisez la **viseur** pour capturer l’écran (canevas + formulaire) : l’IA analyse et remplit les champs via le canevas.',
  'eduflow.designer.aiPanel.askBeforeAct': 'Demander avant d’agir',
  'eduflow.designer.aiPanel.askBeforeActHint':
    'Si activé, les modifications sont proposées dans le chat avant application sur le graphe.',
  'eduflow.designer.aiPanel.captureScreenHint':
    'Capturer le canevas et le panneau Propriétés (analyse visuelle)',
  'eduflow.designer.aiPanel.captureReady': 'Capture prête — envoyez votre consigne.',
  'eduflow.designer.aiPanel.captureFailed': 'Impossible de capturer l’écran.',
  'eduflow.designer.aiPanel.captureAttached': 'Capture d’écran jointe au prochain envoi',
  'eduflow.designer.aiPanel.removeCapture': 'Retirer',
  'eduflow.designer.aiPanel.captureInMessage': 'Capture écran',
  'eduflow.designer.aiPanel.statusCapturing': 'Capture de l’interface…',
  'eduflow.designer.aiPanel.statusAnalyzing': 'Analyse de l’écran et du bloc ouvert…',
  'eduflow.designer.aiPanel.statusWorking': 'Traitement de votre demande…',
  'eduflow.designer.aiPanel.statusApplying': 'Application sur le canevas…',
  'eduflow.designer.aiPanel.captureDisclaimer':
    'L’IA peut se tromper : vérifiez le contenu généré. Collez une image (Ctrl+V) ou utilisez la capture.',
  'eduflow.designer.resizeAiPanel': 'Redimensionner le panneau assistant IA',
  'eduflow.designer.aiPanel.subtitle': 'Copilote : contenu, graphe et objectifs.',
  'eduflow.designer.aiPanel.focusContextLabel': 'Cible :',
  'eduflow.designer.aiPanel.focusPrimary': '« {{label}} » ({{type}}) — Propriétés',
  'eduflow.designer.aiPanel.focusMulti':
    '{{count}} blocs sélectionnés — précisez lequel modifier ou un seul bloc au focus.',
  'eduflow.designer.aiPanel.focusNone':
    'Aucun bloc ciblé — ouvrez Propriétés sur un nœud ou nommez un bloc dans le message.',
  'eduflow.designer.aiPanel.followUpsLabel': 'Suite suggérée',
  'eduflow.designer.aiPanel.ideasToggle': 'Idées prêtes à l’emploi',
  'eduflow.designer.aiPanel.advancedToggle': 'Options (streaming, sources, raccourcis…)',
  'eduflow.designer.aiPanel.removeDocument': 'Retirer',
  'eduflow.designer.aiPanel.importDocumentTitle': 'Importer PDF, Word ou PowerPoint',
  'eduflow.designer.aiPanel.webSourcesLabel': 'Pages web (max 3, optionnel)',
  'eduflow.designer.aiPanel.webSourcesPlaceholder': 'https://exemple.fr/doc\nhttps://…',
  'eduflow.designer.aiPanel.idea.auditPath':
    'Audite tout le graphe : impasses, nœuds isolés et ordre du parcours — propose les corrections nécessaires',
  'eduflow.designer.aiPanel.idea.microLearning':
    'À partir du parcours actuel, propose une variante courte type micro-learning tout en gardant les idées',
  'eduflow.designer.aiPanel.idea.formativeGap':
    'Insère une médiation formative (activité courte) aux endroits les plus denses cognitivement',
  'eduflow.designer.aiPanel.idea.titleCoherence':
    'Aligne les titres des blocs sur le titre du projet et le ton général décrit dans la formation',
  'eduflow.designer.aiPanel.idea.remediation':
    'Ajoute un chemin de remédiation après le principal QCM (branche échec / score bas)',
  'eduflow.designer.aiPanel.idea.mobileWording':
    'Raccourcis et clarifie les textes HTML des blocs trop longs pour lecture mobile',
  'eduflow.designer.aiPanel.idea.quizWording':
    'Unifie le style des consignes (« Choisis », « Sélectionnez », niveau formel…) sur tous les quiz',
  'eduflow.designer.aiPanel.idea.summaryBeforeEnd':
    'Insère ou rédige un bloc de synthèse juste avant le bloc Fin',
  'eduflow.designer.aiPanel.streamMode': 'Streaming',
  'eduflow.designer.aiPanel.streamModeHint':
    'Réponse affichée au fil de l’eau ; le résultat final est traité comme sans streaming.',
  'eduflow.designer.aiPanel.streamError': 'Flux IA interrompu ou refusé.',
  'eduflow.designer.aiPanel.streamIncomplete': 'Réponse incomplète depuis le serveur. Réessayez ou désactivez le streaming.',
  'eduflow.designer.aiPanel.skillPath': 'Audit du parcours (serveur)',
  'eduflow.designer.aiPanel.skillPathHint': 'Analyse structurelle du graphe ajoutée au prompt.',
  'eduflow.designer.aiPanel.skillQuiz': 'Schéma QCM strict (serveur)',
  'eduflow.designer.aiPanel.skillQuizHint': 'Gabarit officiel quiz_mcq dans le prompt.',
  'eduflow.designer.aiPanel.thinking': 'L\'assistant réfléchit…',
  'eduflow.designer.aiPanel.serverSkills': 'Renforts côté serveur',
  'eduflow.designer.aiPanel.quickActions': 'Actions rapides',
  'eduflow.designer.aiPanel.placeholder':
    'Question, idée, relecture, structure du parcours, contenu d’un bloc…',
  'eduflow.designer.aiPanel.apply': 'Appliquer au canevas',
  'eduflow.designer.aiPanel.autoApply': 'Appliquer tout seul au graphe',
  'eduflow.designer.aiPanel.autoApplyHint':
    'Les opérations structurées de l’assistant modifient le parcours sans clic sur « Appliquer ».',
  'eduflow.designer.aiPanel.dismissOps': 'Ignorer',
  'eduflow.designer.aiPanel.applied': 'Modifications appliquées au parcours.',
  'eduflow.designer.aiPanel.applyPartial': 'Appliqué avec avertissements : {{details}}',
  'eduflow.designer.aiPanel.applyFailed':
    'Aucune opération n’a pu être appliquée sur le canevas (format des opérations ou types de blocs invalides).',
  'eduflow.designer.aiPanel.objectivesSaved': 'Objectifs pédagogiques enregistrés (panneau Objectifs).',
  'eduflow.designer.aiPanel.objectivesSaveError': 'Impossible d’enregistrer les objectifs. Réessayez.',
  'eduflow.designer.aiPanel.error': 'Impossible de contacter l’assistant. Vérifiez la configuration IA (clé API) et le réseau.',
  'eduflow.designer.aiPanel.newConversation': 'Nouvelle conversation',
  'eduflow.designer.aiPanel.clearConversation': 'Vider la conversation',
  'eduflow.designer.aiPanel.conversationHistory': 'Historique',
  'eduflow.designer.aiPanel.deleteConversation': 'Supprimer',
  'eduflow.designer.aiPanel.maxConversations':
    'Vous avez atteint la limite de {{max}} conversations enregistrées. Supprimez-en une pour en créer une nouvelle.',
  'eduflow.designer.aiPanel.unnamedConversation': 'Conversation',
  'eduflow.designer.aiQuick.plan': 'Plan de parcours',
  'eduflow.designer.aiQuick.expand': 'Blocs interactifs',
  'eduflow.designer.aiQuick.quiz': 'Module QCM',
  'eduflow.designer.aiQuick.objectives': 'Objectifs (panneau)',
  'eduflow.designer.aiQuick.rewriteFocus': 'Réécrire le bloc Propriétés',
  'eduflow.designer.aiQuick.translateFocus': 'Traduire le bloc Propriétés (EN)',
  'eduflow.designer.voiceShort': 'Voix',
  'eduflow.designer.voiceTitle': 'Configuration vocale',
  'eduflow.designer.objectivesShort': 'Objectifs',
  'eduflow.designer.objectivesTitle': 'Objectifs pédagogiques',
  'eduflow.designer.flowVariablesTitle': 'Variables globales',
  'eduflow.designer.flowVariables.subtitle':
    'Définissez des paires clé / valeur. Utilisez @suivi du nom de la clé dans les titres et descriptions des blocs (ex. @nom_formation). Les valeurs sont substituées pour les apprenants.',
  'eduflow.designer.flowVariables.empty': 'Aucune variable. Ajoutez-en pour personnaliser les textes du parcours.',
  'eduflow.designer.flowVariables.keyLabel': 'Clé',
  'eduflow.designer.flowVariables.keyPlaceholder': 'ex. nom_formation',
  'eduflow.designer.flowVariables.keyHelp':
    'Lettres, chiffres, tiret et underscore uniquement. Sensible à la casse pour @cle.',
  'eduflow.designer.flowVariables.valueLabel': 'Valeur par défaut',
  'eduflow.designer.flowVariables.valuePlaceholder': 'Texte affiché à la place de @cle',
  'eduflow.designer.flowVariables.add': 'Ajouter une variable',
  'eduflow.designer.flowVariables.delete': 'Supprimer',
  'eduflow.designer.flowVariables.invalidKey': 'Chaque clé doit être renseignée et ne contenir que des caractères autorisés.',
  'eduflow.designer.flowVariables.duplicateKey': 'Deux clés identiques (sans tenir compte de la casse) ne sont pas autorisées.',
  'eduflow.designer.flowVariables.savedToast': 'Variables enregistrées',
  'eduflow.designer.flowVariables.errorToast': 'Impossible d’enregistrer les variables.',
  'eduflow.designer.flowVariables.saveNow': 'Enregistrer',
  'eduflow.designer.blockFlowVariablesHint':
    'Variables globales : utilisez @votre_cle dans le titre ou la description (configurez-les via l’icône { } dans la barre d’outils).',
  'eduflow.designer.blockFlowVariablesPreviewTitle': 'Aperçu (titres)',
  'eduflow.designer.blockFlowVariablesPreviewDescription': 'Aperçu (description)',
  'eduflow.designer.flowVariables.mentionNoMatch': 'Aucune variable ne correspond.',
  'eduflow.designer.formFieldsSlash.mentionNoMatch': 'Aucun champ ne correspond.',
  'eduflow.designer.onlineCount': '{{count}} en ligne',
  'eduflow.designer.editingBlocks_one': '{{count}} bloc en édition',
  'eduflow.designer.editingBlocks_other': '{{count}} blocs en édition',
  'eduflow.designer.invite': 'Inviter',
  'eduflow.designer.inviteTitle': 'Inviter un collaborateur',
  'eduflow.designer.exportTitle': 'Exporter le parcours',
  'eduflow.designer.importTitle': 'Importer un parcours (fichier ou collage JSON)',
  'eduflow.designer.exportNeedSavedFlow': 'Enregistrez la formation avant d’exporter un fichier .lyrra.',
  'eduflow.designer.canvasUntitled': 'Sans titre',
  'eduflow.designer.selectBlock': 'Sélectionnez un bloc',
  'eduflow.designer.selectBlockHint': 'Cliquez sur un bloc du canvas pour modifier ses propriétés',
  'eduflow.designer.blockPropertiesSubtitle': 'Propriétés du bloc',
  'eduflow.designer.textVoiceoverHint':
    'La voix off lit la description et le contenu du bloc.',
  'eduflow.designer.blockLockedReadOnly':
    'Ce bloc est verrouillé : les propriétés sont affichées en lecture seule.',
  'eduflow.designer.blockTitleField': 'Titre',
  'eduflow.designer.blockTitlePlaceholder': 'Titre du bloc',
  'eduflow.designer.newFlowDefaultTitle': 'Nouvelle formation',
  'eduflow.designer.you': 'Vous',
  'eduflow.designer.blockInfoPosition': 'ID: {{id}} | Position: ({{x}}, {{y}})',
  'eduflow.designer.toastBlockCopied': 'Bloc copié',
  'eduflow.designer.toastCollaboratorInvited': 'Collaborateur invité',
  'eduflow.designer.toastInviteError': 'Impossible d’inviter ce collaborateur',
  'eduflow.designer.createFlowError': 'Impossible de créer le parcours',
  'eduflow.designer.loadFlowError': 'Impossible de charger cette formation.',
  'eduflow.designer.loadFlowForbidden': 'Vous n’avez pas accès à cette formation.',
  'eduflow.designer.loadFlowNotFound': 'Formation introuvable ou supprimée.',
  'eduflow.duplicateFlowError': 'Impossible de dupliquer la formation.',
  'eduflow.designer.editConnection': 'Modifier la connexion',
  'eduflow.designer.deleteConnection': 'Supprimer la connexion',

  'eduflow.translation.toolbarTitle': 'Traduction du contenu',
  'eduflow.translation.toolbarShort': 'Traduction',
  'eduflow.translation.title': 'Traduction multilingue',
  'eduflow.translation.subtitle':
    'Traduisez les textes des blocs (IA) et régénérez la voix off des descriptions si besoin. Les résultats sont enregistrés par langue.',
  'eduflow.translation.subtitleV2':
    'Choisissez une voix Mistral par langue : elles servent aux voix off générées dans le player (traduction à la demande). Les jobs de traduction ci-dessous restent optionnels.',
  'eduflow.translation.voicesSection': 'Voix TTS par langue',
  'eduflow.translation.voicesSectionHint':
    'Aperçu dans la langue concernée. Sans voix choisie : « Marie - sad » pour le français, l’italien et l’espagnol ; « Paul - sad » pour les autres langues.',
  'eduflow.translation.voicesLangCount':
    '{{count}} langues (FR, EN, ES, DE, IT, PT, LB, UK, RU, AR) : choisissez une voix dans la liste, puis « Écouter » pour un court extrait.',
  'eduflow.translation.voicesLoadError':
    'Impossible de charger les voix. Vérifiez votre connexion ou réessayez.',
  'eduflow.translation.voicePreview': 'Écouter',
  'eduflow.translation.previewStop': 'Stop',
  'eduflow.translation.voicePlaceholder': '— Choisir une voix —',
  'eduflow.translation.voiceSelectAria': 'Voix pour {{lang}}',
  'eduflow.translation.voicesSaveError': 'Impossible de sauvegarder les voix.',
  'eduflow.translation.selectVoiceFirst': 'Choisissez une voix pour cette langue.',
  'eduflow.translation.previewError': 'Échec de la lecture audio.',
  'eduflow.translation.batchSection': 'Traductions précalculées (optionnel)',
  'eduflow.translation.batchSectionHint':
    'Le player peut traduire à la volée ; lancez un job seulement si vous voulez figer texte + audio en base à l’avance.',
  'eduflow.translation.sourceLang': 'Langue source du parcours',
  'eduflow.translation.bulkSection': 'Toutes les langues',
  'eduflow.translation.includeText': 'Texte',
  'eduflow.translation.includeAudio': 'Audio (voix off description)',
  'eduflow.translation.runAll': 'Traduire toutes les langues (sauf la source)',
  'eduflow.translation.runAllHint':
    'Lance un job par langue cible parmi les 11 langues supportées, en excluant la langue source.',
  'eduflow.translation.perLanguage': 'Par langue',
  'eduflow.translation.sourceBadge': 'Source',
  'eduflow.translation.runOne': 'Traduire',
  'eduflow.translation.history': 'Jobs récents',
  'eduflow.translation.noHistory': 'Aucun job pour l’instant.',
  'eduflow.translation.saveFirst': 'Enregistrez le parcours avant de lancer une traduction.',
  'eduflow.translation.prefsSaveError':
    'Impossible d’enregistrer les préférences de traduction (cases Texte / Audio par langue).',
  'eduflow.translation.jobStarted': 'Traduction lancée vers {{lang}}.',
  'eduflow.translation.jobError': 'Impossible de lancer la traduction.',
  'eduflow.translation.bulkStarted': '{{count}} traduction(s) lancée(s).',
  'eduflow.translation.bulkNone':
    'Aucun nouveau job : des traductions sont déjà en cours pour toutes les langues cibles, ou les options sont vides.',
  'eduflow.translation.bulkError': 'Impossible de lancer les traductions groupées.',
  'eduflow.translation.statusPending': 'En attente',
  'eduflow.translation.statusProcessing': 'En cours',
  'eduflow.translation.statusCompleted': 'Terminé',
  'eduflow.translation.statusFailed': 'Échec',
  'eduflow.translation.missingSourceAudioHint':
    'Traduction avec audio : générez d’abord la voix off (langue {{lang}}) pour chaque description longue. Bloc(s) concerné(s) ({{count}}) : {{blocks}}.',
  'eduflow.translation.missingSourceAudioToast':
    '{{count}} bloc(s) sans voix off dans la langue du parcours : {{blocks}}. Générez l’audio depuis les propriétés du bloc, puis relancez.',

  // Projects
  'project.title': 'Projets',
  'project.new': 'Nouveau projet',
  'project.name': 'Nom du projet',
  'project.description': 'Description du projet',
  'project.status.draft': 'Brouillon',
  'project.status.published': 'Publié',
  'project.status.archived': 'Archivé',
  'project.status.in_review': 'En révision',
  'project.visibility.public': 'Public',
  'project.visibility.private': 'Privé',
  'project.visibility.institution': 'Établissement',
  'project.no_projects': 'Aucun projet trouvé',
  'project.delete_confirm': 'Êtes-vous sûr de vouloir supprimer ce projet ?',

  // Audio
  'audio.title': 'Studio Audio',
  'audio.audiobooks': 'Livres audio',
  'audio.new_audiobook': 'Nouveau livre audio',
  'audio.record': 'Enregistrer',
  'audio.stop': 'Arrêter',
  'audio.play': 'Lecture',
  'audio.pause': 'Pause',
  'audio.volume': 'Volume',
  'audio.tracks': 'Pistes',
  'audio.add_track': 'Ajouter une piste',
  'audio.remove_track': 'Supprimer la piste',
  'audio.effects': 'Effets',
  'audio.export': 'Exporter',
  'audio.export_format': 'Format d\'export',
  'audio.duration': 'Durée',
  'audio.narrator': 'Narrateur',
  'audio.chapter': 'Chapitre',

  // Notifications
  'notification.title': 'Notifications',
  'notification.mark_all_read': 'Tout marquer comme lu',
  'notification.no_notifications': 'Aucune notification',
  'notification.new': 'Nouvelle notification',
  'notification.type.info': 'Information',
  'notification.type.success': 'Succès',
  'notification.type.warning': 'Avertissement',
  'notification.type.error': 'Erreur',

  // Settings
  'settings.title': 'Paramètres',
  'settings.general': 'Général',
  'settings.account': 'Compte',
  'settings.security': 'Sécurité',
  'settings.appearance': 'Apparence',
  'settings.notifications': 'Notifications',
  'settings.language': 'Langue',
  'settings.theme': 'Thème',
  'settings.theme.light': 'Clair',
  'settings.theme.dark': 'Sombre',
  'settings.theme.system': 'Système',
  'settings.accessibility': 'Accessibilité',
  'settings.font_size': 'Taille de police',
  'settings.high_contrast': 'Contraste élevé',
  'settings.reduced_motion': 'Mouvements réduits',
  'settings.dyslexia_mode': 'Mode dyslexie',

  // Analytics
  'analytics.title': 'Analytiques',
  'analytics.overview': 'Vue d\'ensemble',
  'analytics.learner_progress': 'Progression des apprenants',
  'analytics.engagement': 'Engagement',
  'analytics.completion': 'Complétion',
  'analytics.time_spent': 'Temps passé',
  'analytics.average_score': 'Score moyen',
  'analytics.export_report': 'Exporter le rapport',

  // Gamification
  'gamification.xp': 'XP',
  'gamification.level': 'Niveau',
  'gamification.streak': 'Série',
  'gamification.badges': 'Badges',
  'gamification.leaderboard': 'Classement',
  'gamification.achievements': 'Réalisations',
  'gamification.daily_goal': 'Objectif quotidien',
  'gamification.congratulations': 'Félicitations !',
  'gamification.new_badge': 'Nouveau badge débloqué !',
  'gamification.level_up': 'Niveau supérieur !',

  // Qualiopi
  'qualiopi.title': 'Conformité Qualiopi',
  'qualiopi.dashboard': 'Tableau de bord Qualiopi',
  'qualiopi.criteria': 'Critère {{number}}',
  'qualiopi.indicator': 'Indicateur {{number}}',
  'qualiopi.compliant': 'Conforme',
  'qualiopi.non_compliant': 'Non conforme',
  'qualiopi.partial': 'Partiellement conforme',
  'qualiopi.not_applicable': 'Non applicable',
  'qualiopi.evidence': 'Preuves',
  'qualiopi.upload_evidence': 'Téléverser une preuve',
  'qualiopi.audit_date': 'Date d\'audit',
  'qualiopi.next_audit': 'Prochain audit',
  'qualiopi.compliance_score': 'Score de conformité',
  'qualiopi.action_plan': 'Plan d\'actions',

  // V2 Training Management
  'training.plan': 'Plan de formation',
  'training.request': 'Demande de formation',
  'training.requests': 'Demandes de formation',
  'training.catalog': 'Catalogue de formations',
  'training.budget': 'Budget formation',
  'training.campaign': 'Campagne d\'entretiens',
  'training.competency': 'Référentiel de compétences',
  'training.gap_analysis': 'Analyse des écarts',
  'training.roi': 'Retour sur investissement',

  // Missions producteur (pipeline RH → formateur)
  'producerAssignments.intro':
    'Ici figurent uniquement les contenus que la RH vous a explicitement assignés : fiches catalogue (« coquilles ») à transformer en parcours dans le designer, et podcasts dont vous êtes le producteur désigné. Si les listes sont vides, aucune mission ne vous est attribuée pour l’instant.',
  'producerAssignments.errorForbidden':
    'Accès refusé : votre rôle ne permet pas d’utiliser cette page. Si vous êtes bien formateur ou RH d’établissement, contactez l’administrateur.',
  'producerAssignments.errorNoInstitution':
    'Votre compte n’a pas d’établissement rattaché dans l’application (données en base). Un administrateur doit associer votre utilisateur à l’organisation, puis vous reconnecter.',
  'producerAssignments.errorGeneric': 'Impossible de charger vos missions.',

  // Common
  'common.loading': 'Chargement...',
  'common.refresh': 'Actualiser',
  'common.saving': 'Enregistrement...',
  'common.saved': 'Enregistré',
  'common.error': 'Erreur',
  'common.success': 'Succès',
  'common.warning': 'Attention',
  'common.info': 'Information',
  'common.yes': 'Oui',
  'common.no': 'Non',
  'common.or': 'ou',
  'common.and': 'et',
  'common.all': 'Tous',
  'common.none': 'Aucun',
  'common.total': 'Total',
  'common.actions': 'Actions',
  'common.created_at': 'Créé le',
  'common.updated_at': 'Modifié le',
  'common.sort_by': 'Trier par',
  'common.per_page': 'Par page',
  'common.page': 'Page',
  'common.of': 'sur',
  'common.results': '{{count}} résultat(s)',
  'common.no_data': 'Aucune donnée',
  'common.coming_soon': 'Bientôt disponible',
  'common.beta': 'Bêta',
  'common.new': 'Nouveau',
  'common.delete_confirm': 'Cette action est irréversible. Continuer ?',
  'common.copied': 'Copié !',
  'common.drag_drop': 'Glisser-déposer',
  'common.online': 'En ligne',
  'common.offline': 'Hors ligne',
  'common.version': 'Version',

  // Player
  'player.next': 'Suivant',
  'player.previous': 'Précédent',
  'player.scrollDownHint': 'Faites défiler pour la suite',
  'player.image.clickForAnnotations': 'Cliquez sur l’image pour voir les annotations',
  'player.image.annotationNoDescription': 'Aucune description pour cette annotation.',
  'player.image.noneAvailable': 'Aucune image disponible',
  'player.visualAi.emptyTitle': 'Aucun visuel IA configuré',
  'player.visualAi.emptyHint': 'Revenez dans le concepteur pour générer ou configurer ce visuel.',
  'player.visualAi.defaultTitle': 'Visuel IA',
  'player.visualAi.subtitle': 'Explorez le diagramme : zoomez, faites défiler ou utilisez le mode plein écran.',
  'player.visualAi.openFullscreen': 'Ouvrir en plein écran',
  'player.visualAi.closeFullscreen': 'Fermer le plein écran',
  'player.visualAi.badge': 'Visuel IA',
  'player.visualAi.viewportLabel': 'Diagramme interactif — zoom et déplacement',
  'player.visualAi.toolbarLabel': 'Contrôles du diagramme',
  'player.visualAi.zoomOut': 'Zoom arrière',
  'player.visualAi.zoomIn': 'Zoom avant',
  'player.visualAi.resetZoom': 'Réinitialiser le zoom à 100 %',
  'player.visualAi.reset': '100 %',
  'player.visualAi.fitView': 'Ajuster le diagramme à la fenêtre',
  'player.visualAi.fit': 'Ajuster',
  'player.visualAi.zoomLevel': 'Zoom {{percent}} %',
  'player.visualAi.viewportHint':
    'Molette + Ctrl (ou Cmd) pour zoomer. Flèches du clavier pour vous déplacer une fois la zone focalisée. Tab pour atteindre les contrôles.',
  'player.complete': 'Terminer',
  'player.exit': 'Quitter',
  'player.fullscreen': 'Plein écran',
  'player.exitFullscreen': 'Quitter le plein écran',
  'player.webinarFollowBanner':
    'Vous suivez l’animateur — la navigation est verrouillée. Vous pouvez changer la langue d’affichage via le bouton globe.',
  'player.textBlockTipsHeading': 'Conseils',
  'player.textBlockReadDone': 'Lecture terminée',
  'player.institutionLogo': "Logo de l'organisation",
  'player.exitConfirmTitle': 'Quitter la formation ?',
  'player.exitConfirmMessage': 'Votre progression sera sauvegardée. Êtes-vous sûr de vouloir quitter ?',
  'player.focusLostTitle': 'Formation en pause',
  'player.focusLostMessage': 'Vous avez quitté la fenêtre de formation. Le temps passé est mis en pause.',
  'player.focusLostResume': 'Cliquer pour reprendre',
  'player.focusLostRemaining': 'Sorties restantes avant blocage : {{remaining}}',
  'player.focusBlockedTitle': 'Formation bloquée',
  'player.focusBlockedMessage':
    'Vous avez dépassé le nombre de sorties autorisées ({{max}}). Vous ne pouvez plus continuer cette formation.',
  'player.unblockRequestButton': 'Demander un déblocage au formateur',
  'player.unblockRequestAgain': 'Demander à nouveau',
  'player.unblockPending': 'Demande envoyée — en attente de réponse de votre formateur.',
  'player.unblockRejected': 'Votre demande de déblocage a été refusée.',
  'player.unblockLoginRequired':
    'Connectez-vous à votre compte apprenant (ou utilisez le lien personnel reçu par e-mail) pour demander un déblocage.',
  'player.unblockPublicLoginRequired':
    'En mode public sans inscription, la demande de déblocage n’est pas disponible. Remplissez le formulaire d’inscription de la formation ou connectez-vous à LyrraStudio.',
  'player.unblockRequestSent': 'Demande envoyée à votre formateur.',
  'player.unblockRequestError': "Impossible d'envoyer la demande. Réessayez.",
  'player.unblockApprovedToast': 'Votre formateur a débloqué la formation. Bonne reprise !',
  'player.registrationFormHint':
    'Ce formulaire crée votre compte apprenant pour cette formation (e-mail, prénom et nom requis).',
  'player.registrationFormSuccess':
    'Inscription enregistrée. Vous pouvez poursuivre la formation.',
  'player.registrationFormError': "Impossible d'enregistrer l'inscription.",
  'notifications.unblock.title': 'Demande de déblocage',
  'notifications.unblock.message': '{{name}} — {{flow}} ({{count}} sortie(s))',
  'notifications.unblock.unknownLearner': 'Un apprenant',
  'notifications.unblock.unknownFlow': 'Formation',
  'notifications.unblock.approve': 'Débloquer',
  'notifications.unblock.reject': 'Refuser',
  'notifications.unblock.approvedToast': 'Formation débloquée pour cet apprenant.',
  'notifications.unblock.rejectedToast': 'Demande de déblocage refusée.',
  'notifications.unblock.resolveError': 'Impossible de traiter la demande. Réessayez.',
  'player.cancel': 'Annuler',
  'player.summary': 'Sommaire',
  'player.summary.badge': 'Synthèse',
  'player.summary.noItems': 'Aucun point de récapitulatif n’a été défini pour ce bloc.',
  'player.summary.blockTranslating': 'Traduction en cours…',
  'player.summary.blockSimplifying': 'Texte simplifié en cours…',
  'player.simplifiedTextLoadingTitle': 'Préparation du texte simplifié…',
  'player.simplifiedTextLoadingHint':
    'Nous simplifions d’abord le bloc affiché, puis le reste du parcours en arrière-plan.',
  'player.contents': 'Contenu',
  'player.progress': 'Progression',
  'player.block': 'Bloc',
  'player.parallelBranches': 'Branches parallèles',
  'player.parallelBranchesTitle': 'À suivre',
  'player.parallelBranchesHint': 'Les autres options seront proposées ensuite.',
  'player.parallelBranchShowFull': 'Voir plus',
  'player.parallelBranchShowLess': 'Voir moins',
  'player.parallelBranchChoose': 'Ouvrir',
  'player.parallelBranchDismiss': 'Fermer',
  'player.parallelBranchDismissHint':
    'Revenir au bloc. Utilisez « Suivant » pour choisir une branche.',
  'player.parallelBranchesPickTitle': 'Choisissez votre prochaine voie',
  'player.parallelBranchesPickHint':
    'Les voies terminées restent cliquables pour les revoir. Parcourez chaque branche au moins une fois avant la suite commune.',
  'player.parallelBranchActionReplay': 'Refaire cette voie',
  'player.parallelBranchFromBlock': 'Depuis : {{title}}',
  'player.parallelBranchesProgress': '{{done}} / {{total}} voies complétées',
  'player.parallelBranchesOverview': 'Progression',
  'player.parallelBranchStatusDone': 'Terminée',
  'player.parallelBranchStatusTodo': 'En attente',
  'player.parallelBranchStatusNext': 'À faire maintenant',
  'player.parallelBranchAction': 'Choisir cette voie',
  'player.parallelBranchesGridLabel': 'Grille des voies parallèles',
  'player.parallelBranchesScrollHint':
    'Faites défiler vers la droite (ou utilisez les flèches) pour voir les autres voies.',
  'player.parallelBranchesPageNav': 'Pages de voies',
  'player.parallelBranchesPageIndicator': 'Page {{current}} / {{total}}',
  'player.parallelBranchesPrevPage': 'Voies précédentes',
  'player.parallelBranchesNextPage': 'Voies suivantes',
  'player.notes': 'Prendre des notes...',
  'player.noBlocks': 'Aucun bloc à afficher.',
  'player.contentTranslatingTitle': 'Adaptation du contenu à votre langue…',
  'player.contentTranslatingHint':
    'Traduction et mise en forme en cours. Cela peut prendre un peu de temps la première fois.',
  'player.contentLanguageReloadFailed':
    'Le contenu n’a pas pu être rechargé dans cette langue (délai ou erreur serveur). La version précédente reste affichée. Réessayez dans un instant ou précalculez les traductions dans le designer.',
  'player.contentLanguageSubflowHint':
    'Pour mettre à jour le texte et l’audio, terminez ou quittez la sous-partie en cours, puis choisissez à nouveau la langue.',
  'player.defaultFlowTitle': 'Formation',
  'player.docTitle.error': 'Formation indisponible',
  'player.docTitle.completed': 'Formation terminée',
  'player.docTitle.loading': 'Chargement de la formation',
  'player.docTitle.fallback': 'Formation',
  'player.initError.title': 'La formation est momentanément indisponible',
  'player.initError.loadFailed': "La formation n'a pas pu être chargée pour le moment.",
  'player.initError.loadFailedWithDetail':
    "La formation n'a pas pu être chargée pour le moment.{{detail}} Veuillez réessayer dans quelques instants.",
  'player.initError.hintsIntro': 'Vous pouvez :',
  'player.initError.hintRefresh': 'rafraîchir la page ;',
  'player.initError.hintConnection': 'vérifier votre connexion Internet ;',
  'player.initError.hintRetryLater': 'réessayer dans quelques minutes.',
  'api.error.network': 'Impossible de contacter le serveur. Vérifiez votre connexion.',
  'api.error.unexpected': 'Une erreur inattendue est survenue.',
  'api.error.badRequest': 'Requête invalide.',
  'api.error.forbidden': 'Accès interdit.',
  'api.error.notFound': 'Ressource introuvable.',
  'api.error.conflict': 'Conflit — cette ressource existe déjà.',
  'api.error.unprocessable': 'Les données envoyées ne sont pas valides.',
  'api.error.tooManyRequests': 'Trop de requêtes. Veuillez réessayer dans un instant.',
  'api.error.server': 'Erreur serveur interne.',
  'api.error.badGateway': 'Service temporairement indisponible.',
  'api.error.maintenance': 'Service en maintenance.',
  'api.error.generic': 'Erreur {{status}}',
  'player.textBlockReadMinutes': '{{count}} min de lecture',
  'player.textBlockWordCount_one': '{{count}} mot',
  'player.textBlockWordCount_other': '{{count}} mots',
  'player.textBlockNoContent': 'Aucun contenu disponible.',
  'player.blockUnknown': 'Bloc inconnu',
  'player.objectivesPanelTitle': 'Objectifs pédagogiques',
  'player.objectivesOpenLabel': 'Voir les objectifs pédagogiques',
  'player.objectivesCloseLabel': 'Fermer le panneau des objectifs',
  'player.objectivesAchievementRate': "Taux d'atteinte",
  'player.objectivesAchievedCount': '{{achieved}}/{{total}} objectifs atteints',
  'player.objectivesNoneDefined': 'Aucun objectif défini pour cette formation.',
  'player.objectiveCategory.savoir': 'Savoir',
  'player.objectiveCategory.savoirFaire': 'Savoir-faire',
  'player.objectiveCategory.savoirEtre': 'Savoir-être',
  'player.objectiveLevel.connaissance': 'Connaissance',
  'player.objectiveLevel.comprehension': 'Compréhension',
  'player.objectiveLevel.application': 'Application',
  'player.objectiveLevel.analyse': 'Analyse',
  'player.objectiveLevel.synthese': 'Synthèse',
  'player.objectiveLevel.evaluation': 'Évaluation',
  'player.objectiveBloom': 'Bloom · {{level}}',
  'player.objectiveRequired': 'Obligatoire',
  'player.objectiveLinkedBlocks': 'Blocs associés',
  'player.objectiveCriterionScore': 'Score ≥ {{min}} %',
  'player.objectiveCriterionCompletion': 'Complétion du bloc',
  'player.objectiveNoLinkedBlocks':
    'Aucun bloc lié — la progression globale ne calcule pas cet objectif.',
  'player.completion.congratulations': '🎉 Félicitations !',
  'player.completion.finished': '📋 Parcours terminé',
  'player.completion.youCompleted': 'Vous avez terminé',
  'player.completion.passed': 'Réussi ✓',
  'player.completion.failed': 'Non validé ✗',
  'player.completion.requiredScore': 'Seuil requis : {{score}}%',
  'player.completion.totalTime': 'Temps total',
  'player.completion.blocksCompleted': 'Blocs complétés',
  'player.completion.quizPassed': 'Quiz réussis',
  'player.completion.successRate': 'Taux de réussite',
  'player.completion.blockCompleted': 'Complété ✓',
  'player.completion.blockFailed': 'Échoué ✗',
  'player.completion.blockSkipped': 'Ignoré',
  'player.completion.detail': '📊 Détail par bloc',
  'player.completion.type': 'Type',
  'player.completion.timeSpent': 'Temps passé',
  'player.completion.downloadCertificate': '📄 Télécharger le certificat',
  'player.completion.certificateSoon': 'Le certificat sera disponible prochainement.',
  'player.completion.restart': '🔄 Recommencer',
  'player.completion.viewDetails': '📊 Voir les détails',
  'player.completion.backToDashboard': '← Retour au tableau de bord',
  'player.completion.embedDismissHint':
    'Vous avez terminé ce module. Pour quitter, utilisez la croix en haut à droite de la fenêtre ou le bouton ci-dessous.',
  'player.completion.embedDismissButton': 'Fermer la fenêtre',
  'player.previewBanner': 'Mode aperçu — cette vue reflète les vraies données de votre formation',
  'player.closePreview': "Fermer l'aperçu",
  'player.flowPreviewMap': 'Schéma du parcours',
  'player.flowPreviewTitle': 'Aperçu du flux',
  'player.flowPreviewHint':
    'Vue statique (comme dans le designer). Le cadre orange indique le bloc affiché dans le lecteur.',
  'player.flowPreviewLegendCurrent': 'Bloc affiché',
  'player.flowPreviewClose': 'Fermer',
  'player.toggleSidebarShow': 'Afficher le panneau',
  'player.toggleSidebarHide': 'Masquer le panneau',
  'player.mobileSummary': 'Sommaire',
  'player.mobileObjectives': 'Objectifs',
  'player.mobileMoreOptions': "Plus d'options",
  'player.sidebarExpand': 'Développer le panneau',
  'player.sidebarCollapse': 'Réduire le panneau',
  'player.keyboardShortcuts': 'Raccourcis clavier',
  'player.keyboardShortcutPrev': 'Bloc précédent',
  'player.keyboardShortcutNext': 'Bloc suivant',
  'player.keyboardShortcutsHint':
    'Activez « Navigation au clavier » dans le centre d’accessibilité (bouton en bas à droite). Inactif dans les champs de saisie.',
  'player.a11yCenter': "Centre d'accessibilité",
  'player.reviewSummary': 'Voir le récapitulatif',
  'player.viewResults': 'Voir les résultats',
  'player.submitFirst': "Soumettre d'abord",
  'player.phraseRevealCompleteFirst': 'Parcourez toutes les phrases',
  'player.phraseReveal.emptyBlock': 'Aucune phrase configurée pour ce bloc.',
  'player.phraseReveal.revealNextAria':
    'Cliquez ou appuyez sur Espace pour révéler la phrase suivante',
  'player.phraseReveal.lastPhraseHint':
    'Dernière phrase — utilisez « Suivant » pour continuer le parcours.',
  'player.phraseReveal.morePhrasesHint': 'Clic, Espace ou flèche droite pour la suite',
  'player.phraseReveal.previousPhrases': 'Phrases précédentes ({{count}})',
  'player.certificateUnavailable': 'Le certificat sera disponible prochainement.',
  'player.browser.embeddedContentTitle': 'Contenu intégré',
  'player.browser.noValidUrl': 'Aucune URL valide',
  'player.browser.enterHttpsHint':
    'Saisissez une adresse commençant par https:// (ou http://) dans les paramètres du bloc.',
  'player.browser.loadingContent': 'Chargement du contenu…',
  'player.browser.defaultPageTitle': 'Page web intégrée',
  'player.browser.continue': 'Continuer',
  'player.browser.stepSaved': 'Étape enregistrée',
  'player.browser.fullscreen': 'Plein écran',
  'player.browser.exitFullscreen': 'Quitter le plein écran',
  'player.podcast.defaultShowTitle': 'Podcast',
  'player.podcast.defaultEpisode': 'Épisode',
  'player.podcast.loadError': 'Impossible de charger le podcast.',
  'player.podcast.loadingEpisodes': 'Chargement des épisodes…',
  'player.podcast.playAll': 'Tout écouter',
  'player.podcast.nextEpisode': 'Épisode suivant',
  'player.podcast.episodesHeading': 'Épisodes',
  'player.podcast.noEpisodes': 'Aucun épisode.',
  'player.podcast.noAudio': "Pas d'audio",
  'player.podcast.chainPlayback': 'Lecture en chaîne · {{current}}/{{total}}',
  'player.podcast.selectOrPlayAll':
    'Sélectionnez un épisode dans la liste ou lancez « Tout écouter ».',
  'player.podcast.audioUnavailable': 'Audio non disponible pour cet épisode.',
  'player.podcast.showEpisodeText': 'Afficher le texte',
  'player.podcast.hideEpisodeText': 'Masquer le texte',
  'player.podcast.episodeCount_one': '{{count}} épisode',
  'player.podcast.episodeCount_other': '{{count}} épisodes',
  'player.podcast.showChapters': 'Afficher les chapitres',
  'player.podcast.hideChapters': 'Masquer les chapitres',
  'player.podcast.coverDialogLabel': 'Couverture : {{title}}',
  'player.podcast.coverEnlargePodcast': 'Agrandir la pochette du podcast',
  'player.podcast.coverEnlargeEpisode': "Agrandir la pochette de l'épisode",
  'player.podcast.coverEnlarge': 'Agrandir la pochette',
  'player.audio.play': 'Lecture',
  'player.audio.pause': 'Pause',
  'player.audio.mute': 'Couper le son',
  'player.audio.unmute': 'Réactiver le son',
  'player.audio.seek': 'Position dans la piste audio',
  'player.audio.playbackSpeed': 'Vitesse de lecture',
  'player.audio.completed': 'Piste audio terminée',
  'player.audio.subtitlesAria': 'Texte synchronisé avec la lecture',
  'player.audio.subtitlesWaiting':
    'Le texte défilera ici pendant la lecture (segment selon la position).',
  'player.audio.subtitlesNeedText':
    'Ajoutez du texte (TTS ou contenu du bloc) pour afficher les segments ici.',
  'player.audio.subtitlesHintSr':
    'Synchronisation approximative : le texte est réparti sur la durée audio, sans timecodes mot à mot.',
  'player.timeline.defaultTitle': 'Frise chronologique',
  'player.timeline.subtitle': 'Frise chronologique à parcourir.',
  'player.timeline.eventFallback': 'Événement',
  'player.timeline.voiceoverLabel': 'Voix off',
  'player.timeline.playVoiceover': 'Lire la voix off',
  'player.timeline.pauseVoiceover': 'Pause voix off',
  'player.timeline.generateVoiceover': 'Générer la voix off',
  'player.timeline.showDetail': 'Voir le détail',
  'player.timeline.hideDetail': 'Masquer le détail',
  'player.timeline.emptyTitle': 'Aucun événement sur cette frise',
  'player.timeline.emptyHint': 'Ajoutez des étapes dans le panneau de configuration du bloc.',
  'player.timeline.voiceoverUnavailableTitle': 'Voix off indisponible',
  'player.timeline.voiceoverUnavailableDesc':
    'Ouvrez ce parcours depuis une formation connectée pour générer l’audio.',
  'player.timeline.voiceoverTextTooShortTitle': 'Texte trop court',
  'player.timeline.voiceoverTextTooShortDesc':
    'Ajoutez une description (au moins 20 caractères) ou un titre plus détaillé pour la voix off.',
  'player.timeline.voiceoverGenerationErrorTitle': 'Génération voix off impossible',
  'player.timeline.voiceoverSlowTitle': 'Voix off',
  'player.timeline.voiceoverSlowDesc':
    'La génération prend plus de temps que prévu. Réessayez dans un instant.',
  'player.timeline.retryLater': 'Réessayez plus tard.',
  'player.timeline.openFullscreen': 'Ouvrir en grand',
  'player.timeline.openFullscreenAria': 'Ouvrir la frise en plein écran',
  'player.timeline.closeFullscreenAria': 'Fermer le plein écran',
  'player.timeline.reviewed': 'Chronologie consultée',
  'player.video.seek': 'Position dans la vidéo',
  'player.video.subtitlesShow': 'Afficher les sous-titres',
  'player.video.subtitlesHide': 'Masquer les sous-titres',
  'player.video.trackLabel': 'Sous-titres',
  'player.video.completed': 'Vidéo terminée',
  'player.video.markWatched': 'J’ai regardé la vidéo',
  'player.video.defaultIframeTitle': 'Vidéo intégrée',
  'player.podcast.playAllAria': 'Tout écouter « {{title}} » — {{count}} épisode(s) avec audio',
  'player.podcast.skipNextQueuedAria': 'Épisode suivant dans la file de lecture',
  'player.podcast.playEpisodeAria': 'Lecture de l’épisode : {{title}}',
  'player.podcast.episodeNoAudioAria': 'Épisode sans fichier audio : {{title}}',
  'player.renderError': 'Erreur lors du rendu du bloc : {{blockType}}',
  'player.blocks.defaultWelcome': 'Bienvenue dans cette formation',
  'player.blocks.defaultEndTitle': 'Félicitations !',
  'player.quiz.selectCorrectAnswer': 'Sélectionnez la bonne réponse :',
  'player.quiz.correctAnswer': '✓ Bonne réponse !',
  'player.quiz.incorrectAnswer': '✗ Mauvaise réponse.',
  'player.blockTimer.aria': 'Temps pour répondre à ce bloc',
  'player.blockTimer.modeShortCountup': 'Minuteur',
  'player.blockTimer.modeShortCountdown': 'Décompte',
  'player.form.successMessage': 'Merci pour votre réponse !',
  'player.form.noFieldsConfigured': "Ce formulaire n'a pas encore de champs configurés.",
  'player.selfAssessment.empty': 'Aucun critère n’est défini pour cette auto-évaluation.',
  'player.selfAssessment.continue': 'Continuer',
  'player.selfAssessment.pick': 'Choisissez une note',
  'player.selfAssessment.rateAria': 'Note {{n}} : {{label}}',
  'player.selfAssessment.criterionFallback': 'critère',
  'player.selfAssessment.commentLabel': 'Commentaire (optionnel)',
  'player.selfAssessment.commentPlaceholder': 'Réflexions personnelles, axes de progrès…',
  'player.selfAssessment.submitted': 'Réponses enregistrées.',
  'player.selfAssessment.submit': 'Valider mon auto-évaluation',
  'player.selfAssessment.textAnswerPlaceholder': 'Votre réponse…',
  'player.selfAssessment.pickBadges': 'Sélectionnez une ou plusieurs propositions :',
  'player.evaluation.commentsLabel': 'Commentaires (optionnel)',
  'player.evaluation.commentsPlaceholder': 'Partagez votre avis...',
  'player.evaluation.submitted': 'Évaluation soumise',
  'player.evaluation.submit': "Soumettre l'évaluation",
  'player.time.lessThanMinute': "Moins d'une minute",
  'player.time.oneMinute': '1 minute',
  'player.time.minutes': '{{count}} minutes',
  'player.time.shortSeconds': '{{count}}s',
  'player.time.shortMinutes': '{{count}}min',
  'player.time.shortMinutesSeconds': '{{minutes}}min {{seconds}}s',
  'player.completion.scoreLabel': 'Score',
  'accessibility.openCenter': "Ouvrir le centre d'accessibilité",
  'accessibility.center': 'Accessibilité',
  'accessibility.modalTitle': 'Centre de lecture adaptée',
  'accessibility.modalDescription': "Ajustez l'interface pour la malvoyance, les troubles dys et la lecture confortable.",
  'accessibility.closeCenter': "Fermer le centre d'accessibilité",
  'accessibility.accessPad.connected': 'Lyrra Access Pad connecté',
  'accessibility.accessPad.playGuide': 'Écouter le guide LyrraPad',
  'accessibility.accessPad.pauseGuide': 'Mettre en pause le guide LyrraPad',
  'accessibility.presets': 'Profils préconfigurés',
  'accessibility.preset.standard.label': 'Standard',
  'accessibility.preset.standard.desc': "Rendu par défaut de l'application.",
  'accessibility.preset.lowVision.label': 'Malvoyance',
  'accessibility.preset.lowVision.desc': 'Contraste renforcé, texte agrandi et repères visuels plus nets.',
  'accessibility.preset.dys.label': 'Dys',
  'accessibility.preset.dys.desc': "Lecture facilitée avec plus d'espace et une police plus lisible.",
  'accessibility.preset.cumulHint': 'Combinez plusieurs profils si besoin',
  'accessibility.preset.clickToAdd': 'Cliquer pour ajouter',
  'accessibility.preset.clickToRemove': 'Cliquer pour retirer',
  'accessibility.typography': 'Réglages typographiques',
  'accessibility.fontScale': 'Taille du texte',
  'accessibility.lineHeight': 'Interlignage',
  'accessibility.letterSpacing': 'Espacement des lettres',
  'accessibility.wordSpacing': 'Espacement des mots',
  'accessibility.visualAids': 'Aides visuelles',
  'accessibility.highContrast': 'Contraste renforcé',
  'accessibility.highContrastDesc': 'Renforce les contrastes de lecture, les bordures et les repères visuels.',
  'accessibility.underlineLinks': 'Liens soulignés',
  'accessibility.underlineLinksDesc': 'Affiche plus clairement les liens et zones interactives.',
  'accessibility.dyslexiaFont': 'Police de lecture facilitée',
  'accessibility.dyslexiaFontDesc': 'Utilise une police plus confortable pour les personnes dys et les lecteurs fatigués.',
  'accessibility.reducedMotion': 'Mouvements réduits',
  'accessibility.reducedMotionDesc': "Diminue les animations et transitions pour limiter l'inconfort visuel.",
  'accessibility.mouseMagnifier': 'Loupe de lecture',
  'accessibility.mouseMagnifierDesc':
    'Affiche une loupe circulaire qui suit le curseur et grossit le contenu du parcours sous la zone pointée (affichage agrandi en lecture seule).',
  'accessibility.readingMask': 'Masque de lecture',
  'accessibility.readingMaskDesc':
    'Une bande opaque suit le curseur pour isoler la ligne en cours — très utile pour la dyslexie et les TDA/H. Astuce : Option ⌥ + touche =/+ ou Option ⌥ + ↑/↓ (macOS) ; Alt + + / Alt − (Windows/Linux) pour ajuster la hauteur.',
  'accessibility.speakOnSelection': 'Synthèse vocale sur sélection',
  'accessibility.speakOnSelectionDesc':
    "Sélectionnez du texte sur la page : un bouton apparaît pour le lire à voix haute à la demande (différent du mode lecture audio automatique).",
  'accessibility.speakSelection.listen': 'Écouter la sélection',
  'accessibility.speakSelection.stop': 'Arrêter la lecture',
  'accessibility.speakSelection.loading': 'Génération…',
  'accessibility.speakSelection.retry': 'Réessayer',
  'accessibility.speakSelection.error': 'Lecture indisponible, réessayez dans quelques instants.',
  'accessibility.speakSelection.region': 'Lire la sélection à voix haute',
  'a11y.voiceGuide.title': 'Assistant vocal accessibilité',
  'a11y.help.shortcuts': 'Raccourcis clavier',
  'a11y.help.kbdEsc': 'Fermer le panneau / arrêter la synthèse vocale',
  'a11y.help.kbdMask': 'Ajuster la hauteur du masque de lecture (masque activé)',
  'a11y.help.kbdMaskMac': 'Touche = ou + (au-dessus des chiffres), ou flèches ↑ ↓ avec Option ⌥',
  'a11y.help.kbdMaskOther': 'Windows / Linux : Alt + + / Alt + −',
  'a11y.help.kbdTab': 'Naviguer entre les éléments interactifs',
  'a11y.help.tips': 'Conseils',
  'a11y.voiceGuide.subtitle': "Décrivez vos besoins, j'active les bonnes options pour vous.",
  'a11y.voiceGuide.cta.title': 'Décrivez vos besoins',
  'a11y.voiceGuide.cta.hint': "Parlez : l'IA active automatiquement les bonnes options pour vous.",
  'a11y.voiceGuide.cta.openLabel': "Ouvrir l'assistant vocal — décrire mes besoins d'accessibilité à voix haute",
  'a11y.voiceGuide.cta.start': "Démarrer l'assistant vocal",
  'a11y.voiceGuide.cta.stop': "Arrêter d'écouter",
  'a11y.voiceGuide.status': 'Statut',
  'a11y.voiceGuide.statusActive': 'En cours',
  'a11y.voiceGuide.youSaid': 'Vous avez dit',
  'a11y.voiceGuide.reformulation': "Voici ce que j'ai compris",
  'a11y.voiceGuide.applied': 'Réglages appliqués',
  'a11y.voiceGuide.resetButton': 'Remettre en normal',
  'a11y.voiceGuide.resetExplanation': "Tous les réglages d'accessibilité ont été remis par défaut.",
  'a11y.voiceGuide.problem': 'Problème',
  'a11y.voiceGuide.micReady': 'Micro prêt',
  'a11y.voiceGuide.micUnsupported': 'Reconnaissance vocale non supportée',
  'a11y.voiceGuide.stage.idle': 'Prêt à vous écouter',
  'a11y.voiceGuide.stage.intro': 'Je vous explique…',
  'a11y.voiceGuide.stage.listening': 'Je vous écoute. Parlez librement.',
  'a11y.voiceGuide.stage.thinking': 'Analyse de vos besoins…',
  'a11y.voiceGuide.stage.confirmPlaying': 'Je résume…',
  'a11y.voiceGuide.stage.confirmListening': 'Dites « oui » pour confirmer ou « non » pour recommencer.',
  'a11y.voiceGuide.stage.applying': 'Application de vos réglages…',
  'a11y.voiceGuide.stage.done': "C'est fait !",
  'a11y.voiceGuide.stage.error': 'Une erreur est survenue.',
  'a11y.voiceGuide.error.mic': "Impossible d'accéder au micro.",
  'a11y.voiceGuide.error.micDenied': "L'accès au microphone a été refusé. Autorisez-le dans les paramètres du navigateur.",
  'a11y.voiceGuide.error.stt': 'Reconnaissance vocale non disponible sur ce navigateur.',
  'a11y.voiceGuide.error.ai': 'Analyse IA indisponible.',
  'a11y.voiceGuide.error.serverUnavailable':
    'Le serveur Lyrra ne répond pas correctement (souvent : backend non déployé ou erreur de build). Vérifiez le déploiement, puis réessayez.',
  'a11y.voiceGuide.error.generic': "Quelque chose s'est mal passé. Réessayez ou ajustez les réglages manuellement.",
  'accessibility.alwaysOn': 'Améliorations actives en continu',
  'accessibility.alwaysOnFocus': 'Focus clavier plus visible',
  'accessibility.alwaysOnLinks': 'Liens et éléments interactifs plus détectables',
  'accessibility.alwaysOnSaved': 'Réglages sauvegardés automatiquement sur cet appareil',
  'accessibility.navigation': 'Navigation',
  'accessibility.keyboardNavigation': 'Navigation au clavier',
  'accessibility.keyboardNavigationDesc':
    'Utilisez les flèches gauche / haut pour le bloc précédent et droite / bas pour le bloc suivant (comme les boutons du bas).',
  // ── Learner Dashboard ──────────────────────────────────
  'learner.header.workspace': 'Espace apprenant',
  'learner.tabs.home': 'Accueil',
  'learner.tabs.parcours': 'Mes Parcours',
  'learner.tabs.formations': 'Mes Formations',
  'learner.tabs.publicFormations': 'Formations publiques',
  'learner.tabs.webinars': 'Webinaires',
  'learner.tabs.podcasts': 'Podcasts',
  'learner.tabs.documents': 'Documents',
  'learner.tabs.progress': 'Ma Progression',
  'learner.tabs.profile': 'Profil',
  'learner.sidebar.tagline': 'Apprenant',
  'learner.sidebar.webinars': 'Webinaires',
  'learner.webinars.title': 'Mes webinaires',
  'learner.webinars.loading': 'Chargement de vos webinaires…',
  'learner.webinars.empty':
    'Aucun webinaire pour le moment. Les invitations reçues et les sessions où vous êtes animateur apparaîtront ici.',
  'learner.webinars.sectionInvited': 'Invitations reçues',
  'learner.webinars.sectionPresenting': 'Mes sessions en tant qu’animateur',
  'learner.webinars.emptyInvited': 'Aucune invitation pour le moment.',
  'learner.webinars.emptyPresenting': 'Vous n’êtes animateur sur aucun webinaire.',
  'learner.webinars.rolePresenter': 'Animateur',
  'learner.webinars.joinAsPresenter': 'Ouvrir la salle (animateur)',
  'learner.webinars.join': 'Rejoindre la salle',
  'learner.webinars.invitedOn': 'Invité le',
  'learner.webinars.scheduledFor': 'Prévu le',
  'learner.webinars.duration': 'Durée : {{duration}}',
  'learner.webinars.loadError': 'Impossible de charger les webinaires.',
  'learner.publicFormations.title': 'Formations publiques',
  'learner.publicFormations.subtitle':
    'Formations en accès public proposées par votre établissement.',
  'learner.publicFormations.loading': 'Chargement des formations publiques…',
  'learner.publicFormations.loadError': 'Impossible de charger les formations publiques.',
  'learner.publicFormations.empty': 'Aucune formation ne correspond à votre recherche.',
  'learner.publicFormations.emptyNoInstitution':
    'Aucune formation publique disponible pour votre établissement pour le moment.',
  'learner.publicFormations.searchPlaceholder': 'Rechercher une formation…',
  'learner.publicFormations.open': 'Ouvrir la formation',
  'learner.webinars.status.draft': 'Brouillon',
  'learner.webinars.status.scheduled': 'Planifié',
  'learner.webinars.status.live': 'En direct',
  'learner.webinars.status.ended': 'Terminé',
  'learner.sidebar.myProfile': 'Mon profil',
  'learner.sidebar.logout': 'Déconnexion',
  'learner.sidebar.collapse': 'Réduire le menu',
  'learner.sidebar.expandAria': 'Agrandir la barre latérale',
  'learner.sidebar.collapseAria': 'Réduire la barre latérale',
  'learner.sidebar.backToTrainer': 'Tableau de bord formateur',
  'learner.sidebar.backToTrainerAria': 'Retour au tableau de bord formateur',
  'learner.home.defaultName': 'apprenant·e',
  'learner.home.greeting': 'Bonjour {{name}},',
  'learner.home.greetingSubtitle':
    'Reprenez votre apprentissage là où vous vous êtes arrêté·e.',
  'learner.home.resumeMyFormation': 'Reprendre ma formation',
  'learner.home.globalProgress': 'Progression globale',
  'learner.home.learningTime': "Temps d'apprentissage",
  'learner.home.badges': 'Badges obtenus',
  'learner.home.calendarKicker': 'Ma progression',
  'learner.home.quickAccess': 'Accès rapides',
  'learner.learningPath': 'Parcours',
  'learner.home.resumeTraining': 'Reprendre vos formations',
  'learner.home.viewAll': 'Voir tout →',
  'learner.home.trainingDomains': 'Domaines de formation',
  'learner.home.courses': 'cours',
  'learner.home.episodes': 'épisodes',
  'learner.home.recommendedPodcasts': 'Podcasts recommandés',
  'learner.home.explore': 'Explorer →',
  'learner.formations.title': 'Mes Formations',
  'learner.formations.searchPlaceholder': 'Rechercher une formation…',
  'learner.formations.all': 'Tous',
  'learner.formations.formation': 'Formation',
  'learner.formations.inProgress': 'En cours',
  'learner.formations.completed': 'Terminé',
  'learner.formations.notStarted': 'Non commencé',
  'learner.formations.loading': 'Chargement de vos formations...',
  'learner.formations.empty': 'Aucune formation trouvée.',
  'learner.formations.progress': 'Progression',
  'learner.formations.instructor': 'Formateur',
  'learner.formations.opening': 'Ouverture...',
  'learner.formations.review': 'Revoir',
  'learner.formations.start': 'Commencer',
  'learner.formations.continue': 'Continuer',
  'learner.podcasts.title': 'Explorer les Podcasts',
  'learner.podcasts.searchPlaceholder': 'Rechercher un podcast…',
  'learner.podcasts.all': 'Tous',
  'learner.podcasts.assigned': 'Podcast assigné',
  'learner.podcasts.defaultCategory': 'Podcast',
  'learner.podcasts.loading': 'Chargement de vos podcasts...',
  'learner.podcasts.empty': 'Aucun podcast assigné.',
  'learner.podcasts.episodes': 'épisodes',
  'learner.podcasts.opening': 'Ouverture...',
  'learner.podcasts.listen': 'Écouter',
  'learner.documents.title': 'Documents partagés',
  'learner.documents.loading': 'Chargement de vos documents...',
  'learner.documents.view': 'Consulter le document',
  'learner.documents.empty': 'Aucun document partagé.',
  'learner.documents.personalTitle': 'Mes documents personnels',
  'learner.documents.personalHint': 'Vos documents personnels ne sont visibles que par vous. Cliquez sur un document pour le consulter et poser des questions à l\'assistant IA.',
  'learner.documents.personalEmpty': 'Aucun document personnel. Déposez un PDF pour commencer.',
  'learner.documents.upload': 'Déposer un document',
  'learner.documents.delete': 'Supprimer',
  'learner.documents.deleteConfirmTitle': 'Supprimer ce document ?',
  'learner.documents.deleteConfirmDesc': 'Cette action est irréversible. Le document et son analyse IA seront supprimés.',
  'learner.documents.cancel': 'Annuler',
  'learner.documents.confirmDelete': 'Supprimer',
  'learner.documents.chatReady': 'Chat disponible',
  'learner.documents.processing': 'Analyse en cours…',
  'learner.documents.backToList': 'Retour aux documents',
  'learner.documents.chatTitle': 'Posez vos questions sur ce document',
  'learner.documents.chatPlaceholder': 'Posez une question sur ce document…',
  'learner.documents.chatPlaceholderHint': 'Posez n\'importe quelle question sur ce document. L\'assistant ne répondra qu\'à partir de son contenu.',
  'learner.documents.chatError': 'Une erreur est survenue.',
  'learner.documents.preview': 'Aperçu',
  'learner.documents.indexing': 'Analyse de votre document en cours…',
  'learner.progress.title': 'Ma Progression',
  'learner.progress.completedFormations': 'Formations terminées',
  'learner.progress.trainingHours': 'Heures de formation',
  'learner.progress.listenedPodcasts': 'Podcasts écoutés',
  'learner.progress.earnedBadges': 'Badges obtenus',
  'learner.progress.weeklyActivity': 'Activité de la semaine',
  'learner.progress.badges': 'Badges',
  'learner.progress.recentActivity': 'Activité récente',
  'learner.progress.rarity.common': 'Commun',
  'learner.progress.rarity.rare': 'Rare',
  'learner.progress.rarity.epic': 'Épique',
  'learner.progress.rarity.legendary': 'Légendaire',
  'learner.progress.days.mon': 'Lun',
  'learner.progress.days.tue': 'Mar',
  'learner.progress.days.wed': 'Mer',
  'learner.progress.days.thu': 'Jeu',
  'learner.progress.days.fri': 'Ven',
  'learner.progress.days.sat': 'Sam',
  'learner.progress.days.sun': 'Dim',
  'learner.profile.title': 'Mon Profil',
  'learner.profile.defaultName': 'Apprenant',
  'learner.profile.defaultEmail': 'apprenant@exemple.com',
  'learner.profile.memberSince': 'Membre depuis janvier 2024',
  'learner.profile.editProfile': 'Modifier le profil',
  'learner.profile.formations': 'Formations',
  'learner.profile.hours': 'Heures',
  'learner.profile.podcasts': 'Podcasts',
  'learner.profile.badges': 'Badges',
  'learner.profile.preferences': 'Préférences',
  'learner.profile.theme': 'Thème',
  'learner.profile.themeLight': 'Clair',
  'learner.profile.themeDark': 'Sombre',
  'learner.profile.themeSystem': 'Système',
  'learner.profile.language': 'Langue',
  'learner.profile.notifications': 'Notifications',
  'learner.profile.fontSize': 'Taille du texte',
  'learner.profile.fontNormal': 'Normal',
  'learner.profile.fontLarge': 'Grand',
  'learner.profile.highContrast': 'Contraste élevé',
  'learner.profile.a11ySection': 'Accessibilité',
  'learner.profile.dangerZone': 'Zone de danger',
  'learner.profile.logoutBtn': 'Se déconnecter',
  'learner.parcours.title': 'Mes Parcours',
  'learner.parcours.assigned': 'parcours assigné(s)',
  'learner.parcours.loading': 'Chargement de vos parcours…',
  'learner.parcours.loadError': 'Impossible de charger vos parcours.',
  'learner.parcours.noAssigned': 'Aucun parcours assigné',
  'learner.parcours.noAssignedDesc': 'Votre formateur vous assignera des parcours ici.',
  'learner.parcours.completedBadge': 'Terminé ✓',
  'learner.parcours.by': 'Par',
  'learner.parcours.contents': 'contenu(s)',
  'learner.parcours.completedItems': 'complété(s)',
  'learner.parcours.emptyPath': 'Aucun contenu dans ce parcours.',
  'learner.parcours.locked': 'Terminez le précédent d\'abord',
  'learner.parcours.review': 'Revoir',
  'learner.parcours.listen': 'Écouter',
  'learner.parcours.open': 'Ouvrir',
  'learner.modal.loading': 'Chargement…',
  'learner.modal.noEpisodes': 'Aucun épisode disponible.',
  'learner.modal.markedDone': '✓ Marqué comme terminé',
  'learner.modal.listenThenMark': 'Écoutez puis marquez comme terminé',
  'learner.modal.alreadyDone': 'Déjà terminé',
  'learner.modal.markDone': 'Marquer comme terminé',
  'learner.modal.formationDone': '✓ Formation terminée',
  'learner.modal.completeThenMark': 'Complétez la formation puis marquez-la comme terminée',
  'learner.modal.formationAlreadyDone': 'Déjà terminée',
  'learner.modal.markFormationDone': 'Marquer comme terminée',
  'learner.modal.formationInstruction': 'Suivez les modules de cette formation dans l\'ordre, puis marquez-la comme terminée pour progresser dans votre parcours.',
  'learner.modal.launchFormation': 'Lancer la formation',
  'learner.modal.openFormationHint':
    'Ouvrez la formation dans un nouvel onglet, puis marquez-la comme terminée dans le parcours.',
  'learner.home.loadingHome': 'Chargement de votre accueil…',
  'learner.home.noFormationsAssigned':
    'Aucune formation assignée pour le moment. Elles apparaîtront ici dès qu’un formateur vous en attribuera.',
  'learner.home.loadingPaths': 'Chargement de vos parcours…',
  'learner.home.noPathsAssigned':
    'Aucun parcours assigné pour le moment. Ils apparaîtront ici dès qu’un formateur vous en attribuera.',
  'learner.home.byTheme': 'Vos contenus par thème',
  'learner.home.themesHint':
    'Les thèmes apparaissent à partir des catégories de vos formations et podcasts assignés.',
  'learner.home.categoryOther': 'Autres',
  'learner.home.contentsCount_one': '{{count}} contenu',
  'learner.home.contentsCount_other': '{{count}} contenus',
  'learner.home.viewThemeFormations': 'Voir les {{count}} formations du thème',
  'learner.home.viewThemePodcasts': 'Voir les {{count}} podcasts du thème',
  'learner.home.resumeSubtitleProgress': 'Progression {{percent}} %',
  'learner.home.resumeSubtitleNotStarted': 'Pas encore commencé',
  'learner.home.podcastsAssigned': 'Podcasts assignés',
  'learner.home.viewAllPodcasts': 'Tout voir →',
  'learner.home.gamificationHomeHint':
    'Le détail des badges et de l’activité est dans l’onglet « Ma progression ».',
  'learner.home.gamificationHomeCta': 'Ouvrir ma progression',
  'learner.podcasts.mineTitle': 'Mes podcasts',
  'learner.podcasts.episodeCount_one': '{{count}} épisode',
  'learner.podcasts.episodeCount_other': '{{count}} épisodes',
  'learner.progress.loading': 'Chargement de votre progression…',
  'learner.progress.statPodcastsCompleted': 'Podcasts terminés',
  'learner.progress.statTimeFormationsHours': 'Temps sur les formations (h)',
  'learner.progress.statAssignedContents': 'Contenus assignés',
  'learner.progress.weeklyEstimated': 'Activité des 7 derniers jours (estimée)',
  'learner.progress.badgesSoon':
    'Les badges et récompenses seront bientôt disponibles dans votre espace.',
  'learner.progress.gamificationSection': 'Niveau et expérience',
  'learner.progress.gamificationLevelLine': 'Niveau {{level}} — {{name}}',
  'learner.progress.gamificationXpToNext': 'Encore {{xp}} XP pour le niveau suivant',
  'learner.progress.rewardFormation': 'Formation terminée',
  'learner.progress.rewardPathItem': 'Étape de parcours',
  'learner.progress.rewardPathComplete': 'Parcours terminé',
  'learner.progress.rewardDesignerBlocks': 'Points formation (blocs)',
  'learner.progress.rewardDesignerPerfect': 'Bonus score parfait',
  'learner.progress.rewardDesignerBadge': 'Badge formation',
  'learner.progress.rewardDesignerBadgeNoXp': 'Sans points XP (récompense visuelle)',
  'learner.progress.badgesEmpty':
    'Complétez une formation ou un parcours pour gagner des points et voir vos récompenses ici.',
  'learner.progress.plusXp': '+{{xp}} XP',
  'learner.progress.noRecentHint':
    'Aucune activité récente enregistrée. Ouvrez une formation ou un podcast pour alimenter ce fil.',
  'learner.progress.hoursShort': '{{hours}} h',
  'learner.profile.statFormationsRatio': 'Formations (terminées / total)',
  'learner.profile.statTimeFormations': 'Temps formations (h)',
  'learner.profile.statPodcastsAssigned': 'Podcasts assignés',
  'learner.profile.statPathsAssigned': 'Parcours assignés',
  'learner.profile.memberSincePrefix': 'Membre depuis {{date}}',
  'learner.parcours.playPath': 'Suivre ce parcours',
  'learner.parcours.playPathTitle': 'Ouvre le parcours en plein écran dans un nouvel onglet',
  'learner.parcours.steps_zero': 'Aucune étape',
  'learner.parcours.steps_one': '1 étape',
  'learner.parcours.steps_other': '{{count}} étapes',
  'learner.parcours.doneBadge': 'Terminé',
  'learner.parcours.completedSingle': '{{done}}/{{total}} complétée',
  'learner.parcours.completedPlural': '{{done}}/{{total}} complétées',
  'learner.parcours.byAuthor': 'Par {{firstName}} {{lastName}}',
  'learner.parcours.pathListAria': 'Liste de vos parcours',
  'learner.parcours.assignedCount_one': '{{count}} parcours assigné',
  'learner.parcours.assignedCount_other': '{{count}} parcours assignés',

  'learner.pathPlayer.loading': 'Chargement du parcours…',
  'learner.pathPlayer.errorGeneric': 'Erreur',
  'learner.pathPlayer.pathNotFound': 'Parcours introuvable',
  'learner.pathPlayer.loadError': 'Impossible de charger le parcours.',
  'learner.pathPlayer.backToPaths': 'Retour aux parcours',
  'learner.pathPlayer.showTocAria': 'Afficher le sommaire',
  'learner.pathPlayer.prevActivityAria': 'Activité précédente',
  'learner.pathPlayer.prev': 'Précédent',
  'learner.pathPlayer.nextActivityAria': 'Activité suivante',
  'learner.pathPlayer.next': 'Suivant',
  'learner.pathPlayer.closePlayerTitle': 'Fermer et retourner aux parcours',
  'learner.pathPlayer.close': 'Fermer',
  'learner.pathPlayer.congrats': 'Félicitations',
  'learner.pathPlayer.pathCompleteTitle': 'Parcours terminé',
  'learner.pathPlayer.pathCompleteBody':
    'Vous avez terminé toutes les activités du parcours « {{title}} ».',
  'learner.pathPlayer.activitiesCompleted_one': '{{count}} activité accomplie',
  'learner.pathPlayer.activitiesCompleted_other': '{{count}} activités accomplies',
  'learner.pathPlayer.seeMyPaths': 'Voir mes parcours',
  'learner.pathPlayer.closeTocAria': 'Fermer le sommaire',
  'learner.pathPlayer.tocNavAria': 'Sommaire du parcours',
  'learner.pathPlayer.pathTypeLabel': 'Parcours de formation',
  'learner.pathPlayer.noDescription': 'Aucune description.',
  'learner.pathPlayer.activityCount_zero': 'Aucune activité',
  'learner.pathPlayer.activityCount_one': '1 activité',
  'learner.pathPlayer.activityCount_other': '{{count}} activités',
  'learner.pathPlayer.progressPercent': 'Progression {{percent}}%',
  'learner.pathPlayer.quit': 'Quitter',
  'learner.pathPlayer.summary': 'Sommaire',
  'learner.pathPlayer.outsideModule': 'Hors module',
  'learner.pathPlayer.moduleContents': 'Contenus du module',
  'learner.pathPlayer.step': 'Étape',
  'learner.pathPlayer.podcastHint':
    'Écoutez l’épisode, puis marquez l’activité comme terminée.',
  'learner.pathPlayer.showChapters': 'Afficher les chapitres',
  'learner.pathPlayer.hideChapters': 'Masquer les chapitres',
  'learner.pathPlayer.showText': 'Afficher le texte',
  'learner.pathPlayer.hideText': 'Masquer le texte',
  'learner.pathPlayer.selectEpisode': 'Sélectionnez un épisode pour commencer.',
  'learner.pathPlayer.loadingShort': 'Chargement…',
  'learner.pathPlayer.noEpisodes': 'Aucun épisode disponible.',
  'learner.pathPlayer.listenThenMark': 'Écoutez puis marquez comme terminé',
  'learner.pathPlayer.updating': 'Mise à jour…',
  'learner.pathPlayer.alreadyDone': 'Déjà terminé',
  'learner.pathPlayer.markComplete': 'Marquer comme terminé',
  'learner.pathPlayer.nextStepBtn': 'Étape suivante',
  'learner.pathPlayer.embeddedFormationHint':
    'La formation s’affiche ci-dessous dans le parcours. Utilisez le sommaire à gauche pour les étapes détaillées.',
  'learner.pathPlayer.loadingFormation': 'Chargement de la formation…',
  'learner.pathPlayer.loadingReader': 'Chargement du lecteur…',
  'learner.pathPlayer.emptyPathTitle': 'Ce parcours ne contient pas encore d’activités.',
  'learner.pathPlayer.emptyPathDesc':
    'Le contenu sera ajouté par votre organisme de formation. Vous pouvez fermer cette fenêtre et revenir plus tard.',
  'learner.pathPlayer.pickActivity': 'Choisissez une activité dans le sommaire pour commencer.',
  'learner.pathPlayer.footerStep': 'Étape {{current}} / {{total}}',
  'learner.pathPlayer.unmute': 'Activer le son',
  'learner.pathPlayer.mute': 'Couper le son',

  'learner.activity.flowCompleted': 'Formation « {{title}} » terminée',
  'learner.activity.flowProgress': 'Formation « {{title}} » — {{progress}} %',
  'learner.activity.podcast': 'Podcast « {{title}} »',
  'learner.webinars.durationMin': '{{m}} min',
  'learner.webinars.durationH': '{{h}} h',
  'learner.webinars.durationHM': '{{h}} h {{m}}',
  'learner.formations.durationHours': '{{hours}} h',

  'rh.page.documentTitle': 'Pilotage RH - LyrraStudio',
  'rh.dashboard.title': 'Tableau de bord RH',
  'rh.dashboard.subtitle': 'Vue d’ensemble formation & développement des compétences',
  'rh.dashboard.activeCampaigns': 'Campagnes actives',
  'rh.dashboard.pendingAssignmentsKpi': 'Assignations (en attente / acceptées)',
  'rh.dashboard.pendingRequestsCount': 'Demandes en attente',
  'rh.dashboard.completionRateLabel': 'Taux de complétion (parcours)',
  'rh.dashboard.budgetUsed': 'Budget campagnes utilisé',
  'rh.dashboard.budgetNotSet': 'Non renseigné',
  'rh.dashboard.progressLabel': 'Progression (%)',
  'rh.dashboard.chartCampaignsTitle': 'Progression des campagnes',
  'rh.dashboard.chartFormationsTitle': 'Progression des parcours (inscriptions)',
  'rh.dashboard.campaignProgress': 'Avancement des campagnes',
  'rh.dashboard.chartCampaignsHint': 'Taux moyen d’avancement des assignations par campagne.',
  'rh.dashboard.chartFormationsHint': 'Part d’apprenants ayant terminé le parcours (sur inscriptions en cours).',
  'rh.dashboard.upcomingDeadlines': 'Échéances à venir',
  'rh.dashboard.recentActivity': 'Activité récente',
  'rh.dashboard.recentActivityHint': 'Assignations de campagnes et parcours terminés.',
  'rh.dashboard.viewAll': 'Tout voir',
  'rh.dashboard.exportReport': 'Exporter le rapport',
  'rh.dashboard.exportSoon': 'Export disponible prochainement.',
  'rh.dashboard.newCampaign': 'Nouvelle campagne',
  'rh.dashboard.createCampaign': 'Créer une campagne',
  'rh.dashboard.reviewRequests': 'Traiter les demandes',
  'rh.dashboard.skillsMap': 'Référentiel compétences',
  'rh.dashboard.budgetOverview': 'Vue budget',
  'rh.dashboard.loadError': 'Impossible de charger le pilotage RH.',
  'rh.dashboard.emptyChart': 'Aucune donnée à afficher pour cette période.',
  'rh.dashboard.emptyDeadlines': 'Aucune échéance de campagne à venir.',
  'rh.dashboard.emptyRecent': 'Aucune activité récente.',
  'rh.dashboard.kindAssignment': 'Campagne',
  'rh.dashboard.kindCompletion': 'Parcours terminé',
  'rh.dashboard.justNow': "À l'instant",
  'rh.dashboard.learnerInsightTitle': 'Pilotage formations (données réelles)',
  'rh.dashboard.learnerInsightHint':
    'Inscriptions aux formations agrégées : scores issus des blocs notés, friction par bloc, dernières évaluations.',
  'rh.dashboard.enrollmentsTotal': 'Inscriptions',
  'rh.dashboard.completionGlobal': 'Complétés',
  'rh.dashboard.avgTimePerEnrollment': 'Temps moyen / inscription',
  'rh.dashboard.notStarted': 'Non démarrés',
  'rh.dashboard.stalled': 'En pause (>30 j. sans activité après début)',
  'rh.dashboard.topFlowsDetail': 'Parcours les plus suivis',
  'rh.dashboard.colFlow': 'Parcours',
  'rh.dashboard.colEnrolled': 'Inscrits',
  'rh.dashboard.colCompletedPct': 'Complétion',
  'rh.dashboard.colAvgTime': 'Temps moyen',
  'rh.dashboard.colAvgScore': 'Score moy.',
  'rh.dashboard.blockFrictionTitle': 'Blocs les plus frictionnels (sessions ≥3)',
  'rh.dashboard.colBlock': 'Bloc',
  'rh.dashboard.colSessions': 'Sessions',
  'rh.dashboard.colBlockCompletion': 'Complétion bloc',
  'rh.dashboard.recentEvalTitle': 'Dernières évaluations',
  'rh.dashboard.evalComment': 'Commentaire',
  'rh.dashboard.exportOk': 'Export CSV téléchargé.',
  'rh.dashboard.exportFail': 'Échec du téléchargement.',
  'institution.dashboard.trainingCardTitle': 'Suivi formations (inscriptions)',
  'institution.dashboard.trainingCardHint': 'Vue agrégée sur tous les parcours de l’établissement.',
  'institution.dashboard.trainingCompleted': 'parcours terminés',
  'institution.dashboard.trainingAvgMinutes': 'min moy. / inscription',
  'rh.status.urgent': 'Urgent',
  'rh.status.soon': 'Proche',
  'rh.status.upcoming': 'À venir',
  'rh.status.pending': 'En cours',
  'rh.status.approved': 'Terminé',
  'rh.status.rejected': 'Annulé',
  'admin.nav.stats': 'Statistiques',
  'admin.nav.users': 'Utilisateurs',
  'admin.nav.support': 'Support et chat',
  'admin.nav.institutions': 'Établissements',
  'admin.nav.contracts': 'Contrats',
  'admin.nav.editors': 'Éditeurs',
  'admin.nav.podcasts': 'Podcasts',
  'admin.nav.sounds': 'Sons Lyrra',
  'admin.nav.templates': 'Templates de formation',
  'admin.nav.templatesImages': 'Modèles d’image',
  'admin.nav.tools': 'Outils',
  'admin.nav.files': 'Fichiers',
  'admin.nav.logs': 'Journaux',
  'admin.nav.webhooks': 'Webhooks',
  'admin.nav.email': 'Email',
  'admin.nav.docusign': 'DocuSign',
  'admin.docusign.title': 'Intégration DocuSign',
  'admin.docusign.subtitle': 'Clé d’intégration, identifiants API et clé privée RSA (JWT). Stockés en base, plus dans Docker.',
  'admin.docusign.statusOk': 'Configuré',
  'admin.docusign.statusIncomplete': 'Incomplet',
  'admin.docusign.keyStored': 'Clé privée enregistrée',
  'admin.docusign.consentHint':
    'Pour la première utilisation JWT, le compte DocuSign doit avoir accepté le consentement d’application (URL fournie par DocuSign). En cas d’erreur 401/403, vérifiez la clé PEM, la clé d’intégration et ce consentement.',
  'admin.docusign.loading': 'Chargement…',
  'admin.docusign.loadError': 'Impossible de charger la configuration DocuSign',
  'admin.docusign.saved': 'Paramètres DocuSign enregistrés',
  'admin.docusign.saveError': 'Enregistrement impossible',
  'admin.docusign.save': 'Enregistrer',
  'admin.docusign.reload': 'Recharger',
  'admin.docusign.fieldIntegrationKey': 'Clé d’intégration (ID client / client ID)',
  'admin.docusign.fieldUserId': 'Identifiant utilisateur DocuSign (GUID)',
  'admin.docusign.fieldAccountId': 'Identifiant de compte (account ID)',
  'admin.docusign.fieldBasePath': 'URL de base de l’API REST',
  'admin.docusign.fieldOauthHost': 'Hôte OAuth (sans https://)',
  'admin.docusign.fieldRsa': 'Clé privée RSA (PEM)',
  'admin.docusign.oauthPlaceholder': 'account-d.docusign.com',
  'admin.docusign.rsaPlaceholderKeep': 'Laisser vide pour conserver la clé actuelle',
  'admin.docusign.rsaPlaceholderNew': 'Collez le fichier .pem complet (BEGIN … END)',
  'admin.smtp.cardTitle': 'Envoi des emails (SMTP)',
  'admin.smtp.subtitle':
    'Hôte, port, authentification et expéditeur. Stocké en base — plus de variables Docker SMTP_* ni EMAIL_FROM.',
  'admin.smtp.host': 'Serveur SMTP (hôte)',
  'admin.smtp.port': 'Port',
  'admin.smtp.secure': 'TLS/SSL (port 465)',
  'admin.smtp.user': 'Utilisateur',
  'admin.smtp.password': 'Mot de passe',
  'admin.smtp.passwordPlaceholder': 'Laisser vide pour conserver le mot de passe actuel',
  'admin.smtp.passwordPlaceholderNew': 'Mot de passe SMTP',
  'admin.smtp.fromEmail': 'Email expéditeur',
  'admin.smtp.fromName': 'Nom affiché (From)',
  'admin.smtp.hint': 'La plupart des fournisseurs exigent utilisateur + mot de passe. Testez via les outils admin (email de test) après enregistrement.',
  'admin.smtp.save': 'Enregistrer SMTP',
  'admin.smtp.saved': 'Paramètres SMTP enregistrés',
  'admin.smtp.saveError': 'Enregistrement impossible',
  'admin.smtp.loadError': 'Impossible de charger SMTP',
  'admin.smtp.loading': 'Chargement…',
  'admin.smtp.configured': 'Paramètres enregistrés',
  'admin.smtp.notConfigured': 'À compléter',
  'admin.nav.smtp': 'Configuration SMTP',
  'admin.nav.regulatoryWatch': 'Veille Lyrra',
  'admin.nav.leBref': 'Le Bref',
  'admin.nav.helpCenter': "Centre d'aide",
  'admin.nav.platform': 'Intégrations plateforme',
  'admin.nav.eduflowPalette': 'Palette EduFlow',
  'admin.users.adminScopeTitle': 'Périmètre du tableau d’administration',
  'admin.users.adminScopeFull': 'Accès complet (tous les onglets)',
  'admin.users.adminScopeRestricted': 'Limiter aux onglets cochés',
  'admin.users.adminScopeHint':
    'Un compte restreint ne voit que les sections sélectionnées. Après modification des onglets, la personne doit se reconnecter (ou rafraîchir sa session) pour que le menu et les appels API reflètent le changement.',
  'admin.users.adminScopeNeedOne': 'Cochez au moins un onglet, ou choisissez l’accès complet.',
  'admin.helpCenter.title': "Centre d'aide",
  'admin.helpCenter.subtitle':
    'Chaque fiche ouvre une modale. Modifiez ici le titre et la description affichés dans cette modale.',
  'admin.helpCenter.reload': 'Actualiser',
  'admin.helpCenter.loadError': 'Chargement impossible.',
  'admin.helpCenter.saveError': 'Enregistrement impossible.',
  'admin.helpCenter.deleteError': 'Suppression impossible.',
  'admin.helpCenter.saved': 'Enregistré',
  'admin.helpCenter.deleted': 'Supprimé',
  'admin.helpCenter.catCreated': 'Catégorie créée',
  'admin.helpCenter.topicCreated': 'Fiche créée',
  'admin.helpCenter.catTitleRequired': 'Titre de catégorie obligatoire.',
  'admin.helpCenter.topicFieldsRequired': 'Le titre anglais de la modale est obligatoire.',
  'admin.helpCenter.fieldModalTitle': 'Titre de la modale',
  'admin.helpCenter.fieldModalTitleHint': 'Titre de la langue sélectionnée, affiché sur la carte et en tête de la modale.',
  'admin.helpCenter.fieldModalLangHint': 'L’anglais est obligatoire. Une langue laissée vide s’affiche en anglais.',
  'admin.helpCenter.fieldModalDescription': 'Description de la modale',
  'admin.helpCenter.fieldModalDescriptionHint': 'Texte de la langue sélectionnée, sous le titre de la modale. Une langue vide s’affiche en anglais.',
  'admin.helpCenter.fieldLegacyTopicId': 'Fiche liée',
  'admin.helpCenter.fieldLegacyTopicIdHint': 'Identifiant de la modale. Ne le changez pas : il relie cette ligne au pas-à-pas déjà rédigé.',
  'admin.helpCenter.newCategory': 'Nouvelle catégorie',
  'admin.helpCenter.createCategory': 'Nouvelle catégorie',
  'admin.helpCenter.editCategory': 'Modifier la catégorie',
  'admin.helpCenter.createTopic': 'Nouvelle fiche',
  'admin.helpCenter.editTopic': 'Modifier la fiche',
  'admin.helpCenter.fieldTitle': 'Titre',
  'admin.helpCenter.fieldDescription': 'Description',
  'admin.helpCenter.fieldIconKey': 'Icône Lucide',
  'admin.helpCenter.fieldIconKeyHint':
    'Choisissez une icône ; un aperçu s’affiche dans la liste et sur le bouton.',
  'admin.helpCenter.fieldFormationUrl': 'Lien formation',
  'admin.helpCenter.formationPickPlaceholder': '— Choisir une formation publique —',
  'admin.helpCenter.formationPickHint':
    'Seuls les parcours publiés avec l’accès public sans compte activé sont proposés.',
  'admin.helpCenter.formationPickLoading': 'Chargement des parcours…',
  'admin.helpCenter.publicFlowsLoadError': 'Impossible de charger la liste des parcours publics.',
  'admin.helpCenter.legacyFormationPickLabel': 'Lien enregistré (hors liste)',
  'admin.helpCenter.fieldSort': 'Ordre',
  'admin.helpCenter.fieldActive': 'Visible',
  'admin.helpCenter.save': 'Enregistrer',
  'admin.helpCenter.edit': 'Modifier',
  'admin.helpCenter.addTopic': 'Fiche',
  'admin.helpCenter.empty': 'Aucune catégorie. Créez une catégorie puis des fiches.',
  'admin.helpCenter.noTopics': 'Aucune fiche dans cette catégorie.',
  'admin.helpCenter.badgeActive': 'Actif',
  'admin.helpCenter.badgeInactive': 'Masqué',
  'admin.helpCenter.iconLabel': 'Icône',
  'admin.helpCenter.confirmDeleteCategory': 'Supprimer cette catégorie et toutes ses fiches ?',
  'admin.helpCenter.confirmDeleteTopic': 'Supprimer cette fiche ?',
  'admin.nav.formationMigration': 'Migration formations',
  'admin.nav.formationDuplicate': 'Duplication formations',
  'admin.nav.lyrraStudioFormations': 'Formations LyrraStudio',
  'admin.lyrraStudioFormations.title': 'Formations gérées par LyrraStudio',
  'admin.lyrraStudioFormations.subtitle':
    'Assignez un parcours à LyrraStudio : le propriétaire technique est réassigné (compte défini par PLATFORM_EDUFLOW_OWNER_USER_ID ou premier administrateur plateforme), les collaborateurs sont retirés, et seuls les administrateurs peuvent modifier le parcours dans le designer.',
  'admin.lyrraStudioFormations.searchLabel': 'Recherche (titre ou ID du parcours)',
  'admin.lyrraStudioFormations.searchPlaceholder': 'Ex. onboarding ou UUID…',
  'admin.lyrraStudioFormations.search': 'Rechercher',
  'admin.lyrraStudioFormations.searchMin': 'Saisissez au moins 2 caractères.',
  'admin.lyrraStudioFormations.results': 'Résultats',
  'admin.lyrraStudioFormations.noResults': 'Aucun parcours trouvé.',
  'admin.lyrraStudioFormations.badge': 'LyrraStudio',
  'admin.lyrraStudioFormations.detailHint':
    'Le créateur et les anciens collaborateurs perdent l’accès en édition. Les apprenants et le lecteur public ne sont pas impactés.',
  'admin.lyrraStudioFormations.assign': 'Assigner à LyrraStudio',
  'admin.lyrraStudioFormations.alreadyAssigned': 'Déjà assignée à LyrraStudio',
  'admin.lyrraStudioFormations.confirm':
    'Confirmer l’assignation à LyrraStudio ? Cette action est irréversible depuis cet écran : le parcours ne pourra plus être modifié que par les administrateurs plateforme.',
  'admin.lyrraStudioFormations.success': 'Parcours « {{title}} » : assignation LyrraStudio effectuée.',
  'admin.lyrraStudioFormations.assignedListTitle': 'Parcours déjà assignés à LyrraStudio',
  'admin.lyrraStudioFormations.assignedListEmpty':
    'Aucun parcours pour l’instant. Utilisez la recherche ci-dessous pour en assigner un.',
  'admin.lyrraStudioFormations.refreshList': 'Actualiser la liste',
  'admin.lyrraStudioFormations.openDesigner': 'Designer',
  'admin.lyrraStudioFormations.assignSectionTitle': 'Rechercher et assigner un autre parcours',
  'admin.lyrraStudioFormations.publicPolicyHint':
    'Les parcours assignés à LyrraStudio sont automatiquement publiés, accessibles sans compte (lien /f/…) et adaptés au partage (QR, iframe centre d’aide).',
  'admin.lyrraStudioFormations.drawerTitle': 'Lien public & QR code',
  'admin.lyrraStudioFormations.publicShortLink': 'Lien court (recommandé)',
  'admin.lyrraStudioFormations.publicLongLink': 'Lien lecteur public (UUID)',
  'admin.lyrraStudioFormations.copy': 'Copier',
  'admin.lyrraStudioFormations.copied': 'Copié dans le presse-papiers.',
  'admin.lyrraStudioFormations.copyFailed': 'Impossible de copier (navigateur ou permissions).',
  'admin.lyrraStudioFormations.publicLinksUnavailable':
    'Liens indisponibles : publiez l’accès public ou utilisez « Mettre à jour l’accès public » si le parcours date d’avant cette règle.',
  'admin.lyrraStudioFormations.publicIncomplete': 'Accès public incomplet',
  'admin.lyrraStudioFormations.publicRepairHint':
    'Ce parcours est géré LyrraStudio mais n’a pas encore les réglages publics complets (publication, accès anonyme, code court).',
  'admin.lyrraStudioFormations.publicRepairCta': 'Mettre à jour l’accès public',
  'admin.lyrraStudioFormations.publicRepairOk': 'Accès public appliqué pour « {{title}} ».',
  'admin.formationDuplicate.title': 'Dupliquer une formation vers un autre établissement',
  'admin.formationDuplicate.subtitle':
    'Recherchez un parcours, choisissez l’établissement et le compte propriétaire de la copie. Le parcours d’origine reste inchangé. La copie est un brouillon (sans catalogue, sessions ni collaborateurs).',
  'admin.formationDuplicate.searchLabel': 'Recherche (titre ou ID du parcours)',
  'admin.formationDuplicate.searchPlaceholder': 'Ex. onboarding ou UUID…',
  'admin.formationDuplicate.search': 'Rechercher',
  'admin.formationDuplicate.searchMin': 'Saisissez au moins 2 caractères.',
  'admin.formationDuplicate.results': 'Résultats',
  'admin.formationDuplicate.noResults': 'Aucun parcours trouvé.',
  'admin.formationDuplicate.currentOwner': 'Propriétaire actuel',
  'admin.formationDuplicate.currentInstitution': 'Établissement d’origine',
  'admin.formationDuplicate.targetInstitution': 'Établissement de la copie',
  'admin.formationDuplicate.pickInstitution': 'Choisir un établissement…',
  'admin.formationDuplicate.newOwner': 'Propriétaire de la copie (formateur / RH)',
  'admin.formationDuplicate.pickOwner': 'Choisir un compte…',
  'admin.formationDuplicate.noCandidates': 'Aucun compte éligible dans cet établissement.',
  'admin.formationDuplicate.duplicate': 'Dupliquer la formation',
  'admin.formationDuplicate.confirm':
    'Créer une copie de cette formation pour le compte sélectionné ? Le parcours source ne sera pas modifié.',
  'admin.formationDuplicate.success':
    'Copie créée : « {{title}} » pour {{email}}. Identifiant du nouveau parcours : {{newFlowId}}.',
  'admin.formationDuplicate.loadInstitutionsError': 'Impossible de charger la liste des établissements.',
  'admin.formationDuplicate.loadCandidatesError': 'Impossible de charger les comptes éligibles.',
  'admin.formationMigration.title': 'Migrer vers un autre établissement',
  'admin.formationMigration.subtitle':
    'Choisissez une formation, un podcast ou un parcours dans la liste, puis l’établissement cible et le nouveau propriétaire. Les partages de classes et de groupes de l’établissement d’origine sont retirés.',
  'admin.formationMigration.filterPlaceholder': 'Filtrer par titre ou établissement…',
  'admin.formationMigration.kindFlow': 'Formations',
  'admin.formationMigration.kindPodcast': 'Podcasts',
  'admin.formationMigration.kindPath': 'Parcours',
  'admin.formationMigration.empty': 'Aucun élément dans cette liste.',
  'admin.formationMigration.loading': 'Chargement des listes…',
  'admin.formationMigration.migratePodcast': 'Migrer le podcast',
  'admin.formationMigration.migratePath': 'Migrer le parcours',
  'admin.formationMigration.searchLabel': 'Recherche (titre ou ID du parcours)',
  'admin.formationMigration.searchPlaceholder': 'Ex. onboarding ou UUID…',
  'admin.formationMigration.search': 'Rechercher',
  'admin.formationMigration.searchMin': 'Saisissez au moins 2 caractères.',
  'admin.formationMigration.results': 'Résultats',
  'admin.formationMigration.noResults': 'Aucun parcours trouvé.',
  'admin.formationMigration.currentOwner': 'Propriétaire actuel',
  'admin.formationMigration.currentInstitution': 'Établissement d’origine',
  'admin.formationMigration.targetInstitution': 'Établissement cible',
  'admin.formationMigration.pickInstitution': 'Choisir un établissement…',
  'admin.formationMigration.newOwner': 'Nouveau propriétaire (formateur / RH)',
  'admin.formationMigration.pickOwner': 'Choisir un compte…',
  'admin.formationMigration.noCandidates': 'Aucun compte éligible dans cet établissement.',
  'admin.formationMigration.migrate': 'Migrer la formation',
  'admin.formationMigration.confirm':
    'Confirmer la migration ? Les collaborateurs et catégories seront retirés, les liens RH/catalogue incohérents seront supprimés ou déliés.',
  'admin.formationMigration.success': 'Migration effectuée : « {{title}} » est désormais attribuée à {{email}}.',
  'admin.formationMigration.clearedCatalog': 'Lien fiche catalogue retiré (fiche d’un autre établissement).',
  'admin.formationMigration.loadInstitutionsError': 'Impossible de charger la liste des établissements.',
  'admin.formationMigration.loadCandidatesError': 'Impossible de charger les comptes éligibles.',
  'admin.regulatoryWatch.title': 'Veille — alertes Lyrra Studio',
  'admin.regulatoryWatch.subtitle':
    'Publie une alerte sur la timeline « Veille réglementaire » de tous les établissements actifs. Le contenu est verrouillé côté OF ; ils peuvent accuser réception, annoter et planifier une action.',
  'admin.regulatoryWatch.refresh': 'Actualiser',
  'admin.regulatoryWatch.loadError': 'Chargement impossible.',
  'admin.regulatoryWatch.validationRequired': 'Titre, description et source sont obligatoires.',
  'admin.regulatoryWatch.publishError': 'Publication impossible.',
  'admin.regulatoryWatch.newBroadcastTitle': 'Nouvelle alerte globale',
  'admin.regulatoryWatch.placeholderTitle': 'Titre',
  'admin.regulatoryWatch.placeholderDescription': 'Description (visible par tous les OF)',
  'admin.regulatoryWatch.placeholderSource': 'Lien ou référence source',
  'admin.regulatoryWatch.category.reglementaire': 'Réglementaire',
  'admin.regulatoryWatch.category.pedagogique': 'Pédagogique',
  'admin.regulatoryWatch.category.technique': 'Technique',
  'admin.regulatoryWatch.category.sectoriel': 'Sectoriel',
  'admin.regulatoryWatch.impact.fort': 'Impact fort',
  'admin.regulatoryWatch.impact.moyen': 'Impact moyen',
  'admin.regulatoryWatch.impact.faible': 'Impact faible',
  'admin.regulatoryWatch.publishing': 'Publication…',
  'admin.regulatoryWatch.publishButton': 'Publier sur tous les établissements actifs',
  'admin.regulatoryWatch.historyTitle': 'Historique & accusés de réception',
  'admin.regulatoryWatch.loading': 'Chargement…',
  'admin.regulatoryWatch.empty': 'Aucune alerte publiée pour le moment.',
  'admin.regulatoryWatch.institutionsCount': '{{count}} établissement(s)',
  'admin.regulatoryWatch.ackProgress': 'Pris en compte : {{ack}}/{{total}} ({{pct}}%)',
  'admin.regulatoryWatch.byAuthor': 'Par {{who}}',
  'admin.regulatoryWatch.unknownAuthor': '—',
  'admin.leBref.title': 'Le Bref — calendrier publication',
  'admin.leBref.subtitle':
    'Brief quotidien affiché sur la page publique /le-bref. Un seul brief « publié » par jour calendaire.',
  'admin.leBref.refresh': 'Actualiser',
  'admin.leBref.loadError': 'Chargement impossible.',
  'admin.leBref.validationTitle': 'Le titre est obligatoire.',
  'admin.leBref.validationBlocks': 'Ajoutez au moins un bloc (texte, audio, quiz ou sondage).',
  'admin.leBref.validationBlocksInvalid':
    'Aucun bloc valide : vérifiez les quiz (question + 2 réponses minimum, bonne réponse cohérente), les URL audio, et du texte non vide.',
  'admin.leBref.blocksHint':
    'Composez le brief avec des blocs (texte, médias, quiz, sondage — sans HTML). Les anciens briefs au format HTML sont convertis à l’ouverture.',
  'admin.leBref.block.text': 'Texte',
  'admin.leBref.block.audio': 'Audio',
  'admin.leBref.block.quiz': 'Quiz',
  'admin.leBref.block.poll': 'Sondage',
  'admin.leBref.blocksEmpty': 'Ajoutez des blocs (texte, audio, quiz, sondage). Plus besoin de saisir du HTML.',
  'admin.leBref.block.moveUp': 'Monter',
  'admin.leBref.block.moveDown': 'Descendre',
  'admin.leBref.block.remove': 'Supprimer le bloc',
  'admin.leBref.block.textPlaceholder': 'Paragraphe (texte brut ou sauts de ligne)',
  'admin.leBref.block.audioUrl': 'URL du fichier audio',
  'admin.leBref.block.audioCaption': 'Légende (optionnel)',
  'admin.leBref.block.quizQuestion': 'Question',
  'admin.leBref.block.quizOptions': 'Réponses',
  'admin.leBref.block.optionLabel': 'Option',
  'admin.leBref.block.addOption': 'Ajouter une réponse',
  'admin.leBref.block.quizFeedback': 'Feedback après validation (optionnel)',
  'admin.leBref.block.pollQuestion': 'Question du sondage',
  'admin.leBref.block.pollMulti': 'Choix multiples',
  'admin.leBref.block.image': 'Image',
  'admin.leBref.block.video': 'Vidéo',
  'admin.leBref.block.imageUrl': 'URL de l’image (HTTPS ou média hébergé)',
  'admin.leBref.block.imageAlt': 'Texte alternatif (accessibilité)',
  'admin.leBref.block.imageCaption': 'Légende affichée sous l’image (optionnel)',
  'admin.leBref.block.videoHint':
    'Lien YouTube, Vimeo ou URL directe vers une vidéo (mp4, WebM…), comme dans le designer.',
  'admin.leBref.block.videoUrl': 'URL de la vidéo',
  'admin.leBref.block.videoPoster':
    'Image d’affiche (poster), URL optionnelle — surtout pour les fichiers vidéo directs',
  'admin.leBref.block.videoCaption': 'Titre ou légende (optionnel)',
  'admin.leBref.block.audioTtsHint':
    'Le texte est lu par Mistral TTS ; la voix est choisie automatiquement selon la langue du texte (comme pour les blocs audio du designer).',
  'admin.leBref.block.audioTtsPlaceholder': 'Texte à lire à voix haute…',
  'admin.leBref.block.audioTtsGenerate': 'Synthétiser (Mistral TTS)',
  'admin.leBref.block.audioTtsReady':
    'Audio prêt — sera régénéré à l’enregistrement si le texte change.',
  'admin.leBref.block.audioUrlReadonly': 'Fichier audio',
  'admin.leBref.block.audioPreviewUnsupported': 'Aperçu non supporté',
  'admin.leBref.block.audioTtsNeedText': 'Saisissez d’abord le texte à lire.',
  'admin.leBref.block.audioTtsBadResponse': 'Réponse TTS invalide.',
  'admin.leBref.block.audioTtsError': 'Synthèse impossible (Mistral TTS / intégrations).',
  'admin.leBref.saveError': 'Enregistrement impossible.',
  'admin.leBref.deleteError': 'Suppression impossible.',
  'admin.leBref.confirmDelete': 'Supprimer ce brief ?',
  'admin.leBref.quizJsonInvalid': 'JSON du quiz invalide.',
  'admin.leBref.aiError': 'Génération IA impossible (clé Mistral / intégrations plateforme).',
  'admin.leBref.prevMonth': 'Mois précédent',
  'admin.leBref.nextMonth': 'Mois suivant',
  'admin.leBref.legend': 'Légende',
  'admin.leBref.status.published': 'Publié',
  'admin.leBref.status.draft': 'Brouillon',
  'admin.leBref.status.archived': 'Archivé',
  'admin.leBref.clickDayHint': 'Cliquez un jour pour préparer un nouveau brief à cette date.',
  'admin.leBref.editBrief': 'Modifier le brief',
  'admin.leBref.newBrief': 'Nouveau brief',
  'admin.leBref.aiBlockTitle': 'Assistant IA (Mistral)',
  'admin.leBref.aiThemePlaceholder':
    'Thème ou consigne (ex. résumé + quiz, sondage pratiques RH, URL article, lien YouTube…)',
  'admin.leBref.aiContextHint':
    'Précisez l’objectif : résumé, quiz, sondage d’avis, version audio, ou collez une URL / un lien image ou vidéo — l’IA adapte les types de blocs (texte, audio, image, vidéo, quiz, sondage).',
  'admin.leBref.aiGenerate': 'Remplir avec l’IA',
  'admin.leBref.titlePlaceholder': 'Titre',
  'admin.leBref.excerptPlaceholder': 'Chapô / extrait court',
  'admin.leBref.bodyPlaceholder': 'Corps (HTML autorisé : p, strong, em, ul, li, br)',
  'admin.leBref.audioUrlPlaceholder': 'URL audio optionnelle (mp3, etc.)',
  'admin.leBref.quizPlaceholder':
    'Quiz JSON optionnel : {"questions":[{"question":"…","options":["A","B","C","D"],"correctIndex":0,"feedback":"…"}]}',
  'admin.leBref.save': 'Enregistrer',
  'admin.leBref.create': 'Créer',
  'admin.leBref.resetForm': 'Nouveau / effacer',
  'admin.leBref.delete': 'Supprimer',
  'admin.leBref.monthListTitle': 'Briefs du mois',
  'admin.leBref.loading': 'Chargement…',
  'admin.leBref.emptyMonth': 'Aucun brief sur cette période.',
  'admin.leBref.edit': 'Éditer',
  'admin.smtp.pageTitle': 'Configuration SMTP',
  'admin.smtp.pageHint': 'Serveur sortant pour les emails transactionnels (invitations, réinitialisations, etc.).',
  'support.chat.title': 'Support Lyrra',
  'support.chat.subtitle': 'Posez votre question : notre équipe vous répond ici.',
  'support.chat.empty': 'Écrivez un message pour démarrer la conversation.',
  'support.chat.placeholder': 'Votre message…',
  'support.chat.send': 'Envoyer',
  'support.chat.open': 'Ouvrir le chat support',
  'support.chat.fromSupport': 'Support',
  'support.chat.fromYou': 'Vous',
  'admin.support.title': 'Conversations support',
  'admin.support.loadError': 'Impossible de charger les conversations.',
  'admin.support.replyError': "Impossible d'envoyer la réponse.",
  'admin.support.selectThread': 'Sélectionnez une conversation pour voir les messages.',
  'admin.support.replyPlaceholder': 'Votre réponse à l’utilisateur…',
  'admin.support.sendReply': 'Envoyer',
  'admin.support.refresh': 'Actualiser',
  'admin.support.user': 'Utilisateur',
  'admin.support.subject': 'Sujet',
  'admin.support.status': 'Statut',
  'admin.support.priority': 'Priorité',
  'admin.support.updated': 'Mise à jour',
  'admin.support.messages': 'Fil de discussion',
  'admin.support.newestFirst': 'plus récent en haut',
  'admin.support.allStatuses': 'Tous les statuts',
  'admin.support.linearCreate': 'Créer un ticket Linear',
  'admin.support.linearOpen': 'Ouvrir dans Linear',
  'admin.support.linearError': 'Impossible de créer le ticket Linear. Vérifiez LINEAR_API_KEY sur le serveur.',
  // ── Injected from locales/fr.json ──
  'producerAssignments.createInDesigner': 'Créer / ouvrir formation',
  'producerAssignments.draft': 'Brouillon',
  'producerAssignments.enterprise': 'Entreprise',
  'producerAssignments.enterprisePaths': 'Parcours entreprise',
  'producerAssignments.episodes': 'ép.',
  'producerAssignments.formations': 'Fiches formation',
  'producerAssignments.markCompleted': 'Marquer terminé',
  'producerAssignments.markInProgress': 'En production',
  'producerAssignments.markReady': 'Prêt',
  'producerAssignments.modulesCatalog': '{{count}} module(s) catalogue · mis à jour {{date}}',
  'producerAssignments.noneEnterprisePaths': 'Aucune demande de parcours entreprise.',
  'producerAssignments.noneFormations': 'Aucune fiche assignée.',
  'producerAssignments.nonePodcasts': 'Aucun podcast assigné.',
  'producerAssignments.openInDesigner': 'Ouvrir dans le designer',
  'producerAssignments.openPodcasts': 'Studio podcast',
  'producerAssignments.planningDeadline': 'Échéance {{date}}',
  'producerAssignments.planningFrom': 'À partir du {{date}}',
  'producerAssignments.planningRange': '{{start}} → {{end}}',
  'producerAssignments.podcasts': 'Podcasts à produire',
  'producerAssignments.productionStatus.completed': 'Terminé',
  'producerAssignments.productionStatus.in_progress': 'En cours',
  'producerAssignments.productionStatus.not_started': 'Pas commencé',
  'producerAssignments.productionStatus.published': 'Publié',
  'producerAssignments.publishedCatalog': 'Publié catalogue',
  'producerAssignments.rhPlanning': 'Planning RH',
  'producerAssignments.subtitle': 'Formations à créer dans le designer et podcasts à produire, assignés par la RH.',
  'producerAssignments.title': 'Mes missions de production',
  'podcasts.actions.cancel': 'Annuler',
  'podcasts.actions.close': 'Fermer',
  'podcasts.actions.create': 'Créer',
  'podcasts.actions.edit': 'Modifier',
  'podcasts.actions.retry': 'Réessayer',
  'podcasts.actions.save': 'Enregistrer',
  'podcasts.actions.saving': 'Sauvegarde…',
  'podcasts.actions.share': 'Partager',
  'podcasts.category.ariaGroup': 'Catégorie du podcast',
  'podcasts.category.emptyBanner': 'Ajoutez des catégories dans l\'espace Institution pour mieux organiser les podcasts.',
  'podcasts.category.emptyInstitutionHint': 'Aucune catégorie institution pour l\'instant — vous pouvez publier sans.',
  'podcasts.category.noneChip': 'Sans catégorie',
  'podcasts.category.sectionTitle': 'Catégorie',
  'podcasts.category.withCategoriesHint': 'Classement dans le catalogue et filtres côté institution.',
  'podcasts.cover.ariaDropzone': 'Choisir ou déposer une image de couverture',
  'podcasts.cover.browseFiles': 'parcourir les fichiers',
  'podcasts.cover.cancelNewFile': 'Annuler le nouveau fichier',
  'podcasts.cover.dragDrop': 'Glissez-déposez une image',
  'podcasts.cover.dropHere': 'Déposez l\'image ici',
  'podcasts.cover.errorMaxSize': 'L\'image ne doit pas dépasser 5 Mo.',
  'podcasts.cover.errorUnsupportedFormat': 'Format d\'image non supporté.',
  'podcasts.cover.formatsHint': 'PNG, JPG, WebP, GIF · max 5 Mo',
  'podcasts.cover.help': 'Format carré recommandé. Glissez-déposez un fichier ou cliquez pour parcourir.',
  'podcasts.cover.or': 'ou',
  'podcasts.cover.replaceHint': 'Remplacer l\'image',
  'podcasts.cover.sectionTitle': 'Image de couverture',
  'podcasts.deleteDialog.body': '« {{title}} » sera mis en corbeille. Les épisodes rattachés uniquement à ce podcast seront supprimés (audio inclus). Les épisodes partagés avec d’autres podcasts seront conservés. Un administrateur peut restaurer le podcast depuis la corbeille Institution.',
  'podcasts.deleteDialog.confirm': 'Mettre en corbeille',
  'podcasts.deleteDialog.title': 'Mettre le podcast en corbeille ?',
  'podcasts.editPanel.createdOn': 'Créé le ',
  'podcasts.editPanel.headerTitle': 'Modifier le podcast',
  'podcasts.editPanel.saving': 'Enregistrement…',
  'podcasts.editPanel.statsEpisodes': 'Épisodes : ',
  'podcasts.editPanel.statsSubscribers': 'Abonnés : ',
  'podcasts.editPanel.successSaved': 'Modifications enregistrées',
  'podcasts.episodeStatus.draft': 'Brouillon',
  'podcasts.episodeStatus.published': 'Publié',
  'podcasts.episodesPanel.addEpisode': 'Ajouter un épisode',
  'podcasts.episodesPanel.emptySubtitle': 'Ajoutez des épisodes à ce podcast',
  'podcasts.episodesPanel.emptyTitle': 'Aucun épisode',
  'podcasts.episodesPanel.episodeCount': '{{count}} épisode(s)',
  'podcasts.episodesPanel.noEpisodeAvailable': 'Aucun épisode disponible',
  'podcasts.episodesPanel.remove': 'Retirer',
  'podcasts.episodesPanel.searchPlaceholder': 'Rechercher un épisode…',
  'podcasts.episodesPanel.title': 'Épisodes du podcast',
  'podcasts.pdfImport.openButton': 'Importer un document',
  'podcasts.pdfImport.pickPodcastTitle': 'Choisir le podcast cible',
  'podcasts.pdfImport.noPodcast': 'Créez d’abord un podcast non archivé pour importer un document.',
  'podcasts.pdfImport.noPodcastMatch': 'Aucun podcast ne correspond à la recherche.',
  'podcasts.list.actionImportDocument': 'Importer un document (PDF ou Markdown)',
  'podcasts.pdfImport.title': 'Importer un document (PDF ou Markdown)',
  'podcasts.pdfImport.subtitle': 'Créer les épisodes du podcast « {{podcast}} » à partir de la structure du document',
  'podcasts.pdfImport.hint':
    'Le document est découpé automatiquement : Titre 1 / # → épisode, Titre 2 / ## → chapitre, paragraphes → blocs. Les PDF sont d’abord convertis en Markdown pour relecture et validation. Formats : PDF ou .md.',
  'podcasts.pdfImport.convertingPdf': 'Conversion du PDF en Markdown…',
  'podcasts.pdfImport.markdownReviewTitle': 'Valider le Markdown généré',
  'podcasts.pdfImport.markdownReviewSubtitle':
    'Podcast « {{podcast}} » — Markdown généré depuis votre PDF. Relisez, corrigez les titres (# / ##) puis validez.',
  'podcasts.pdfImport.markdownReviewHint':
    'Vérifiez que chaque épisode commence par # et chaque chapitre par ##. Vous pouvez corriger le texte avant de continuer.',
  'podcasts.pdfImport.markdownEditorLabel': 'Contenu Markdown',
  'podcasts.pdfImport.markdownEditorHint': '# Épisode · ## Chapitre · paragraphes = blocs de script',
  'podcasts.pdfImport.validateMarkdown': 'Valider et voir la structure',
  'podcasts.pdfImport.editMarkdown': 'Modifier le Markdown',
  'podcasts.pdfImport.markdownEmpty': 'Le Markdown est vide.',
  'podcasts.pdfImport.dropLabel': 'Déposez un PDF ou un fichier .md, ou choisissez un fichier',
  'podcasts.pdfImport.chooseFile': 'Choisir un fichier',
  'podcasts.pdfImport.analyzing': 'Analyse du document…',
  'podcasts.pdfImport.pageCount': '{{count}} page(s)',
  'podcasts.pdfImport.lineCount': '{{count}} ligne(s)',
  'podcasts.pdfImport.stats': '{{episodes}} épisode(s) · {{chapters}} chapitre(s) · {{blocks}} section(s)',
  'podcasts.pdfImport.episodeLabel': 'Épisode {{index}} — {{title}}',
  'podcasts.pdfImport.sectionsCount': '{{count}} section(s)',
  'podcasts.pdfImport.confirm': 'Créer les épisodes',
  'podcasts.pdfImport.importing': 'Création en cours…',
  'podcasts.pdfImport.previewError': 'Impossible d’analyser ce document.',
  'podcasts.pdfImport.importError': 'Échec de l’import.',
  'podcasts.pdfImport.invalidFile': 'Seuls les fichiers PDF et Markdown (.md) sont acceptés.',
  'podcasts.pdfImport.success': '{{episodes}} épisode(s), {{chapters}} chapitre(s) et {{blocks}} section(s) créés.',
  'podcasts.pdfImport.hintEditorPage':
    'Après sélection du fichier, vous serez redirigé vers l’éditeur Markdown avec un panneau d’aide à la structure (épisodes, chapitres, alertes).',
  'podcasts.pdfImport.editorPageTitle': 'Préparer l’import du document',
  'podcasts.pdfImport.editorPageSubtitle': 'Podcast « {{podcast}} » — {{file}}',
  'podcasts.pdfImport.editorEmptyHint':
    'Choisissez un PDF ou un fichier Markdown pour commencer. Vous pourrez corriger la structure avant de créer les épisodes.',
  'podcasts.pdfImport.backToPodcasts': 'Retour aux podcasts',
  'podcasts.pdfImport.replaceFile': 'Changer de fichier',
  'podcasts.pdfImport.documentTitleLabel': 'Titre du document',
  'podcasts.pdfImport.untitledDocument': 'Sans titre',
  'podcasts.pdfImport.invalidRoute': 'Podcast introuvable.',
  'podcasts.pdfImport.fixErrorsBeforeImport': 'Corrigez les erreurs de structure (panneau de droite) avant de créer les épisodes.',
  'podcasts.pdfImport.structurePanelTitle': 'Aide à la conception',
  'podcasts.pdfImport.structurePanelSubtitle': 'Estimation et alertes en temps réel pendant l’édition.',
  'podcasts.pdfImport.estimatedStructure': 'Structure estimée',
  'podcasts.pdfImport.statEpisodes': 'Épisodes',
  'podcasts.pdfImport.statChapters': 'Chapitres',
  'podcasts.pdfImport.statBlocks': 'Blocs',
  'podcasts.pdfImport.noPreviewYet': 'Saisissez ou importez du contenu pour analyser la structure.',
  'podcasts.pdfImport.markdownMeta': '{{lines}} lignes · {{h1}} titre(s) # · {{h2}} titre(s) ##',
  'podcasts.pdfImport.structureBlocked': 'Des erreurs bloquent l’import — corrigez le Markdown.',
  'podcasts.pdfImport.structureReady': 'Structure exploitable — vous pouvez créer les épisodes.',
  'podcasts.pdfImport.structureIssues': 'Alertes de structure',
  'podcasts.pdfImport.goToLine': 'Aller à la ligne {{line}} dans l’éditeur',
  'podcasts.pdfImport.suggestions': 'Suggestions',
  'podcasts.pdfImport.outlinePreview': 'Aperçu de l’arborescence',
  'podcasts.pdfImport.conceptionGuide': 'Guide de structuration',
  'podcasts.pdfImport.guideH1': '# Titre 1 = un épisode du podcast (ex. chapitre de livre, partie de cours).',
  'podcasts.pdfImport.guideH2': '## Titre 2 = chapitre à l’intérieur de l’épisode (sections, sous-parties).',
  'podcasts.pdfImport.guideBlocks': 'Paragraphes et listes = blocs de narration lus à la voix.',
  'podcasts.pdfImport.guidePdf':
    'Chaque PDF est différent : relisez toujours le Markdown généré et ajustez les # / ## avant l’import.',
  'podcasts.error.retry': 'Réessayer',
  'podcasts.filters.allCategories': 'Toutes catégories',
  'podcasts.filters.categoryLabel': 'Catégorie',
  'podcasts.filters.searchLabel': 'Recherche texte',
  'podcasts.filters.searchPlaceholder': 'Titre, description…',
  'podcasts.filters.viewCategories': 'Vue catégories',
  'podcasts.filters.viewList': 'Vue liste',
  'podcasts.form.descriptionLabel': 'Description',
  'podcasts.form.descriptionPlaceholder': 'Décrivez votre podcast…',
  'podcasts.form.namePlaceholder': 'Nom du podcast',
  'podcasts.form.titleLabel': 'Titre',
  'podcasts.episodeOrder.label': 'Ordre des épisodes',
  'podcasts.episodeOrder.help': 'Ordre de lecture dans le podcast (suit l’ordre du document à l’import).',
  'podcasts.episodeOrder.ariaGroup': 'Ordre des épisodes',
  'podcasts.episodeOrder.newestFirst': 'Plus récent → plus ancien',
  'podcasts.episodeOrder.oldestFirst': 'Plus ancien → plus récent',
  'podcasts.language.ariaGroup': 'Langue du podcast',
  'podcasts.language.help': 'Parmi les 11 langues de la plateforme — pour cadrer le podcast avec la même locale que le lecteur et l\'interface apprenant.',
  'podcasts.language.label': 'Langue',
  'podcasts.lightbox.ariaClose': 'Fermer',
  'podcasts.lightbox.ariaDialog': 'Aperçu cover podcast',
  'podcasts.list.actionActivate': 'Activer',
  'podcasts.list.actionDeactivate': 'Désactiver',
  'podcasts.list.actionDelete': 'Supprimer',
  'podcasts.list.filterAll': 'Tous',
  'podcasts.list.filterArchived': 'Archivés',
  'podcasts.list.archivePodcast': 'Archiver le podcast',
  'podcasts.list.restoreFromArchive': 'Restaurer depuis les archives',
  'podcasts.list.archiveSuccess': 'Podcast archivé',
  'podcasts.list.archiveError': 'Impossible d’archiver le podcast.',
  'podcasts.list.restoreSuccess': 'Podcast restauré',
  'podcasts.list.restoreError': 'Impossible de restaurer le podcast.',
  'podcasts.list.badgeArchived': 'Archivé',
  'podcasts.list.emptyArchivedNone': 'Aucun podcast archivé.',
  'podcasts.list.emptyAllArchived': 'Tous les podcasts sont archivés. Ouvrez l’onglet « Archivés » pour les retrouver.',
  'podcasts.list.actionManageEpisodes': 'Gérer les épisodes',
  'podcasts.list.actionPreview': 'Aperçu & écoute',
  'podcasts.list.coverPreviewAria': 'Aperçu cover du podcast {{title}}',
  'podcasts.list.createFirst': 'Créer un podcast',
  'podcasts.list.emptySubtitle': 'Créez votre premier podcast pour commencer',
  'podcasts.list.emptyTitle': 'Aucun podcast',
  'podcasts.list.loadError': 'Impossible de charger les podcasts.',
  'podcasts.list.enterpriseBadge': 'Entreprise',
  'podcasts.list.enterpriseNeedTitle': 'Besoin défini par votre entreprise',
  'podcasts.list.episodesShort': '{{count}} ép.',
  'podcasts.list.groupPodcastCount': '{{count}} podcast(s)',
  'podcasts.list.subscribersShort': '{{count}} abon.',
  'podcasts.list.uncategorized': 'Sans catégorie',
  'podcasts.modal.errorRequiredFields': 'Le titre et la description sont requis.',
  'podcasts.modal.errorSaveFailed': 'Erreur lors de la sauvegarde.',
  'podcasts.modal.subtitleNew': 'Renseignez titre, description et options. Vous pourrez ajouter des épisodes ensuite.',
  'podcasts.modal.titleEdit': 'Modifier le podcast',
  'podcasts.modal.titleNew': 'Nouveau podcast',
  'podcasts.page.episodesLink': 'Épisodes',
  'podcasts.page.newButton': 'Nouveau',
  'podcasts.page.title': 'Podcasts',
  'podcasts.page.totalCount': '{{count}} podcast(s)',
  'podcasts.pagination.pageOf': 'Page {{page}} / {{total}}',
  'podcasts.pagination.totalSummary': '{{count}} podcast(s) au total',
  'podcasts.player.listen': 'Écouter',
  'podcasts.player.noAudio': 'Pas d\'audio',
  'podcasts.player.pause': 'Pause',
  'podcasts.learner.pageTitle': 'Écouter le podcast',
  'podcasts.learner.invalidLink': 'Lien invalide ou expiré',
  'podcasts.learner.loadError': 'Impossible de charger le podcast',
  'podcasts.learner.loading': 'Chargement du podcast…',
  'podcasts.learner.noEpisodes': 'Aucun épisode publié disponible.',
  'podcasts.learner.listen': 'Écouter',
  'podcasts.learner.pause': 'Pause',
  'podcasts.preview.emptySubtitle': 'Ce podcast n\'a pas encore d\'épisodes',
  'podcasts.preview.emptyTitle': 'Aucun épisode',
  'podcasts.preview.episodeCount': '{{count}} épisode(s)',
  'podcasts.preview.fallbackDescription': 'Écoutez les épisodes audio disponibles.',
  'podcasts.preview.playAll': 'Tout écouter',
  'podcasts.preview.title': 'Aperçu du podcast',
  'podcasts.public': 'Podcast',
  'podcasts.public.subscribeButton': 'S’abonner aux nouveautés',
  'podcasts.public.subscribeModalTitle': 'Recevoir les nouveaux épisodes par email',
  'podcasts.public.subscribeModalIntro': 'Indiquez vos coordonnées. Nous vous enverrons un email lors de la publication d’un nouvel épisode.',
  'podcasts.public.fieldFirstName': 'Prénom',
  'podcasts.public.fieldLastName': 'Nom',
  'podcasts.public.fieldEmail': 'Email',
  'podcasts.public.fieldCompany': 'Société (facultatif)',
  'podcasts.public.submitSubscribe': 'Valider l’abonnement',
  'podcasts.public.cancel': 'Annuler',
  'podcasts.public.done': 'Terminé',
  'podcasts.public.subscribeSuccess': 'Merci ! Vous serez informé(e) à chaque nouvel épisode.',
  'podcasts.public.subscribeError': 'Impossible de finaliser l’abonnement. Réessayez plus tard.',
  'podcasts.public.subscribeValidationRequired': 'Le prénom, le nom et l’email sont obligatoires.',
  'podcasts.public.totalDuration': '{{duration}} au total',
  'podcasts.public.listenExcerpt': "Écouter l'extrait",
  'podcasts.playback.resumeAt': 'Reprise à {{time}}',
  'podcasts.playback.listened': 'Écouté',
  'podcasts.playback.listenedCount': '{{count}} écouté(s)',
  'podcasts.share.assignedLearnersHeading': '{{count}} apprenant(s) assigné(s)',
  'podcasts.share.copied': 'Copié',
  'podcasts.share.copyLink': 'Copier le lien',
  'podcasts.share.emailPlaceholder': 'Email *',
  'podcasts.share.errorCreateLearner': 'Impossible de créer l\'apprenant.',
  'podcasts.share.errorPublicLink': 'Impossible de générer le lien public.',
  'podcasts.share.errorRemoveLearner': 'Impossible de retirer l\'apprenant.',
  'podcasts.share.errorShare': 'Impossible de partager ce podcast.',
  'podcasts.share.firstNamePlaceholder': 'Prénom *',
  'podcasts.share.generatingLink': 'Génération du lien…',
  'podcasts.share.hideQuickCreate': 'Masquer la création rapide',
  'podcasts.share.lastNamePlaceholder': 'Nom *',
  'podcasts.share.loadingAssigned': 'Chargement…',
  'podcasts.share.loadingLearners': 'Chargement des apprenants...',
  'podcasts.share.messageAccessRemoved': 'Accès retiré pour l\'apprenant.',
  'podcasts.share.messageCreatedAndShared': 'Compte créé/lié, podcast partagé et email d\'invitation envoyé.',
  'podcasts.share.messageShared': 'Podcast partagé et invitation envoyée à l\'apprenant.',
  'podcasts.share.noAssignedLearners': 'Aucun apprenant assigné pour ce podcast.',
  'podcasts.share.noLearnerAvailable': 'Aucun apprenant disponible.',
  'podcasts.share.panelTitle': 'Partager le podcast',
  'podcasts.share.publicLinkSection': 'Lien public (sans connexion)',
  'podcasts.share.removeAccessTitle': 'Retirer l\'accès',
  'podcasts.share.searchLearnerPlaceholder': 'Rechercher un apprenant (nom, prénom, email)',
  'podcasts.share.selectExistingLearnerLabel': 'Sélectionner un apprenant existant',
  'podcasts.share.showQuickCreate': 'Créer un nouvel apprenant',
  'podcasts.share.submitCreateAndInvite': 'Créer, partager et envoyer l\'invitation',
  'podcasts.share.socialNetworksHeading': 'Partager sur les réseaux',
  'podcasts.share.shareLinkedIn': 'Partager sur LinkedIn',
  'podcasts.share.shareFacebook': 'Partager sur Facebook',
  'podcasts.share.shareTwitter': 'Partager sur X (Twitter)',
  'podcasts.share.publicShareStatsHeading': 'Statistiques page publique',
  'podcasts.share.publicShareStatsVisits': 'Visites (page publique)',
  'podcasts.share.publicShareStatsLoading': 'Chargement des statistiques…',
  'podcasts.share.publicShareStatsUnavailable': 'Statistiques indisponibles pour le moment.',
  'podcasts.share.publicShareStatsLinkedIn': 'LinkedIn',
  'podcasts.share.publicShareStatsFacebook': 'Facebook',
  'podcasts.share.publicShareStatsTwitter': 'X (Twitter)',
  'podcasts.share.publicSubscribersHeading': 'Abonnés (notifications email)',
  'podcasts.share.publicSubscribersEmpty': 'Aucun abonné pour l’instant.',
  'podcasts.share.publicSubscribersCompany': 'Entreprise',
  'share.publicQrTitle': 'QR code du lien public',
  'share.publicQrError': 'Impossible de générer le QR code.',
  'share.downloadQrPng': 'Télécharger en PNG',
  'podcasts.status.active': 'Actif',
  'podcasts.status.inactive': 'Inactif',
  'learningPaths.card.contents': '{{count}} contenu(s)',
  'learningPaths.card.delete': 'Supprimer',
  'learningPaths.card.edit': 'Modifier',
  'learningPaths.card.learners': '{{count}} apprenant(s)',
  'learningPaths.card.publish': 'Publier',
  'learningPaths.card.unpublish': 'Dépublier',
  'learningPaths.card.updatedAt': 'Mis à jour {{date}}',
  'learningPaths.createPanel.cancel': 'Annuler',
  'learningPaths.createPanel.description': 'Donnez un nom à votre parcours ; vous pourrez y ajouter podcasts et formations.',
  'learningPaths.createPanel.descriptionLabel': 'Description',
  'learningPaths.createPanel.descriptionPlaceholder': 'Objectif du parcours, public visé…',
  'learningPaths.createPanel.nameLabel': 'Nom du parcours',
  'learningPaths.createPanel.namePlaceholder': 'Ex. : Parcours d\'onboarding RH…',
  'learningPaths.createPanel.submit': 'Créer le parcours',
  'learningPaths.createPanel.title': 'Nouveau parcours',
  'learningPaths.deleteDialog.body': 'Vous allez supprimer « {{title}} ». Cette action est irréversible.',
  'learningPaths.deleteDialog.cancel': 'Annuler',
  'learningPaths.deleteDialog.confirm': 'Supprimer',
  'learningPaths.deleteDialog.title': 'Supprimer le parcours',
  'learningPaths.empty.createFirst': 'Créer un parcours',
  'learningPaths.empty.noPathsHint': 'Créez votre premier parcours en combinant podcasts et formations.',
  'learningPaths.empty.noPathsYet': 'Aucun parcours pour l\'instant',
  'learningPaths.empty.noResults': 'Aucun résultat',
  'learningPaths.empty.noResultsHint': 'Modifiez vos filtres ou créez un nouveau parcours.',
  'learningPaths.error.loadFailed': 'Impossible de charger les parcours',
  'learningPaths.error.retry': 'Réessayer',
  'learningPaths.filters.allStatuses': 'Tous les statuts',
  'learningPaths.filters.draft': 'Brouillon',
  'learningPaths.filters.published': 'Publié',
  'learningPaths.filters.searchPlaceholder': 'Rechercher un parcours…',
  'learningPaths.page.newButton': 'Nouveau parcours',
  'learningPaths.page.subtitle': 'Créez des parcours de formation ordonnés',
  'learningPaths.page.title': 'Parcours',
  'learningPaths.page.totalCount': '{{count}} parcours',
  'learningPaths.pagination.next': 'Suivant',
  'learningPaths.pagination.pageOf': 'Page {{page}} / {{total}} · {{count}} parcours',
  'learningPaths.pagination.previous': 'Précédent',
  'learningPaths.status.draft': 'Brouillon',
  'learningPaths.status.published': 'Publié',
  'learningPaths.editor.backToList': 'Retour aux parcours',
  'learningPaths.editor.pathNameLabel': 'Nom du parcours *',
  'learningPaths.editor.idCopyHint': 'Identifiant du parcours (pour le support)',
  'learningPaths.editor.sectionNavAria': 'Sections de l’éditeur de parcours',
  'learningPaths.editor.tabContents': 'Contenus',
  'learningPaths.editor.tabLearners': 'Apprenants',
  'webinars.card.code': 'Code {{code}}',
  'webinars.card.copyLink': 'Copier le lien',
  'webinars.card.exportAttendance': 'Télécharger la présence (CSV)',
  'webinars.card.attendanceExportError': 'Impossible de télécharger le fichier de présence.',
  'webinars.card.delete': 'Supprimer',
  'webinars.card.edit': 'Modifier',
  'webinars.card.openRoom': 'Ouvrir la salle',
  'webinars.card.publicSignupBadge': 'Inscription publique',
  'webinars.deleteDialog.body': 'Supprimer « {{title}} » ? Cette action est irréversible.',
  'webinars.deleteDialog.cancel': 'Annuler',
  'webinars.deleteDialog.confirm': 'Supprimer',
  'webinars.deleteDialog.title': 'Supprimer le webinaire',
  'webinars.eligibility.default': 'Les webinaires ne sont pas disponibles pour votre compte (vérifiez la connexion à l\'API ou contactez votre administrateur).',
  'webinars.eligibility.moduleDisabled': 'Le module webinaires n\'est pas encore activé pour votre établissement. Un administrateur doit cocher « Activer les webinaires » dans Établissement → Webinaires.',
  'webinars.eligibility.noInstitution': 'Aucun établissement n\'est associé à votre compte.',
  'webinars.eligibility.notAuthorized': 'Votre compte n\'est pas dans la liste des animateurs autorisés. Demandez à l\'établissement de vous ajouter (Pilotage webinaires → Animateurs autorisés).',
  'webinars.eligibility.roleNotAllowed': 'Votre rôle ne permet pas d\'animer des webinaires avec ce compte.',
  'webinars.empty.createFirst': 'Créer un webinaire',
  'webinars.empty.noResults': 'Aucun résultat',
  'webinars.empty.noResultsHint': 'Modifiez votre recherche.',
  'webinars.empty.noWebinarsHint': 'Créez une salle et invitez vos participants depuis le panneau latéral.',
  'webinars.empty.noWebinarsYet': 'Aucun webinaire pour l\'instant',
  'webinars.externalInvite.closeButton': 'Fermer',
  'webinars.externalInvite.description': 'Personnes qui ne figurent pas dans la liste des apprenants : chaque adresse reçoit un e-mail avec le lien de la salle et une pièce jointe pour le calendrier.',
  'webinars.externalInvite.done': 'Terminé.',
  'webinars.externalInvite.duplicatesCount_one': '{{count}} déjà invité',
  'webinars.externalInvite.duplicatesCount_other': '{{count}} déjà invités',
  'webinars.externalInvite.emailsHint': 'Une adresse par ligne, ou séparées par des virgules. Les doublons et les invitations déjà envoyées sont ignorés.',
  'webinars.externalInvite.emailsLabel': 'Adresses e-mail',
  'webinars.externalInvite.emailsPlaceholder': 'une.adresse@exemple.fr\nautre@domaine.org',
  'webinars.externalInvite.errorMax200': 'Maximum 200 adresses par envoi.',
  'webinars.externalInvite.errorMinOne': 'Saisissez au moins une adresse e-mail valide.',
  'webinars.externalInvite.errorsLabel': 'erreurs : {{errors}}',
  'webinars.externalInvite.sendButton': 'Envoyer les invitations',
  'webinars.externalInvite.sendFailed': 'Échec d\'envoi',
  'webinars.externalInvite.sentCount_one': '{{count}} e-mail envoyé',
  'webinars.externalInvite.sentCount_other': '{{count}} e-mails envoyés',
  'webinars.externalInvite.skippedCount_one': '{{count}} ignorée',
  'webinars.externalInvite.skippedCount_other': '{{count}} ignorées',
  'webinars.externalInvite.title': 'Invitations par e-mail',
  'webinars.invite.duplicatesCount_one': '{{count}} déjà invité',
  'webinars.invite.duplicatesCount_other': '{{count}} déjà invités',
  'webinars.invite.errorsLabel': 'erreurs : {{errors}}',
  'webinars.invite.externalButton': 'Hors liste (e-mail)',
  'webinars.invite.failed': 'Échec',
  'webinars.invite.groupSuccess': 'Invitations groupe traitées (e-mails envoyés aux membres).',
  'webinars.invite.inviteButton': 'Inviter',
  'webinars.invite.learnersHelp': 'Recherchez un apprenant de votre établissement : un <strong>e-mail d\'invitation</strong> (lien + agenda) est envoyé à chaque invitation.',
  'webinars.invite.learnersTitle': 'Apprenants',
  'webinars.invite.noCandidates': 'Aucun apprenant à afficher (essayez une autre recherche ou utilisez « Hors liste » pour des e-mails externes).',
  'webinars.invite.noInvitations': 'Aucune invitation enregistrée.',
  'webinars.invite.searchLabel': 'Rechercher un apprenant',
  'webinars.invite.searchPlaceholder': 'Nom ou e-mail…',
  'webinars.invite.searching': 'Recherche…',
  'webinars.invite.sentCount_one': '{{count}} invitation envoyée (e-mail)',
  'webinars.invite.sentCount_other': '{{count}} invitations envoyées (e-mail)',
  'webinars.invite.sentInvitationsToggle': 'Invitations envoyées',
  'webinars.page.newButton': 'Nouveau webinaire',
  'webinars.page.searchPlaceholder': 'Rechercher…',
  'webinars.page.subtitle': 'Liste de vos salles — ouvrez le panneau pour créer, modifier ou inviter des participants.',
  'webinars.page.title': 'Webinaires',
  'webinars.panel.backgroundHint':
    'Image derrière le contenu partagé pour les participants : saisissez une URL (https ou http), importez un fichier (enregistré dans Mes images) ou choisissez une image déjà importée. Laissez vide pour le visuel Lyrra intégré.',
  'webinars.panel.backgroundLabel': 'Image de fond (vue participant)',
  'webinars.panel.backgroundPlaceholder': 'https://… ou chemin Mes images',
  'webinars.panel.backgroundUrlLabel': 'Adresse de l’image (URL)',
  'webinars.panel.backgroundUploadTitle': 'Importer une image (Mes images)',
  'webinars.panel.backgroundLibraryTitle': 'Mes images',
  'webinars.panel.backgroundLibraryEmpty':
    'Aucune image pour l’instant — importez ci-dessus ou depuis la page Images du tableau de bord.',
  'webinars.panel.backgroundOpenImagesPage': 'Ouvrir la page Images',
  'webinars.panel.backgroundUseDefault': 'Utiliser le fond Lyrra par défaut',
  'webinars.panel.backgroundUploading': 'Envoi en cours…',
  'webinars.panel.backgroundUploadError': 'Échec de l’envoi de l’image.',
  'webinars.panel.copyLink': 'Copier le lien',
  'webinars.panel.createButton': 'Créer',
  'webinars.panel.createDescription': 'Renseignez les informations puis enregistrez. Vous pourrez inviter des participants ensuite.',
  'webinars.panel.createTitle': 'Nouveau webinaire',
  'webinars.panel.dateLabel': 'Jour (optionnel)',
  'webinars.panel.deleteButton': 'Supprimer',
  'webinars.panel.descriptionLabel': 'Description',
  'webinars.panel.durationError': 'Indiquez une durée au format heures:minutes (ex. 1:00 ou 0:45).',
  'webinars.panel.durationHint': 'Obligatoire si un jour est renseigné. Ex. 1:30 pour 1 h 30, 0:45 pour 45 min.',
  'webinars.panel.durationLabel': 'Durée (h:mm)',
  'webinars.panel.editTitle': 'Webinaire',
  'webinars.panel.loading': 'Chargement…',
  'webinars.panel.openRoom': 'Ouvrir la salle',
  'webinars.panel.presentersLabel': 'Co-présentateurs',
  'webinars.panel.roomCode': 'Code salle :',
  'webinars.panel.timeHint': 'Si vide : 9:00.',
  'webinars.panel.timeLabel': 'Heure de début',
  'webinars.panel.titleLabel': 'Titre',
  'webinars.panel.waitingHtmlLabel': 'HTML salle d\'attente (optionnel)',
  'webinars.panel.waitingHtmlPlaceholder': '<p>Bienvenue…</p>',
  'webinars.panel.publicAccessTitle': 'Inscription publique (formulaire)',
  'webinars.panel.publicAccessHelp':
    'Les participants non co-présentateurs remplissent un formulaire après connexion Lyrra : e-mail, mot de passe (qu’ils choisissent) et les champs supplémentaires que vous ajoutez ci-dessous.',
  'webinars.panel.publicFormFixedFieldsHint':
    'E-mail et mot de passe (8 caractères min.) sont toujours demandés aux participants ; vous ajoutez ici d’autres champs optionnels ou obligatoires.',
  'webinars.panel.publicFormAddFieldButton': 'Ajouter un champ',
  'webinars.panel.publicFormFieldNumber': 'Champ {{n}}',
  'webinars.panel.publicFormFieldLabel': 'Libellé affiché',
  'webinars.panel.publicFormFieldType': 'Type',
  'webinars.panel.publicFormFieldTypeText': 'Texte court',
  'webinars.panel.publicFormFieldTypeTextarea': 'Texte long',
  'webinars.panel.publicFormFieldRequired': 'Obligatoire',
  'webinars.panel.publicFormFieldRemove': 'Retirer',
  'webinars.panel.publicFormFieldIdHint': 'Identifiant technique :',
  'webinars.panel.publicFormFieldLabelRequired': 'Chaque champ doit avoir un libellé.',
  'webinars.panel.publicAccessLoginLabel': 'Identifiant du formulaire d’inscription',
  'webinars.panel.publicAccessPasswordLabel': 'Mot de passe du formulaire',
  'webinars.panel.publicAccessPasswordPlaceholderNew': 'Au moins 8 caractères',
  'webinars.panel.publicAccessPasswordPlaceholderKeep': 'Laisser vide pour conserver le mot de passe actuel',
  'webinars.panel.publicAccessPasswordHint':
    'À la création ou à la première activation : mot de passe obligatoire (8 caractères min.). Ensuite : laissez vide pour ne pas le changer.',
  'webinars.panel.publicAccessLoginRequired': 'Indiquez un identifiant pour l’inscription publique.',
  'webinars.panel.publicAccessPasswordRequired': 'Définissez un mot de passe d’au moins 8 caractères.',
  'webinars.panel.publicAccessPasswordMin': 'Le mot de passe public doit contenir au moins 8 caractères.',
  'webinars.status.draft': 'Brouillon',
  'webinars.status.ended': 'Terminé',
  'webinars.status.live': 'En direct',
  'webinars.status.scheduled': 'Planifié',
  'videos.addPanel.cancel': 'Annuler',
  'videos.addPanel.continue': 'Continuer',
  'videos.addPanel.description': 'Importez MP4, WebM ou MOV pour les utiliser dans vos formations.',
  'videos.addPanel.dropzoneFormats': 'MP4, WebM, MOV',
  'videos.addPanel.dropzoneLabel': 'Cliquer pour choisir une vidéo',
  'videos.addPanel.title': 'Ajouter une vidéo',
  'videos.addPanel.titleLabel': 'Titre',
  'videos.addPanel.titlePlaceholder': 'Titre de la vidéo…',
  'videos.card.delete': 'Supprimer',
  'videos.card.edit': 'Modifier',
  'videos.card.noThumbnail': 'Aucune miniature',
  'videos.card.publishedStudio': 'Publié (modifiable Studio)',
  'videos.card.draft': 'Brouillon',
  'videos.deleteDialog.body': '« {{title}} » sera supprimée définitivement.',
  'videos.deleteDialog.cancel': 'Annuler',
  'videos.deleteDialog.confirm': 'Supprimer',
  'videos.deleteDialog.title': 'Supprimer la vidéo ?',
  'videos.empty.addButton': 'Ajouter une vidéo',
  'videos.empty.description': 'Importez votre première vidéo pour la gérer et l\'utiliser dans vos formations.',
  'videos.empty.title': 'Aucune vidéo',
  'videos.page.addButton': 'Ajouter une vidéo',
  'videos.page.count_one': '{{count}} vidéo dans votre bibliothèque',
  'videos.page.count_other': '{{count}} vidéos dans votre bibliothèque',
  'videos.page.title': 'Vidéos',
  'videos.page.studioButton': 'Studio',
  'videos.studio.title': 'Studio vidéo',
  'videos.studio.rail.prep': 'Prépa',
  'videos.studio.rail.capture': 'Capture',
  'videos.studio.rail.post': 'Montage',
  'videos.studio.rail.aria': 'Étapes du studio vidéo',
  'videos.studio.badge.setup': 'Préparation',
  'videos.studio.badge.capture': 'Capture',
  'videos.studio.badge.recording': 'Enregistrement',
  'videos.studio.badge.post': 'Montage',
  'videos.studio.cannotLeaveRecord': 'Arrêtez l’enregistrement avant de changer d’étape.',
  'videos.studio.railNeedRecording': 'Enregistrez une vidéo pour accéder au montage.',
  'videos.studio.sidebar.captureHelp':
    'La capture enregistre l’intégralité du flux partagé (écran, onglet ou fenêtre). Le rognage pour ne garder qu’une zone utile se fera à l’étape Montage.',
  'videos.studio.sidebar.sources': 'Sources',
  'videos.studio.sidebar.recordingLive': 'Enregistrement en cours',
  'videos.studio.sidebar.finalizeHint':
    'Pour enregistrer dans la bibliothèque, utilisez le bouton dans la barre supérieure.',
  'videos.studio.canvas.setupEmpty':
    'Consultez les indications dans le panneau latéral, puis passez à l’étape Capture.',
  'videos.studio.stepSetup': 'Préparation',
  'videos.studio.stepCapture': 'Capture',
  'videos.studio.stepPost': 'Montage',
  'videos.studio.titleLabel': 'Titre de la vidéo',
  'videos.studio.titlePlaceholder': 'Ma capture…',
  'videos.studio.saveSetup': 'Continuer vers la capture',
  'videos.studio.fieldDescription': 'Description',
  'videos.studio.fieldCategories': 'Catégories',
  'videos.studio.categoriesHint':
    'Une ou plusieurs catégories de votre établissement (optionnel).',
  'videos.studio.categoriesEmptyInstitution':
    'Aucune catégorie n’est configurée pour votre établissement.',
  'videos.studio.audioTrackPlanned': 'Piste audio prévue',
  'videos.studio.voiceHumanPlan': 'Voix humaine (micro enregistré)',
  'videos.studio.voiceAiPlan': 'Voix IA (choix pour le montage)',
  'videos.studio.postVoiceHumanKeep':
    'Vous avez choisi la voix humaine : la vidéo conserve l’audio du micro. Passez à la finalisation lorsque vous êtes prêt.',
  'videos.studio.titleUntitled': 'Sans titre',
  'videos.studio.voiceAiNoVoices': 'Aucune voix IA disponible pour le moment.',
  'videos.studio.postVoiceAiPipeline':
    'Voix IA : préparez le texte, générez la piste audio puis appliquez-la à la vidéo.',
  'videos.studio.allowMedia': 'Autoriser écran et microphone',
  'videos.studio.controlsHint':
    'Astuce : pour éviter d’enregistrer cette barre, partagez un onglet ou une fenêtre plutôt que tout l’écran, ou utilisez un second écran. Espace = pause / reprise lorsque cette page a le focus.',
  'videos.studio.warnTabOnly':
    'Vous avez sélectionné un seul onglet : seul cet onglet sera enregistré, même si vous en ouvrez d’autres. Pour capturer plusieurs onglets ou fenêtres, relancez la capture et choisissez « Écran entier ».',
  'videos.studio.templateMenuTitle': 'Menu des modèles',
  'videos.studio.templateEmpty': 'Aucun modèle enregistré',
  'videos.studio.templatesHintCompact': 'Cliquez sur + pour enregistrer, appliquer ou supprimer un modèle.',
  'videos.studio.overlayAddMusic': 'Musique de fond',
  'videos.studio.musicInvalidType': 'Format invalide. Utilisez un audio (MP3, WAV, M4A, OGG).',
  'videos.studio.musicTooLarge': 'Le fichier audio ne doit pas dépasser 50 Mo.',
  'videos.studio.musicDefault': 'Musique',
  'videos.studio.musicAdded': 'Musique de fond ajoutée à la timeline.',
  'videos.studio.musicUploadError': "Impossible d'ajouter la musique.",
  'videos.studio.layerScreenBorder': 'Bordure extérieure',
  'videos.studio.layerScreenBorderSize': 'Taille',
  'videos.studio.layerScreenBorderColor': 'Couleur',
  'videos.studio.layerScreenBorderRadius': 'Arrondi des angles',
  'videos.studio.segmentToolsTitle': 'Outils de section',
  'videos.studio.segmentZoom': 'Zoom',
  'videos.studio.segmentResetView': 'Réinit. vue',
  'videos.studio.segmentTransition': 'Transition',
  'videos.studio.transitionNone': 'Aucune',
  'videos.studio.transitionFade': 'Fondu',
  'videos.studio.transitionWipeLeft': 'Balayage →',
  'videos.studio.transitionSlideUp': 'Glissé ↑',
  'videos.studio.textEditor.toolbar': 'Mise en forme',
  'videos.studio.textEditor.bold': 'Gras',
  'videos.studio.textEditor.italic': 'Italique',
  'videos.studio.textEditor.underline': 'Souligné',
  'videos.studio.textEditor.textColor': 'Couleur du texte (sélection)',
  'videos.studio.textEditor.size': 'Taille',
  'videos.studio.textEditor.font': 'Police',
  'videos.studio.textEditor.fontFamily.system': 'Système',
  'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
  'videos.studio.textEditor.fontFamily.serif': 'Serif',
  'videos.studio.textEditor.fontFamily.mono': 'Mono',
  'videos.studio.textEditor.fontFamily.script': 'Script',
  'videos.studio.controlsHintTab':
    'Vous partagez un autre onglet : la vidéo ne montre pas cette page Studio (les boutons n’y figurent donc pas). Gardez le Studio dans une autre fenêtre du navigateur ou sur un second écran, à côté de votre contenu. Pour Pause, Arrêter ou Terminer, basculez vers la fenêtre où est ouvert le Studio (Alt+Tab ou Cmd+Tab). La touche Espace ne fonctionne que lorsque le focus est sur la page Studio.',
  'videos.studio.controlsHintMonitor':
    'Vous partagez tout l’écran : cette barre peut apparaître dans l’enregistrement. Pour l’éviter, choisissez plutôt « Onglet » ou « Fenêtre » dans le partage, ou placez les contrôles sur un autre moniteur.',
  'videos.studio.controlsHintWindow':
    'Vous partagez une fenêtre : le reste du bureau (et les autres fenêtres) n’est en principe pas enregistré. Utilisez les boutons ci-dessous pour Pause, Arrêt ou Terminer.',
  'videos.studio.startRecord': 'Enregistrer',
  'videos.studio.pause': 'Pause',
  'videos.studio.resume': 'Reprendre',
  'videos.studio.stop': 'Arrêter',
  'videos.studio.terminer': 'Terminer',
  'videos.studio.processingUpload': 'Envoi et transcription…',
  'videos.studio.timeline': 'Lecture',
  'videos.studio.transcript': 'Texte reconnu',
  'videos.studio.transcriptRefining': 'Amélioration de la transcription en cours…',
  'videos.studio.transcriptPlaceholder': 'Le texte reconnu depuis votre enregistrement apparaîtra ici…',
  'videos.studio.voiceMode': 'Piste audio',
  'videos.studio.voiceOriginal': 'Ma voix (micro enregistré)',
  'videos.studio.voiceAi': 'Voix IA (Mistral TTS)',
  'videos.studio.voiceSelect': 'Voix',
  'videos.studio.polish': 'Préparer le texte pour la voix IA',
  'videos.studio.generateTts': 'Générer la voix off',
  'videos.studio.applyVoiceover': 'Appliquer la voix IA à la vidéo',
  'videos.studio.preview': 'Prévisualiser',
  'videos.studio.preview.noFile': 'Aucun fichier vidéo à prévisualiser.',
  'videos.studio.bgImage': 'Image de fond',
  'videos.studio.bgImageChoose': 'Choisir',
  'videos.studio.bgImageInvalidType': 'Format invalide. Utilisez une image (PNG, JPG, WebP).',
  'videos.studio.bgImageTooLarge': "L'image ne doit pas dépasser 10 Mo.",
  'videos.studio.bgImageLoadError': 'Impossible de charger l’image de fond.',
  'videos.studio.bgImageUploading': 'Envoi…',
  'videos.studio.bgImageUploadError': "Impossible d'envoyer l'image. Réessayez.",
  'videos.studio.bgImageLibraryTitle': 'Fond studio vidéo',
  'videos.studio.postLayoutTitle': 'Cadre & fond',
  'videos.studio.postLayoutHint':
    'Aperçu aligné sur la vidéo publiée : enregistrez le brouillon puis publiez pour appliquer fond, taille et position.',
  'videos.studio.bgImageVideoSize': 'Taille vidéo',
  'videos.studio.dragToMove': 'Glisser pour déplacer',
  'videos.studio.finalize': 'Finaliser et enregistrer',
  'videos.studio.saveDraft': 'Brouillon',
  'videos.studio.publish': 'Publier',
  'videos.studio.successDraft': 'Vidéo enregistrée en brouillon',
  'videos.studio.successPublish': 'Vidéo publiée dans votre bibliothèque',
  'videos.studio.processingPublish': 'Traitement de la vidéo en cours…',
  'videos.studio.back': 'Retour aux vidéos',
  'videos.studio.errorMedia': 'Impossible d’accéder à l’écran ou au micro.',
  'videos.studio.errorInsecureContext':
    'La capture nécessite HTTPS ou localhost. Ouvrez l’application via https:// ou http://127.0.0.1 / localhost.',
  'videos.studio.errorNotSupported':
    'Ce navigateur ou appareil ne prend pas en charge la capture d’écran ici. Utilisez Chrome ou Edge à jour sur ordinateur.',
  'videos.studio.errorMicDenied':
    'Micro refusé : autorisez le micro pour ce site (icône cadenas ou ⓘ dans la barre d’adresse), puis réessayez.',
  'videos.studio.errorScreenDenied':
    'Partage d’écran refusé : dans la fenêtre du navigateur, choisissez un écran ou un onglet et validez « Partager ».',
  'videos.studio.errorMicMissing': 'Aucun micro détecté. Vérifiez le branchement et les réglages système.',
  'videos.studio.errorScreenMissing': 'Partage d’écran indisponible sur cet appareil ou ce navigateur.',
  'videos.studio.errorMicBusy': 'Micro déjà utilisé par une autre application. Fermez-la puis réessayez.',
  'videos.studio.errorScreenBusy': 'Capture d’écran impossible (ressource occupée). Réessayez ou redémarrez le navigateur.',
  'videos.studio.errorScreenCancelled': 'Partage d’écran annulé.',
  'videos.studio.errorMicCancelled': 'Accès au micro annulé.',
  'videos.studio.errorMicGeneric': 'Impossible d’accéder au micro.',
  'videos.studio.errorScreenGeneric': 'Impossible de capturer l’écran.',
  'videos.studio.errorScreenEnded': 'Partage d’écran arrêté.',
  'videos.studio.mediaHints':
    'Chrome ou Edge sur ordinateur. Si l’URL est en http:// avec une IP type 192.168.x.x, le navigateur bloque souvent la capture : utilisez http://localhost:5173 ou un HTTPS valide.',
  'videos.studio.errorRecord': 'Enregistrement impossible sur ce navigateur.',
  'videos.studio.errorUpload': 'Échec de l’envoi.',
  'videos.studio.errorLoadDraft': 'Impossible de charger le brouillon.',
  'videos.studio.errorTranscribe': 'Transcription indisponible.',
  'videos.studio.errorPolish': 'Reformulation IA impossible.',
  'videos.studio.errorTts': 'Synthèse vocale impossible.',
  'videos.studio.errorMux': 'Montage audio impossible.',
  'videos.studio.muxSuccess': 'Voix IA appliquée à la vidéo',
  'videos.studio.successFinal': 'Vidéo enregistrée dans votre bibliothèque',
  'videos.studio.generatingTimeline': 'Génération de la timeline…',
  'videos.studio.crop': 'Recadrer',
  'videos.studio.cropReset': 'Réinitialiser le recadrage',
  'videos.studio.cropActive': 'Recadrage actif — déplacez ou redimensionnez la zone sur la vidéo',
  'videos.studio.cropDone': 'Terminer',
  'videos.studio.cropApplied': 'Rognage appliqué',
  'videos.studio.cropEdit': 'Modifier',
  'videos.studio.cropRemove': 'Supprimer',
  'videos.studio.trimIn': 'IN',
  'videos.studio.trimOut': 'OUT',
  'videos.studio.trimDuration': 'durée',
  'videos.studio.errorCamDenied': "Caméra refusée : cliquez sur l’icône de cadenas dans la barre d’adresse et autorisez la caméra pour ce site.",
  'videos.studio.errorCamMissing': "Aucune caméra détectée. Branchez une webcam ou vérifiez les réglages système.",
  'videos.studio.errorCamBusy': "Caméra déjà utilisée par une autre application. Fermez l’autre app puis réessayez.",
  'videos.studio.errorCamGeneric': "Impossible d’accéder à la caméra.",
  'videos.studio.leaveWhileRecording': "Un enregistrement est en cours : il sera perdu si vous quittez. Quitter quand même ?",
  'videos.studio.leaveUnsaved': "Des modifications ne sont pas enregistrées. Quitter le studio quand même ?",
  'videos.studio.unsavedChanges': "Modifications non enregistrées",
  'videos.studio.saveDraftShortcut': "Enregistrer le brouillon (Ctrl+S)",
  'videos.studio.pendingRecordingTitle': "Un enregistrement non envoyé a été retrouvé",
  'videos.studio.pendingRecordingBody': "Son envoi avait été interrompu. Vous pouvez l’envoyer maintenant pour le monter, ou le supprimer.",
  'videos.studio.pendingRecordingResume': "Envoyer et monter",
  'videos.studio.pendingRecordingDiscard': "Supprimer",
  'videos.studio.uploadFailedTitle': "L’envoi de l’enregistrement a échoué",
  'videos.studio.uploadFailedBody': "Votre prise est conservée dans ce navigateur. Vérifiez la connexion puis réessayez : rien n’est perdu.",
  'videos.studio.uploadRetry': "Réessayer l’envoi",
  'videos.studio.captionsInProgress': "Transcription et sous-titres en cours… vous pouvez continuer le montage.",
  'videos.studio.muxConfirm': "La piste micro de la vidéo va être remplacée définitivement par la voix IA. Continuer ?",
  'videos.studio.play': "Lecture",
  'videos.studio.back5': "Reculer de 5 s",
  'videos.studio.forward5': "Avancer de 5 s",
  'videos.studio.keySpace': "Espace",
  'videos.studio.mute': "Couper le son",
  'videos.studio.unmute': "Activer le son",
  'videos.studio.fullscreen': "Plein écran",
  'videos.studio.showSubtitles': "Afficher les sous-titres",
  'videos.studio.hideSubtitles': "Masquer les sous-titres",
  'videos.studio.subtitlesTrackLabel': "Sous-titres",
  'videos.studio.seekBar': "Position de lecture",
  'videos.preview.noFile': 'Aucun fichier disponible.',
  'videos.status.draft': 'Brouillon',
  'videos.status.published': 'Publié',
  'sounds.card.delete': 'Supprimer',
  'sounds.card.download': 'Télécharger',
  'sounds.card.mute': 'Couper le son',
  'sounds.deleteDialog.body': '« {{title}} » sera supprimé définitivement du serveur et de votre bibliothèque.',
  'sounds.deleteDialog.cancel': 'Annuler',
  'sounds.deleteDialog.confirm': 'Supprimer',
  'sounds.deleteDialog.title': 'Supprimer ce son ?',
  'sounds.empty.description': 'Importez vos fichiers : ils sont enregistrés en base et copiés sur le serveur pour ne rien perdre.',
  'sounds.empty.importButton': 'Importer un son',
  'sounds.empty.title': 'Aucun son importé',
  'sounds.fileSize.bytes': '{{size}} o',
  'sounds.fileSize.kb': '{{size}} Ko',
  'sounds.fileSize.mb': '{{size}} Mo',
  'sounds.importPanel.cancel': 'Annuler',
  'sounds.importPanel.changeFile': 'Changer de fichier',
  'sounds.importPanel.createTag': 'Créer « {{tag}} »',
  'sounds.importPanel.description': 'Ajoutez un fichier audio à votre bibliothèque pour vos parcours.',
  'sounds.importPanel.descriptionLabel': 'Description',
  'sounds.importPanel.descriptionPlaceholder': 'Décrivez ce son…',
  'sounds.importPanel.dropzoneBrowse': 'parcourez',
  'sounds.importPanel.dropzoneFormats': 'MP3, WAV, OGG, M4A, AAC, FLAC',
  'sounds.importPanel.dropzoneLabel': 'Glissez un fichier audio ou',
  'sounds.importPanel.optional': '(optionnel)',
  'sounds.importPanel.submit': 'Importer',
  'sounds.importPanel.submitting': 'Envoi…',
  'sounds.importPanel.tagsHint': 'Tapez et appuyez sur Entrée · Les nouveaux tags sont créés automatiquement',
  'sounds.importPanel.tagsLabel': 'Tags',
  'sounds.importPanel.tagsPlaceholder': 'Ajouter des tags…',
  'sounds.importPanel.title': 'Importer un son',
  'sounds.importPanel.titleLabel': 'Titre',
  'sounds.importPanel.titlePlaceholder': 'Nom du son…',
  'sounds.importPanel.titleRequired': 'Le titre est obligatoire',
  'sounds.noResults': 'Aucun son ne correspond à « {{query}} »',
  'sounds.page.count_one': '{{count}} fichier — sauvegardé en base et sur le serveur',
  'sounds.page.count_other': '{{count}} fichiers — sauvegardés en base et sur le serveur',
  'sounds.page.importButton': 'Importer',
  'sounds.page.searchPlaceholder': 'Titre, description, tag…',
  'sounds.page.title': 'Sons',
  'documents.count_one': '{{count}} document',
  'documents.count_other': '{{count}} documents',
  'documents.delete.body': 'Le document « {{filename}} » sera retiré de votre bibliothèque.',
  'documents.delete.cancel': 'Annuler',
  'documents.delete.confirm': 'Supprimer',
  'documents.delete.irreversible': 'Cette action est irréversible.',
  'documents.delete.title': 'Supprimer le document ?',
  'documents.importPanel.cancel': 'Annuler',
  'documents.importPanel.chooseFile': 'Choisir un document PDF',
  'documents.importPanel.description': 'Le document sera disponible dans vos blocs PDF et dans cette bibliothèque.',
  'documents.importPanel.descriptionLabel': 'Description',
  'documents.importPanel.descriptionPlaceholder': 'Description (optionnelle)',
  'documents.importPanel.fileHint': 'Le document sera disponible dans votre bloc PDF et dans cette bibliothèque.',
  'documents.importPanel.submit': 'Importer',
  'documents.importPanel.title': 'Importer un PDF',
  'documents.importPanel.titleLabel': 'Titre',
  'documents.importPanel.titlePlaceholder': 'Titre du document',
  'documents.importPanel.uploading': 'Upload en cours',
  'documents.importPdf': 'Importer un PDF',
  'documents.loading': 'Chargement de la bibliothèque…',
  'documents.newDocument': 'Nouveau document',
  'documents.noDocuments': 'Aucun document',
  'documents.noDocumentsHint': 'Importez vos PDFs ici pour les réutiliser ensuite dans le bloc PDF.',
  'documents.noResults': 'Aucun document ne correspond à votre recherche.',
  'documents.pages_one': '{{count}} page',
  'documents.pages_other': '{{count}} pages',
  'documents.previewTitle': 'Aperçu du document',
  'documents.searchPlaceholder': 'Rechercher un PDF...',
  'documents.share.accessRemoved': 'Accès retiré pour l\'apprenant.',
  'documents.share.assignedTitle': 'Apprenants ayant accès',
  'documents.share.close': 'Fermer',
  'documents.share.createAndShare': 'Créer, partager et envoyer l\'invitation',
  'documents.share.createError': 'Impossible de créer l\'apprenant.',
  'documents.share.createLearner': 'Créer un nouvel apprenant',
  'documents.share.createdSuccess': 'Compte créé/lié, document partagé et email envoyé.',
  'documents.share.creating': 'Envoi...',
  'documents.share.emailPlaceholder': 'Email *',
  'documents.share.firstNamePlaceholder': 'Prénom *',
  'documents.share.hideCreateLearner': 'Masquer la création rapide',
  'documents.share.lastNamePlaceholder': 'Nom *',
  'documents.share.loadingAssigned': 'Chargement…',
  'documents.share.loadingLearners': 'Chargement des apprenants...',
  'documents.share.noAssigned': 'Aucun apprenant assigné pour ce document.',
  'documents.share.noLearners': 'Aucun apprenant disponible.',
  'documents.share.removeButton': 'Retirer',
  'documents.share.removeError': 'Impossible de retirer l\'accès.',
  'documents.share.searchPlaceholder': 'Rechercher un apprenant (nom, prénom, email)',
  'documents.share.selectLearner': 'Sélectionner un apprenant existant',
  'documents.share.shareButton': 'Partager',
  'documents.share.shareError': 'Impossible de partager le document.',
  'documents.share.sharedSuccess': 'Document partagé et invitation envoyée.',
  'documents.share.title': 'Partager le document',
  'documents.subtitle': 'Bibliothèque de documents PDF réutilisable dans le designer.',
  'documents.title': 'Documents',
  'documents.toast.importError': 'Import impossible',
  'documents.toast.importErrorDefault': 'L\'import a échoué.',
  'documents.toast.importSuccess': 'Document importé',
  'documents.toast.importSuccessDesc': '{{filename}} a été ajouté à votre bibliothèque documents.',
  'documents.updating': 'Mise à jour…',
  'help.categories.analytics.description': 'Tableaux de bord, filtres et exports pour piloter l\'impact des formations.',
  'help.categories.analytics.title': 'Analytiques & rapports',
  'help.categories.apprenants.description': 'Suivre les inscriptions, les cohortes et la progression des participants.',
  'help.categories.apprenants.title': 'Apprenants, groupes & suivi',
  'help.categories.demarrage.description': 'Prendre en main l\'interface, votre profil et la navigation au quotidien dans Lyrra.',
  'help.categories.demarrage.title': 'Démarrage & compte',
  'help.categories.donnees.description': 'RGPD, conservation, droits des personnes et bonnes pratiques de sécurité.',
  'help.categories.donnees.title': 'Données personnelles & sécurité',
  'help.categories.eduflow.description': 'Concevoir des parcours pédagogiques avec blocs, connexions et logique de navigation.',
  'help.categories.eduflow.title': 'Parcours & designer EduFlow',
  'help.categories.evaluation.description': 'Quiz, évaluations, sondages et retours pour mesurer les acquis.',
  'help.categories.evaluation.title': 'Évaluation & pédagogie active',
  'help.categories.institution.description': 'Paramétrer votre structure, les collaborateurs et l\'organisation des contenus.',
  'help.categories.institution.title': 'Établissement & équipe',
  'help.categories.marque.description': 'Aligner couleurs, libellés et parcours apprenant sur votre identité.',
  'help.categories.marque.title': 'Marque & expérience utilisateur',
  'help.categories.medias.description': 'Gérer bibliothèque, imports et outils audio / vidéo / image intégrés à Lyrra.',
  'help.categories.medias.title': 'Médias & studio créatif',
  'help.categories.parcours-v2.description': 'Campagnes, compétences, intégrations : tout ce qui étoffe le pilotage formation.',
  'help.categories.parcours-v2.title': 'Fonctions avancées & feuille de route',
  'help.categories.podcasts.description': 'Créer et diffuser des contenus audio longs format hors du parcours bloc par bloc.',
  'help.categories.podcasts.title': 'Podcasts & livres audio',
  'help.categories.publication.description': 'Rendre un parcours disponible aux apprenants et partager les bons accès.',
  'help.categories.publication.title': 'Publication & diffusion',
  'help.categories.qualite.description': 'Cadres qualité français : traçabilité, objectifs et preuves pour les audits.',
  'help.categories.qualite.title': 'Qualité, Qualiopi & conformité pédagogique',
  'help.categories.support.description': 'En cas de blocage technique ou fonctionnel, savoir où chercher et qui contacter.',
  'help.categories.support.title': 'Dépannage & support',
  'help.configHint': 'Contenu éditorial : branchez vos URLs, articles ou base de connaissances quand vous serez prêt.',
  'help.description': 'Documentation et support',
  'help.hubIntro': 'Thématiques couvrant l\'usage de Lyrra : vous pourrez bientôt lier chaque fiche à votre documentation, tutoriels ou tickets support.',
  'help.modal.expectedAnswer': 'Réponse attendue :',
  'help.modal.fallbackHighlight1': 'Fiche',
  'help.modal.fallbackHighlight2': 'Plateforme formation',
  'help.modal.fallbackHighlight3': 'À compléter',
  'help.modal.fallbackSectionHeading': 'À retenir',
  'help.modal.fallbackSectionParagraph': 'Le contenu détaillé de cette fiche sera enrichi ou relié à votre documentation interne.',
  'help.modal.fallbackStep1Desc': 'Utilisez le menu latéral ou le tableau de bord pour accéder au module concerné.',
  'help.modal.fallbackStep1Title': 'Ouvrir la fonctionnalité',
  'help.modal.fallbackStep2Desc': 'Les écrans vous guident : enregistrez souvent et testez en prévisualisation lorsque c\'est disponible.',
  'help.modal.fallbackStep2Title': 'Suivre le flux métier',
  'help.modal.fallbackStep3Desc': 'Notez les spécificités de votre établissement (process RH, nommage, charte) pour votre équipe.',
  'help.modal.fallbackStep3Title': 'Documenter en interne',
  'help.modal.fallbackTip': 'En cas de doute, vérifiez votre rôle (formateur, admin, RH) : les menus affichés en dépendent.',
  'help.modal.quiz': 'Quiz de validation',
  'help.modal.related': 'Voir aussi',
  'help.modal.resources': 'Ressources',
  'help.modal.steps': 'En pratique',
  'help.modal.tips': 'Conseils',
  'help.noResults': 'Aucun résultat pour cette recherche.',
  'help.onboarding.ai-designer': 'Formation — Assistant IA du designer LyrraStudio (onboarding)',
  'help.onboarding.blocks': 'Formation — Types de blocs EduFlow (onboarding)',
  'help.onboarding.branches': 'Formation — Branches & conditions EduFlow (onboarding)',
  'help.onboarding.catalog-visibility': 'Formation — Catalogue & visibilité LyrraStudio (onboarding)',
  'help.onboarding.certificates': 'Formation — Certificats délivrés LyrraStudio (onboarding)',
  'help.onboarding.dashboard': 'Formation — Tableau de bord Lyrra (onboarding)',
  'help.onboarding.designer-overview': 'Formation — Vue d\'ensemble du concepteur EduFlow (onboarding)',
  'help.onboarding.enrollments': 'Formation — Inscriptions & participants LyrraStudio (onboarding)',
  'help.onboarding.groups-cohorts': 'Formation — Groupes & cohortes LyrraStudio (onboarding)',
  'help.onboarding.media-library': 'Formation — Bibliothèque média LyrraStudio (onboarding)',
  'help.onboarding.navigation': 'Formation — Navigation & menu latéral Lyrra (onboarding)',
  'help.onboarding.objectives': 'Formation — Objectifs pédagogiques EduFlow (onboarding)',
  'help.onboarding.profile': 'Formation — Profil & préférences Lyrra (onboarding)',
  'help.onboarding.progress': 'Formation — Progression & temps passé LyrraStudio (onboarding)',
  'help.onboarding.publication-links': 'Formation — Liens d\'accès & partage LyrraStudio (onboarding)',
  'help.onboarding.publication-status': 'Formation — Brouillon, publication et mises à jour LyrraStudio (onboarding)',
  'help.onboarding.roles': 'Formation — Rôles et droits d\'accès Lyrra (onboarding)',
  'help.onboarding.validation': 'Formation — Validation du parcours EduFlow (onboarding)',
  'help.onboardingFlowMissing': 'Formation introuvable',
  'help.onboardingFlowMissingDesc': 'Exécutez les scripts db:seed:onboarding-eduflow et db:seed:onboarding-navigation-eduflow (backend), ou les variables VITE_ONBOARDING_*_FLOW_ID.',
  'help.openArticle': 'Ouvrir la fiche',
  'help.searchPlaceholder': 'Rechercher une thématique…',
  'help.title': 'Centre d\'aide',
  'help.topics.analytics-exports.description': 'Préparer les extractions pour le reporting interne ou réglementaire.',
  'help.topics.analytics-exports.title': 'Exports & reporting RH',
  'help.topics.analytics-indicateurs.description': 'Taux de complétion, engagement, répartition par statut et temps sur les blocs.',
  'help.topics.analytics-indicateurs.title': 'Indicateurs clés',
  'help.topics.analytics-vue.description': 'Personnaliser graphiques, périodes et filtre par formation pour analyser les données.',
  'help.topics.analytics-vue.title': 'Vue d\'ensemble analytique',
  'help.topics.app-certificats.description': 'Vérifier les attestations générées et les conditions de réussite associées.',
  'help.topics.app-certificats.title': 'Certificats délivrés',
  'help.topics.app-groupes.description': 'Utiliser les groupes pour segmenter les publics et faciliter les campagnes ou rapports.',
  'help.topics.app-groupes.title': 'Groupes & cohortes',
  'help.topics.app-inscriptions.description': 'Consulter la liste des apprenants sur un parcours, leurs statuts et historiques d\'accès.',
  'help.topics.app-inscriptions.title': 'Inscriptions & participants',
  'help.topics.app-progression.description': 'Interpréter la progression, les complétions et le temps d\'engagement dans les blocs.',
  'help.topics.app-progression.title': 'Progression & temps passé',
  'help.topics.demarrage-navigation.description': "Le menu de gauche, la recherche rapide, la langue, le thème et le menu de votre compte.",
  'help.topics.demarrage-navigation.title': "Navigation & menu latéral",
  'help.topics.demarrage-profil.description': "Vos informations, votre photo, votre mot de passe, vos notifications et la langue de l’interface.",
  'help.topics.demarrage-profil.title': "Profil & préférences",
  'help.topics.demarrage-roles.description': "Enseignant, professeur principal, direction, multi-site et élève : qui voit quoi.",
  'help.topics.demarrage-roles.title': "Rôles et droits d’accès",
  'help.topics.demarrage-formation-simple.description': 'Créer une formation en mode Simple : modules, chapitres et blocs sans le designer graphique EduFlow.',
  'help.topics.demarrage-formation-simple.title': 'Création de formation simple',
  'help.topics.demarrage-tableau-bord.description': "Créer un cours, reprendre votre dernier travail et lire vos indicateurs dès la connexion.",
  'help.topics.demarrage-tableau-bord.title': "Tableau de bord formation",
  'help.topics.donnees-conservation.description': 'Principes de durée d\'archivage des progressions et des preuves de formation.',
  'help.topics.donnees-conservation.title': 'Durée de conservation',
  'help.topics.donnees-mots-de-passe.description': 'Bonnes pratiques de mots de passe, sessions et délégation d\'accès.',
  'help.topics.donnees-mots-de-passe.title': 'Comptes & authentification',
  'help.topics.donnees-rgpd.description': 'Finalités des données collectées dans Lyrra et responsabilité du responsable de formation.',
  'help.topics.donnees-rgpd.title': 'RGPD & registre des traitements',
  'help.topics.eduflow-blocs.description': "Les familles de blocs de la palette, comment trouver le bon et comment le régler.",
  'help.topics.eduflow-blocs.title': "Types de blocs (contenu, quiz, médias…)",
  'help.topics.eduflow-branches.description': "Orienter l’élève selon sa réponse au quiz, proposer des voies au choix et faire converger les branches.",
  'help.topics.eduflow-branches.title': "Branches & conditions",
  'help.topics.eduflow-concept.description': "Barre d’outils, palette de blocs, canevas, panneau Propriétés et aperçu : le designer de parcours en un coup d’œil.",
  'help.topics.eduflow-concept.title': "Vue d’ensemble du concepteur",
  'help.topics.eduflow-ia.description': "Demander à l’IA d’écrire, de vérifier ou de modifier des blocs, et générer un QCM à partir de votre leçon, en gardant toujours la main.",
  'help.topics.eduflow-ia.title': "Assistant IA dans le designer",
  'help.topics.eduflow-objectifs.description': "Écrire les objectifs du cours, les classer (savoir, savoir-faire, savoir-être, niveau de Bloom) et les relier aux blocs qui les travaillent.",
  'help.topics.eduflow-objectifs.title': "Objectifs pédagogiques",
  'help.topics.eduflow-validation.description': "Repérer et corriger ce qui empêche la publication : titres manquants, blocs sans suite, branches qui ne se rejoignent pas.",
  'help.topics.eduflow-validation.title': "Validation du parcours",
  'help.topics.eval-auto-eval.description': 'Proposer des parcours adaptatifs en fonction des résultats.',
  'help.topics.eval-auto-eval.title': 'Auto-évaluation & remédiation',
  'help.topics.eval-ouverte.description': 'Organiser la collecte de réponses libres et la notation manuelle ou semi-automatisée.',
  'help.topics.eval-ouverte.title': 'Questions ouvertes & correction',
  'help.topics.eval-quiz.description': 'Configurer barèmes, feedbacks et tentatives pour un QCM efficace.',
  'help.topics.eval-quiz.title': 'Quiz & questions à choix',
  'help.topics.inst-collaborateurs.description': 'Ajouter des formateurs, administrateurs ou RH et gérer les permissions.',
  'help.topics.inst-collaborateurs.title': 'Utilisateurs & invitations',
  'help.topics.inst-structure.description': 'Définir ce qui est partagé au niveau établissement versus projets personnels.',
  'help.topics.inst-structure.title': 'Structure & périmètre',
  'help.topics.inst-trash.description': 'Retrouver formations ou podcasts supprimés et les restaurer si besoin.',
  'help.topics.inst-trash.title': 'Corbeille & restauration',
  'help.topics.marque-accessibilite.description': 'Contrastes, alternatives textuelles et navigation clavier dans les contenus.',
  'help.topics.marque-accessibilite.title': 'Accessibilité & lisibilité',
  'help.topics.marque-charte.description': 'Comment la marque se reflète dans le player et les espaces apprenants.',
  'help.topics.marque-charte.title': 'Charte & couleurs institutionnelles',
  'help.topics.marque-landing.description': 'Présenter votre offre de formation côté visiteur lorsque le module est activé.',
  'help.topics.marque-landing.title': 'Pages publiques & catalogue',
  'help.topics.medias-bibliotheque.description': 'Importer, organiser et réutiliser vidéos, sons, images et documents dans vos blocs.',
  'help.topics.medias-bibliotheque.title': 'Bibliothèque média',
  'help.topics.medias-documents.description': 'Joindre des ressources téléchargeables et lier la documentation au parcours.',
  'help.topics.medias-documents.title': 'Documents & PDF',
  'help.topics.medias-studio-audio.description': 'Enregistrer, mixer ou utiliser la synthèse vocale pour enrichir vos contenus.',
  'help.topics.medias-studio-audio.title': 'Studio audio & voix',
  'help.topics.medias-video.description': 'Bonnes pratiques de format, poids et accessibilité pour les lecteurs intégrés.',
  'help.topics.medias-video.title': 'Vidéos & intégrations',
  'help.topics.podcasts-creation.description': 'Structurer des épisodes, métadonnées et couvertures pour votre offre audio.',
  'help.topics.podcasts-creation.title': 'Création d\'un podcast',
  'help.topics.podcasts-diffusion.description': 'Paramètres d\'écoute, partage et suivi d\'usage côté institution.',
  'help.topics.podcasts-diffusion.title': 'Diffusion & apprenants',
  'help.topics.pub-catalogue.description': 'Rattacher une formation au catalogue institutionnel ou à une offre lorsque c\'est activé.',
  'help.topics.pub-catalogue.title': 'Catalogue & visibilité',
  'help.topics.pub-liens.description': 'Générer ou copier les liens pour les apprenants, présentateurs ou prévisualisation.',
  'help.topics.pub-liens.title': 'Liens d\'accès & partage',
  'help.topics.pub-statuts.description': 'Cycle de vie d\'une formation : enregistrer, tester, publier et versionner les changements.',
  'help.topics.pub-statuts.title': 'Brouillon, publication et mises à jour',
  'help.topics.qualite-qualiopi.description': 'Utiliser les espaces dédiés pour préparer les critères et le dossier qualité.',
  'help.topics.qualite-qualiopi.title': 'Pré-audit & indicateurs Qualiopi',
  'help.topics.qualite-tracabilite.description': 'Relier objectifs, contenus et évaluations pour documenter le parcours certifiant.',
  'help.topics.qualite-tracabilite.title': 'Traçabilité pédagogique',
  'help.topics.support-contact.description': 'Canal support, création de ticket et informations à fournir pour un diagnostic rapide.',
  'help.topics.support-contact.title': 'Contacter le support Lyrra',
  'help.topics.support-faq.description': 'Lecteur qui ne charge pas, média manquant, erreur de publication : premiers réflexes.',
  'help.topics.support-faq.title': 'Problèmes fréquents',
  'help.topics.support-navigateur.description': 'Versions recommandées, cookies et extensions qui peuvent interférer.',
  'help.topics.support-navigateur.title': 'Navigateur & compatibilité',
  'help.topics.v2-campagnes.description': 'Planifier le déploiement de parcours auprès de populations ciblées dans le temps.',
  'help.topics.v2-campagnes.title': 'Campagnes & plans de formation',
  'help.topics.v2-competences.description': 'Mapper les blocs ou parcours aux compétences attendues pour le reporting métiers.',
  'help.topics.v2-competences.title': 'Référentiels de compétences',
  'help.topics.v2-integrations.description': 'Échanges avec votre SI-RH, LMS externe ou outils de BI lorsque connecteurs disponibles.',
  'help.topics.v2-integrations.title': 'Intégrations & API',
  'pages.images.cancel': 'Annuler',
  'pages.images.changeFile': 'Changer de fichier',
  'pages.images.copyUrl': 'Copier l\'URL',
  'pages.images.copyUrlSuccess': 'URL copiée dans le presse-papiers.',
  'pages.images.copyUrlError': 'Impossible de copier l’URL.',
  'pages.images.urlLabel': 'URL publique',
  'pages.images.download': 'Télécharger',
  'pages.images.downloadSuccess': 'Téléchargement lancé.',
  'pages.images.downloadError': 'Téléchargement impossible.',
  'pages.images.count_one': '{{count}} image dans votre bibliothèque',
  'pages.images.count_other': '{{count}} images dans votre bibliothèque',
  'pages.images.delete': 'Supprimer',
  'pages.images.deleteAction': 'Supprimer',
  'pages.images.deleteConfirmHint': 'sera supprimée définitivement.',
  'pages.images.deleteTitle': 'Supprimer cette image ?',
  'pages.images.descriptionLabel': 'Description (optionnel)',
  'pages.images.descriptionPlaceholder': 'Description rapide…',
  'pages.images.dropzoneBrowse': 'parcourez',
  'pages.images.dropzoneFormats': 'PNG, JPG, WEBP, GIF',
  'pages.images.dropzoneLabel': 'Glissez une image ou',
  'pages.images.fileSize.bytes': '{{size}} o',
  'pages.images.fileSize.kb': '{{size}} Ko',
  'pages.images.fileSize.mb': '{{size}} Mo',
  'pages.images.fileSizeKb': '{{size}} Ko',
  'pages.images.importButton': 'Importer',
  'pages.images.importSubtitle': 'Ajoutez une image à votre bibliothèque pour vos formations.',
  'pages.images.importTitle': 'Importer une image',
  'pages.images.noImages': 'Aucune image importée',
  'pages.images.noResults': 'Aucun résultat',
  'pages.images.searchPlaceholder': 'Rechercher une image…',
  'pages.images.submit': 'Importer',
  'pages.images.titleLabel': 'Titre (optionnel)',
  'pages.images.titlePlaceholder': 'Nom de l\'image…',
  'pages.images.viewGrid': 'Grille',
  'pages.images.viewList': 'Liste',
  'pages.images.editPanelTitle': 'Modifier l’image',
  'pages.images.editPanelSubtitle':
    'Titre, description et nom de fichier. Remplacer le fichier conserve la même URL (les contenus qui l’utilisent restent valides).',
  'pages.images.replaceFileLabel': 'Nouvelle image',
  'pages.images.replaceFileHint':
    'Le format est adapté à l’extension actuelle du fichier. L’URL publique ne change pas.',
  'pages.images.fileOriginalNameLabel': 'Nom du fichier d’origine',
  'pages.images.saveChanges': 'Enregistrer',
  'pages.images.saveSuccess': 'Image mise à jour.',
  'pages.images.saveError': 'Enregistrement impossible.',
  'pages.images.editAction': 'Modifier',
  // ── Auto-injected missing t() fallbacks ──
  'accessibility.simplifiedTextMode': 'Mode texte simplifié',
  'accessibility.simplifiedTextModeDesc':
    'Simplifie les textes de la page affichée par IA (mise en page conservée). Dans une formation, les textes du parcours sont traités bloc par bloc.',
  'accessibility.simplifiedTextPageLoading': 'Simplification du contenu de cette page par IA…',
  'accessibility.simplifiedTextPageError':
    "Impossible de simplifier le contenu de cette page pour le moment. Réessayez dans un instant.",
  'accessibility.imageHoverAudio': 'Mode malvoyant: lecture audio image au survol',
  'accessibility.imageHoverAudioDesc':
    "Quand une image est survolée, l'IA analyse son contenu visuel (envoi base64) et lit la description à voix haute.",
  'accessibility.imageHoverAudio.loading': 'Chargement de la description audio…',
  'accessibility.imageHoverAudio.loadingTts': 'Génération de la description audio…',
  'accessibility.imageHoverAudio.loadingVision': 'Analyse de l’image et génération audio…',
  'accessibility.imageHoverAudio.playing': 'Lecture de la description…',
  'accessibility.imageHoverAudio.error': 'Lecture audio impossible.',
  'accessibility.imageHoverAudio.unavailable': 'Description audio indisponible pour cette image.',
  'admin.dashboard.monthlyRevenue': 'Revenus factures payées (€)',
  'admin.dashboard.recentActivity': 'Activité récente',
  'admin.dashboard.revenue': 'Revenus (mois)',
  'admin.dashboard.storageUsed': 'Stockage estimé',
  'admin.dashboard.systemHealth': 'Santé système',
  'admin.dashboard.totalUsers': 'Utilisateurs',
  'admin.dashboard.userGrowth': 'Nouveaux utilisateurs / mois',
  'admin.dashboard.vsLastMonth': 'vs mois dernier',
  'admin.dashboard.activeToday': 'Actifs aujourd’hui',
  'admin.dashboard.quickActions': 'Actions rapides',
  'admin.shell.collapse': 'Réduire',
  'admin.shell.collapseSidebar': 'Réduire la barre latérale',
  'admin.shell.expandSidebar': 'Agrandir la barre latérale',
  'admin.shell.title': 'Administration',
  'admin.header.workspace': 'Administration plateforme',
  'admin.institutionDetail.pageTitle': 'Établissement — Administration',
  'admin.institutionDetail.backToList': 'Retour aux établissements',
  'auth.acceptTerms': 'I accept the',
  'auth.accessDenied': 'Access Denied',
  'auth.and': 'and',
  'auth.backToLogin': 'Back to login',
  'auth.checkInbox': 'Check your inbox for a password reset link. It may take a few minutes.',
  'auth.confirmPassword': 'Confirm password',
  'auth.confirmRequired': 'Please confirm your password',
  'auth.createAccount': 'Create your account',
  'auth.email': 'Email',
  'auth.emailInvalid': 'Invalid email address',
  'auth.emailPassword': 'Email & Password',
  'auth.emailRequired': 'Email is required',
  'auth.emailSent': 'Email sent!',
  'auth.firstName': 'First name',
  'auth.firstNameRequired': 'Required',
  'auth.forgotError': 'Failed to send reset email',
  'auth.forgotPassword': 'Forgot password?',
  'auth.forgotPasswordTitle': 'Forgot password?',
  'auth.goBack': 'Go back',
  'auth.goToLogin': 'Go to login',
  'auth.hasAccount': 'Already have an account?',
  'auth.hidePassword': 'Hide password',
  'auth.institutionRegister': 'Inscription Institution',
  'auth.invalidLink': 'Invalid reset link',
  'auth.invalidLinkDesc': 'This password reset link is invalid or has expired.',
  'auth.invalidToken': 'Invalid or expired reset token',
  'auth.lastName': 'Last name',
  'auth.lastNameRequired': 'Required',
  'auth.loginError': 'Login failed. Please check your credentials.',
  'auth.loginOTP': 'Login with OTP',
  'auth.loginSubtitle': 'Sign in to your LyrraStudio account',
  'auth.newPassword': 'New password',
  'auth.otpSubtitle': 'We sent a 6-digit code to your email',
  'auth.otpTitle': 'Enter verification code',
  'auth.password': 'Password',
  'auth.passwordMin': 'Minimum 8 characters',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordRequired': 'Password is required',
  'auth.passwordReset': 'Password reset!',
  'auth.passwordResetDesc': 'Your password has been successfully updated.',
  'auth.privacy': 'Privacy Policy',
  'auth.registerError': 'Registration failed',
  'auth.registerSubtitle': 'Start creating with LyrraStudio',
  'auth.requestNewLink': 'Request a new link',
  'auth.resendCode': 'Resend code',
  'auth.resendIn': 'Resend code in',
  'auth.resetError': 'Failed to reset password',
  'auth.resetPassword': 'Reset password',
  'auth.resetPasswordSubtitle': 'Choose a strong password for your account',
  'auth.resetPasswordTitle': 'Set new password',
  'auth.sendOTP': 'Send OTP code',
  'auth.sendResetLink': 'Send reset link',
  'auth.showPassword': 'Show password',
  'auth.terms': 'Terms of Service',
  'auth.termsRequired': 'You must accept the terms',
  'auth.welcomeBack': 'Welcome back',
  'campaign.analytics.avgProgress': 'Progression moy.',
  'campaign.analytics.avgProgressChart': 'Progression moyenne',
  'campaign.analytics.completion': 'Taux de complétion',
  'campaign.analytics.completionPct': 'Complétion %',
  'campaign.analytics.completionRate': 'Taux de complétion',
  'campaign.analytics.now': 'Actuel',
  'campaign.analytics.totalParticipants': 'Participants',
  'campaign.budget.none': 'Aucun budget renseigné.',
  'campaign.budget.title': 'Budget prévu',
  'campaign.col.participant': 'Participant',
  'campaign.col.progress': 'Progression',
  'campaign.col.status': 'Statut',
  'campaign.content.placeholderTitle': 'Contenus pédagogiques',
  'campaign.detail.actionError': 'Action impossible.',
  'campaign.detail.back': 'Retour aux campagnes',
  'campaign.detail.cancel': 'Annuler',
  'campaign.detail.complete': 'Clôturer',
  'campaign.detail.description': 'Description',
  'campaign.detail.launch': 'Lancer',
  'campaign.detail.loadError': 'Impossible de charger la campagne.',
  'campaign.detail.missingId': 'Identifiant manquant.',
  'campaign.detail.noDescription': 'Aucune description.',
  'campaign.detail.timeline': 'Calendrier',
  'campaign.participants.empty': 'Aucun participant assigné.',
  'campaign.participants.unknown': 'Inconnu',
  'campaign.stats.completed': 'Terminés',
  'campaign.stats.enrolled': 'Inscrits',
  'campaign.stats.inProgress': 'En cours',
  'campaign.stats.notStarted': 'Non démarrés',
  'campaign.tab.analytics': 'Analytique',
  'campaign.tab.budget': 'Budget',
  'campaign.tab.content': 'Contenu',
  'campaign.tab.overview': 'Vue d’ensemble',
  'campaign.tab.participants': 'Participants',
  'campaign.timeline.end': 'Fin prévue',
  'campaign.timeline.start': 'Début de campagne',
  'campaigns.col.budget': 'Budget',
  'campaigns.col.completion': 'Complétion',
  'campaigns.col.enrolled': 'Inscrits',
  'campaigns.col.name': 'Nom',
  'campaigns.col.period': 'Période',
  'campaigns.col.status': 'Statut',
  'campaigns.create.budget': 'Budget (€)',
  'campaigns.create.budgetPlaceholder': '25000',
  'campaigns.create.departments': 'Directions',
  'campaigns.create.description': 'Description',
  'campaigns.create.endDate': 'Date de fin',
  'campaigns.create.error': 'Création impossible.',
  'campaigns.create.individualSearchPlaceholder': 'Rechercher des personnes…',
  'campaigns.create.individuals': 'Personnes',
  'campaigns.create.launch': 'Lancer la campagne',
  'campaigns.create.launchNote':
    'Le lancement préviendra tous les participants et démarrera la campagne à la date de début choisie.',
  'campaigns.create.name': 'Nom de la campagne',
  'campaigns.create.namePlaceholder': 'ex. Leadership 2025',
  'campaigns.create.review': 'Vérification',
  'campaigns.create.reviewFieldBudget': 'Budget',
  'campaigns.create.reviewFieldName': 'Nom',
  'campaigns.create.reviewFieldPeriod': 'Période',
  'campaigns.create.reviewContentItems': 'Contenus',
  'campaigns.create.contentSelected': '{{count}} sélectionné(s)',
  'campaigns.create.selectContent': 'Sélectionner les formations',
  'campaigns.create.startDate': 'Date de début',
  'campaigns.create.steps.audience': 'Public cible',
  'campaigns.create.steps.basic': 'Informations générales',
  'campaigns.create.steps.content': 'Contenu',
  'campaigns.create.steps.review': 'Vérification et lancement',
  'campaigns.create.title': 'Créer une campagne',
  'campaigns.dept.engineering': 'Ingénierie',
  'campaigns.dept.product': 'Produit',
  'campaigns.dept.sales': 'Commerce',
  'campaigns.dept.marketing': 'Marketing',
  'campaigns.dept.finance': 'Finance',
  'campaigns.dept.legal': 'Juridique',
  'campaigns.dept.hr': 'Ressources humaines',
  'campaigns.list.create': 'Nouvelle campagne',
  'campaigns.list.empty': 'Aucune campagne trouvée.',
  'campaigns.list.loadError': 'Impossible de charger les campagnes.',
  'campaigns.list.results': 'campagnes',
  'campaigns.list.search': 'Rechercher des campagnes…',
  'campaigns.list.title': 'Campagnes',
  'campaigns.page.documentTitle': 'Campagnes - LyrraStudio',
  'campaigns.sort.completion': 'Complétion',
  'campaigns.sort.enrolled': 'Inscrits',
  'campaigns.sort.name': 'Nom',
  'campaigns.sort.startDate': 'Date de début',
  'campaigns.status.active': 'Active',
  'campaigns.status.all': 'Toutes',
  'campaigns.status.cancelled': 'Annulée',
  'campaigns.status.completed': 'Terminée',
  'campaigns.status.draft': 'Brouillon',
  'campaigns.status.paused': 'En pause',
  'campaigns.status.scheduled': 'Planifiée',
  'campaigns.validation.budgetRequired': 'Le budget est obligatoire.',
  'campaigns.validation.endDateRequired': 'La date de fin est obligatoire.',
  'campaigns.validation.nameRequired': 'Le nom de la campagne est obligatoire.',
  'campaigns.validation.startDateRequired': 'La date de début est obligatoire.',
  'catalog.backToCatalog': 'Retour au catalogue',
  'catalog.categories': 'Catégories',
  'catalog.category': 'Catégorie',
  'catalog.detailLoadError': 'Impossible de charger la formation.',
  'catalog.enrollError': 'Inscription impossible.',
  'catalog.enrollOk': 'Inscription enregistrée.',
  'catalog.enrollments': 'insc.',
  'catalog.flowFromTeacher': 'Parcours formateur',
  'catalog.format': 'Format',
  'catalog.language': 'Langue',
  'catalog.loadError': 'Impossible de charger le catalogue.',
  'catalog.missingId': 'Identifiant manquant.',
  'catalog.noObjectives': 'Aucune compétence liée.',
  'catalog.noResults': 'Aucune formation ne correspond à vos critères.',
  'catalog.objectives': 'Objectifs / compétences',
  'catalog.prerequisites': 'Prérequis',
  'catalog.prerequisitesPlaceholder': 'Les prérequis détaillés seront affichés lorsque renseignés sur la fiche.',
  'catalog.related': 'Formations populaires',
  'catalog.results': 'formation(s)',
  'catalog.search': 'Rechercher une formation…',
  'catalog.sortDuration': 'Durée',
  'catalog.sortName': 'Nom',
  'catalog.sortPopular': 'Popularité',
  'catalog.sortRating': 'Note',
  'certificate.completed': 'has successfully completed',
  'certificate.date': 'Date',
  'certificate.download': 'Download PDF',
  'certificate.title': 'Certificate of Completion',
  'certificate.verification': 'Verification',
  'certification.completeToUnlock': 'Complétez toutes les étapes pour débloquer votre certificat.',
  'certification.download': 'Télécharger',
  'certification.earned': 'Certification obtenue',
  'certification.locked': 'Verrouillé',
  'certification.milestones': 'étapes',
  'certification.preview': 'Certificat',
  'certification.requirements': 'Prérequis à valider :',
  'certification.subtitle': 'Suivez votre progression vers vos certifications professionnelles.',
  'certification.title': 'Parcours de certification',
  'codir.card.budgetAllocated': 'Budget alloué (année)',
  'codir.card.campaigns': 'Campagnes actives',
  'codir.card.completionYtd': 'Taux complétion (année)',
  'codir.card.used': 'utilisé',
  'codir.dashboard.budgetOverview': 'Budget (année en cours)',
  'codir.dashboard.competencyGaps': 'Écarts compétences critiques',
  'codir.dashboard.completionTrends': 'Achèvements par mois',
  'codir.dashboard.completionsPerMonth': 'Achèvements / mois',
  'codir.dashboard.deptPerformance': 'Budget par direction',
  'codir.dashboard.loadError': 'Impossible de charger le tableau de bord.',
  'codir.dashboard.noBudget': 'Non renseigné',
  'codir.dashboard.noDeptData': 'Aucune ligne budgétaire',
  'codir.dashboard.noGaps': 'Aucun écart critique recensé.',
  'codir.dashboard.remaining': 'Restant',
  'codir.dashboard.spent': 'Consommé',
  'codir.dashboard.strategicKPIs': 'Indicateurs clés',
  'codir.dashboard.subtitle': 'Indicateurs formation et budget',
  'codir.dashboard.title': 'Tableau de bord CODIR',
  'codir.dashboard.totalBudget': 'Budget total',
  'codir.dashboard.utilization': 'Utilisation %',
  'codir.kpi.budget': 'Budget consommé',
  'codir.kpi.completion': 'Complétion',
  'codir.kpi.recentCompletions': 'Achèvements 30 j.',
  'collab.certificates.active': 'actif(s)',
  'collab.certificates.empty': 'Aucun certificat pour ce filtre.',
  'collab.certificates.expires': 'Expire le',
  'collab.certificates.issued': 'Délivré le',
  'collab.certificates.title': 'Mes certificats',
  'collab.certificates.verify': 'Vérifier',
  'collab.dashboard.activeTrainings': 'Formations en cours',
  'collab.dashboard.certificates': 'Certificats',
  'collab.dashboard.completed': 'Terminées',
  'collab.dashboard.level': 'Niveau',
  'collab.dashboard.noActiveTrainings': 'Aucune formation en cours.',
  'collab.dashboard.noCompleted': 'Aucune formation terminée.',
  'collab.dashboard.noUpcoming': 'Aucune formation à venir.',
  'collab.dashboard.title': 'Mes formations',
  'collab.dashboard.totalXp': 'XP Total',
  'collab.dashboard.upcoming': 'À venir',
  'collab.page.documentTitle': 'My training - LyrraStudio',
  'collab.page.subtitle': 'Track your progress and manage your training',
  'collab.page.tabs.certificates': 'Certificates',
  'collab.page.tabs.dashboard': 'Dashboard',
  'collab.page.tabs.request': 'Make a request',
  'collab.page.tabs.skills': 'My skills',
  'collab.page.title': 'My training',
  'collab.request.history': 'Historique de mes demandes',
  'collab.request.justification': 'Justification',
  'collab.request.justificationPlaceholder': 'Expliquez pourquoi cette formation est nécessaire (min. 10 caractères)…',
  'collab.request.newRequest': 'Nouvelle demande',
  'collab.request.noHistory': 'Aucune demande pour le moment.',
  'collab.request.preferredDate': 'Date souhaitée',
  'collab.request.searchCatalog': 'Rechercher dans le catalogue…',
  'collab.request.submit': 'Envoyer la demande',
  'collab.request.title': 'Demander une formation',
  'collab.request.training': 'Formation',
  'collab.skills.current': 'Niveau actuel',
  'collab.skills.empty': 'Aucune compétence renseignée.',
  'collab.skills.emptyHint': 'Vos compétences apparaîtront ici une fois ajoutées.',
  'collab.skills.radar': 'Radar de compétences',
  'collab.skills.selfAssess': 'Auto-évaluation',
  'collab.skills.target': 'Objectif',
  'collab.skills.title': 'Mes compétences',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.collaborator': 'Collaborateur',
  'common.comingSoon': 'En construction',
  'common.comment': 'Commentaire',
  'common.comments': 'Comments',
  'common.complete': 'Complete',
  'common.confirm': 'Confirmer',
  'common.cost': 'Coût',
  'common.current': 'Current',
  'common.date': 'Date souhaitée',
  'common.delete': 'Supprimer',
  'common.description': 'Description',
  'common.download': 'Télécharger',
  'common.invalidEmail': 'Invalid email',
  'common.justification': 'Justification',
  'common.managerComment': 'Commentaire manager',
  'common.moveDown': 'Descendre',
  'common.moveUp': 'Monter',
  'common.next': 'Suivant',
  'common.noExpiry': 'Sans expiration',
  'common.noResults': 'Aucun résultat',
  'common.overall': 'Global',
  'common.preview': 'Preview',
  'common.previous': 'Précédent',
  'common.publish': 'Publish',
  'common.read': 'read',
  'common.remove': 'Supprimer',
  'common.required': 'Required',
  'common.restore': 'Restore',
  'common.retry': 'Réessayer',
  'common.save': 'Save',
  'common.search': 'Rechercher…',
  'common.seeAll': 'Voir tout',
  'common.select': 'Select...',
  'common.sending': 'Envoi…',

  // Training request panel
  'trainingRequest.panelTitle': 'Demande de formation — contenus pédagogiques',
  'trainingRequest.intro': 'Décrivez le besoin. Les équipes RH valident ou non la demande ; en cas de validation, elle passe en préparation de contenu.',
  'trainingRequest.fieldTitle': 'Titre de la formation',
  'trainingRequest.fieldDescription': 'Description du besoin',
  'trainingRequest.fieldJustification': 'Justification / objectifs',
  'trainingRequest.fieldDate': 'Date souhaitée',
  'trainingRequest.datePlaceholder': 'ex. T2 2026',
  'trainingRequest.fieldFormateur': 'Formateur de l\'établissement',
  'trainingRequest.formateurPlaceholder': 'Choisir un formateur…',
  'trainingRequest.formateursEmpty': 'Aucun compte formateur listé pour votre établissement.',
  'trainingRequest.titleRequired': 'Indiquez un titre (2 caractères minimum).',
  'trainingRequest.sent': 'Demande enregistrée. Vous serez informé·e par e-mail de la décision.',
  'trainingRequest.sendError': 'Impossible d’enregistrer la demande. Vérifiez votre connexion ou vos droits.',
  'trainingRequest.submit': 'Envoyer la demande',
  'common.share': 'Partager',
  'common.startedOn': 'Débuté le',
  'common.status': 'Statut',
  'common.submit': 'Submit',
  'common.submittedAt': 'Soumis le',
  'common.title': 'Title',
  'common.training': 'Formation',
  'common.view': 'Consulter',
  'common.words': 'words',
  'compliance.download': 'Télécharger',
  'compliance.generate': 'Générer',
  'compliance.generating': 'Génération…',
  'compliance.lastGenerated': 'Généré le',
  'compliance.subtitle': 'Générez et gérez vos documents réglementaires et pédagogiques.',
  'compliance.title': 'Documents de conformité',
  'compliance.uploadEvidence': 'Importer un justificatif',
  'confirmDialog.cancel': 'Cancel',
  'confirmDialog.confirm': 'Confirm',
  'dashboard.collaborators': 'Collaborateurs',
  'dysModule.font': 'OpenDyslexic',
  'dysModule.letter': 'Aa',
  'dysModule.ruler': 'Ruler',
  'dysModule.settings': 'Dyslexia settings',
  'dysModule.word': 'W',
  'eduflow.adaptiveReplacement.assist': 'Assisté',
  'eduflow.adaptiveReplacement.auto': 'Auto',
  'eduflow.adaptiveReplacement.force': 'Forcer la version jeu simplifiée pour ce bloc',
  'eduflow.adaptiveReplacement.manual': 'Manuel',
  'eduflow.adaptiveReplacement.mode': 'Mode',
  'eduflow.adaptiveReplacement.title': 'Remplacement adaptatif',
  'eduflow.answerSubmitted': '✓ Answer submitted successfully',
  'eduflow.collaborativeBadge': 'Collaboratif',
  'eduflow.contents': 'Contents',
  'eduflow.correct': '✓ Correct!',
  'eduflow.designer.descriptionPlaceholder': "Description optionnelle...",
  'eduflow.designer.gridToggle': "Grille : {{state}}",
  'eduflow.designer.conditionalLogic': "Logique conditionnelle",
  'eduflow.designer.conditionalLogicSoon': "Les conditions de branchement seront disponibles prochainement.",
  'eduflow.designer.descriptionLabel': "Description",
  'eduflow.designer.contentLabel': "Contenu",
  'eduflow.designer.autoLayoutDone': 'Disposition automatique appliquée',
  'eduflow.designer.autoLayoutFailed': 'Impossible de calculer la disposition.',
  'eduflow.designer.autoLayoutNoBlocks': 'Ajoutez au moins un bloc pour lancer la disposition.',
  'eduflow.designer.paletteCategory.institution': 'Blocs institution',
  'eduflow.duration': 'Duration (seconds)',
  'eduflow.evaluation': 'Evaluation',
  'eduflow.evaluationSubmitted': 'Evaluation submitted',
  'eduflow.exportFlow': 'Export Flow',
  'eduflow.flowSettings': 'Flow Settings',
  'eduflow.form': 'Form',
  'eduflow.formSubmitted': 'Form submitted',
  'eduflow.incorrect': '✗ Incorrect. Try again.',
  'eduflow.list.coverLabel': 'Image de couverture',
  'eduflow.list.coverRemove': 'Retirer la couverture',
  'eduflow.list.coverUpload': 'Choisir une image',
  'eduflow.list.flowProperties': 'Titre, description et couverture',
  'eduflow.list.propertiesPanelTitle': 'Propriétés de la formation',
  'eduflow.list.duplicatePanelTitle': 'Dupliquer la formation',
  'eduflow.list.duplicatePanelSubtitle': 'Formation d’origine : {{name}}',
  'eduflow.list.duplicateTitleSuffix': 'copie',
  'eduflow.list.duplicateSuccess': 'Formation dupliquée.',
  'eduflow.list.duplicateCoverHint':
    'La couverture et le contenu pédagogique de la formation d’origine seront repris.',
  'eduflow.list.propertiesSaveError': 'Impossible d’enregistrer les propriétés.',
  'eduflow.list.propertiesSaved': 'Propriétés enregistrées.',
  'eduflow.list.propertiesUploadError': 'Échec du téléversement de l’image.',
  'eduflow.mindmap': 'Mindmap',
  'eduflow.next': 'Suivant',
  'eduflow.noExtraConfig': 'No additional configuration for this block type.',
  'eduflow.openQuestion': 'Open Question',
  'eduflow.phraseReveal.addPhrase': 'Ajouter une phrase',
  'eduflow.phraseReveal.autoScroll': 'Défilement automatique',
  'eduflow.phraseReveal.displayDuration': 'Durée par phrase (s)',
  'eduflow.phraseReveal.emptyPhrase': 'Phrase vide',
  'eduflow.phraseReveal.noPhrases': 'Aucune phrase pour l’instant.',
  'eduflow.phraseReveal.phraseContent': 'Contenu de la phrase',
  'eduflow.phraseReveal.phrasePlaceholder': 'Saisissez le texte de cette étape…',
  'eduflow.phraseReveal.showHistory': 'Afficher l’historique des phrases',
  'eduflow.quiz': 'Quiz',
  'eduflow.readComplete': 'Read complete',
  'eduflow.takeNotes': 'Take notes...',
  'eduflow.timeline.addEvent': 'Événement',
  'eduflow.timeline.alternatingSides': 'Côtés alternés',
  'eduflow.timeline.animatedReveal': 'Révélation animée',
  'eduflow.timeline.colorHint': 'Couleur du point (optionnel)',
  'eduflow.timeline.colorLabel': 'Couleur',
  'eduflow.timeline.datePlaceholder': 'Date ou période (ex. 1789, janv. 2024)',
  'eduflow.timeline.descPlaceholder': 'Description (affichée au développement)',
  'eduflow.timeline.events': 'Événements',
  'eduflow.timeline.horizontal': 'Horizontale',
  'eduflow.timeline.orientation': 'Orientation',
  'eduflow.timeline.settings': 'Paramètres',
  'eduflow.timeline.showConnectors': 'Lignes de connexion',
  'eduflow.timeline.untitled': 'Sans titre',
  'eduflow.timeline.vertical': 'Verticale',
  'eduflow.typeAnswer': 'Type your answer...',
  'eduflow.typeConfig': 'Type Configuration',
  'eduflow.unsaved': 'Unsaved changes',
  'eduflow.versionHistory.activateSuccess': 'Version {{label}} activée (publique)',
  'eduflow.versionHistory.activateTitle': 'Activer {{label}} ?',
  'eduflow.versionHistory.confirmActivate': 'Activer',
  'eduflow.versionHistory.copyFailed': 'Copie impossible',
  'eduflow.versionHistory.copyHtml': 'Copier le HTML',
  'eduflow.versionHistory.copyPlain': 'Copier le texte seul',
  'eduflow.versionHistory.createMajor': 'Version majeure',
  'eduflow.versionHistory.createMajorSuccess': 'Version majeure créée',
  'eduflow.versionHistory.createMinor': 'Version mineure',
  'eduflow.versionHistory.createMinorSuccess': 'Version mineure créée',
  'eduflow.versionHistory.deleteError': 'Erreur lors de la suppression',
  'eduflow.versionHistory.deleteSuccess': 'Version supprimée',
  'eduflow.versionHistory.deleteTitle': 'Supprimer {{label}} ?',
  'eduflow.versionHistory.deleteWarning': 'Cette action est irréversible.',
  'eduflow.versionHistory.duplicateConfirm': 'Dupliquer',
  'eduflow.versionHistory.duplicateError': 'Erreur lors de la duplication',
  'eduflow.versionHistory.duplicateMinor': 'Dupliquer (mineure)',
  'eduflow.versionHistory.duplicateSuccess': 'Version dupliquée — contenu restauré dans l’éditeur',
  'eduflow.versionHistory.duplicateTitle': 'Dupliquer {{label}} ?',
  'eduflow.versionHistory.newFromCurrent': 'Nouvelle version à partir du contenu actuel',
  'eduflow.versionHistory.preview': 'Aperçu',
  'eduflow.versionHistory.previewBlocks': '{{count}} blocs',
  'eduflow.versionHistory.previewEdges': '{{count}} connexions',
  'eduflow.versionHistory.previewError': 'Impossible de charger l’aperçu',
  'eduflow.versionHistory.previewTitle': 'Aperçu {{label}}',
  'eduflow.versionHistory.setPublic': 'Activer',
  'eduflow.versionHistory.startDescModalTitle': 'Descriptif — bloc Démarrage ({{version}})',
  'eduflow.videoCompleted': 'Video watched',
  'gamification.active': 'actives',
  'gamification.bestStreak': 'Meilleur streak',
  'gamification.claim': 'Récupérer',
  'gamification.completed': 'Terminées',
  'gamification.days': 'jours',
  'gamification.maxLevel': '✨ Niveau max atteint !',
  'gamification.missions': 'Missions',
  'gamification.name': 'Nom',
  'gamification.streakActive': 'Streak en cours 🔥',
  'gamification.streakInactive': 'Pas de streak actif',
  'gamification.xpRemaining': '${formatXp(xpForNextLevel - currentXp)} XP restants',
  'header.language': 'Language',
  'institution.categories.color': 'Couleur',
  'institution.categories.coverLabel': 'Image de couverture',
  'institution.categories.coverUploadError': 'Échec du téléversement de la couverture.',
  'institution.categories.removeCover': 'Retirer l’image',
  'institution.categories.descShortPh': 'Décrivez l’usage de la catégorie',
  'institution.categories.edit': 'Modifier la catégorie',
  'institution.categories.episodes': 'épisode(s)',
  'institution.categories.learners': 'apprenant(s)',
  'institution.categories.namePlaceholder': 'Nom de la catégorie',
  'institution.categories.new': 'Nouvelle catégorie',
  'institution.categories.preview': 'Aperçu',
  'institution.categories.previewApiModeOff': 'Prévisualisation indisponible (mode local).',
  'institution.categories.previewDesc': 'Podcasts et parcours reliés à cette catégorie.',
  'institution.categories.previewError': 'Erreur lors du chargement de l’aperçu.',
  'institution.categories.previewFormations': 'Parcours / formations',
  'institution.categories.previewLoading': 'Chargement de l’aperçu…',
  'institution.categories.previewNoFlows': 'Aucune formation rattachée à cette catégorie.',
  'institution.categories.previewNoPodcasts': 'Aucun podcast rattaché à cette catégorie.',
  'institution.categories.previewPodcasts': 'Podcasts',
  'institution.categories.saveError': 'Erreur lors de la sauvegarde.',
  'institution.categories.subscribers': 'abonné(s)',
  'institution.categories.usageItems': 'élément(s)',
  'institution.common.add': 'Ajouter',
  'institution.common.cancel': 'Annuler',
  'institution.common.close': 'Fermer',
  'institution.common.delete': 'Supprimer',
  'institution.common.description': 'Description',
  'institution.common.email': 'Email',
  'institution.common.loading': 'Chargement…',
  'institution.common.name': 'Nom',
  'institution.common.refresh': 'Actualiser',
  'institution.common.required': 'Requis',
  'institution.common.retry': 'Réessayer',
  'institution.common.save': 'Enregistré',
  'institution.contentPipeline.confirmDeletePath': 'Supprimer ce parcours ?',
  'institution.contentPipeline.createPath': 'Créer le parcours',
  'institution.contentPipeline.createPodcast': 'Créer le podcast',
  'institution.contentPipeline.createShell': 'Créer la fiche',
  'institution.contentPipeline.descPh': 'Description / objectifs',
  'institution.contentPipeline.newPath': 'Nouveau parcours',
  'institution.contentPipeline.newPodcastShell': 'Pré-créer un podcast',
  'institution.contentPipeline.newShell': 'Nouvelle fiche formation',
  'institution.contentPipeline.openDesigner': 'Ouvrir dans le designer',
  'institution.contentPipeline.openStudio': 'Épisodes',
  'institution.contentPipeline.pathDescLabel': 'Description',
  'institution.contentPipeline.pathPlannedLaunch': 'Date cible de lancement',
  'institution.contentPipeline.pathTitlePh': 'Titre du parcours',
  'institution.contentPipeline.pickProducer': 'Assigner producteur…',
  'institution.contentPipeline.pickTeacher': 'Assigner…',
  'institution.contentPipeline.plannedDeadline': 'Échéance',
  'institution.contentPipeline.plannedStart': 'Début prévu',
  'institution.contentPipeline.planningColumn': 'Planning création',
  'institution.contentPipeline.planningOptional': 'Planning de création (optionnel)',
  'institution.contentPipeline.podDescPh': 'Brief / description pour le formateur',
  'institution.contentPipeline.savePlanning': 'Enregistrer le planning',
  'institution.contentPipeline.statFormations': 'Fiches catalogue',
  'institution.contentPipeline.statPaths': 'Parcours certifiants',
  'institution.contentPipeline.statPodcasts': 'Podcasts',
  'institution.contentPipeline.tabFormations': 'Fiches formations',
  'institution.contentPipeline.tabPaths': 'Parcours',
  'institution.contentPipeline.tabPodcasts': 'Podcasts',
  'institution.contentPipeline.title': 'Préparation du contenu',
  'institution.contentPipeline.titlePh': 'Titre',
  'institution.learnerAnalytics.chartActivity': 'Activité et complétions (8 dernières semaines)',
  'institution.learnerAnalytics.chartProgress': 'Progression globale (par apprenant)',
  'institution.learnerAnalytics.chartStatus': 'Répartition par statut',
  'institution.learnerAnalytics.chartTopFlows': 'Formations les plus suivies',
  'institution.learnerAnalytics.colDone': 'Terminé',
  'institution.learnerAnalytics.colFlow': 'Formation',
  'institution.learnerAnalytics.colLastAccess': 'Dernier accès',
  'institution.learnerAnalytics.colName': 'Apprenant',
  'institution.learnerAnalytics.colTime': 'Temps',
  'institution.learnerAnalytics.count': 'Effectif',
  'institution.learnerAnalytics.done': 'Terminés',
  'institution.learnerAnalytics.enrolled': 'Inscrits',
  'institution.learnerAnalytics.kpiEnrollments': 'Inscriptions',
  'institution.learnerAnalytics.kpiLearners': 'Apprenants',
  'institution.learnerAnalytics.kpiLearnersSub': 'Participants enregistrés',
  'institution.learnerAnalytics.kpiRate': 'Taux de complétion',
  'institution.learnerAnalytics.kpiRateSub': 'Sur l’ensemble des inscriptions',
  'institution.learnerAnalytics.kpiTime': 'Temps cumulé',
  'institution.learnerAnalytics.kpiTimeSub': 'Temps passé sur les parcours',
  'institution.learnerAnalytics.loadError': 'Impossible de charger les analyses.',
  'institution.learnerAnalytics.noData': 'Pas encore de données.',
  'institution.learnerAnalytics.seriesActivity': 'Accès (inscriptions)',
  'institution.learnerAnalytics.seriesCompletions': 'Complétions',
  'institution.learnerAnalytics.tableEmpty': 'Aucune ligne à afficher.',
  'institution.learnerAnalytics.tableSearch': 'Rechercher nom, email, formation…',
  'institution.learnerAnalytics.tableTitle': 'Détail des inscriptions',
  'institution.learnerAnalytics.tableTruncated': 'Affichage limité à 500 lignes — utilisez l’export pour la liste complète.',
  'institution.learnerAnalytics.title': 'Analyse & suivi des apprenants',
  'institution.learnerAnalytics.xapiDataSourceTitle': 'Source des données & xAPI',
  'institution.learners.flowDone': 'Terminé',
  'institution.learners.inProgress': 'En cours',
  'institution.menuBlocked.title': 'Fonctionnalité non disponible',
  'institution.rhModule.pickFromMenu': 'Choisissez un outil dans le menu Formation & RH.',
  'institution.sirh.apiKey': 'Clé API',
  'institution.sirh.apiKeyStored': 'déjà enregistrée — laissez vide pour ne pas changer',
  'institution.sirh.apiSecret': 'Secret / second jeton (optionnel)',
  'institution.sirh.apiSecretHint': 'Envoyé en en-tête X-API-Secret si renseigné.',
  'institution.sirh.baseUrl': 'URL de base de l’API',
  'institution.sirh.baseUrlHint': 'Lyrra appellera GET {URL}/employees avec Authorization: Bearer &lt;clé API&gt;.',
  'institution.sirh.enabled': 'Synchronisation activée',
  'institution.sirh.formatTitle': 'Format attendu pour GET /employees',
  'institution.sirh.loadError': 'Impossible de charger la configuration SIRH.',
  'institution.sirh.mappingJob': 'Poste / fonction (jobTitle ou ex. position)',
  'institution.sirh.mappingTitle': 'Correspondance des champs (JSON SIRH)',
  'institution.sirh.name': 'Libellé interne',
  'institution.sirh.saveError': 'Enregistrement impossible.',
  'institution.sirh.saved': 'Configuration enregistrée.',
  'institution.sirh.secretStored': 'déjà enregistré',
  'institution.sirh.syncDone': 'Synchronisation : ${s.created} créés, ${s.updated} mis à jour, ${s.errors} erreurs, ${s.skipped} ignorés (autre établissement).',
  'institution.sirh.syncError': 'Synchronisation impossible.',
  'institution.sirh.syncNow': 'Synchroniser les collaborateurs',
  'institution.sirh.syncOk': 'Synchronisation terminée.',
  'institution.sirh.test': 'Tester la connexion',
  'institution.sirh.testError': 'Test impossible.',
  'institution.sirh.title': 'SIRH — connexion API',
  'institution.sirh.type': 'Type / famille',
  'institution.sirh.typeCustom': 'API HTTP personnalisée',
  'institution.templates.blockType': 'Type de bloc',
  'institution.templates.create': 'Créer un modèle',
  'institution.templates.createDrawerTitle': 'Créer un modèle de bloc',
  'institution.templates.createNameRequired': 'Veuillez renseigner le titre du modèle.',
  'institution.templates.editDrawerTitle': 'Modifier le modèle',
  'institution.templates.modelDescPh': 'Description du modèle',
  'institution.templates.modelNamePh': 'Titre du modèle',
  'institution.templates.previewDrawerTitle': 'Aperçu du modèle',
  'institution.templates.publish': 'Publier',
  'institution.templates.published': 'Publié',
  'institution.templates.typeConfig': 'Configuration du bloc',
  'institution.templates.unpublish': 'Dépublier',
  'institution.xapi.accessToken': 'Jeton d’accès (Bearer)',
  'institution.xapi.actorField': 'Champ acteur (référence)',
  'institution.xapi.authBasic': 'HTTP Basic (utilisateur / mot de passe)',
  'institution.xapi.authBearer': 'Bearer / jeton OAuth',
  'institution.xapi.authType': 'Authentification LRS',
  'institution.xapi.enable': 'Activer l’envoi des statements vers le LRS',
  'institution.xapi.formTitle': 'Paramètres LRS',
  'institution.xapi.loadError': 'Impossible de charger la configuration xAPI.',
  'institution.xapi.loadStatements': 'Afficher 15 statements (JSON)',
  'institution.xapi.lrsReadTitle': 'Lecture distante (diagnostic)',
  'institution.xapi.lrsUrl': 'URL de base du LRS',
  'institution.xapi.optionalKeep': 'optionnel pour conserver l’actuel',
  'institution.xapi.password': 'Mot de passe / secret',
  'institution.xapi.payloadOptions': 'Données incluses dans les statements (parcours)',
  'institution.xapi.profile': 'Profil / usage',
  'institution.xapi.save': 'Enregistrer',
  'institution.xapi.saveError': 'Enregistrement impossible.',
  'institution.xapi.saveSuccess': 'Configuration xAPI enregistrée.',
  'institution.xapi.sendCompletion': 'Envoyer complétion',
  'institution.xapi.sendDuration': 'Envoyer durée',
  'institution.xapi.sendScore': 'Envoyer score',
  'institution.xapi.statementsError': 'Impossible de charger les statements.',
  'institution.xapi.sync': 'Lire un lot (aperçu)',
  'institution.xapi.syncError': 'Lecture LRS impossible.',
  'institution.xapi.test': 'Tester le LRS',
  'institution.xapi.testError': 'Test impossible.',
  'institution.xapi.testFail': 'Test LRS échoué.',
  'institution.xapi.testOk': 'LRS joignable.',
  'institution.xapi.title': 'xAPI — Learning Record Store (LRS)',
  'institution.xapi.username': 'Utilisateur (Basic Auth)',
  'loading.label': 'Loading…',
  'manager.dashboard.loadError': 'Chargement impossible.',
  'manager.dashboard.noReports': 'Aucun collaborateur rattaché comme manager dans le SIRH.',
  'manager.dashboard.pendingCount': 'en attente',
  'manager.dashboard.pendingWorkflowTitle': 'Demandes en circuit',
  'manager.dashboard.title': 'Tableau de bord Manager',
  'manager.dashboard.workflowBox': 'Validation',
  'manager.kpi.activeTrainings': 'Formations actives',
  'manager.kpi.completionRate': 'Taux de complétion',
  'manager.kpi.pendingWorkflow': 'Workflows en attente',
  'manager.nav.requests': 'Demandes de formation',
  'manager.nav.reviews': 'Évaluations',
  'manager.page.documentTitle': 'Manager workspace - LyrraStudio',
  'manager.page.subtitle': 'Manage training plans and your team progress',
  'manager.page.tabs.dashboard': 'Dashboard',
  'manager.page.tabs.requests': 'Requests',
  'manager.page.tabs.reviews': 'Reviews',
  'manager.page.tabs.team': 'Team',
  'manager.page.title': 'Manager workspace',
  'manager.requests.commentPlaceholder': 'Ajouter un commentaire…',
  'manager.requests.empty': 'Aucune demande pour ce filtre.',
  'manager.requests.title': 'Demandes de formation',
  'manager.reviews.commentPlaceholder': 'Commentaire sur cette compétence…',
  'manager.reviews.lastAssessment': 'Dernière éval.',
  'manager.reviews.selectMember': 'Sélectionnez un collaborateur pour évaluer',
  'manager.reviews.submitted': 'Évaluation soumise',
  'manager.reviews.target': 'Cible',
  'manager.reviews.title': 'Évaluations de compétences',
  'manager.team.loadError': 'Chargement impossible.',
  'notifications.empty': 'No notifications yet',
  'notifications.label': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.markRead': 'Mark as read',
  'notifications.title': 'Notifications',
  'notifications.viewAll': 'View all notifications',
  'pages.branding.comingSoonDesc': 'Configurez les couleurs, logos et la charte graphique de votre espace de formation.',
  'pages.branding.title': 'Identité visuelle',
  'pages.budget.comingSoonDesc': 'Planifiez et suivez votre budget de formation, gérez les enveloppes et les dépenses par département.',
  'pages.budget.description': 'Gestion du budget de formation',
  'pages.budget.title': 'Budget',
  'pages.campaigns.description': 'Gestion des campagnes de formation',
  'pages.campaigns.title': 'Campagnes',
  'pages.catalog.comingSoonDesc': 'Parcourez le catalogue de formations disponibles, recherchez par compétence et inscrivez-vous aux sessions.',
  'pages.catalog.description': 'Catalogue des formations',
  'pages.catalog.title': 'Catalogue',
  'pages.certificates.comingSoonDesc': 'Consultez et téléchargez vos certificats de formation, attestations et badges obtenus.',
  'pages.certificates.description': 'Vos certifications et attestations',
  'pages.certificates.title': 'Certificats',
  'pages.competencies.comingSoonDesc': 'Gérez votre référentiel de compétences, cartographiez les savoir-faire et identifiez les besoins en formation.',
  'pages.competencies.description': 'Référentiel de compétences',
  'pages.competencies.title': 'Compétences',
  'pages.compliance.comingSoonDesc': 'Suivez les formations obligatoires, les certifications et la conformité réglementaire de votre organisation.',
  'pages.compliance.description': 'Suivi de la conformité réglementaire',
  'pages.compliance.title': 'Conformité',
  'pages.integrations.comingSoonDesc': 'Connectez votre plateforme à vos outils RH, SIRH, LMS et autres services pour synchroniser vos données.',
  'pages.integrations.description': 'Connexions aux services externes',
  'pages.integrations.title': 'Intégrations',
  'pages.learners.statsAdaptiveChoice': 'Choix assistes - acceptes: {{accepted}} · refuses: {{declined}}',
  'pages.learners.statsAdaptiveEvaluated': 'Adaptations evaluees: {{count}}',
  'pages.learners.statsAdaptiveReplaced': 'Versions simplifiees utilisees: {{count}}',
  'pages.reports.description': 'Rapports et bilans de formation',
  'pages.reports.title': 'Rapports',
  'pages.requests.description': 'Gestion des demandes de formation',
  'pages.requests.title': 'Demandes',
  'pages.reviews.comingSoonDesc': 'Gérez les entretiens annuels, les évaluations de compétences et le suivi de progression de votre équipe.',
  'pages.reviews.description': 'Entretiens et évaluations',
  'pages.reviews.title': 'Évaluations',
  'pages.settings.description': 'Configuration de votre compte et préférences',
  'pages.settings.title': 'Paramètres',
  'pages.skills.description': 'Votre profil de compétences',
  'pages.skills.title': 'Mes compétences',
  'pages.team.comingSoonDesc': 'Visualisez votre équipe, suivez les compétences et les formations de chaque collaborateur.',
  'pages.team.description': 'Gestion de votre équipe',
  'pages.team.title': 'Mon équipe',
  'pages.training.comingSoonDesc': 'Accédez à vos formations en cours, suivez votre progression et reprenez là où vous vous êtes arrêté.',
  'pages.training.description': 'Vos parcours de formation en cours',
  'pages.training.title': 'Mes formations',
  'pages.webhooks.comingSoonDesc': 'Configurez des webhooks pour connecter votre plateforme à des services externes et automatiser vos flux.',
  'pages.webhooks.description': 'Configuration des webhooks et intégrations',
  'pages.webhooks.title': 'Webhooks',
  'player.adaptiveGame.done': 'Objectif valide en mode simplifie',
  'player.adaptiveGame.next': 'Etape suivante',
  'player.adaptiveGame.step': 'Etape {{index}} / {{total}}',
  'player.adaptiveGame.subtitle': 'Une alternative plus accessible pour valider cet objectif.',
  'player.adaptiveGame.title': 'Version simplifiee interactive',
  'player.adaptiveGame.validate': 'Valider',
  'player.adaptivePrompt.keepOriginal': 'Garder le bloc standard',
  'player.adaptivePrompt.subtitle': 'Souhaitez-vous utiliser une version interactive plus accessible ?',
  'player.adaptivePrompt.title': 'Version simplifiee disponible pour ce bloc',
  'player.adaptivePrompt.useSimplified': 'Utiliser la version simplifiee',
  'player.descriptionTeleprompter': 'Mode téléprompteur',
  'player.descriptionTeleprompterOff': 'Afficher le bloc',
  'player.descriptionVoiceover': 'Voix off — description du bloc',
  'player.manualVoiceoverMic': 'Enregistrer la voix off pour cette langue',
  'player.manualVoiceoverStudioTitle': 'Studio d\u2019enregistrement voix off',
  'player.manualVoiceoverStudioLang': 'Langue de l\u2019enregistrement',
  'player.manualVoiceoverRecord': 'Enregistrer',
  'player.manualVoiceoverStop': 'Arrêter',
  'player.manualVoiceoverPlay': 'Écouter',
  'player.manualVoiceoverSave': 'Enregistrer sur le bloc',
  'player.manualVoiceoverClose': 'Fermer',
  'player.manualVoiceoverHint':
    'À droite : le texte ou l\u2019image du bloc dans la langue choisie. Enregistrez, puis enregistrez sur le bloc pour associer l\u2019audio à cette langue.',
  'player.manualVoiceoverPreviewLoading': 'Chargement du contenu…',
  'player.manualVoiceoverNoMic': 'Micro ou enregistrement indisponible dans ce navigateur.',
  'player.manualVoiceoverNoText': 'Aucun texte de description suffisant pour ce bloc.',
  'player.manualVoiceoverSaveError': 'Impossible d\u2019enregistrer la voix sur le bloc.',
  'player.manualVoiceoverUploadError': 'Échec du téléversement audio.',
  'player.manualVoiceoverSaved': 'Voix off enregistrée sur le bloc.',
  'player.subflow.blockTitle': 'Sous-formation',
  'player.subflow.defaultTitle': 'Sous-formation',
  'player.subflow.loadError': 'La sous-formation n’a pas pu être chargée.',
  'player.subflow.loading': 'Chargement…',
  'player.subflow.missingId': 'Aucune sous-formation n’est liée à ce bloc.',
  'player.subflow.noContext': 'Impossible de charger la sous-formation (contexte lecteur manquant).',
  'player.subflow.start': 'Commencer la sous-formation',
  'player.teleprompterNextParagraph': 'Paragraphe suivant',
  'player.teleprompterPrevParagraph': 'Paragraphe précédent',
  'podcast.episodes.chapters': 'Chapitres',
  'podcast.episodes.hideText': 'Masquer le texte',
  'podcast.episodes.noChapters': 'Aucun chapitre disponible.',
  'podcast.episodes.previewEpisodeTitle': 'Épisode',
  'podcast.episodes.showText': 'Afficher le texte',
  'qualiopi.actions': 'Actions',
  'qualiopi.completion': 'Conformité',
  'qualiopi.noDocuments': 'Aucun document',
  'qualiopi.notes': 'Notes',
  'qualiopi.notesPlaceholder': 'Ajouter une note…',
  'quickActions.hint': 'Press',
  'quickActions.hintToggle': 'to toggle',
  'quickActions.title': 'Quick Actions',
  'rh.achievements': 'Achievements',
  'rh.addComment': 'Add a comment...',
  'rh.addCompetency': 'Add Competency',
  'rh.afterApproval': 'After Approval',
  'rh.alert.forecast': 'Prévision fin d’année',
  'rh.alert.workflow': 'Workflows',
  'rh.all': 'All',
  'rh.allCompetencies': 'All Competencies',
  'rh.allDepartments': 'All Departments',
  'rh.allocated': 'Alloué',
  'rh.approvalHistory': 'Approval History',
  'rh.approve': 'Approve',
  'rh.approveConfirm': 'Are you sure you want to approve this training request?',
  'rh.approveRequest': 'Approve Request',
  'rh.approveSelected': 'Approve',
  'rh.approved': 'Approved',
  'rh.auditDateUnknown': 'Non renseigné',
  'rh.auditOverdue': 'Audit en retard',
  'rh.auditScheduleHint': 'À définir dans les paramètres établissement',
  'rh.avgGap': 'Avg Gap Size',
  'rh.avgLevel': 'Avg Level',
  'rh.avgOverall': 'Avg Overall',
  'rh.budget.subtitle': 'Suivi budgétaire et coût par complétion',
  'rh.budget.title': 'Budget formation',
  'rh.budgetAlerts': 'Alertes',
  'rh.budgetDashboard': 'Budget formation',
  'rh.budgetLoadError': 'Impossible de charger le budget.',
  'rh.budgetRemaining': 'Budget Remaining',
  'rh.byDepartment': 'Budget par direction',
  'rh.competencies': 'competencies',
  'rh.competencyMap': 'Competency Heatmap',
  'rh.competencyRadar': 'Competency Radar',
  'rh.completion': 'Avancement',
  'rh.compliance': 'Conformité',
  'rh.complianceAuditAction.qualiopi_corrective_action_create': "Création d'une action corrective Qualiopi",
  'rh.complianceAuditAction.qualiopi_corrective_action_delete': "Suppression d'une action corrective Qualiopi",
  'rh.complianceAuditAction.qualiopi_corrective_action_update': "Mise à jour d'une action corrective Qualiopi",
  'rh.complianceAuditAction.qualiopi_evidence_create': "Ajout d'une preuve Qualiopi",
  'rh.complianceAuditAction.qualiopi_indicator_update': "Mise à jour d'un indicateur Qualiopi",
  'rh.complianceAuditAction.compliance_regulatory_watch_create': "Création d'un élément de veille réglementaire",
  'rh.complianceAuditAction.compliance_regulatory_watch_update': "Mise à jour d'un élément de veille réglementaire",
  'rh.complianceAuditAction.compliance_regulatory_watch_delete': "Suppression d'un élément de veille réglementaire",
  'rh.complianceCritical': 'Critique : plusieurs critères sont en dessous des seuils acceptables.',
  'rh.complianceGood': 'Votre organisme respecte les exigences Qualiopi. Continuez sur cette lancée.',
  'rh.compliancePage.documentTitle': 'Conformité & Qualiopi - LyrraStudio',
  'rh.compliancePage.subtitle': 'Pilotage de la conformité réglementaire et de la certification Qualiopi',
  'rh.compliancePage.tabs.dashboard': 'Tableau de bord',
  'rh.compliancePage.tabs.difficulty': 'Difficultés',
  'rh.compliancePage.tabs.handicap': 'Référent handicap',
  'rh.compliancePage.tabs.learnerBilan': 'Bilan apprenants',
  'rh.compliancePage.tabs.qualiopi': 'Qualiopi',
  'rh.compliancePage.tabs.regulatory': 'Veille réglementaire',
  'rh.regulatoryWatch.lyrraBadge': 'Lyrra Studio',
  'rh.regulatoryWatch.ofResponse': 'Notre réponse / positionnement',
  'rh.regulatoryWatch.ofResponsePlaceholder': 'Ce que nous avons fait ou prévu…',
  'rh.regulatoryWatch.ackStatus': 'Statut',
  'rh.regulatoryWatch.ack.pending': 'À traiter',
  'rh.regulatoryWatch.ack.read': 'Pris en compte (lu)',
  'rh.regulatoryWatch.ack.actionTaken': 'Action menée',
  'rh.regulatoryWatch.ack.notApplicable': 'Non applicable',
  'rh.regulatoryWatch.pdf.title': 'Registre de veille réglementaire',
  'rh.regulatoryWatch.pdf.subtitleFallback': 'Établissement',
  'rh.regulatoryWatch.pdf.generatedAt': 'Document généré le {{date}}',
  'rh.regulatoryWatch.pdf.colDate': 'Date',
  'rh.regulatoryWatch.pdf.colCategory': 'Cat.',
  'rh.regulatoryWatch.pdf.colTitle': 'Titre',
  'rh.regulatoryWatch.pdf.colDesc': 'Description (extrait)',
  'rh.regulatoryWatch.pdf.colImpact': 'Impact',
  'rh.regulatoryWatch.pdf.colOrigin': 'Origine',
  'rh.regulatoryWatch.pdf.colStatus': 'Statut',
  'rh.regulatoryWatch.pdf.colAction': 'Action',
  'rh.regulatoryWatch.pdf.colWho': 'Utilisateur',
  'rh.regulatoryWatch.pdf.colWhen': 'Horodatage',
  'rh.regulatoryWatch.pdf.originLyrra': 'Lyrra Studio',
  'rh.regulatoryWatch.pdf.originOf': 'OF',
  'rh.regulatoryWatch.pdf.readYes': 'Lu',
  'rh.regulatoryWatch.pdf.readNo': 'Non lu',
  'rh.regulatoryWatch.pdf.actionDone': 'Action faite',
  'rh.regulatoryWatch.pdf.actionOpen': 'Action en cours',
  'rh.regulatoryWatch.pdf.emptyWatch': 'Aucun élément enregistré',
  'rh.regulatoryWatch.pdf.auditSection': 'Journal interne (traçabilité)',
  'rh.regulatoryWatch.pdf.auditHint': 'Actions enregistrées automatiquement dans la plateforme.',
  'rh.regulatoryWatch.pdf.emptyAudit': 'Aucun événement récent',
  'rh.regulatoryWatch.exportPdf': 'Exporter PDF',
  'rh.regulatoryWatch.exportPdfBusy': 'Export PDF…',
  'rh.regulatoryWatch.exportPdfError': 'Export PDF impossible',
  'rh.compliancePage.tabs.satisfaction': 'Satisfaction',
  'rh.compliancePage.title': 'Conformité & Qualiopi',
  'rh.complianceWarning': 'Certains critères nécessitent une action avant le prochain audit.',
  'rh.compliant': 'conformes',
  'rh.confirm': 'Confirm Approval',
  'rh.confirmReject': 'Confirm Rejection',
  'rh.consumed': 'Consommé',
  'rh.costInfo': 'Cost Info',
  'rh.critical': 'critiques',
  'rh.criticalGaps': 'Critical (High)',
  'rh.current': 'Current',
  'rh.currentLevel': 'Current Level',
  'rh.date': 'Date',
  'rh.daysRemaining': 'jours restants',
  'rh.department': 'Department',
  'rh.departmentBreakdown': 'Détail par direction',
  'rh.departments': 'departments',
  'rh.description': 'Description',
  'rh.edit': 'Edit',
  'rh.employee': 'Employee',
  'rh.employeeInfo': 'Employee Info',
  'rh.evolution': 'Evolution',
  'rh.evolutionTimeline': 'Evolution Timeline',
  'rh.exportReport': 'Exporter',
  'rh.gapAnalysis': 'Gap Analysis',
  'rh.growth': 'Growth this year',
  'rh.lastUpdated': 'Dernière mise à jour',
  'rh.level': 'Level',
  'rh.manager': 'Manager',
  'rh.needsWork': 'à renforcer',
  'rh.nextAudit': 'Prochain audit',
  'rh.noAlerts': 'Aucune alerte.',
  'rh.noAllocations': 'Aucune ligne budgétaire pour cette année.',
  'rh.noDeptData': 'Aucune donnée',
  'rh.noGaps': 'No gaps found for this filter.',
  'rh.of': 'of',
  'rh.ofTotal': 'du total',
  'rh.overallCompliance': 'Score global de conformité',
  'rh.pending': 'Pending',
  'rh.provider': 'Provider',
  'rh.qualiopiCompliance': 'Conformité Qualiopi',
  'rh.qualiopiEmpty': 'Aucune donnée Qualiopi disponible pour cet établissement.',
  'rh.qualiopiLoading': 'Chargement du référentiel Qualiopi…',
  'v2.compliance': 'Conformité & Qualiopi',
  'rh.radar': 'Radar',
  'rh.reject': 'Reject',
  'rh.rejectConfirm': 'Are you sure you want to reject this training request?',
  'rh.rejectRequest': 'Reject Request',
  'rh.rejectSelected': 'Reject',
  'rh.rejected': 'Rejected',
  'rh.remaining': 'Restant',
  'rh.requestInfo': 'Request Info',
  'rh.requests.subtitle': 'Workflows de demandes et validations',
  'rh.requests.title': 'Demandes de formation',
  'rh.required': 'Required',
  'rh.rh': 'RH',
  'rh.saveAssessment': 'Save Assessment',
  'rh.searchCompetencies': 'Search competencies…',
  'rh.selfAssess': 'Self',
  'rh.showing': 'Showing',
  'rh.skillsSelfAssessment': 'Skills & Self-Assessment',
  'rh.spending': 'Dépenses',
  'rh.spendingTimeline': 'Dépenses par année',
  'rh.status': 'Status',
  'rh.targetLevel': 'Target Level',
  'rh.totalBudget': 'Budget total',
  'rh.totalGaps': 'Total Gaps',
  'rh.training': 'Training',
  'rh.trainingCost': 'Training Cost',
  'rh.trainingRequests': 'Training Requests',
  'search.close': 'pour fermer',
  'search.input': 'Champ de recherche',
  'search.navigate': 'pour se déplacer',
  'search.placeholder': 'Rechercher une formation, un podcast, un parcours…',
  'search.recent': 'Récents',
  'search.select': 'pour ouvrir',
  'search.title': 'Recherche',
  'status.completed': 'Terminé',
  'status.inProgress': 'En cours',
  'status.notStarted': 'Non commencé',
  'webinar.room.closeParticipantCamerasView': 'Fermer',
  'webinar.room.hideParticipantCameras': 'Fermer la vue caméras',
  'webinar.room.participantCamerasViewTitle': 'Caméras des participants',
  'webinar.room.showParticipantCameras': 'Voir les caméras des participants',
  // ── Auto-injected missing t() fallbacks ──
  'designer.ctx.addBlock': 'Ajouter un bloc',
  'designer.ctx.blockInfo': 'Info du bloc',
  'designer.ctx.changeColor': 'Changer la couleur',
  'designer.ctx.copy': 'Copier',
  'designer.ctx.delete': 'Supprimer',
  'designer.ctx.duplicate': 'Dupliquer',
  'designer.ctx.edit': 'Modifier',
  'designer.ctx.fitView': 'Ajuster la vue',
  'designer.ctx.lock': 'Verrouiller',
  'designer.ctx.paste': 'Coller',
  'designer.ctx.toggleGrid': 'Grille ON/OFF',
  'designer.ctx.toggleMinimap': 'Minimap ON/OFF',
  'designer.ctx.unlock': 'Déverrouiller',
  'designer.edge.cancel': 'Annuler',
  'designer.edge.color': 'Couleur',
  'designer.edge.name': 'Nom',
  'designer.edge.namePlaceholder': 'ex. Réussi, Branche A…',
  'designer.edge.save': 'Enregistrer',
  'designer.edge.title': 'Connexion',
  'designer.gamification.badgeName': 'Nom du badge',
  'designer.gamification.badges': 'Badges',
  'designer.gamification.blocksGte': 'Blocs ≥',
  'designer.gamification.bonusPerfect': 'Bonus score parfait (quiz)',
  'designer.gamification.condition': 'Condition',
  'designer.gamification.conditionPlaceholder': 'ex: quiz_completed >= 3',
  'designer.gamification.description': 'Description',
  'designer.gamification.enable': 'Activer la gamification',
  'designer.gamification.enableLeaderboard': 'Activer le classement',
  'designer.gamification.icon': 'Icône',
  'designer.gamification.leaderboard': 'Classement',
  'designer.gamification.missionDesc': 'Description (ex: Terminer 3 quiz)',
  'designer.gamification.missionTitle': 'Titre de la mission',
  'designer.gamification.missions': 'Missions',
  'designer.gamification.noBadges': 'Aucun badge défini',
  'designer.gamification.noMissions': 'Aucune mission définie',
  'designer.gamification.points': 'Points',
  'designer.gamification.pointsPerBlock': 'Points par bloc complété',
  'designer.gamification.pointsPerSecond': 'Points par seconde gagnée',
  'designer.gamification.publicLeaderboard': 'Classement public',
  'designer.gamification.reward': 'Récompense',
  'designer.gamification.scoreGte': 'Score ≥',
  'designer.gamification.timeBonus': 'Bonus de rapidité',
  'designer.gamification.title': 'Gamification',
  'designer.gamification.topN': 'Afficher le top N',
  'designer.objectives.add': 'Ajouter',
  'designer.objectives.addTitle': 'Ajouter un objectif',
  'designer.objectives.cancel': 'Annuler',
  'designer.objectives.category': 'Catégorie',
  'designer.objectives.completed': 'Complété',
  'designer.objectives.empty': 'Aucun objectif défini.',
  'designer.objectives.emptyHint': 'Ajoutez vos premiers objectifs pédagogiques.',
  'designer.objectives.errorToast': 'Erreur lors de la sauvegarde',
  'designer.objectives.level': 'Niveau (Bloom)',
  'designer.objectives.linkedBlocks': 'Blocs liés',
  'designer.objectives.noContentBlocks': 'Aucun bloc de contenu disponible.',
  'designer.objectives.required': 'Obligatoire',
  'designer.objectives.save': 'Sauvegarder',
  'designer.objectives.savedToast': 'Objectifs sauvegardés',
  'designer.objectives.scoreGte': 'Score ≥',
  'designer.objectives.title': 'Objectifs pédagogiques',
  'designer.objectives.lvl.evaluation': "Évaluation",
  'designer.objectives.lvl.synthese': "Synthèse",
  'designer.objectives.lvl.analyse': "Analyse",
  'designer.objectives.lvl.application': "Application",
  'designer.objectives.lvl.comprehension': "Compréhension",
  'designer.objectives.lvl.connaissance': "Connaissance",
  'designer.objectives.cat.savoirEtre': "Savoir-être",
  'designer.objectives.cat.savoirFaire': "Savoir-faire",
  'designer.objectives.cat.savoir': "Savoir",
  'designer.objectives.linkedCount_other': "{{count}} blocs",
  'designer.objectives.linkedCount_one': "{{count}} bloc",
  'designer.objectives.defined_other': "{{count}} objectifs définis",
  'designer.objectives.defined_one': "{{count}} objectif défini",
  'designer.objectives.placeholder': "L'apprenant sera capable de…",
  'designer.objectives.update': 'Mettre à jour',
  'designer.validation.circularPath': 'Chemin circulaire détecté',
  'designer.validation.duplicateStart': 'Bloc Départ en doublon',
  'designer.validation.invalid': 'Invalide',
  'designer.validation.noEnd': 'Aucun bloc Fin trouvé. Il doit y en avoir au moins 1.',
  'designer.validation.noIssues': 'Aucun problème détecté',
  'designer.validation.noStart': 'Il doit y avoir exactement un bloc Départ (aucun trouvé).',
  'designer.validation.title': 'Validation',
  'designer.validation.untitled': 'Sans titre',
  'designer.validation.valid': 'Valide',
  'designer.validation.errorCount': '{{count}} erreur(s)',
  'designer.validation.warningCount': '{{count}} avertissement(s)',
  'designer.validation.titleRequired': 'Le bloc « {{type}} » doit avoir un titre.',
  'designer.validation.summary': 'Parcours : {{blocks}} bloc(s), {{edges}} liaison(s).',
  'designer.validation.multipleStart': 'Plusieurs blocs Départ ({{count}}) : un seul est autorisé.',
  'designer.validation.noOutgoing': 'Le bloc « {{type}} » n’a pas de connexion sortante vers un bloc suivant.',
  'designer.validation.tooManyOutputs': '« {{label}} » : trop de liaisons sortantes ({{count}}, maximum {{max}}).',
  'designer.validation.deadEndBranch': 'Branche sans chemin vers un bloc Fin (depuis « {{label}} »).',
  'designer.validation.moreDeadEnds': '… et {{count}} autre(s) branche(s) sans issue vers la fin.',
  'designer.validation.noConvergence': 'Les branches depuis « {{label}} » ne convergent pas vers un bloc commun.',
  'designer.validation.orphanBlock': 'Bloc isolé du graphe principal : « {{title}} ».',
  'designer.validation.endHasOutgoing': 'Le bloc Fin « {{title}} » a encore une liaison sortante.',
  'designer.validation.noScoring': 'Paramètres de score non définis pour « {{title}} ».',
  'designer.validation.noCorrectAnswer': 'Aucune réponse correcte choisie pour « {{title}} ».',
  'designer.validation.emptyText': 'Le bloc texte « {{title}} » est vide.',
  'designer.validation.textTooLong':
    'Le bloc texte « {{title}} » est estimé à {{lines}} lignes. Recommandé : {{min}} à {{max}} lignes.',
  'designer.validation.splitTextBlock': 'Séparer',
  'designer.validation.textSplitNotNeeded':
    'Ce bloc ne dépasse pas le seuil recommandé de lecture.',
  'designer.validation.textSplitUnable':
    'Impossible de proposer une séparation automatique fiable pour ce bloc.',
  'designer.validation.textSplitComplexFlow':
    'Séparation automatique non disponible pour ce bloc (branchements multiples/conditionnels).',
  'designer.validation.textSplitDone': '{{count}} blocs texte créés',
  'designer.validation.textSplitConfirmTitle':
    'Séparer automatiquement ce bloc texte ?',
  'designer.validation.textSplitConfirmMessage':
    'Le bloc « {{title}} » est estimé à {{lines}} lignes. Nous allons le découper en {{parts}} blocs (séparation guidée par les points) et les placer juste en dessous, dans l’ordre.',
  'designer.validation.textSplitConfirmAction': 'Séparer le bloc',
  'designer.validation.formFieldRefsMissing':
    '« {{title}} » : référence(s) vers champ(s) de formulaire inexistants : {{refs}}.',
  'designer.validation.formFieldRefsOrder':
    '« {{title}} » : variable(s) de formulaire incorrecte(s) {{refs}} — le bloc formulaire doit être atteint avant ce bloc dans le parcours.',
  'designer.confirmDeleteForm.title': 'Supprimer ce bloc formulaire ?',
  'designer.confirmDeleteForm.message':
    'D’autres blocs peuvent utiliser ses champs sous la forme /identifiant/. Ces références deviendront invalides. Vous pouvez annuler avec ⌘Z ou Ctrl+Z.',
  'designer.confirmDeleteForm.batchTitle': 'Supprimer la sélection ?',
  'designer.confirmDeleteForm.batchMessage':
    'La sélection contient au moins un bloc formulaire. Des références /identifiant/ ailleurs dans le parcours peuvent devenir invalides. Vous pouvez annuler avec ⌘Z ou Ctrl+Z.',
  'designer.variables.camelCaseError': 'Le nom doit être en camelCase (ex: maVariable)',
  'designer.variables.copy': 'Copier',
  'designer.variables.custom': 'Variables personnalisées',
  'designer.variables.defaultValue': 'Valeur par défaut',
  'designer.variables.howToTitle': 'Comment utiliser les variables :',
  'designer.variables.noCustom': 'Aucune variable personnalisée',
  'designer.variables.system': 'Variables système',
  'designer.variables.systemHint': 'Lecture seule — disponibles dans tous les blocs',
  'designer.variables.title': 'Variables',
  'designer.variables.type': 'Type',
  'voicePanel.title': 'Voix par défaut',
  'voicePanel.provider': 'Fournisseur : Mistral AI',
  'voicePanel.hint': 'Sélectionnez la voix utilisée pour la narration des blocs.',
  'voicePanel.multiLangHint':
    'Pour une voix Mistral par langue (les 11 langues de l’interface), ouvrez le panneau Traduction (icône langues dans la barre du designer).',
  'voicePanel.translationFrenchFallback':
    'Traduction audio : sans voix enregistrée pour une langue, le moteur utilise « Marie - sad » (Mistral) pour le français, l’italien et l’espagnol, et « Paul - sad » pour les autres.',
  'voicePanel.search': 'Rechercher…',
  'voicePanel.allLanguages': 'Toutes les langues',
  'voicePanel.loading': 'Chargement des voix…',
  'voicePanel.loadError': 'Impossible de charger les voix',
  'voicePanel.empty': 'Aucune voix disponible.',
  'voicePanel.emptyHint': 'Vérifiez la clé API Mistral dans Administration → Intégrations.',
  'voicePanel.noResults': 'Aucune voix ne correspond à votre recherche.',
  'voicePanel.saved': 'Voix par défaut sauvegardée',
  'voicePanel.saveError': 'Erreur lors de la sauvegarde',
  'voicePanel.default': 'Par défaut',
  'voicePanel.female': 'Féminine',
  'voicePanel.male': 'Masculine',
  'voicePanel.playPreview': 'Tester la voix',
  'voicePanel.stopPreview': 'Arrêter',
  'voicePanel.noPreview': 'Aperçu non disponible',
  'voicePanel.previewLoading': 'Génération…',
  'voicePanel.previewGenerateError': 'Impossible de générer l’aperçu audio.',
  'voicePanel.previewPlayError': 'Échec de la lecture audio.',
  'voicePanel.filterNoVoicesForLang':
    'Aucune voix du catalogue ne correspond à cette langue — essayez « Toutes les langues ».',
  'episodeEditor.voicePicker.blockVoice': 'Voix pour ce bloc',
  'episodeEditor.voicePicker.defaultVoice': 'Voix par défaut',
  'episodeEditor.voicePicker.voices': 'voix',
  'episodeEditor.voicePicker.searchPlaceholder': 'Rechercher une voix…',
  'episodeEditor.voicePicker.noVoiceFound': 'Aucune voix trouvée',
  'episodeEditor.voicePicker.female': 'Féminin',
  'episodeEditor.voicePicker.male': 'Masculin',
  'episodeEditor.voicePicker.stop': 'Arrêter',
  'episodeEditor.voicePicker.preview': 'Tester la voix',
  'episodeEditor.voicePicker.stopPlayback': 'Arrêter la lecture',
  'episodeEditor.errors.notFound': 'Épisode introuvable',
  'episodeEditor.errors.titleRequired': 'Le titre est requis.',
  'episodeEditor.errors.saveFailed': 'Erreur lors de la sauvegarde.',
  'episodeEditor.errors.noVoicesAvailable': 'Aucune voix disponible. Renseignez la clé API dans Administration → Intégrations plateforme, puis rechargez la page.',
  'episodeEditor.errors.selectVoiceFirst': 'Choisissez une voix dans l\'onglet « Voix » avant de générer la voix off.',
  'episodeEditor.errors.invalidBlockVoice': 'Un ou plusieurs blocs ont une voix invalide. Ouvrez le sélecteur de voix par bloc ou rechargez la liste des voix.',
  'episodeEditor.errors.addTextFirst': 'Ajoutez du texte avant de générer l\'audio.',
  'episodeEditor.errors.audioGenerationFailedApi': 'Génération audio impossible. {{message}} (durée affichée = estimation.)',
  'episodeEditor.errors.audioGenerationFailed': 'Erreur lors de la génération audio. Durée affichée = estimation.',
  'episodeEditor.success.published': 'Épisode publié !',
  'episodeEditor.success.draftSaved': 'Brouillon enregistré',
  'episodeEditor.success.audioGenerated': 'Audio généré avec {{count}} bloc(s)',
  'episodeEditor.tabs.edit': 'Édition',
  'episodeEditor.tabs.chapters': 'Chapitres',
  'episodeEditor.tabs.voice': 'Voix',
  'episodeEditor.tabs.podcasts': 'Podcasts',
  'episodeEditor.toolbar.undo': 'Annuler (Ctrl+Z)',
  'episodeEditor.toolbar.redo': 'Rétablir (Ctrl+Y)',
  'episodeEditor.toolbar.generating': 'Génération…',
  'episodeEditor.toolbar.generateAudio': 'Générer audio',
  'episodeEditor.toolbar.save': 'Enregistrer',
  'episodeEditor.toolbar.publish': 'Publier',
  'episodeEditor.titlePlaceholder': 'Titre de l\'épisode…',
  'episodeEditor.status.published': 'Publié',
  'episodeEditor.status.draft': 'Brouillon',
  'episodeEditor.warnings.mistralNotConfigured': 'La voix off des épisodes utilise Mistral Voxtral TTS. Ajoutez la clé Mistral dans Administration → Intégrations plateforme, puis rechargez cette page.',
  'episodeEditor.warnings.mistralNoVoices': 'La clé Mistral est reconnue mais l\'API n\'a renvoyé aucune voix. Consultez les logs du backend.',
  'episodeEditor.warnings.voiceLoadFailed': 'Impossible de charger la liste des voix. Vérifiez la configuration Mistral dans Administration → Intégrations plateforme.',
  'episodeEditor.warnings.voiceOffDisabled': 'Sans voix réelle, la génération de voix off reste désactivée.',
  'episodeEditor.edit.voiceOff': 'Voix off',
  'episodeEditor.edit.voiceDescription':
    'Par bloc : synthèse Mistral Voxtral (onglet Voix ou pastille à gauche) ou enregistrement micro. « Générer audio » assemble tous les segments dans l’ordre.',
  'episodeEditor.edit.coverImage': 'Image de couverture',
  'episodeEditor.edit.coverAlt': 'Couverture de l’épisode',
  'episodeEditor.edit.deleteCover': 'Supprimer',
  'episodeEditor.edit.dropOrClickImage': 'Cliquez ou déposez une image',
  'episodeEditor.edit.selectedVoice': 'Voix sélectionnée',
  'episodeEditor.edit.selectVoice': 'Sélectionner une voix',
  'episodeEditor.edit.defaultVoiceByLang': 'Voix par défaut (langue du podcast)',
  'episodeEditor.edit.lastAudioGenerated': 'Dernier audio généré',
  'episodeEditor.edit.audioNotFound': 'Fichier audio introuvable — regénérez la voix off.',
  'episodeEditor.edit.generatingInProgress': 'Génération en cours…',
  'episodeEditor.edit.generateVoiceOff': 'Générer la voix off',
  'episodeEditor.chapters.title': 'Chapitres',
  'episodeEditor.chapters.add': 'Ajouter',
  'episodeEditor.chapters.defaultChapterTitle': 'Introduction',
  'episodeEditor.chapters.newChapterTitle': 'Chapitre {{n}}',
  'episodeEditor.chapters.blockCount': '{{count}} bloc(s)',
  'episodeEditor.chapters.readByAi': 'Titre lu par l’IA',
  'episodeEditor.chapters.readByAiHint':
    "Si coché, l'IA lit le titre du chapitre avant le texte de ses blocs (même voix off). Les blocs restent toujours inclus.",
  'episodeEditor.voiceTab.availableVoices': 'Voix disponibles',
  'episodeEditor.podcasts.linked': 'Podcasts liés',
  'episodeEditor.podcasts.assign': 'Affecter',
  'episodeEditor.podcasts.noneAvailable': 'Aucun podcast disponible',
  'episodeEditor.podcasts.close': 'Fermer',
  'episodeEditor.podcasts.noneLinked': 'Aucun podcast lié',
  'episodeEditor.podcasts.assignHint': 'Affectez cet épisode à un ou plusieurs podcasts',
  'episodeEditor.podcasts.remove': 'Retirer',
  'episodeEditor.editor.addTextBlock': 'Ajouter un bloc de texte',
  'episodeEditor.editor.scriptPlaceholder': 'Commencez à écrire votre script…',
  'episodeEditor.editor.addBlock': 'Ajouter un bloc',
  'episodeEditor.block.source': 'Source',
  'episodeEditor.block.sourceTts': 'IA',
  'episodeEditor.block.sourceMic': 'Micro',
  'episodeEditor.block.pauseBefore': 'Pause avant',
  'episodeEditor.block.pauseBeforeHint': 'Silence avant de lire ce bloc (la suite).',
  'episodeEditor.block.pause0': '0 s',
  'episodeEditor.block.pause05': '0,5 s',
  'episodeEditor.block.pause1': '1 s',
  'episodeEditor.block.record': 'Enregistrer',
  'episodeEditor.block.stopRecording': 'Arrêter',
  'episodeEditor.block.microMode': 'Mode micro pour ce bloc',
  'episodeEditor.block.notesRecordingPlaceholder': 'Notes optionnelles (non lues automatiquement en micro)',
  'episodeEditor.block.removeRecording': 'Supprimer audio',
  'episodeEditor.edit.audioMixedHumanAndAi': 'Piste IA + voix micro',
  'episodeEditor.errors.addTextOrRecording': "Ajoutez du texte (synthèse IA) ou enregistrez votre voix sur au moins un bloc avant de générer l'audio.",
  'episodeEditor.errors.saveDraftBeforeRecording': 'Impossible de créer l’épisode pour le micro. Ajoutez un titre ou enregistrez depuis un épisode existant.',
  'episodeEditor.errors.microNotSupported': 'Micro non disponible dans ce navigateur.',
  'episodeEditor.errors.microPermission': 'Impossible d’accéder au micro (permission refusée).',
  'episodeEditor.errors.recordingUploadFailed': 'Échec de l’upload de l’enregistrement.',
  'episodeEditor.player.loop': 'Boucle',
  'episodeEditor.player.playbackSpeed': 'Vitesse de lecture',
  'episodeEditor.player.collapse': 'Réduire',
  'episodeEditor.player.expand': 'Agrandir',
  'episodeEditor.player.waveformHidden': 'Waveform masquée',
  'podcast.episodes.listen': 'Écouter',
  'podcast.episodes.noAudioShort': 'Pas d\'audio disponible',
  'podcast.episodes.edit': 'Modifier',
  'podcast.episodes.delete': 'Supprimer',
  'podcast.episodes.unmute': 'Activer le son',
  'podcast.episodes.mute': 'Couper le son',
  'podcast.episodes.totalEpisodes': '{{count}} épisode(s) au total',
  'eduflow.designer.toast.blockCopied': 'Block copié',
  'eduflow.designer.toast.blockPasted': 'Block collé',
  'eduflow.designer.toast.blockDuplicated': 'Block dupliqué',
  'eduflow.designer.toast.connectionUpdated': 'Connexion mise à jour',
  'eduflow.designer.toast.flowExported': 'Parcours exporté',
  'eduflow.designer.toast.flowImported': 'Parcours importé',
  'eduflow.designer.toast.flowPastedFromClipboard': 'Parcours créé à partir du presse-papiers',
  'eduflow.designer.toast.invalidFile': 'Fichier invalide',
  'eduflow.designer.toast.exportFailed': 'Export impossible.',
  'eduflow.designer.toast.importPackageFailed': 'Import du package .lyrra impossible.',
  'eduflow.designer.untitled': 'Sans titre',
  'eduflow.designer.importedFlow': 'Parcours importé',
  'eduflow.designer.collaborator.online': 'En ligne',
  'eduflow.designer.collaborator.offline': 'Hors ligne',
  'eduflow.designer.collaborator.defaultName': 'Utilisateur',
  'eduflow.designer.collaborator.editor': 'Editeur',
  'eduflow.designer.collaborator.viewer': 'Lecteur',
  'eduflow.designer.resizePanel': 'Redimensionner le panneau',
  'eduflow.designer.published': 'Publié',
  'eduflow.designer.draft': 'Brouillon',
  'eduflow.designer.quizOutput.success': 'Réussi',
  'eduflow.designer.quizOutput.failure': 'Échoué',
  'eduflow.designer.loopHandle.loop': 'Boucle',
  'eduflow.designer.loopHandle.end': 'Fin',
  'eduflow.designer.handle.default': 'Défaut',
  'eduflow.designer.genError': 'Erreur lors de la génération. Réessayez.',
  'trainerTour.welcome.title': 'Bienvenue sur votre espace formateur',
  'trainerTour.welcome.description':
    'Cette courte visite vous présente le tableau de bord et le menu. Ensuite, à chaque page que vous ouvrez pour la première fois, un rappel vous explique l’essentiel. Vous pouvez fermer une étape avec la croix : la visite ne se relancera plus.',
  'trainerTour.dashboard.header.title': 'Votre tableau de bord',
  'trainerTour.dashboard.header.description':
    'Vue d’ensemble de votre activité : indicateurs et raccourcis pour créer du contenu rapidement.',
  'trainerTour.dashboard.actions.title': 'Créer du contenu',
  'trainerTour.dashboard.actions.description':
    'Raccourcis vers une nouvelle formation EduFlow, un podcast ou un parcours. Utilisez-les pour démarrer un projet sans passer par le menu.',
  'trainerTour.dashboard.stats.title': 'Indicateurs',
  'trainerTour.dashboard.stats.description':
    'Formations, parcours publiés, apprenants concernés et podcasts : suivez l’ampleur de votre catalogue en un coup d’œil.',
  'trainerTour.dashboard.activity.title': 'Activité récente',
  'trainerTour.dashboard.activity.description':
    'Les derniers contenus modifiés. Cliquez une ligne pour rouvrir la formation, le podcast ou le parcours correspondant.',
  'trainerTour.sidebar.title': 'Menu latéral',
  'trainerTour.sidebar.description':
    'Pilotage (accueil, apprenants, analytiques, missions), création (formations, podcasts, parcours, webinaires si votre établissement les active), bibliothèque média et centre d’aide. Cliquez les entrées pour explorer : une aide contextuelle s’affiche la première fois sur chaque page.',
  'trainerTour.routes.learners.title': 'Apprenants',
  'trainerTour.routes.learners.description':
    'Liste des personnes inscrites à vos contenus, progression, exports. C’est votre cockpit pour le suivi pédagogique.',
  'trainerTour.routes.analytics.title': 'Analytiques',
  'trainerTour.routes.analytics.description':
    'Tableaux et graphiques sur l’engagement et la complétion. Personnalisez la disposition pour suivre ce qui compte pour vous.',
  'trainerTour.routes.eduflow.title': 'Formations (EduFlow)',
  'trainerTour.routes.eduflow.description':
    'Créez et gérez vos parcours interactifs, versions, publication et partage. C’est le cœur de la conception pédagogique.',
  'trainerTour.routes.podcasts.title': 'Podcasts',
  'trainerTour.routes.podcasts.description':
    'Émissions audio, épisodes, visibilité et partage avec les groupes ou apprenants de votre établissement.',
  'trainerTour.routes.parcours.title': 'Parcours',
  'trainerTour.routes.parcours.description':
    'Enchaînements de formations et ressources : structurez des parcours longs et assignez-les à vos publics.',
  'trainerTour.routes.videos.title': 'Vidéos',
  'trainerTour.routes.videos.description':
    'Bibliothèque de vidéos réutilisables dans vos formations et documents.',
  'trainerTour.routes.sounds.title': 'Sons',
  'trainerTour.routes.sounds.description':
    'Fichiers audio et tags pour alimenter podcasts et blocs sonores des parcours.',
  'trainerTour.routes.images.title': 'Images',
  'trainerTour.routes.images.description':
    'Visuels centralisés : import, dossiers et réutilisation dans le contenu.',
  'trainerTour.routes.documents.title': 'Documents',
  'trainerTour.routes.documents.description':
    'PDF et pièces jointes partagées, avec partage ciblé vers les apprenants si besoin.',
  'trainerTour.routes.help.title': 'Aide',
  'trainerTour.routes.help.description':
    'Articles, tutoriels et parcours d’apprentissage sur LyrraStudio. Revenez-y quand vous bloquez sur une fonction.',
  'trainerTour.routes.producerAssignments.title': 'Missions de production',
  'trainerTour.routes.producerAssignments.description':
    'Lorsque votre RH vous confie des contenus à produire (catalogue établissement), elles apparaissent ici avec l’état d’avancement.',
  'trainerTour.routes.webinars.title': 'Webinaires',
  'trainerTour.routes.webinars.description':
    'Planifiez des sessions live, invitations et liens de diffusion pour vos groupes.',
  'trainerTour.driver.progress': '{{current}} / {{total}}',
  'trainerTour.driver.next': 'Suivant',
  'trainerTour.driver.prev': 'Précédent',
  'trainerTour.driver.done': 'OK',
  'trainerTour.driver.gotIt': 'Compris',
  'player.lightMode': 'Mode clair',
  'player.darkMode': 'Mode sombre',
  'a11y.skipToContent': 'Aller au contenu principal',
  'a11y.skipToNav': 'Aller à la navigation',
  'a11y.skipToPlayerMain': 'Aller au contenu de la formation',
  'a11y.playerMainLandmark': 'Contenu de la formation',
  'a11y.dialog': 'Dialogue',
  'a11y.closeDialog': 'Fermer la boîte de dialogue',
  'a11y.dismissNotification': 'Fermer la notification',
  'a11y.notificationsRegion': 'Notifications',
  'a11y.skipLinksLandmark': 'Liens d’accès rapide',
  ...eduflowBlockEditorsFr,
  ...institutionMcpFr,
};

const enTranslations: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Dashboard',
  'nav.projects': 'Projects',
  'nav.eduflow': 'EduFlow',
  'nav.audiobooks': 'Audiobooks',
  'nav.audio_studio': 'Audio Studio',
  'nav.analytics': 'Analytics',
  'nav.users': 'Users',
  'nav.settings': 'Settings',
  'nav.profile': 'Profile',
  'profile.institution': 'Institution',
  'profile.institutionHint':
    'Your LyrraStudio account is linked to this institution. This link is managed by your administrator.',
  'profile.institutionUnknownName': 'Institution (name unavailable)',
  'nav.notifications': 'Notifications',
  'nav.help': 'Help',
  'nav.community': 'Community',
  'nav.library': 'Library',
  'nav.certificates': 'Certificates',
  'nav.institution': 'Institution',
  'nav.groups': 'Groups',
  'nav.supportGroups': 'Support groups',
  'profile.page.currentPassword': "Current password",
  'profile.page.newPassword': "New password",
  'profile.page.confirmPassword': "Confirm new password",
  'profile.page.changePassword': "Change password",
  'profile.page.languageRegion': "Language and region",
  'profile.page.notificationsTitle': "Notifications",
  'profile.page.prefEmail': "Email notifications",
  'profile.page.prefEmailHint': "Receive an email for important events",
  'profile.page.prefPush': "Browser notifications",
  'profile.page.prefPushHint': "Receive notifications in your browser",
  'profile.page.prefWeekly': "Weekly report",
  'profile.page.prefWeeklyHint': "Receive a summary of your activity every week",
  'profile.page.prefLanguage': "Interface language",
  'profile.page.prefTimezone': "Time zone",
  'profile.page.saving': "Saving…",
  'profile.page.savePreferences': "Save preferences",
  'nav.networkGroup': 'Group',
  'nav.learners': 'Learners',
  'nav.myLearning': 'My learning',
  'nav.reports': 'Reports',
  'nav.qualiopi': 'Qualiopi',
  'nav.training_plan': 'Training Plan',
  'nav.catalog': 'Catalog',
  'nav.competencies': 'Competencies',
  'nav.budget': 'Budget',
  'nav.campaigns': 'Campaigns',
  'nav.codir': 'CODIR',
  'nav.home': 'Home',
  'nav.producerAssignments': 'Production missions',
  'nav.videos': 'Videos',
  'nav.sounds': 'Sounds',
  'nav.images': 'Images',
  'pages.images.title': 'Images',
  'pages.images.pasteHint': 'Or paste a screenshot (Ctrl+V / Cmd+V)',
  'pages.images.emptyHint':
    'Drag and drop, click to import, or paste a screenshot (Ctrl+V / Cmd+V).',
  'pages.images.viewLarge': 'View full size',
  'pages.images.lightboxTitle': 'Image preview',
  'pages.images.lightboxClose': 'Close',
  'pages.images.lightboxBackdropClose': 'Close preview',
  'pages.images.lightboxPrev': 'Previous image',
  'pages.images.lightboxNext': 'Next image',
  'nav.documents': 'Documents',
  'nav.podcasts': 'Podcasts',
  'nav.webinars': 'Webinars',
  'nav.webinarsEnterprise': 'Webinars (organization)',
  'webinar.room.capacityFull':
    'This room has reached its maximum capacity ({{max}} people connected at once). Please try again later.',
  'webinar.room.participants': 'Participants',
  'webinar.room.chat': 'Chat',
  'webinar.room.speechTab': 'Speech',
  'webinar.room.speechPanelHint':
    'Text captured from presenters’ microphones (browser speech recognition). The name shown is the person currently speaking.',
  'webinar.room.speechEmpty':
    'When a presenter speaks with the microphone on, their words appear here with their name. Punctuation follows the browser’s transcript.',
  'webinar.room.speechUnknownSpeaker': 'Presenter',
  'webinar.room.speechPresenterActive':
    'Your voice is transcribed by the browser and sent to participants (Speech tab).',
  'webinar.room.speechUnsupportedBrowser':
    'Automatic presenter captions are not available in this browser (try Chrome or Edge).',
  'webinar.room.sidePanelTabs': 'Participants, presenter speech, poll, and chat',
  'webinar.room.you': 'you',
  'webinar.room.showParticipantsPanel': 'Show participants panel',
  'webinar.room.hideParticipantsPanel': 'Collapse panel',
  'webinar.room.leave': 'Leave room',
  'webinar.room.contentFocusExpand': 'Content fills the stage',
  'webinar.room.contentFocusCollapse': 'Show backdrop and presenters',
  'webinar.room.contentFocusAriaExpand':
    'Expand shared course, uploaded document or screen share full width without stage backdrop or presenter cameras',
  'webinar.room.liveDocumentLabel': 'Upload (PDF or PowerPoint)',
  'webinar.room.liveDocumentUploadChoice': 'Pick a file to display',
  'webinar.room.liveDocumentUploading': 'Uploading…',
  'webinar.room.liveDocumentUploadError':
    'Could not upload the file. Check the format (PDF or PowerPoint) and size (max 40 MB).',
  'webinar.room.liveDocumentClose': 'Remove document',
  'webinar.room.liveDocumentBehindScreen':
    'The document stays available — stop screen sharing to show it again.',
  'webinar.room.waitingSharedContentExtended':
    'Waiting for the presenter to share a catalog course, a file (PDF or PowerPoint), or their screen.',
  'webinar.room.liveDocumentOfficeHint':
    'If preview fails (plain HTTP locally or oversized file), use the link above.',
  'webinar.room.liveDocumentOpenOrDownload': 'Open or download the file',
  'webinar.room.liveDocumentNoInlinePreview':
    'This file cannot be embedded automatically. Open it in a new tab.',
  'webinar.room.liveDocumentOpenTab': 'Open',
  'webinar.room.pollTab': 'Poll',
  'webinar.room.poll.regionTitle': 'Webinar poll',
  'webinar.room.poll.presenterHint':
    'Add a question and 2–8 answers. Everyone sees the poll here; only hosts see live results.',
  'webinar.room.poll.questionLabel': 'Question',
  'webinar.room.poll.questionPlaceholder': 'E.g. Which topic should we cover next?',
  'webinar.room.poll.optionsLabel': 'Answer choices',
  'webinar.room.poll.optionNumber': 'Answer',
  'webinar.room.poll.choiceLabel': 'Choice',
  'webinar.room.poll.addOption': 'Add an answer',
  'webinar.room.poll.removeLastOption': 'Remove last',
  'webinar.room.poll.launch': 'Start poll',
  'webinar.room.poll.waitingParticipant': 'No active poll yet. The host can start one.',
  'webinar.room.poll.voteHint':
    'One vote saved per person: you can change your answer by picking another option.',
  'webinar.room.poll.yourVote': 'your pick',
  'webinar.room.poll.resultsHeading': 'Results (hosts)',
  'webinar.room.poll.noVotesYet': 'No votes yet.',
  'webinar.room.poll.totalVotes': 'Total votes: {{n}}',
  'webinar.room.poll.closePoll': 'Close poll',
  'webinar.room.contentFocusAriaCollapse': 'Restore stage backdrop and presenter column',
  'webinar.room.exitDirectTitle': 'Leave this webinar?',
  'webinar.room.exitDirectMessage':
    'You opened this room from a direct link (not from the dashboard). You can close this tab. If the browser blocks closing, you will be redirected to your home area.',
  'webinar.room.exitDirectConfirm': 'Close tab',
  'webinar.room.publicGateTitle': 'Webinar signup',
  'webinar.room.publicGateHint':
    'The organizer enabled public signup: enter your email, choose a password (min. 8 characters) and complete the requested fields.',
  'webinar.room.publicGateEmail': 'Email',
  'webinar.room.publicGateLogin': 'Login',
  'webinar.room.publicGatePassword': 'Password',
  'webinar.room.publicGatePasswordHintParticipant':
    'First visit: registration. Next visits: same email and password. Minimum 8 characters.',
  'webinar.room.publicGateSubmit': 'Enter room',
  'webinar.room.publicGateSubmitting': 'Verifying…',
  'webinar.room.publicGateDenied': 'Incorrect email or password.',
  'webinar.room.socketGateDenied':
    'Could not join the live room: public signup session missing or expired. Reload the page and sign in again with the same email and password.',
  'nav.parcours': 'Learning paths',
  'nav.eduflowList': 'Courses',
  'nav.section.pilotage': 'Overview',
  'nav.section.creation': 'Create',
  'nav.section.medias': 'Media',
  'nav.section.aide': 'Help',
  'sidebar.brandTagline': 'Training',
  'sidebar.logoInstitutionAlt': 'Organization logo',
  'sidebar.expand': 'Expand menu',
  'sidebar.collapse': 'Collapse menu',
  'sidebar.expandAria': 'Expand sidebar',
  'sidebar.collapseAria': 'Collapse sidebar',
  'dashboard.kindPodcast': 'Podcast',
  'pages.learners.title': 'Learners',
  'pages.learners.description': 'Track your learners’ progress',
  'pages.learners.enrolledFormations': 'Enrolled courses',
  'pages.learners.notStarted': 'Not started',
  'pages.learners.startedOn': 'Started {{date}}',
  'pages.learners.detailedTraces': 'Detailed activity · {{flowTitle}}',
  'pages.learners.statsEvents': 'Events: {{count}}',
  'pages.learners.statsResponses': 'Responses: {{count}}',
  'pages.learners.statsBlockSessions': 'Block sessions: {{count}}',
  'pages.learners.timePerBlock': 'Time per block',
  'pages.learners.lastEvents': 'Recent events',
  'pages.learners.blockSessionInProgress': '(in progress)',
  'pages.learners.blockTimeOnBlock': '({{duration}} on this block)',
  'pages.learners.trackingEvent.unknown': 'Event',
  'pages.learners.trackingEvent.training_enter': 'Opened the course',
  'pages.learners.trackingEvent.training_exit': 'Left or closed the course',
  'pages.learners.trackingEvent.training_completed': 'Course completed',
  'pages.learners.trackingEvent.block_enter': 'Entered a block',
  'pages.learners.trackingEvent.block_exit': 'Left a block',
  'pages.learners.trackingEvent.response_submitted': 'Submitted a response',
  'pages.learners.trackingEvent.progress_sync': 'Progress synced',
  'pages.learners.refresh': 'Refresh',
  'pages.learners.exportCsv': 'Export CSV',
  'pages.learners.statTotal': 'Total learners',
  'pages.learners.statInProgress': 'In progress',
  'pages.learners.statCompleted': 'Completed',
  'pages.learners.statAvgProgress': 'Avg. progress',
  'pages.learners.searchPlaceholder': 'Search by name or email…',
  'pages.learners.filterAllFlows': 'All courses',
  'pages.learners.sortRecent': 'Most recent',
  'pages.learners.sortName': 'Name A-Z',
  'pages.learners.sortProgress': 'Progress',
  'pages.learners.colLearner': 'Learner',
  'pages.learners.colFormations': 'Courses',
  'pages.learners.colProgress': 'Progress',
  'pages.learners.colCompleted': 'Completed',
  'pages.learners.colTime': 'Time',
  'pages.learners.colLastAccess': 'Last access',
  'pages.learners.colActions': 'Actions',
  'pages.learners.openProgressDashboard': 'Progress dashboard',
  'pages.learners.dashboardTitle': 'Learner progress',
  'pages.learners.dashboardBack': 'Back to list',
  'pages.learners.dashboardInvalidId': 'Invalid learner id',
  'pages.learners.dashboardNotFound': 'Learner not found or access denied',
  'pages.learners.dashboardOverviewHint':
    'Consolidated view: EduFlow courses this participant is enrolled in, plus learning paths and podcasts assigned to the Lyrra account with the same email.',
  'pages.learners.tabOverview': 'Overview',
  'pages.learners.tabFormations': 'Courses',
  'pages.learners.tabParcours': 'Paths',
  'pages.learners.tabPodcasts': 'Podcasts',
  'pages.learners.sectionFormations': 'Courses',
  'pages.learners.sectionParcours': 'Learning paths',
  'pages.learners.sectionPodcasts': 'Podcasts',
  'pages.learners.emptyParcours': 'No learning path assigned to a Lyrra user with this email.',
  'pages.learners.emptyPodcasts': 'No podcast assigned to a Lyrra user with this email.',
  'pages.learners.linkedAccountHint':
    'Paths and podcasts show when a Lyrra user exists with the same email as this participant.',
  'pages.learners.kpiEduflows': 'Courses',
  'pages.learners.kpiAvgProgress': 'Avg. progress',
  'pages.learners.kpiPaths': 'Paths',
  'pages.learners.kpiPodcasts': 'Podcasts',
  'pages.learners.kpiCompletedFormations': '{{count}} completed',
  'pages.learners.pathProgressLabel': '{{done}} / {{total}} activities',
  'pages.learners.pathAssignedOn': 'Added on {{date}}',
  'pages.learners.podcastCompleted': 'Completed',
  'pages.learners.podcastInProgress': 'In progress',
  'pages.learners.pathStatusPublished': 'Published',
  'pages.learners.pathStatusDraft': 'Draft',
  'pages.learners.openInDesigner': 'Open in designer',
  'pages.learners.openParcoursEditor': 'Open path',
  'pages.learners.viewPodcasts': 'View podcasts',
  'pages.learners.loading': 'Loading learners…',
  'pages.learners.loadError': 'Could not load learners',
  'pages.learners.retry': 'Retry',
  'pages.learners.emptyFiltered': 'No learners found',
  'pages.learners.emptyFilteredHint': 'Try changing your search or filters',
  'pages.learners.emptyNone': 'No learners yet',
  'pages.learners.emptyNoneHint': 'Students in your classes and learners enrolled in your courses will appear here',
  'pages.learners.pagination': 'Page {{page}} of {{totalPages}} · {{total}} learner(s)',
  'pages.learners.prev': 'Previous',
  'pages.learners.filterAllClasses': 'All my classes',
  'pages.learners.noFormation': 'No course assigned',
  'pages.learners.csvClasses': 'Classes',
  'pages.learners.next': 'Next',
  'pages.learners.csvLastName': 'Last name',
  'pages.learners.csvFirstName': 'First name',
  'pages.learners.csvEmail': 'Email',
  'pages.learners.csvFormations': 'Courses',
  'pages.learners.csvAvgProgress': 'Average progress',
  'pages.learners.csvTotalTime': 'Total time',
  'pages.learners.csvLastActivity': 'Last activity',
  'eduflowList.status.draft': 'Draft',
  'eduflowList.status.published': 'Published',
  'eduflowList.status.archived': 'Archived',
  'eduflowList.badge.enterpriseShell': 'Enterprise request',
  'eduflowList.badge.enterpriseLinked': 'Enterprise',
  'eduflowList.badge.toProduce': 'To produce',
  'eduflow.list.blocksCount': '{{count}} blocks',
  'eduflow.list.noContent': 'No content',
  'eduflow.list.minutesShort': '{{count}} min',
  'eduflow.list.tooltip.openMissions': 'Open My assignments',
  'eduflow.list.tooltip.edit': 'Edit',
  'eduflow.list.shellHint':
    'Defined by your organization — create the course from « My assignments ».',
  'breadcrumb.label': 'Breadcrumb',
  'breadcrumb.home': 'Home',
  'breadcrumb.dashboard': 'Dashboard',
  'breadcrumb.projects': 'Projects',
  'breadcrumb.eduflow': 'Courses',
  'breadcrumb.learners': 'Learners',
  'breadcrumb.analytics': 'Analytics',
  'breadcrumb.videos': 'Videos',
  'breadcrumb.sounds': 'Sounds',
  'breadcrumb.images': 'Images',
  'breadcrumb.documents': 'Documents',
  'breadcrumb.help': 'Help',
  'breadcrumb.admin': 'Administration',
  'breadcrumb.settings': 'Settings',
  'breadcrumb.profile': 'Profile',
  'breadcrumb.groups': 'Groups',
  'breadcrumb.branding': 'Branding',
  'breadcrumb.landingPage': 'Landing page',
  'breadcrumb.webhooks': 'Webhooks',
  'breadcrumb.campaigns': 'Campaigns',
  'breadcrumb.competencies': 'Competencies',
  'breadcrumb.compliance': 'Compliance',
  'breadcrumb.reports': 'Reports',
  'breadcrumb.budget': 'Budget',
  'breadcrumb.catalog': 'Catalog',
  'breadcrumb.integrations': 'Integrations',
  'breadcrumb.requests': 'Requests',
  'breadcrumb.team': 'Team',
  'breadcrumb.reviews': 'Reviews',
  'breadcrumb.training': 'Training',
  'breadcrumb.skills': 'Skills',
  'breadcrumb.certificates': 'Certificates',
  'breadcrumb.new': 'New',
  'breadcrumb.edit': 'Edit',
  'breadcrumb.podcasts': 'Podcasts',
  'breadcrumb.episodes': 'Episodes',
  'breadcrumb.parcours': 'Learning paths',
  'breadcrumb.producerAssignments': 'Production missions',
  'header.toggleMenu': 'Toggle menu',
  'header.search': 'Search…',
  'header.toggleTheme': 'Toggle light or dark theme',
  'header.profile': 'Profile',
  'header.settings': 'Settings',
  'header.backToInstitutionAdmin': 'Institution administration',
  'header.openTrainerWorkspace': 'Trainer workspace & create content',
  'header.logout': 'Log out',
  'footer.madeWith': 'Made with',
  'footer.forEducation': 'for education',
  'footer.legal': 'Legal links',
  'footer.terms': 'Terms',
  'footer.privacy': 'Privacy',
  'footer.accessibility': 'Accessibility',
  // Common buttons
  'btn.save': 'Save',
  'btn.cancel': 'Cancel',
  'btn.delete': 'Delete',
  'btn.edit': 'Edit',
  'btn.create': 'Create',
  'btn.add': 'Add',
  'btn.close': 'Close',
  'btn.confirm': 'Confirm',
  'btn.back': 'Back',
  'btn.next': 'Next',
  'btn.previous': 'Previous',
  'btn.submit': 'Submit',
  'btn.search': 'Search',
  'btn.filter': 'Filter',
  'btn.export': 'Export',
  'btn.import': 'Import',
  'btn.download': 'Download',
  'btn.upload': 'Upload',
  'btn.refresh': 'Refresh',
  'btn.retry': 'Retry',
  'btn.view': 'View',
  'btn.details': 'Details',
  'btn.publish': 'Publish',
  'btn.unpublish': 'Unpublish',
  'btn.duplicate': 'Duplicate',
  'btn.archive': 'Archive',
  'btn.restore': 'Restore',
  'btn.send': 'Send',
  'btn.apply': 'Apply',
  'btn.reset': 'Reset',
  'btn.select_all': 'Select all',
  'btn.deselect_all': 'Deselect all',
  'btn.load_more': 'Load more',
  'btn.show_more': 'Show more',
  'btn.show_less': 'Show less',
  'btn.copy': 'Copy',
  'btn.share': 'Share',
  'btn.preview': 'Preview',
  'btn.continue': 'Continue',
  'btn.skip': 'Skip',
  // Form labels
  'form.email': 'Email address',
  'form.password': 'Password',
  'form.password_confirm': 'Confirm password',
  'form.first_name': 'First name',
  'form.last_name': 'Last name',
  'form.name': 'Name',
  'form.phone': 'Phone',
  'form.address': 'Address',
  'form.city': 'City',
  'form.zip': 'Zip code',
  'form.country': 'Country',
  'form.title': 'Title',
  'form.description': 'Description',
  'form.category': 'Category',
  'form.tags': 'Tags',
  'form.status': 'Status',
  'form.role': 'Role',
  'form.language': 'Language',
  'form.date': 'Date',
  'form.start_date': 'Start date',
  'form.end_date': 'End date',
  'form.duration': 'Duration',
  'form.price': 'Price',
  'form.avatar': 'Profile picture',
  'form.required': 'Required',
  'form.optional': 'Optional',
  'form.select_placeholder': 'Select...',
  'form.search_placeholder': 'Search...',
  'form.no_results': 'No results',
  'form.file_drop': 'Drag and drop a file here or click to browse',
  'form.max_size': 'Maximum size: {{size}}',
  'form.institution': 'Institution',
  'form.department': 'Department',
  'form.group': 'Group',
  'form.level': 'Level',
  'form.notes': 'Notes',
  'form.url': 'URL',
  'form.content': 'Content',
  'form.thumbnail': 'Thumbnail',
  // Validation errors
  'error.required': 'This field is required',
  'error.email_invalid': 'Invalid email address',
  'error.password_min': 'Password must be at least {{min}} characters',
  'error.password_match': 'Passwords do not match',
  'error.min_length': 'Minimum {{min}} characters',
  'error.max_length': 'Maximum {{max}} characters',
  'error.min_value': 'Minimum value is {{min}}',
  'error.max_value': 'Maximum value is {{max}}',
  'error.invalid_url': 'Invalid URL',
  'error.file_too_large': 'File is too large',
  'error.file_type': 'Unsupported file type',
  'error.generic': 'An error occurred',
  'error.network': 'Network connection error',
  'error.unauthorized': 'You must be logged in',
  'error.forbidden': 'Access denied',
  'error.not_found': 'Resource not found',
  'error.server': 'Internal server error',
  'error.conflict': 'This resource already exists',
  'error.rate_limit': 'Too many requests, please wait',
  // Auth
  'auth.login': 'Log in',
  'auth.logout': 'Log out',
  'auth.register': 'Sign up',
  'auth.forgot_password': 'Forgot password?',
  'auth.reset_password': 'Reset password',
  'auth.otp_title': 'Login with code',
  'auth.otp_sent': 'A verification code has been sent to {{email}}',
  'auth.otp_placeholder': 'Enter the 6-digit code',
  'auth.google_login': 'Continue with Google',
  'auth.remember_me': 'Remember me',
  'auth.no_account': 'No account yet?',
  'auth.has_account': 'Already have an account?',
  'auth.welcome_back': 'Welcome back, {{name}}!',
  'auth.login_success': 'Login successful',
  'auth.register_success': 'Account created successfully',
  'auth.logout_success': 'Logout successful',
  // Roles
  'role.teacher': 'Teacher',
  'role.institution': 'Institution',
  'role.admin': 'Administrator',
  'role.drh': 'HR Director',
  'role.rh': 'HR Manager',
  'role.manager': 'Manager',
  'role.collaborator': 'Collaborator',
  'role.learner': 'Learner',
  // Dashboard
  'dashboard.title': 'Dashboard',
  'dashboard.welcome': 'Welcome, {{name}}',
  'dashboard.subtitle': 'Overview of your activity',
  'dashboard.home.greeting': 'Hello {{name}}',
  'dashboard.home.greetingSubtitle':
    'Manage your courses, learning paths and podcasts from one place.',
  'dashboard.home.primaryCta': 'Create a course',
  'dashboard.home.continueEditing': 'Resume last edit',
  'dashboard.home.publishRate': 'Publication rate',
  'dashboard.home.totalFormations': 'Courses created',
  'dashboard.home.uniqueLearners': 'Learners reached',
  'dashboard.home.calendarKicker': 'Activity this month',
  'dashboard.home.recentWork': 'Recent updates',
  'dashboard.home.viewAll': 'View all',
  'dashboard.home.quickAccess': 'Quick access',
  'dashboard.home.loading': 'Loading dashboard…',
  'dashboard.home.defaultName': 'trainer',
  'dashboard.welcomeSub': 'Here is what is happening across your projects.',
  'dashboard.projects': 'Projects',
  'dashboard.eduflows': 'Training paths',
  'dashboard.publishedFlows': 'Published paths',
  'dashboard.certificatesIssued': 'Certificates issued',
  'dashboard.activityEmpty': 'No recent activity on your paths or podcasts.',
  'dashboard.loadError': 'Could not load dashboard data.',
  'dashboard.learners': 'Learners',
  'dashboard.revenue': 'Revenue',
  'dashboard.newProject': 'New project',
  'dashboard.newEduFlow': 'New course',
  'dashboard.uploadMedia': 'Upload media',
  'dashboard.viewDocs': 'Documentation',
  'dashboard.recentProjects': 'Recent projects',
  'dashboard.noRecentProjects': 'No recent projects',
  'dashboard.activity': 'Activity',
  'dashboard.searchProjects': 'Search projects...',
  'dashboard.noProjects': 'No projects yet',
  'dashboard.createFirst': 'Create your first project to get started',
  'dashboard.createProject': 'Create project',
  'dashboard.projectTitle': 'Project title',
  'dashboard.recentActivity': 'Recent activity',
  'project.status.draft': 'Draft',
  'project.status.published': 'Published',
  'project.status.archived': 'Archived',
  'dashboard.roleSpecific': 'Your space is personalized according to your role.',
  'dashboard.calendar.mon': 'Mon',
  'dashboard.calendar.wed': 'Wed',
  'dashboard.calendar.fri': 'Fri',
  'dashboard.calendar.less': 'Less',
  'dashboard.calendar.more': 'More',
  'dashboard.calendar.tooltip': '{{count}} activit(ies) on {{date}}',
  'dashboard.mock.updated2h': '2h ago',
  'dashboard.mock.updated5h': '5h ago',
  'dashboard.mock.updatedYesterday': 'Yesterday',
  'dashboard.home': 'Home',
  'dashboard.analytics': 'Analytics',
  'dashboard.settings': 'Settings',
  'dashboard.analyticsComingSoon': 'Analytics coming soon',
  'dashboard.settingsComingSoon': 'Settings coming soon',
  'pages.analytics.title': 'Analytics',
  'pages.analytics.description': 'Dashboards and statistics',
  'pages.analytics.subtitle':
    'Filter data, drag to reorder charts, and resize widgets to match how you work.',
  'pages.analytics.filters.period': 'Period',
  'pages.analytics.filters.flow': 'Course',
  'pages.analytics.filters.allFlows': 'All courses',
  'pages.analytics.period.7d': 'Last 7 days',
  'pages.analytics.period.30d': 'Last 30 days',
  'pages.analytics.period.90d': 'Last 90 days',
  'pages.analytics.period.365d': 'Last 12 months',
  'pages.analytics.addChart': 'Add chart',
  'pages.analytics.resetLayout': 'Reset layout',
  'pages.analytics.dragHint': 'Drag to reorder',
  'pages.analytics.size.sm': 'S',
  'pages.analytics.size.md': 'M',
  'pages.analytics.size.lg': 'L',
  'pages.analytics.removeChart': 'Remove this chart',
  'pages.analytics.kind.completion_line': 'Completion over time',
  'pages.analytics.kind.learners_bar': 'Learners by module',
  'pages.analytics.kind.status_pie': 'Status distribution',
  'pages.analytics.kind.engagement_area': 'Engagement time',
  'pages.analytics.series.completion': 'Completion rate (%)',
  'pages.analytics.series.learners': 'Learners',
  'pages.analytics.series.hours': 'Hours',
  'pages.analytics.status.completed': 'Completed',
  'pages.analytics.status.in_progress': 'In progress',
  'pages.analytics.status.dropped': 'Dropped',
  'pages.analytics.status.not_started': 'Not started',
  'pages.analytics.loadError': 'Could not load analytics data.',
  'pages.analytics.retry': 'Retry',
  'pages.analytics.axis.bucket': 'P. {{n}}',
  'pages.analytics.mock.wd0': 'Mon',
  'pages.analytics.mock.wd1': 'Tue',
  'pages.analytics.mock.wd2': 'Wed',
  'pages.analytics.mock.wd3': 'Thu',
  'pages.analytics.mock.wd4': 'Fri',
  'pages.analytics.mock.wd5': 'Sat',
  'pages.analytics.mock.wd6': 'Sun',
  'pages.analytics.emptyCharts': 'Add at least one chart to see your data.',
  'pages.analytics.comingSoonDesc':
    'See training stats, completion rates, learner engagement, and overall performance.',
  'pages.help.title': 'Help center',
  'pages.help.description': 'Documentation and support',
  'pages.help.hubIntro':
    'Topics covering how to use Lyrra — you will soon be able to link each card to your docs, tutorials, or support tickets.',
  'pages.help.searchPlaceholder': 'Search topics…',
  'pages.help.noResults': 'No results for this search.',
  'pages.help.configHint':
    'Sample content: wire up your URLs, articles, or knowledge base when you are ready.',
  'pages.help.comingSoonDesc':
    'Access documentation, tutorials, and technical support to get the most out of the platform.',
  'pages.help.modal.steps': 'In practice',
  'pages.help.modal.tips': 'Tips',
  'pages.help.modal.related': 'See also',
  'pages.help.openArticle': 'Open article',
  'pages.help.onboardingFlowMissing': 'Training not found',
  'pages.help.onboardingFlowMissingDesc':
    'Run backend seeds (db:seed:onboarding-eduflow, db:seed:onboarding-navigation-eduflow, db:seed:onboarding-profile-eduflow, db:seed:onboarding-roles-eduflow, db:seed:onboarding-block-types-eduflow, db:seed:onboarding-designer-overview-eduflow, db:seed:onboarding-branches-eduflow, db:seed:onboarding-validation-eduflow, db:seed:onboarding-objectives-eduflow, db:seed:onboarding-ai-designer-eduflow, db:seed:onboarding-publication-status-eduflow, db:seed:onboarding-publication-links-eduflow, db:seed:onboarding-catalog-visibility-eduflow, db:seed:onboarding-enrollments-eduflow, db:seed:onboarding-groups-cohorts-eduflow, db:seed:onboarding-progress-eduflow, db:seed:onboarding-certificates-eduflow, and db:seed:onboarding-media-library-eduflow) or set VITE_ONBOARDING_DASHBOARD_FLOW_ID / VITE_ONBOARDING_NAVIGATION_FLOW_ID / VITE_ONBOARDING_PROFILE_FLOW_ID / VITE_ONBOARDING_ROLES_FLOW_ID / VITE_ONBOARDING_BLOCK_TYPES_FLOW_ID / VITE_ONBOARDING_DESIGNER_OVERVIEW_FLOW_ID / VITE_ONBOARDING_BRANCHES_FLOW_ID / VITE_ONBOARDING_VALIDATION_FLOW_ID / VITE_ONBOARDING_OBJECTIVES_FLOW_ID / VITE_ONBOARDING_AI_DESIGNER_FLOW_ID / VITE_ONBOARDING_PUBLICATION_STATUS_FLOW_ID / VITE_ONBOARDING_PUBLICATION_LINKS_FLOW_ID / VITE_ONBOARDING_CATALOG_VISIBILITY_FLOW_ID / VITE_ONBOARDING_ENROLLMENTS_FLOW_ID / VITE_ONBOARDING_GROUPS_COHORTS_FLOW_ID / VITE_ONBOARDING_PROGRESS_FLOW_ID / VITE_ONBOARDING_CERTIFICATES_FLOW_ID / VITE_ONBOARDING_MEDIA_LIBRARY_FLOW_ID.',
  'pages.help.onboardingDashboardPlayerTitle': 'Training — Lyrra creator dashboard (onboarding)',
  'pages.help.onboardingNavigationPlayerTitle':
    'Training — Lyrra navigation & sidebar (onboarding)',
  'pages.help.onboardingProfilePlayerTitle': 'Training — Lyrra profile & preferences (onboarding)',
  'pages.help.onboardingRolesPlayerTitle':
    'Training — Lyrra roles and access rights (onboarding)',
  'pages.help.onboardingBlockTypesPlayerTitle':
    'Training — EduFlow block types (onboarding)',
  'pages.help.onboardingDesignerOverviewPlayerTitle':
    'Training — EduFlow designer overview (onboarding)',
  'pages.help.onboardingBranchesPlayerTitle':
    'Training — EduFlow branches & conditions (onboarding)',
  'pages.help.onboardingValidationPlayerTitle':
    'Training — EduFlow parcours validation (onboarding)',
  'pages.help.onboardingObjectivesPlayerTitle':
    'Training — EduFlow learning objectives (onboarding)',
  'pages.help.onboardingAiDesignerPlayerTitle':
    'Training — LyrraStudio AI designer assistant (onboarding)',
  'pages.help.onboardingPublicationStatusPlayerTitle':
    'Training — LyrraStudio draft, publication and updates (onboarding)',
  'pages.help.onboardingPublicationLinksPlayerTitle':
    'Training — LyrraStudio access links and sharing (onboarding)',
  'pages.help.onboardingCatalogVisibilityPlayerTitle':
    'Training — LyrraStudio catalog and visibility (onboarding)',
  'pages.help.onboardingEnrollmentsPlayerTitle':
    'Training — LyrraStudio enrollments & participants (onboarding)',
  'pages.help.onboardingGroupsCohortsPlayerTitle':
    'Training — LyrraStudio groups & cohorts (onboarding)',
  'pages.help.onboardingProgressPlayerTitle':
    'Training — LyrraStudio progress & time spent (onboarding)',
  'pages.help.onboardingCertificatesPlayerTitle':
    'Training — LyrraStudio delivered certificates (onboarding)',
  'pages.help.onboardingMediaLibraryPlayerTitle':
    'Training — LyrraStudio media library (onboarding)',
  'dashboard.period.week': 'Week',
  'dashboard.period.month': 'Month',
  'dashboard.period.quarter': 'Quarter',
  'dashboard.vsLastPeriod': 'vs previous period ({{period}})',
  'dashboard.completionPercent': 'Completion %',
  'dashboard.activeLearners': 'Active learners',
  'dashboard.sessions': 'Sessions',
  'dashboard.activityType.completion': 'Completed',
  'dashboard.activityType.enrollment': 'Enrollment',
  'dashboard.activityType.milestone': 'Milestone',
  'dashboard.activityType.feedback': 'Feedback',
  'dashboard.activityType.start': 'Start',
  'dashboard.totalLearners': 'Total learners',
  'dashboard.activeEduFlows': 'Active training paths',
  'dashboard.publishedTraining': 'Published courses',
  'dashboard.activeUsersPeriod': 'Active users (period)',
  'dashboard.completionRate': 'Completion rate',
  'dashboard.npsScore': 'NPS score',
  'dashboard.topEduFlows': 'Top training paths',
  'dashboard.topEduFlowsHint':
    'Completion rate among the most enrolled courses (sorted by learner count).',
  'dashboard.engagement': 'Learner engagement',
  'common.seeAll': 'See all',
  'common.startedOn': 'Started on',
  'admin.dashboard.totalUsers': 'Total users',
  'admin.dashboard.activeToday': 'Active today',
  'admin.dashboard.revenue': 'Revenue',
  'admin.dashboard.storageUsed': 'Storage used',
  'admin.dashboard.vsLastMonth': 'vs last month',
  'admin.dashboard.userGrowth': 'User growth',
  'admin.dashboard.monthlyRevenue': 'Monthly revenue (€)',
  'admin.dashboard.recentActivity': 'Recent activity',
  'admin.dashboard.systemHealth': 'System health',
  'admin.dashboard.quickActions': 'Quick actions',
  'admin.nav.stats': 'Statistics',
  'admin.nav.users': 'Users',
  'admin.nav.support': 'Support & chat',
  'support.chat.title': 'Lyrra support',
  'support.chat.subtitle': 'Ask your question — our team replies here.',
  'support.chat.empty': 'Write a message to start the conversation.',
  'support.chat.placeholder': 'Your message…',
  'support.chat.send': 'Send',
  'support.chat.open': 'Open support chat',
  'support.chat.fromSupport': 'Support',
  'support.chat.fromYou': 'You',
  'admin.support.title': 'Support conversations',
  'admin.support.loadError': 'Could not load conversations.',
  'admin.support.replyError': 'Could not send the reply.',
  'admin.support.selectThread': 'Select a conversation to view messages.',
  'admin.support.replyPlaceholder': 'Your reply to the user…',
  'admin.support.sendReply': 'Send',
  'admin.support.refresh': 'Refresh',
  'admin.support.user': 'User',
  'admin.support.subject': 'Subject',
  'admin.support.status': 'Status',
  'admin.support.priority': 'Priority',
  'admin.support.updated': 'Updated',
  'admin.support.messages': 'Thread',
  'admin.support.newestFirst': 'newest first',
  'admin.support.allStatuses': 'All statuses',
  'admin.support.linearCreate': 'Create Linear issue',
  'admin.support.linearOpen': 'Open in Linear',
  'admin.support.linearError': 'Could not create the Linear issue. Check LINEAR_API_KEY on the server.',
  'admin.nav.institutions': 'Institutions',
  'admin.nav.contracts': 'Contracts',
  'admin.nav.editors': 'Editors',
  'admin.nav.podcasts': 'Podcasts',
  'admin.nav.sounds': 'Lyrra sounds',
  'admin.nav.templates': 'Video templates',
  'admin.nav.templatesImages': 'Image templates',
  'admin.nav.tools': 'Tools',
  'admin.nav.files': 'Files',
  'admin.nav.logs': 'Logs',
  'admin.nav.webhooks': 'Webhooks',
  'admin.nav.email': 'Email',
  'admin.nav.docusign': 'DocuSign',
  'admin.docusign.title': 'DocuSign integration',
  'admin.docusign.subtitle':
    'Integration key, API identifiers and RSA private key (JWT). Stored in the database, not in Docker.',
  'admin.docusign.statusOk': 'Configured',
  'admin.docusign.statusIncomplete': 'Incomplete',
  'admin.docusign.keyStored': 'Private key on file',
  'admin.docusign.consentHint':
    'For first-time JWT use, the DocuSign account must grant application consent (URL from DocuSign). If you get 401/403, check the PEM key, integration key and consent.',
  'admin.docusign.loading': 'Loading…',
  'admin.docusign.loadError': 'Could not load DocuSign settings',
  'admin.docusign.saved': 'DocuSign settings saved',
  'admin.docusign.saveError': 'Could not save',
  'admin.docusign.save': 'Save',
  'admin.docusign.reload': 'Reload',
  'admin.docusign.fieldIntegrationKey': 'Integration key (client ID)',
  'admin.docusign.fieldUserId': 'User ID (GUID)',
  'admin.docusign.fieldAccountId': 'Account ID',
  'admin.docusign.fieldBasePath': 'REST API base URL',
  'admin.docusign.fieldOauthHost': 'OAuth host (without https://)',
  'admin.docusign.fieldRsa': 'RSA private key (PEM)',
  'admin.docusign.oauthPlaceholder': 'account-d.docusign.com',
  'admin.docusign.rsaPlaceholderKeep': 'Leave empty to keep the current key',
  'admin.docusign.rsaPlaceholderNew': 'Paste the full .pem file (BEGIN … END)',
  'admin.nav.smtp': 'SMTP configuration',
  'admin.nav.regulatoryWatch': 'Lyrra regulatory watch',
  'admin.nav.leBref': 'Le Bref',
  'admin.nav.helpCenter': 'Help center',
  'admin.nav.platform': 'Platform integrations',
  'admin.nav.eduflowPalette': 'EduFlow palette',
  'admin.users.adminScopeTitle': 'Admin dashboard scope',
  'admin.users.adminScopeFull': 'Full access (all tabs)',
  'admin.users.adminScopeRestricted': 'Restrict to selected tabs',
  'admin.users.adminScopeHint':
    'Restricted accounts only see the selected sections. After changing tabs, the user must sign in again (or refresh the session) for the menu and API access to update.',
  'admin.users.adminScopeNeedOne': 'Select at least one tab, or choose full access.',
  'admin.helpCenter.title': 'Help center',
  'admin.helpCenter.subtitle':
    'Each article opens a modal. Edit the title and description shown in that modal.',
  'admin.helpCenter.reload': 'Refresh',
  'admin.helpCenter.loadError': 'Could not load.',
  'admin.helpCenter.saveError': 'Could not save.',
  'admin.helpCenter.deleteError': 'Could not delete.',
  'admin.helpCenter.saved': 'Saved',
  'admin.helpCenter.deleted': 'Deleted',
  'admin.helpCenter.catCreated': 'Category created',
  'admin.helpCenter.topicCreated': 'Article created',
  'admin.helpCenter.catTitleRequired': 'Category title is required.',
  'admin.helpCenter.topicFieldsRequired': 'The English modal title is required.',
  'admin.helpCenter.fieldModalTitle': 'Modal title',
  'admin.helpCenter.fieldModalTitleHint': 'Title for the selected language, shown on the card and at the top of the modal.',
  'admin.helpCenter.fieldModalLangHint': 'English is required. An empty language is shown in English.',
  'admin.helpCenter.fieldModalDescription': 'Modal description',
  'admin.helpCenter.fieldModalDescriptionHint': 'Text for the selected language, under the modal title. An empty language is shown in English.',
  'admin.helpCenter.fieldLegacyTopicId': 'Linked article',
  'admin.helpCenter.fieldLegacyTopicIdHint': 'Modal identifier. Leave it unchanged: it links this row to the existing step-by-step guide.',
  'admin.helpCenter.newCategory': 'New category',
  'admin.helpCenter.createCategory': 'New category',
  'admin.helpCenter.editCategory': 'Edit category',
  'admin.helpCenter.createTopic': 'New article',
  'admin.helpCenter.editTopic': 'Edit article',
  'admin.helpCenter.fieldTitle': 'Title',
  'admin.helpCenter.fieldDescription': 'Description',
  'admin.helpCenter.fieldIconKey': 'Lucide icon',
  'admin.helpCenter.fieldIconKeyHint':
    'Pick an icon; a preview appears in the list and on the trigger button.',
  'admin.helpCenter.fieldFormationUrl': 'Formation link',
  'admin.helpCenter.formationPickPlaceholder': '— Pick a public course —',
  'admin.helpCenter.formationPickHint':
    'Only published courses with anonymous public access enabled are listed.',
  'admin.helpCenter.formationPickLoading': 'Loading courses…',
  'admin.helpCenter.publicFlowsLoadError': 'Could not load the list of public courses.',
  'admin.helpCenter.legacyFormationPickLabel': 'Saved link (not in list)',
  'admin.helpCenter.fieldSort': 'Order',
  'admin.helpCenter.fieldActive': 'Visible',
  'admin.helpCenter.save': 'Save',
  'admin.helpCenter.edit': 'Edit',
  'admin.helpCenter.addTopic': 'Article',
  'admin.helpCenter.empty': 'No categories yet. Create a category, then articles.',
  'admin.helpCenter.noTopics': 'No articles in this category.',
  'admin.helpCenter.badgeActive': 'Active',
  'admin.helpCenter.badgeInactive': 'Hidden',
  'admin.helpCenter.iconLabel': 'Icon',
  'admin.helpCenter.confirmDeleteCategory': 'Delete this category and all its articles?',
  'admin.helpCenter.confirmDeleteTopic': 'Delete this article?',
  'admin.nav.formationMigration': 'Course migration',
  'admin.nav.formationDuplicate': 'Duplicate courses',
  'admin.nav.lyrraStudioFormations': 'LyrraStudio courses',
  'admin.lyrraStudioFormations.title': 'LyrraStudio-managed courses',
  'admin.lyrraStudioFormations.subtitle':
    'Assign an EduFlow to LyrraStudio: the technical owner is reassigned (PLATFORM_EDUFLOW_OWNER_USER_ID or the first platform admin account), collaborators are removed, and only platform administrators can edit the course in the designer.',
  'admin.lyrraStudioFormations.searchLabel': 'Search (title or course ID)',
  'admin.lyrraStudioFormations.searchPlaceholder': 'e.g. onboarding or UUID…',
  'admin.lyrraStudioFormations.search': 'Search',
  'admin.lyrraStudioFormations.searchMin': 'Enter at least 2 characters.',
  'admin.lyrraStudioFormations.results': 'Results',
  'admin.lyrraStudioFormations.noResults': 'No course found.',
  'admin.lyrraStudioFormations.badge': 'LyrraStudio',
  'admin.lyrraStudioFormations.detailHint':
    'The original author and former collaborators lose edit access. Learners and public playback are not affected.',
  'admin.lyrraStudioFormations.assign': 'Assign to LyrraStudio',
  'admin.lyrraStudioFormations.alreadyAssigned': 'Already LyrraStudio-managed',
  'admin.lyrraStudioFormations.confirm':
    'Confirm LyrraStudio assignment? This cannot be undone from here: only platform administrators will be able to edit the course.',
  'admin.lyrraStudioFormations.success': 'Course "{{title}}": LyrraStudio assignment completed.',
  'admin.lyrraStudioFormations.assignedListTitle': 'Courses already assigned to LyrraStudio',
  'admin.lyrraStudioFormations.assignedListEmpty': 'None yet. Use the search below to assign one.',
  'admin.lyrraStudioFormations.refreshList': 'Refresh list',
  'admin.lyrraStudioFormations.openDesigner': 'Designer',
  'admin.lyrraStudioFormations.assignSectionTitle': 'Search and assign another course',
  'admin.lyrraStudioFormations.publicPolicyHint':
    'Courses assigned to LyrraStudio are automatically published, anonymously accessible (/f/… short link), and suitable for sharing (QR, help-center iframe).',
  'admin.lyrraStudioFormations.drawerTitle': 'Public link & QR code',
  'admin.lyrraStudioFormations.publicShortLink': 'Short link (recommended)',
  'admin.lyrraStudioFormations.publicLongLink': 'Public player link (UUID)',
  'admin.lyrraStudioFormations.copy': 'Copy',
  'admin.lyrraStudioFormations.copied': 'Copied to clipboard.',
  'admin.lyrraStudioFormations.copyFailed': 'Could not copy (browser or permissions).',
  'admin.lyrraStudioFormations.publicLinksUnavailable':
    'Links unavailable: enable public access or use “Update public access” for courses created before this rule.',
  'admin.lyrraStudioFormations.publicIncomplete': 'Public access incomplete',
  'admin.lyrraStudioFormations.publicRepairHint':
    'This course is LyrraStudio-managed but is missing full public settings (published state, anonymous access, short code).',
  'admin.lyrraStudioFormations.publicRepairCta': 'Update public access',
  'admin.lyrraStudioFormations.publicRepairOk': 'Public access applied for “{{title}}”.',
  'admin.formationDuplicate.title': 'Duplicate a course to another institution',
  'admin.formationDuplicate.subtitle':
    'Search for a course, then pick the institution and account that will own the copy. The original course is left unchanged. The copy is a draft (no catalogue, sessions, or collaborators).',
  'admin.formationDuplicate.searchLabel': 'Search (title or course ID)',
  'admin.formationDuplicate.searchPlaceholder': 'e.g. onboarding or UUID…',
  'admin.formationDuplicate.search': 'Search',
  'admin.formationDuplicate.searchMin': 'Enter at least 2 characters.',
  'admin.formationDuplicate.results': 'Results',
  'admin.formationDuplicate.noResults': 'No course found.',
  'admin.formationDuplicate.currentOwner': 'Current owner',
  'admin.formationDuplicate.currentInstitution': 'Source institution',
  'admin.formationDuplicate.targetInstitution': 'Copy destination institution',
  'admin.formationDuplicate.pickInstitution': 'Select an institution…',
  'admin.formationDuplicate.newOwner': 'Copy owner (trainer / HR)',
  'admin.formationDuplicate.pickOwner': 'Select an account…',
  'admin.formationDuplicate.noCandidates': 'No eligible account in this institution.',
  'admin.formationDuplicate.duplicate': 'Duplicate course',
  'admin.formationDuplicate.confirm':
    'Create a copy of this course for the selected account? The source course will not be modified.',
  'admin.formationDuplicate.success':
    'Copy created: "{{title}}" for {{email}}. New course ID: {{newFlowId}}.',
  'admin.formationDuplicate.loadInstitutionsError': 'Could not load institutions.',
  'admin.formationDuplicate.loadCandidatesError': 'Could not load eligible accounts.',
  'admin.formationMigration.title': 'Move content to another institution',
  'admin.formationMigration.subtitle':
    'Pick a course, podcast or learning path from the list, then the target institution and the new owner. Class and group shares from the source institution are removed.',
  'admin.formationMigration.filterPlaceholder': 'Filter by title or institution…',
  'admin.formationMigration.kindFlow': 'Courses',
  'admin.formationMigration.kindPodcast': 'Podcasts',
  'admin.formationMigration.kindPath': 'Learning paths',
  'admin.formationMigration.empty': 'Nothing in this list.',
  'admin.formationMigration.loading': 'Loading lists…',
  'admin.formationMigration.migratePodcast': 'Move podcast',
  'admin.formationMigration.migratePath': 'Move learning path',
  'admin.formationMigration.searchLabel': 'Search (title or course ID)',
  'admin.formationMigration.searchPlaceholder': 'e.g. onboarding or UUID…',
  'admin.formationMigration.search': 'Search',
  'admin.formationMigration.searchMin': 'Enter at least 2 characters.',
  'admin.formationMigration.results': 'Results',
  'admin.formationMigration.noResults': 'No course found.',
  'admin.formationMigration.currentOwner': 'Current owner',
  'admin.formationMigration.currentInstitution': 'Source institution',
  'admin.formationMigration.targetInstitution': 'Target institution',
  'admin.formationMigration.pickInstitution': 'Select an institution…',
  'admin.formationMigration.newOwner': 'New owner (trainer / HR)',
  'admin.formationMigration.pickOwner': 'Select an account…',
  'admin.formationMigration.noCandidates': 'No eligible account in this institution.',
  'admin.formationMigration.migrate': 'Migrate course',
  'admin.formationMigration.confirm':
    'Confirm migration? Collaborators and categories will be cleared; inconsistent HR/catalogue links will be removed.',
  'admin.formationMigration.success': 'Done: "{{title}}" is now assigned to {{email}}.',
  'admin.formationMigration.clearedCatalog': 'Catalogue link removed (item belonged to another institution).',
  'admin.formationMigration.loadInstitutionsError': 'Could not load institutions.',
  'admin.formationMigration.loadCandidatesError': 'Could not load eligible accounts.',
  'admin.regulatoryWatch.title': 'Regulatory watch — Lyrra Studio alerts',
  'admin.regulatoryWatch.subtitle':
    'Publish an alert to the “Regulatory watch” timeline of every active institution. Content is locked on the training organisation side; they can acknowledge it, add notes, and plan follow-up.',
  'admin.regulatoryWatch.refresh': 'Refresh',
  'admin.regulatoryWatch.loadError': 'Could not load data.',
  'admin.regulatoryWatch.validationRequired': 'Title, description, and source are required.',
  'admin.regulatoryWatch.publishError': 'Could not publish.',
  'admin.regulatoryWatch.newBroadcastTitle': 'New global alert',
  'admin.regulatoryWatch.placeholderTitle': 'Title',
  'admin.regulatoryWatch.placeholderDescription': 'Description (visible to all institutions)',
  'admin.regulatoryWatch.placeholderSource': 'Link or source reference',
  'admin.regulatoryWatch.category.reglementaire': 'Regulatory',
  'admin.regulatoryWatch.category.pedagogique': 'Pedagogical',
  'admin.regulatoryWatch.category.technique': 'Technical',
  'admin.regulatoryWatch.category.sectoriel': 'Sector',
  'admin.regulatoryWatch.impact.fort': 'High impact',
  'admin.regulatoryWatch.impact.moyen': 'Medium impact',
  'admin.regulatoryWatch.impact.faible': 'Low impact',
  'admin.regulatoryWatch.publishing': 'Publishing…',
  'admin.regulatoryWatch.publishButton': 'Publish to all active institutions',
  'admin.regulatoryWatch.historyTitle': 'History & acknowledgements',
  'admin.regulatoryWatch.loading': 'Loading…',
  'admin.regulatoryWatch.empty': 'No alerts published yet.',
  'admin.regulatoryWatch.institutionsCount': '{{count}} institution(s)',
  'admin.regulatoryWatch.ackProgress': 'Acknowledged: {{ack}}/{{total}} ({{pct}}%)',
  'admin.regulatoryWatch.byAuthor': 'By {{who}}',
  'admin.regulatoryWatch.unknownAuthor': '—',
  'admin.leBref.title': 'Le Bref — publication calendar',
  'admin.leBref.subtitle':
    'Daily brief shown on the public page /le-bref. Only one “published” brief per calendar day.',
  'admin.leBref.refresh': 'Refresh',
  'admin.leBref.loadError': 'Could not load data.',
  'admin.leBref.validationTitle': 'Title is required.',
  'admin.leBref.validationBlocks': 'Add at least one block (text, audio, quiz, or poll).',
  'admin.leBref.validationBlocksInvalid':
    'No valid blocks: check quizzes (question + at least 2 answers, valid correct index), audio URLs, and non-empty text.',
  'admin.leBref.blocksHint':
    'Build the brief with blocks (text, media, quiz, poll — no HTML). Legacy HTML briefs are converted when you open them.',
  'admin.leBref.block.text': 'Text',
  'admin.leBref.block.audio': 'Audio',
  'admin.leBref.block.quiz': 'Quiz',
  'admin.leBref.block.poll': 'Poll',
  'admin.leBref.blocksEmpty': 'Add blocks (text, audio, quiz, poll). No need to type raw HTML.',
  'admin.leBref.block.moveUp': 'Move up',
  'admin.leBref.block.moveDown': 'Move down',
  'admin.leBref.block.remove': 'Remove block',
  'admin.leBref.block.textPlaceholder': 'Paragraph (plain text or line breaks)',
  'admin.leBref.block.audioUrl': 'Audio file URL',
  'admin.leBref.block.audioCaption': 'Caption (optional)',
  'admin.leBref.block.quizQuestion': 'Question',
  'admin.leBref.block.quizOptions': 'Answers',
  'admin.leBref.block.optionLabel': 'Option',
  'admin.leBref.block.addOption': 'Add answer',
  'admin.leBref.block.quizFeedback': 'Feedback after check (optional)',
  'admin.leBref.block.pollQuestion': 'Poll question',
  'admin.leBref.block.pollMulti': 'Allow multiple choices',
  'admin.leBref.block.image': 'Image',
  'admin.leBref.block.video': 'Video',
  'admin.leBref.block.imageUrl': 'Image URL (HTTPS or hosted media)',
  'admin.leBref.block.imageAlt': 'Alt text (accessibility)',
  'admin.leBref.block.imageCaption': 'Caption below the image (optional)',
  'admin.leBref.block.videoHint':
    'YouTube, Vimeo, or direct video URL (mp4, WebM…), same idea as the designer video block.',
  'admin.leBref.block.videoUrl': 'Video URL',
  'admin.leBref.block.videoPoster': 'Poster image URL (optional) — mainly for direct video files',
  'admin.leBref.block.videoCaption': 'Title or caption (optional)',
  'admin.leBref.block.audioTtsHint':
    'Text is read with Mistral TTS; the voice is picked automatically from the text language (same idea as designer audio blocks).',
  'admin.leBref.block.audioTtsPlaceholder': 'Text to speak…',
  'admin.leBref.block.audioTtsGenerate': 'Synthesize (Mistral TTS)',
  'admin.leBref.block.audioTtsReady': 'Audio ready — will be regenerated on save if the text changes.',
  'admin.leBref.block.audioUrlReadonly': 'Audio file',
  'admin.leBref.block.audioPreviewUnsupported': 'Preview not supported',
  'admin.leBref.block.audioTtsNeedText': 'Enter the text to read first.',
  'admin.leBref.block.audioTtsBadResponse': 'Invalid TTS response.',
  'admin.leBref.block.audioTtsError': 'Synthesis failed (Mistral TTS / integrations).',
  'admin.leBref.saveError': 'Could not save.',
  'admin.leBref.deleteError': 'Could not delete.',
  'admin.leBref.confirmDelete': 'Delete this brief?',
  'admin.leBref.quizJsonInvalid': 'Invalid quiz JSON.',
  'admin.leBref.aiError': 'AI generation failed (Mistral key / platform integrations).',
  'admin.leBref.prevMonth': 'Previous month',
  'admin.leBref.nextMonth': 'Next month',
  'admin.leBref.legend': 'Legend',
  'admin.leBref.status.published': 'Published',
  'admin.leBref.status.draft': 'Draft',
  'admin.leBref.status.archived': 'Archived',
  'admin.leBref.clickDayHint': 'Click a day to start a new brief for that date.',
  'admin.leBref.editBrief': 'Edit brief',
  'admin.leBref.newBrief': 'New brief',
  'admin.leBref.aiBlockTitle': 'AI assistant (Mistral)',
  'admin.leBref.aiThemePlaceholder':
    'Theme or instructions (e.g. summary + quiz, HR practices poll, article URL, YouTube link…)',
  'admin.leBref.aiContextHint':
    'State your goal: summary, quiz, opinion poll, audio version, or paste a URL / image or video link — the AI adapts block types (text, audio, image, video, quiz, poll).',
  'admin.leBref.aiGenerate': 'Fill with AI',
  'admin.leBref.titlePlaceholder': 'Title',
  'admin.leBref.excerptPlaceholder': 'Short excerpt',
  'admin.leBref.bodyPlaceholder': 'Body (HTML allowed: p, strong, em, ul, li, br)',
  'admin.leBref.audioUrlPlaceholder': 'Optional audio URL (mp3, etc.)',
  'admin.leBref.quizPlaceholder':
    'Optional quiz JSON: {"questions":[{"question":"…","options":["A","B","C","D"],"correctIndex":0,"feedback":"…"}]}',
  'admin.leBref.save': 'Save',
  'admin.leBref.create': 'Create',
  'admin.leBref.resetForm': 'New / clear',
  'admin.leBref.delete': 'Delete',
  'admin.leBref.monthListTitle': 'Briefs this month',
  'admin.leBref.loading': 'Loading…',
  'admin.leBref.emptyMonth': 'No briefs in this range.',
  'admin.leBref.edit': 'Edit',
  'admin.smtp.pageTitle': 'SMTP configuration',
  'admin.smtp.pageHint': 'Outgoing server for transactional platform email.',
  'admin.smtp.cardTitle': 'Outbound email (SMTP)',
  'admin.smtp.subtitle':
    'Host, port, credentials, and sender. Stored in the database — no Docker SMTP_* or EMAIL_FROM variables.',
  'admin.smtp.host': 'SMTP host',
  'admin.smtp.port': 'Port',
  'admin.smtp.secure': 'TLS/SSL (e.g. port 465)',
  'admin.smtp.user': 'Username',
  'admin.smtp.password': 'Password',
  'admin.smtp.passwordPlaceholder': 'Leave empty to keep the current password',
  'admin.smtp.passwordPlaceholderNew': 'SMTP password',
  'admin.smtp.fromEmail': 'From email',
  'admin.smtp.fromName': 'From display name',
  'admin.smtp.hint': 'Most providers require a username and password. Use the admin email test tool after saving.',
  'admin.smtp.save': 'Save SMTP',
  'admin.smtp.saved': 'SMTP settings saved',
  'admin.smtp.saveError': 'Could not save',
  'admin.smtp.loadError': 'Could not load SMTP settings',
  'admin.smtp.loading': 'Loading…',
  'admin.smtp.configured': 'Settings saved',
  'admin.smtp.notConfigured': 'Incomplete',
  'admin.shell.title': 'Administration',
  'admin.shell.expandSidebar': 'Expand sidebar',
  'admin.shell.collapseSidebar': 'Collapse sidebar',
  'admin.shell.collapse': 'Collapse menu',
  'admin.header.workspace': 'Platform administration',
  'admin.institutionDetail.pageTitle': 'Institution — Administration',
  'admin.institutionDetail.backToList': 'Back to institutions',
  'manager.page.documentTitle': 'Manager workspace - LyrraStudio',
  'manager.page.title': 'Manager workspace',
  'manager.page.subtitle': 'Manage training plans and your team progress',
  'manager.page.tabs.dashboard': 'Dashboard',
  'manager.page.tabs.team': 'Team',
  'manager.page.tabs.requests': 'Requests',
  'manager.page.tabs.reviews': 'Reviews',
  'collab.page.documentTitle': 'My training - LyrraStudio',
  'collab.page.title': 'My training',
  'collab.page.subtitle': 'Track your progress and manage your training',
  'collab.page.tabs.dashboard': 'Dashboard',
  'collab.page.tabs.skills': 'My skills',
  'collab.page.tabs.request': 'Make a request',
  'collab.page.tabs.certificates': 'Certificates',
  'rh.page.documentTitle': 'HR dashboard - LyrraStudio',
  'rh.compliancePage.documentTitle': 'Compliance & Qualiopi - LyrraStudio',
  'rh.compliancePage.title': 'Compliance & Qualiopi',
  'rh.compliancePage.subtitle': 'Manage regulatory compliance and Qualiopi certification',
  'rh.compliancePage.tabs.dashboard': 'Dashboard',
  'rh.compliancePage.tabs.difficulty': 'Learning difficulties',
  'rh.compliancePage.tabs.handicap': 'Disability liaison',
  'rh.compliancePage.tabs.learnerBilan': 'Learner summary',
  'rh.compliancePage.tabs.qualiopi': 'Qualiopi',
  'rh.compliancePage.tabs.regulatory': 'Regulatory watch',
  'rh.compliancePage.tabs.satisfaction': 'Satisfaction',
  'rh.regulatoryWatch.lyrraBadge': 'Lyrra Studio',
  'rh.regulatoryWatch.ofResponse': 'Our response / position',
  'rh.regulatoryWatch.ofResponsePlaceholder': 'What we did or plan to do…',
  'rh.regulatoryWatch.ackStatus': 'Status',
  'rh.regulatoryWatch.ack.pending': 'To process',
  'rh.regulatoryWatch.ack.read': 'Acknowledged (read)',
  'rh.regulatoryWatch.ack.actionTaken': 'Action taken',
  'rh.regulatoryWatch.ack.notApplicable': 'Not applicable',
  'rh.regulatoryWatch.pdf.title': 'Regulatory watch register',
  'rh.regulatoryWatch.pdf.subtitleFallback': 'Institution',
  'rh.regulatoryWatch.pdf.generatedAt': 'Generated on {{date}}',
  'rh.regulatoryWatch.pdf.colDate': 'Date',
  'rh.regulatoryWatch.pdf.colCategory': 'Cat.',
  'rh.regulatoryWatch.pdf.colTitle': 'Title',
  'rh.regulatoryWatch.pdf.colDesc': 'Description (excerpt)',
  'rh.regulatoryWatch.pdf.colImpact': 'Impact',
  'rh.regulatoryWatch.pdf.colOrigin': 'Origin',
  'rh.regulatoryWatch.pdf.colStatus': 'Status',
  'rh.regulatoryWatch.pdf.colAction': 'Action',
  'rh.regulatoryWatch.pdf.colWho': 'User',
  'rh.regulatoryWatch.pdf.colWhen': 'Timestamp',
  'rh.regulatoryWatch.pdf.originLyrra': 'Lyrra Studio',
  'rh.regulatoryWatch.pdf.originOf': 'Training org.',
  'rh.regulatoryWatch.pdf.readYes': 'Read',
  'rh.regulatoryWatch.pdf.readNo': 'Unread',
  'rh.regulatoryWatch.pdf.actionDone': 'Action done',
  'rh.regulatoryWatch.pdf.actionOpen': 'Action in progress',
  'rh.regulatoryWatch.pdf.emptyWatch': 'No items recorded',
  'rh.regulatoryWatch.pdf.auditSection': 'Internal audit log',
  'rh.regulatoryWatch.pdf.auditHint': 'Actions recorded automatically by the platform.',
  'rh.regulatoryWatch.pdf.emptyAudit': 'No recent events',
  'rh.regulatoryWatch.exportPdf': 'Export PDF',
  'rh.regulatoryWatch.exportPdfBusy': 'Exporting PDF…',
  'rh.regulatoryWatch.exportPdfError': 'PDF export failed',
  'rh.compliance': 'Compliance',
  'rh.complianceAuditAction.qualiopi_corrective_action_create': 'Qualiopi corrective action created',
  'rh.complianceAuditAction.qualiopi_corrective_action_delete': 'Qualiopi corrective action deleted',
  'rh.complianceAuditAction.qualiopi_corrective_action_update': 'Qualiopi corrective action updated',
  'rh.complianceAuditAction.qualiopi_evidence_create': 'Qualiopi evidence added',
  'rh.complianceAuditAction.qualiopi_indicator_update': 'Qualiopi indicator updated',
  'rh.complianceAuditAction.compliance_regulatory_watch_create': 'Regulatory watch item created',
  'rh.complianceAuditAction.compliance_regulatory_watch_update': 'Regulatory watch item updated',
  'rh.complianceAuditAction.compliance_regulatory_watch_delete': 'Regulatory watch item deleted',
  'rh.auditDateUnknown': 'Not set',
  'rh.auditScheduleHint': 'Set in institution settings',
  'rh.qualiopiEmpty': 'No Qualiopi data available for this institution.',
  'rh.qualiopiLoading': 'Loading Qualiopi reference data…',
  'v2.compliance': 'Compliance & Qualiopi',
  'rh.dashboard.title': 'HR dashboard',
  'rh.dashboard.subtitle': 'Training & development overview',
  'rh.dashboard.activeCampaigns': 'Active campaigns',
  'rh.dashboard.pendingAssignmentsKpi': 'Assignments (pending / accepted)',
  'rh.dashboard.pendingRequestsCount': 'Pending requests',
  'rh.dashboard.completionRateLabel': 'Completion rate (learning paths)',
  'rh.dashboard.budgetUsed': 'Campaign budget used',
  'rh.dashboard.budgetNotSet': 'Not set',
  'rh.dashboard.progressLabel': 'Progress (%)',
  'rh.dashboard.chartCampaignsTitle': 'Campaign progress',
  'rh.dashboard.chartFormationsTitle': 'Learning path progress',
  'rh.dashboard.chartCampaignsHint': 'Average assignment completion rate per campaign.',
  'rh.dashboard.chartFormationsHint': 'Share of learners who completed the path (of current enrollments).',
  'rh.dashboard.upcomingDeadlines': 'Upcoming deadlines',
  'rh.dashboard.recentActivity': 'Recent activity',
  'rh.dashboard.recentActivityHint': 'Campaign assignments and completed paths.',
  'rh.dashboard.viewAll': 'View all',
  'rh.dashboard.exportReport': 'Export report',
  'rh.dashboard.exportSoon': 'Export coming soon.',
  'rh.dashboard.newCampaign': 'New campaign',
  'rh.dashboard.campaignProgress': 'Campaign progress',
  'rh.dashboard.createCampaign': 'Create campaign',
  'rh.dashboard.reviewRequests': 'Review requests',
  'rh.dashboard.skillsMap': 'Skills framework',
  'rh.dashboard.budgetOverview': 'Budget overview',
  'rh.dashboard.loadError': 'Could not load HR dashboard.',
  'rh.dashboard.emptyChart': 'No data to display yet.',
  'rh.dashboard.emptyDeadlines': 'No upcoming campaign deadlines.',
  'rh.dashboard.emptyRecent': 'No recent activity.',
  'rh.dashboard.kindAssignment': 'Campaign',
  'rh.dashboard.kindCompletion': 'Path completed',
  'rh.dashboard.justNow': 'Just now',
  'rh.dashboard.learnerInsightTitle': 'Training operations (live data)',
  'rh.dashboard.learnerInsightHint':
    'Aggregated training enrollments: scores from graded blocks, block friction, latest evaluations.',
  'rh.dashboard.enrollmentsTotal': 'Enrollments',
  'rh.dashboard.completionGlobal': 'Completed',
  'rh.dashboard.avgTimePerEnrollment': 'Avg. time per enrollment',
  'rh.dashboard.notStarted': 'Not started',
  'rh.dashboard.stalled': 'Stalled (>30d inactive after start)',
  'rh.dashboard.topFlowsDetail': 'Most subscribed paths',
  'rh.dashboard.colFlow': 'Path',
  'rh.dashboard.colEnrolled': 'Enrolled',
  'rh.dashboard.colCompletedPct': 'Completion',
  'rh.dashboard.colAvgTime': 'Avg. time',
  'rh.dashboard.colAvgScore': 'Avg. score',
  'rh.dashboard.blockFrictionTitle': 'Highest-friction blocks (≥3 sessions)',
  'rh.dashboard.colBlock': 'Block',
  'rh.dashboard.colSessions': 'Sessions',
  'rh.dashboard.colBlockCompletion': 'Block completion',
  'rh.dashboard.recentEvalTitle': 'Latest evaluations',
  'rh.dashboard.evalComment': 'Has comment',
  'rh.dashboard.exportOk': 'CSV downloaded.',
  'rh.dashboard.exportFail': 'Download failed.',
  'institution.dashboard.trainingCardTitle': 'Training follow-up (enrollments)',
  'institution.dashboard.trainingCardHint': 'Aggregated across all institution learning paths.',
  'institution.dashboard.trainingCompleted': 'paths completed',
  'institution.dashboard.trainingAvgMinutes': 'avg min / enrollment',
  'rh.status.urgent': 'Urgent',
  'rh.status.soon': 'Soon',
  'rh.status.upcoming': 'Upcoming',
  'rh.status.pending': 'Pending',
  'rh.status.approved': 'Approved',
  'rh.status.rejected': 'Rejected',
  'producerAssignments.intro':
    'This page lists only content HR has assigned to you: catalog “shell” items to turn into EduFlow courses in the designer, and podcasts you are set as producer for. Empty lists mean nothing is assigned yet.',
  'producerAssignments.errorForbidden':
    'Access denied: your role cannot use this page. If you are a trainer or HR for your organization, contact an administrator.',
  'producerAssignments.errorNoInstitution':
    'Your account has no institution linked in the database. An administrator must attach your user to the organization, then sign in again.',
  'producerAssignments.errorGeneric': 'Could not load your assignments.',
  // Common
  'common.loading': 'Loading...',
  'common.refresh': 'Refresh',
  'common.saving': 'Saving...',
  'common.saved': 'Saved',
  'common.error': 'Error',
  'common.success': 'Success',
  'common.warning': 'Warning',
  'common.info': 'Information',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.or': 'or',
  'common.and': 'and',
  'common.all': 'All',
  'common.none': 'None',
  'common.total': 'Total',
  'common.actions': 'Actions',
  'common.created_at': 'Created at',
  'common.updated_at': 'Updated at',
  'common.sort_by': 'Sort by',
  'common.per_page': 'Per page',
  'common.page': 'Page',
  'common.of': 'of',
  'common.results': '{{count}} result(s)',
  'common.no_data': 'No data',
  'common.coming_soon': 'Coming soon',
  'common.beta': 'Beta',
  'common.new': 'New',
  'common.delete_confirm': 'This action is irreversible. Continue?',
  'common.copied': 'Copied!',
  'common.drag_drop': 'Drag and drop',
  'common.online': 'Online',
  'common.offline': 'Offline',
  'common.version': 'Version',
  // Player
  'player.next': 'Next',
  'player.previous': 'Previous',
  'player.scrollDownHint': 'Scroll down for more',
  'player.image.clickForAnnotations': 'Click the image to view annotations',
  'player.image.annotationNoDescription': 'No description for this annotation.',
  'player.image.noneAvailable': 'No image available',
  'player.visualAi.emptyTitle': 'No AI visual configured',
  'player.visualAi.emptyHint': 'Return to the designer to generate or configure this visual.',
  'player.visualAi.defaultTitle': 'AI visual',
  'player.visualAi.subtitle': 'Explore the diagram: zoom, scroll, or use full screen mode.',
  'player.visualAi.openFullscreen': 'Open full screen',
  'player.visualAi.closeFullscreen': 'Close full screen',
  'player.visualAi.badge': 'AI visual',
  'player.visualAi.viewportLabel': 'Interactive diagram — zoom and pan',
  'player.visualAi.toolbarLabel': 'Diagram controls',
  'player.visualAi.zoomOut': 'Zoom out',
  'player.visualAi.zoomIn': 'Zoom in',
  'player.visualAi.resetZoom': 'Reset zoom to 100%',
  'player.visualAi.reset': '100%',
  'player.visualAi.fitView': 'Fit diagram to window',
  'player.visualAi.fit': 'Fit',
  'player.visualAi.zoomLevel': 'Zoom {{percent}}%',
  'player.visualAi.viewportHint':
    'Ctrl (or Cmd) + scroll wheel to zoom. Arrow keys to pan when the viewport is focused. Tab to reach the controls.',
  'player.manualVoiceoverMic': 'Record voice-over for this language',
  'player.manualVoiceoverStudioTitle': 'Voice-over recording studio',
  'player.manualVoiceoverStudioLang': 'Recording language',
  'player.manualVoiceoverRecord': 'Record',
  'player.manualVoiceoverStop': 'Stop',
  'player.manualVoiceoverPlay': 'Listen',
  'player.manualVoiceoverSave': 'Save to block',
  'player.manualVoiceoverClose': 'Close',
  'player.manualVoiceoverHint':
    'On the right: block text or image in the selected language. Record, then save to attach the audio to this language.',
  'player.manualVoiceoverPreviewLoading': 'Loading content…',
  'player.manualVoiceoverNoMic': 'Microphone or recording is not available in this browser.',
  'player.manualVoiceoverNoText': 'No description text long enough for this block.',
  'player.manualVoiceoverSaveError': 'Could not save the voice-over to the block.',
  'player.manualVoiceoverUploadError': 'Audio upload failed.',
  'player.manualVoiceoverSaved': 'Voice-over saved to the block.',
  'eduflow.versionHistory': 'Version history',
  'eduflow.versionHistory.count': '{{count}} version(s)',
  'eduflow.versionHistory.create': 'Create a version',
  'eduflow.versionHistory.createVersion': 'Create v{{version}}',
  'eduflow.versionHistory.minor': 'Minor',
  'eduflow.versionHistory.major': 'Major',
  'eduflow.versionHistory.minorBadge': 'Minor version',
  'eduflow.versionHistory.majorBadge': 'Major version',
  'eduflow.versionHistory.latest': 'Latest',
  'eduflow.versionHistory.public': 'Public version',
  'eduflow.versionHistory.publicActive': 'Live',
  'eduflow.versionHistory.publish': 'Publish this version',
  'eduflow.versionHistory.publishCta': 'Publish',
  'eduflow.versionHistory.openPublicPlayer': 'Open public player',
  'eduflow.versionHistory.copyPublicPlayer': 'Copy public link',
  'eduflow.versionHistory.copyPublicPlayerError': 'Unable to copy the public link',
  'eduflow.versionHistory.systemPublished': 'Published {{version}}',
  'eduflow.versionHistory.systemSaved': 'Saved {{version}}',
  'eduflow.versionHistory.systemAutosaveRestore': 'Auto-save before restoring {{version}}',
  'eduflow.versionHistory.descriptionPlaceholder': 'Describe the changes…',
  'eduflow.versionHistory.empty': 'No version created yet.',
  'eduflow.versionHistory.emptyHint': 'Save a version to access it here.',
  'eduflow.versionHistory.blocks': '{{count}} blocks',
  'eduflow.versionHistory.restoreTitle': 'Restore version v{{version}}?',
  'eduflow.versionHistory.restoreWarning': 'The current content will be replaced. An automatic backup will be created before the restore.',
  'eduflow.versionHistory.createSuccess': 'Version {{version}} created',
  'eduflow.versionHistory.createError': 'Error while creating the version',
  'eduflow.versionHistory.restoreSuccess': 'Version {{version}} restored',
  'eduflow.versionHistory.restoreError': 'Error while restoring the version',
  'eduflow.versionHistory.publishSuccess': 'Version {{version}} published',
  'eduflow.versionHistory.publishError': 'Error while publishing the version',
  'eduflow.versionHistory.now': 'just now',
  'eduflow.versionHistory.minutesAgo': '{{count}} min ago',
  'eduflow.versionHistory.hoursAgo': '{{count}} h ago',
  'eduflow.versionHistory.yesterday': 'yesterday',
  'eduflow.versionHistory.daysAgo': '{{count}} days ago',
  // Podcast — episodes list & create
  'podcast.episodes.pageTitle': 'Episodes',
  'podcast.episodes.count': '{{count}} episode(s)',
  'podcast.episodes.newEpisode': 'New episode',
  'podcast.episodes.searchPlaceholder': 'Search by title…',
  'podcast.episodes.filterAllStatuses': 'All statuses',
  'podcast.episodes.filterAllPodcasts': 'All podcasts',
  'podcast.episodes.filterDraft': 'Draft',
  'podcast.episodes.filterPublished': 'Published',
  'podcast.episodes.unassignedPodcast': 'No podcast',
  'podcast.episodes.generateVoiceOff': 'Generate voice-over',
  'podcast.episodes.generateVoiceConfirmTitle': 'Generate voice-over?',
  'podcast.episodes.generateVoiceConfirmMessage':
    'Episode « {{title}} » will be synthesized ({{count}} segment(s)). This may take several minutes.',
  'podcast.episodes.generateVoiceConfirmButton': 'Generate',
  'podcast.episodes.generateNoBlocks':
    'No script text or microphone recording on this episode — open the editor to add content.',
  'podcast.episodes.generateSuccess': 'Voice-over generated for « {{title}} » ({{count}} segment(s)).',
  'podcast.episodes.generateError': 'Voice-over generation failed.',
  'podcast.episodes.publish': 'Publish episode',
  'podcast.episodes.publishConfirmTitle': 'Publish this episode?',
  'podcast.episodes.publishConfirmMessage':
    '« {{title}} » will be visible to learners assigned to the podcast (published status).',
  'podcast.episodes.publishConfirmButton': 'Publish',
  'podcast.episodes.publishSuccess': '« {{title}} » is published.',
  'podcast.episodes.publishError': 'Publication failed.',
  'podcast.episodes.emptyTitle': 'No episodes found',
  'podcast.episodes.emptyHint': 'Create your first episode to get started',
  'podcast.episodes.emptyCreateCta': 'Create an episode',
  'podcast.episodes.backPodcasts': 'Podcasts',
  'podcast.episodes.paginationTotal': '{{total}} episode(s) total',
  'podcast.episodes.pageOf': 'Page {{page}} / {{totalPages}}',
  'podcast.episodes.deleteTitle': 'Delete this episode?',
  'podcast.episodes.deleteHint': '“{{title}}” will be permanently deleted.',
  'podcast.episodes.deleteCancel': 'Cancel',
  'podcast.episodes.deleteConfirm': 'Delete',
  'podcast.episodes.statusPublished': 'Published',
  'podcast.episodes.statusDraft': 'Draft',
  'podcast.episodes.noDescription': 'No description',
  'podcast.episodes.noAudio': 'No audio',
  'podcast.episodes.audioReady': 'Audio ready',
  'podcast.episodes.retry': 'Retry',
  'podcast.createEpisode.panelTitle': 'New episode',
  'podcast.createEpisode.panelSubtitle':
    'Enter a title and optionally a description and cover image. After you confirm, the episode is created and the editor opens to build the content.',
  'podcast.createEpisode.titleLabel': 'Title',
  'podcast.createEpisode.descriptionLabel': 'Description',
  'podcast.createEpisode.coverLabel': 'Cover image (optional)',
  'podcast.createEpisode.coverHint': 'JPEG, PNG, WebP or GIF — max 5 MB.',
  'podcast.createEpisode.coverChoose': 'Choose image',
  'podcast.createEpisode.coverRemove': 'Remove image',
  'podcast.createEpisode.titlePlaceholder': 'e.g. Safety module introduction',
  'podcast.createEpisode.descriptionPlaceholder': 'Summary, goals… (optional)',
  'podcast.createEpisode.validate': 'Create and open editor',
  'podcast.createEpisode.titleRequired': 'Title is required.',
  'podcast.createEpisode.error': 'Could not create the episode.',
  'podcast.createEpisode.uploadCoverError': 'Episode created, but cover upload failed.',
  'podcast.createEpisode.coverTooLarge': 'Image must be 5 MB or smaller.',
  'podcast.editor.defaultNewTitle': 'New episode',
  'eduflow.title': 'EduFlow editor',
  'eduflow.new_flow': 'New course',
  'eduflow.list.title': 'Courses',
  'eduflow.list.subtitle': 'Design your interactive learning experiences',
  'eduflow.list.searchPlaceholder': 'Search a course…',
  'eduflow.list.import': 'Import',
  'eduflow.list.importHint': '.lyrra package (media included) or legacy Lyrra V1 JSON export.',
  'eduflow.list.importSuccess': 'Course imported. Opening the designer.',
  'eduflow.list.importError':
    'Could not import — check the file (valid .lyrra or JSON export, at least one block).',
  'eduflow.list.filterAll': 'All',
  'eduflow.list.filterDraft': 'Drafts',
  'eduflow.list.filterPublished': 'Published',
  'eduflow.list.filterArchived': 'Archived',
  'eduflow.list.archiveFormation': 'Archive course',
  'eduflow.list.restoreFromArchive': 'Restore from archive (draft)',
  'eduflow.list.archiveSuccess': 'Course archived',
  'eduflow.list.archiveError': 'Could not archive the course.',
  'eduflow.list.restoreSuccess': 'Course restored as draft',
  'eduflow.list.restoreError': 'Could not restore the course.',
  'eduflow.list.viewModeGrid': 'Grid view',
  'eduflow.list.viewModeList': 'List view',
  'eduflow.list.viewByCategory': 'Category view',
  'eduflow.list.categoryBack': 'All categories',
  'eduflow.list.categoryBrowseHint': 'Pick a category to see its courses.',
  'eduflow.list.categoryFormationCount': '{{count}} course(s)',
  'eduflow.list.categoryEmptyFiltered': 'No courses in this view (active filter or search).',
  'eduflow.list.uncategorizedCardHint': 'Courses without a category tag.',
  'eduflow.list.uncategorized': 'Uncategorized',
  'eduflow.list.manageCategories': 'Manage categories',
  'eduflow.list.categoriesPanelTitle': 'Course categories',
  'eduflow.list.noCategoryAvailable': 'No category available.',
  'eduflow.list.categoriesSaveError': 'Unable to save categories.',
  'eduflow.list.emptyFilteredTitle': 'No results',
  'eduflow.list.emptyFilteredHint': 'Try changing your search or filters.',
  'eduflow.list.emptyFirstTitle': 'Create your first course',
  'eduflow.list.emptyFirstHint': 'Build interactive learning with the visual designer.',
  'eduflow.list.emptyCreateCta': 'Create my first course',
  'eduflow.list.deleteToTrashTitle': 'Move this course to trash?',
  'eduflow.list.deleteToTrashBody':
    '“{{title}}” will be removed from your list (soft delete). Your organization can restore it from the institution trash.',
  'eduflow.list.deleteToTrashConfirm': 'Move to trash',
  'eduflow.createFormation.panelTitle': 'New course',
  'eduflow.createFormation.modeSimple': "Simple",
  'eduflow.createFormation.modeSimpleHint': "Modules, chapters and video / text / audio / quiz blocks",
  'eduflow.createFormation.modeAdvanced': "Advanced",
  'eduflow.createFormation.modeAdvancedHint': "Full EduFlow designer (diagram, branches, 35+ blocks)",
  'eduflow.createFormation.panelSubtitle':
    'Enter a title and description. After you confirm, the course is created and the designer opens to build the flow.',
  'eduflow.createFormation.titleLabel': 'Title',
  'eduflow.createFormation.descriptionLabel': 'Description',
  'eduflow.createFormation.titlePlaceholder': 'e.g. Safety module introduction',
  'eduflow.createFormation.descriptionPlaceholder': 'Goals, audience, etc. (optional)',
  'eduflow.createFormation.validate': 'Create and open designer',
  'eduflow.createFormation.titleRequired': 'Title is required.',
  'eduflow.createFormation.error': 'Could not create the course.',
  'eduflow.shareFormation.title': 'Share course',
  'eduflow.shareFormation.publicLinkTitle': 'Public link (no sign-in)',
  'eduflow.shareFormation.publicLinkCopy': 'Copy link',
  'eduflow.shareFormation.publicLinkCopied': 'Copied',
  'eduflow.shareFormation.publishRequired': 'Publish the course to get a link that works without sign-in.',
  'eduflow.shareFormation.embedTitle': 'Embed button and player modal',
  'eduflow.shareFormation.embedHint':
    'Paste this block on your site. Include the script only once per page; for more courses, add extra buttons by copying the button line and changing the URL in data-lyrra-eduflow-url. The player URL includes sidebar=0 so the outline starts collapsed in the modal embed.',
  'eduflow.shareFormation.embedCopy': 'Copy HTML code',
  'eduflow.shareFormation.embedCopied': 'Copied',
  'eduflow.shareFormation.embedCloseAria': 'Close',
  'eduflow.shareFormation.embedButtonLabel': 'Open: {{title}}',
  'eduflow.shareFormation.embedToggle': 'Website embed',
  'eduflow.shareFormation.removeAccessSuccess': 'Access removed for the learner.',
  'eduflow.shareFormation.removeAccessError': 'Unable to remove learner access.',
  'eduflow.shareFormation.assignSuccess': 'Course shared and invitation email sent.',
  'eduflow.shareFormation.assignError': 'Unable to assign this learner.',
  'eduflow.shareFormation.createAssignSuccess': 'Account created/linked, course assigned, and invitation email sent.',
  'eduflow.shareFormation.createLearnerError': 'Unable to create learner.',
  'eduflow.shareFormation.selectExistingLearner': 'Select an existing learner',
  'eduflow.shareFormation.searchLearnerPlaceholder': 'Search learner (first name, last name, email)',
  'eduflow.shareFormation.loadingLearners': 'Loading learners...',
  'eduflow.shareFormation.noLearnerAvailable': 'No learner available.',
  'eduflow.shareFormation.hideQuickCreate': 'Hide quick create',
  'eduflow.shareFormation.createNewLearner': 'Create a new learner',
  'eduflow.shareFormation.firstNameRequired': 'First name *',
  'eduflow.shareFormation.lastNameRequired': 'Last name *',
  'eduflow.shareFormation.emailRequired': 'Email *',
  'eduflow.shareFormation.createShareInvite': 'Create, share, and send invitation',
  'eduflow.shareFormation.assignedLearnersCount_one': '{{count}} learner assigned',
  'eduflow.shareFormation.assignedLearnersCount_other': '{{count}} learners assigned',
  'eduflow.shareFormation.noAssignedLearner': 'No learner assigned to this course.',
  'eduflow.shareFormation.removeAccessTitle': 'Remove access',
  'eduflow.shareFormation.inviteEmailsTitle': 'Invite by email (existing account)',
  'eduflow.shareFormation.inviteEmailsHint':
    'One or more addresses: LyrraStudio accounts only (including another institution). People without an account are skipped — use “Create new learner” to invite them.',
  'eduflow.shareFormation.inviteEmailsPlaceholder':
    'One address per line or comma-separated\nexample@company.com, other@org.fr',
  'eduflow.shareFormation.inviteEmailsSubmit': 'Add these accounts to the course',
  'eduflow.shareFormation.inviteEmailsInvalid': 'Enter at least one valid email address.',
  'eduflow.shareFormation.inviteEmailsError': 'Could not add the addresses.',
  'eduflow.shareFormation.inviteEmailsSummaryOk': '{{count}} account(s) added.',
  'eduflow.shareFormation.inviteEmailsSummaryNoAccount': '{{count}} with no LyrraStudio account.',
  'eduflow.shareFormation.inviteEmailsSummaryAlready': '{{count}} already assigned.',
  'eduflow.participant.flowTitle': 'Learning path',
  'eduflow.participant.stepWelcome': 'Welcome',
  'eduflow.participant.stepModule1': 'Module 1',
  'eduflow.participant.stepEvaluation': 'Assessment',
  'eduflow.participant.stepContentPlaceholder': 'Interactive step content displayed for the learner.',
  'eduflow.edit_flow': 'Edit path',
  'eduflow.flow_name': 'Path name',
  'eduflow.block_palette': 'Block palette',
  'eduflow.canvas': 'Canvas',
  'eduflow.properties': 'Properties',
  'eduflow.publish': 'Publish',
  'eduflow.publish_confirm': 'Are you sure you want to publish this path?',
  'eduflow.save_draft': 'Save draft',
  'eduflow.preview': 'Preview',
  'eduflow.versions': 'Versions',
  'eduflow.add_block': 'Add block',
  'eduflow.remove_block': 'Remove block',
  'eduflow.connect_blocks': 'Connect blocks',
  'eduflow.undo': 'Undo',
  'eduflow.redo': 'Redo',
  'eduflow.zoom_in': 'Zoom in',
  'eduflow.zoom_out': 'Zoom out',
  'eduflow.fit_view': 'Fit view',
  'eduflow.auto_layout': 'Auto layout',
  'eduflow.unsaved_changes': 'Unsaved changes',
  'eduflow.blocks.quiz_mcq': 'MCQ',
  'eduflow.blocks.quiz_open': 'Open question',
  'eduflow.blocks.video': 'Video',
  'eduflow.blocks.audio': 'Audio',
  'eduflow.blocks.text': 'Text',
  'eduflow.blocks.evaluation': 'Assessment',
  'eduflow.blocks.form': 'Form',
  'eduflow.blocks.mindmap': 'Mind map',
  'eduflow.blocks.poll': 'Poll',
  'eduflow.blocks.flashcard': 'Flashcard',
  'eduflow.blocks.timeline': 'Timeline',
  'eduflow.blocks.pdf': 'PDF document',
  'eduflow.blocks.image': 'Image',
  'eduflow.blocks.code': 'Code',
  'eduflow.blocks.whiteboard': 'Whiteboard',
  'eduflow.blocks.ai_chat': 'AI chat',
  'eduflow.blocks.certificate': 'Certificate',
  'eduflow.blocks.attendance': 'Attendance',
  'eduflow.blocks.sortable': 'Ranking',
  'eduflow.blocks.pair_matching': 'Matching',
  'eduflow.blocks.drawing': 'Drawing',
  'eduflow.blocks.embed': 'Embedded content',
  'eduflow.blocks.h5p': 'H5P',
  'eduflow.blocks.scorm': 'SCORM',
  'eduflow.blocks.conditional': 'Condition',
  'eduflow.blocks.timer': 'Timer',
  'eduflow.blocks.randomizer': 'Random',
  'eduflow.blocks.grouping': 'Grouping',
  'eduflow.blocks.debate': 'Debate',
  'eduflow.blocks.roleplay': 'Role play',
  'eduflow.blocks.simulation': 'Simulation',
  'eduflow.blocks.scenario': 'Scenario',
  'eduflow.blocks.checklist': 'Checklist',
  'eduflow.blocks.kanban': 'Kanban',
  'eduflow.blocks.peer_review': 'Peer review',
  'eduflow.designer.paletteCategory.structure': 'Path & structure',
  'eduflow.designer.paletteCategory.content': 'Content & media',
  'eduflow.designer.paletteCategory.interactive': 'Activities & assessment',
  'eduflow.designer.paletteCategory.accessibility': 'Accessibility (DYS)',
  'eduflow.designer.paletteCategory.enterprise': 'Compliance & advanced',
  'eduflow.designer.flowSettings.toolbarTitle': 'Course & player settings',
  'eduflow.designer.flowSettings.panelTitle': 'Course settings',
  'eduflow.designer.flowSettings.intro':
    'Shown in the player (title, description, cover). You can hide the “Previous” button for a strictly linear path.',
  'eduflow.designer.flowSettings.titleLabel': 'Course title',
  'eduflow.designer.flowSettings.descriptionLabel': 'Course description',
  'eduflow.designer.flowSettings.coverLabel': 'Cover image',
  'eduflow.designer.flowSettings.coverUpload': 'Upload an image',
  'eduflow.designer.flowSettings.coverUrlPlaceholder': 'Or paste an image URL (https://…)',
  'eduflow.designer.flowSettings.coverRemove': 'Remove cover',
  'eduflow.designer.flowSettings.allowPreviousLabel': '“Previous” button in the player',
  'eduflow.designer.flowSettings.allowPreviousHint':
    'When off, learners cannot go back to the previous block (including keyboard shortcuts).',
  'eduflow.designer.flowSettings.focusGuard.title': 'Focus loss check',
  'eduflow.designer.flowSettings.focusGuard.hint':
    'If the learner leaves the tab or page, the training is paused (dimmed screen, click to resume).',
  'eduflow.designer.flowSettings.focusGuard.limitLabel': 'Allowed exits',
  'eduflow.designer.flowSettings.focusGuard.unlimited': 'Unlimited',
  'eduflow.designer.flowSettings.focusGuard.limited': 'Limited',
  'eduflow.designer.flowSettings.focusGuard.lossesUnit': 'allowed focus loss(es)',
  'eduflow.designer.flowSettings.focusGuard.blockWarning':
    'Beyond this number, the learner can no longer continue the training.',
  'eduflow.designer.flowSettings.coverUploadError': 'Could not upload the cover image.',
  'eduflow.designer.flowSettings.certificate.uploadError': 'Invalid server response.',
  'eduflow.designer.flowSettings.certificate.uploadFailed': 'Could not upload the Word template.',
  'eduflow.designer.blockLabel.start': 'Start',
  'eduflow.designer.blockLabel.end': 'End',
  'eduflow.designer.blockLabel.summary': 'Summary',
  'eduflow.designer.blockLabel.form': 'Form',
  'eduflow.designer.blockLabel.loop': 'Loop',
  'eduflow.designer.blockLabel.merge': 'Merge',
  'eduflow.designer.blockLabel.split': 'Branch',
  'eduflow.designer.blockLabel.subflow': 'Sub-training',
  'eduflow.designer.blockLabel.timer': 'Timer',
  'eduflow.designer.blockLabel.email': 'Automated email',
  'eduflow.designer.blockLabel.text': 'Text',
  'eduflow.designer.blockLabel.podcast': 'Podcast',
  'eduflow.designer.blockLabel.video': 'Video',
  'eduflow.designer.blockLabel.audio': 'Audio',
  'eduflow.designer.blockLabel.image': 'Annotated image',
  'eduflow.designer.blockLabel.pdf': 'PDF document',
  'eduflow.designer.blockLabel.browser': 'Embedded web page',
  'eduflow.designer.blockLabel.presentation': 'Slideshow',
  'eduflow.designer.blockLabel.timeline': 'Timeline',
  'eduflow.designer.blockLabel.chart': 'Interactive chart',
  'eduflow.designer.blockLabel.mindmap': 'Mind map',
  'eduflow.designer.blockLabel.visual_ai': 'AI visual',
  'eduflow.designer.blockLabel.quiz_mcq': 'Quiz (MCQ / T-F / order)',
  'eduflow.designer.blockLabel.poll': 'Live poll',
  'eduflow.designer.blockLabel.self_assessment': 'Self-assessment',
  'eduflow.selfAssessment.panelTitle': 'Self-assessment',
  'eduflow.selfAssessment.panelHint':
    'Define each dimension to rate: a short title and, if needed, a description to guide the learner.',
  'eduflow.selfAssessment.scaleLabel': 'Rating scale',
  'eduflow.selfAssessment.scaleHint': 'From 1 to N (e.g. 5 = Likert)',
  'eduflow.selfAssessment.criteriaSection': 'Criteria',
  'eduflow.selfAssessment.addCriterion': 'Add',
  'eduflow.selfAssessment.emptyCriteria':
    'No criteria yet. Add at least one so learners can self-assess.',
  'eduflow.selfAssessment.criterionTitle': 'Title',
  'eduflow.selfAssessment.criterionTitlePh': 'e.g. Communication, Autonomy…',
  'eduflow.selfAssessment.criterionDescription': 'Description (optional)',
  'eduflow.selfAssessment.criterionDescriptionPh':
    'Clarify what the learner should reflect on or observe…',
  'eduflow.selfAssessment.moveUp': 'Move up',
  'eduflow.selfAssessment.moveDown': 'Move down',
  'eduflow.selfAssessment.removeCriterion': 'Remove this criterion',
  'eduflow.selfAssessment.responseTypeLabel': 'Response type',
  'eduflow.selfAssessment.typeNumeric': 'Numbers',
  'eduflow.selfAssessment.typeNumericHint': 'Buttons 1 to N',
  'eduflow.selfAssessment.typeStars': 'Stars',
  'eduflow.selfAssessment.typeStarsHint': 'Visual rating',
  'eduflow.selfAssessment.typeText': 'Free text',
  'eduflow.selfAssessment.typeTextHint': 'Badges (short words) or free text per criterion',
  'eduflow.selfAssessment.badgeOptionsLabel': 'Options (badges)',
  'eduflow.selfAssessment.badgeOptionsHint':
    'Short words or phrases — learners pick them as chips. If empty → free-text field.',
  'eduflow.selfAssessment.removeBadge': 'Remove',
  'eduflow.selfAssessment.badgeInputPlaceholder': 'e.g. Often, Never, Sometimes…',
  'eduflow.selfAssessment.addBadge': 'Add',
  'eduflow.selfAssessment.starCountLabel': 'Number of stars',
  'eduflow.selfAssessment.starCountHint': 'Often 5',
  'eduflow.selfAssessment.textModeHint':
    'Learners write an answer for each criterion. The global comment field remains below in the player.',
  'eduflow.designer.blockLabel.evaluation': 'Multi-question assessment',
  'eduflow.designer.blockLabel.phrase_reveal': 'Progressive reveal',
  'eduflow.designer.blockLabel.voice_assessment': 'Voice assessment',
  'eduflow.designer.blockLabel.dys_reader': 'DYS reader (syllables + TTS)',
  'eduflow.designer.blockLabel.dys_image_zones': 'Interactive image zones',
  'eduflow.designer.blockLabel.dys_clock': 'DYS color clock',
  'eduflow.designer.blockLabel.dys_reading_practice': 'Adapted reading exercise',
  'eduflow.designer.blockLabel.glossary': 'Glossary + built-in quiz',
  'eduflow.designer.blockLabel.html': 'Custom HTML / JavaScript',
  'eduflow.designer.blockLabel.certification': 'Certificate (Word template)',
  'eduflow.designer.blockLabel.signature': 'Learner signature',
  'eduflow.designer.blockLabel.facial_recognition': 'Facial recognition',
  'eduflow.designer.blockLabel.attendance': 'Digital attendance',
  'eduflow.designer.startBlockTitle': 'Start',
  'eduflow.designer.placeholderUntitled': 'Untitled',
  'eduflow.designer.placeholderFlowDescription': 'Training description (max 255 characters)',
  'eduflow.designer.saving': 'Saving…',
  'eduflow.designer.unsaved': 'Not saved',
  'eduflow.designer.saved': 'Saved',
  'eduflow.designer.blocksPanelTitle': 'Blocks',
  'eduflow.designer.paletteSearchLabel': 'Search blocks',
  'eduflow.designer.paletteSearchPlaceholder': 'Search blocks…',
  'eduflow.designer.paletteSearchEmpty': 'No blocks match your search.',
  'eduflow.designer.autoLayoutShort': 'Auto-layout',
  'eduflow.designer.validate': 'Validate',
  'eduflow.designer.validateTitle': 'Validate path',
  'eduflow.designer.validationBeforePreview': 'Fix path errors before preview.',
  'eduflow.designer.validationBeforePublish': 'Fix path errors before publishing.',
  'eduflow.designer.aiShort': 'AI',
  'eduflow.designer.aiTitle': 'AI assistant',
  'eduflow.designer.ragChatbotTitle': 'Course chatbot',
  'eduflow.ragChatbot.panelTitle': 'Course chatbot (RAG)',
  'eduflow.ragChatbot.loadError': 'Could not load chatbot settings.',
  'eduflow.ragChatbot.needDoc': 'Add at least one document before enabling the chatbot.',
  'eduflow.ragChatbot.enabledOk': 'Chatbot enabled for the player.',
  'eduflow.ragChatbot.disabledOk': 'Chatbot disabled.',
  'eduflow.ragChatbot.toggleError': 'Could not update.',
  'eduflow.ragChatbot.uploadOk': 'Document indexed.',
  'eduflow.ragChatbot.uploadSavedNotIndexed':
    'Document saved. It will not be used for RAG until the vector store is configured on the server.',
  'eduflow.ragChatbot.ragIndexFailed':
    'Document saved but not indexed. Check pgvector (DATABASE_VECTOR_URL) and Mistral, then re-upload if needed.',
  'eduflow.ragChatbot.uploadError': 'Upload or indexing failed.',
  'eduflow.ragChatbot.confirmDelete': 'Delete this document and its index?',
  'eduflow.ragChatbot.deleted': 'Document removed.',
  'eduflow.ragChatbot.deleteError': 'Could not delete.',
  'eduflow.ragChatbot.vectorHint':
    'To index PDF / TXT / MD: set DATABASE_VECTOR_URL or PGVECTOR_HOST (PostgreSQL + pgvector) and Mistral in Admin → Platform integrations (API key + embedding model). Otherwise upload and indexing fail.',
  'eduflow.ragChatbot.chatHint':
    'Player answers and RAG embeddings use Mistral. Uploaded documents are retrieved with Mistral embeddings + pgvector; with no documents, only block text is used as context.',
  'eduflow.ragChatbot.playerToggle': 'Show in player',
  'eduflow.ragChatbot.playerToggleHint':
    'Learners ask questions only about this course: block text in the path, plus indexed documents if you added any.',
  'eduflow.ragChatbot.addDocument': 'Add document (PDF, TXT, MD)',
  'eduflow.ragChatbot.docCount': 'Indexed documents: {{count}}',
  'eduflow.ragChatbot.emptyDocs':
    'No documents. The chatbot will use course block text only.',
  'eduflow.ragChatbot.open': 'Open assistant',
  'eduflow.ragChatbot.dialogTitle': 'Course assistant',
  'eduflow.ragChatbot.playerDisclaimer':
    'Answers are based on this course’s documents and content only.',
  'eduflow.ragChatbot.playerWelcome': 'Ask a question about the course or the provided documents.',
  'eduflow.ragChatbot.playerError': 'Could not get a response. Please try again later.',
  'eduflow.ragChatbot.placeholder': 'Your question…',
  'eduflow.ragChatbot.send': 'Send',
  'eduflow.designer.aiPanel.welcome':
    'Describe what you want. The block open in **Properties** is the default target. Use the **crosshair** to capture the screen (canvas + form) for visual analysis and direct edits.',
  'eduflow.designer.aiPanel.askBeforeAct': 'Ask before acting',
  'eduflow.designer.aiPanel.askBeforeActHint':
    'When on, changes are shown in chat before applying to the graph.',
  'eduflow.designer.aiPanel.captureScreenHint': 'Capture canvas and Properties panel',
  'eduflow.designer.aiPanel.captureReady': 'Screenshot ready — send your instruction.',
  'eduflow.designer.aiPanel.captureFailed': 'Could not capture the screen.',
  'eduflow.designer.aiPanel.captureAttached': 'Screenshot will be sent with your next message',
  'eduflow.designer.aiPanel.removeCapture': 'Remove',
  'eduflow.designer.aiPanel.captureInMessage': 'Screen capture',
  'eduflow.designer.aiPanel.statusCapturing': 'Capturing the interface…',
  'eduflow.designer.aiPanel.statusAnalyzing': 'Analyzing screen and focused block…',
  'eduflow.designer.aiPanel.statusWorking': 'Working on your request…',
  'eduflow.designer.aiPanel.statusApplying': 'Applying to the canvas…',
  'eduflow.designer.aiPanel.captureDisclaimer':
    'AI can make mistakes — review generated content. Paste an image (Ctrl+V) or use capture.',
  'eduflow.designer.resizeAiPanel': 'Resize AI assistant panel',
  'eduflow.designer.aiPanel.subtitle': 'Copilot: content, graph, and objectives.',
  'eduflow.designer.aiPanel.focusContextLabel': 'Target:',
  'eduflow.designer.aiPanel.focusPrimary': '"{{label}}" ({{type}}) — Properties',
  'eduflow.designer.aiPanel.focusMulti':
    '{{count}} blocks selected — say which to edit, or focus one block.',
  'eduflow.designer.aiPanel.focusNone':
    'No focused block — open Properties on a node or name a block in your message.',
  'eduflow.designer.aiPanel.followUpsLabel': 'Suggested next steps',
  'eduflow.designer.aiPanel.ideasToggle': 'Ready-made ideas',
  'eduflow.designer.aiPanel.advancedToggle': 'Options (stream, sources, shortcuts…)',
  'eduflow.designer.aiPanel.removeDocument': 'Remove',
  'eduflow.designer.aiPanel.importDocumentTitle': 'Import PDF, Word, or PowerPoint',
  'eduflow.designer.aiPanel.webSourcesLabel': 'Web pages (max 3, optional)',
  'eduflow.designer.aiPanel.webSourcesPlaceholder': 'https://example.org/doc\nhttps://…',
  'eduflow.designer.aiPanel.idea.auditPath':
    'Audit the graph: broken paths, isolated nodes and flow order — apply fixes',
  'eduflow.designer.aiPanel.idea.microLearning':
    'Design a concise micro‑learning variant from the current flow while preserving intent',
  'eduflow.designer.aiPanel.idea.formativeGap':
    'Insert short formative bridging activities wherever the pathway is cognitively heavy',
  'eduflow.designer.aiPanel.idea.titleCoherence':
    'Align block titles with the project title and the overall training tone',
  'eduflow.designer.aiPanel.idea.remediation':
    'Add a remediation path after the main MCQ quiz (failure / low-score branch)',
  'eduflow.designer.aiPanel.idea.mobileWording':
    'Shorten and clarify HTML text in blocks that are too long on mobile',
  'eduflow.designer.aiPanel.idea.quizWording':
    'Unify wording style across all quiz instructions (“Select…”, tone, formal register)',
  'eduflow.designer.aiPanel.idea.summaryBeforeEnd':
    'Add or rewrite a recap block immediately before the End node',
  'eduflow.designer.aiPanel.streamMode': 'Streaming',
  'eduflow.designer.aiPanel.streamModeHint':
    'Response streams as it generates; the final payload is normalized like non-streaming mode.',
  'eduflow.designer.aiPanel.streamError': 'Stream interrupted or refused.',
  'eduflow.designer.aiPanel.streamIncomplete': 'Incomplete response from server. Retry or disable streaming.',
  'eduflow.designer.aiPanel.skillPath': 'Path audit (server)',
  'eduflow.designer.aiPanel.skillPathHint': 'Structural graph analysis added to the prompt.',
  'eduflow.designer.aiPanel.skillQuiz': 'Strict MCQ schema (server)',
  'eduflow.designer.aiPanel.skillQuizHint': 'Official Lyrra quiz_mcq template in the prompt.',
  'eduflow.designer.aiPanel.thinking': 'The assistant is thinking…',
  'eduflow.designer.aiPanel.serverSkills': 'Server boosts',
  'eduflow.designer.aiPanel.quickActions': 'Quick actions',
  'eduflow.designer.aiPanel.placeholder': 'Ask a question, brainstorm, review content, or change the path…',
  'eduflow.designer.aiPanel.apply': 'Apply to canvas',
  'eduflow.designer.aiPanel.autoApply': 'Auto-apply to graph',
  'eduflow.designer.aiPanel.autoApplyHint':
    'Structured assistant operations update the path without clicking Apply.',
  'eduflow.designer.aiPanel.dismissOps': 'Dismiss',
  'eduflow.designer.aiPanel.applied': 'Changes applied to the path.',
  'eduflow.designer.aiPanel.applyPartial': 'Applied with warnings: {{details}}',
  'eduflow.designer.aiPanel.applyFailed':
    'No operations could be applied to the canvas (invalid operation format or block types).',
  'eduflow.designer.aiPanel.objectivesSaved': 'Learning objectives saved (Objectives panel).',
  'eduflow.designer.aiPanel.objectivesSaveError': 'Could not save objectives. Please try again.',
  'eduflow.designer.aiPanel.error': 'Could not reach the assistant. Check your AI configuration (API key) and network.',
  'eduflow.designer.aiPanel.newConversation': 'New conversation',
  'eduflow.designer.aiPanel.clearConversation': 'Clear conversation',
  'eduflow.designer.aiPanel.conversationHistory': 'History',
  'eduflow.designer.aiPanel.deleteConversation': 'Delete',
  'eduflow.designer.aiPanel.maxConversations':
    'You’ve reached the limit of {{max}} saved conversations. Delete one to start a new one.',
  'eduflow.designer.aiPanel.unnamedConversation': 'Conversation',
  'eduflow.designer.aiQuick.plan': 'Path plan',
  'eduflow.designer.aiQuick.expand': 'Interactive blocks',
  'eduflow.designer.aiQuick.quiz': 'Quiz module',
  'eduflow.designer.aiQuick.objectives': 'Objectives (panel)',
  'eduflow.designer.aiQuick.rewriteFocus': 'Rewrite Properties block',
  'eduflow.designer.aiQuick.translateFocus': 'Translate Properties block (FR)',
  'eduflow.designer.voiceShort': 'Voice',
  'eduflow.designer.voiceTitle': 'Voice settings',
  'eduflow.designer.objectivesShort': 'Objectives',
  'eduflow.designer.objectivesTitle': 'Learning objectives',
  'eduflow.designer.flowVariablesTitle': 'Global variables',
  'eduflow.designer.flowVariables.subtitle':
    'Set key / value pairs. Use @ followed by the key name in block titles and descriptions (e.g. @course_name). Values are substituted for learners.',
  'eduflow.designer.flowVariables.empty': 'No variables yet. Add some to personalize course wording.',
  'eduflow.designer.flowVariables.keyLabel': 'Key',
  'eduflow.designer.flowVariables.keyPlaceholder': 'e.g. course_name',
  'eduflow.designer.flowVariables.keyHelp':
    'Letters, numbers, hyphen and underscore only. @key matching is case-sensitive.',
  'eduflow.designer.flowVariables.valueLabel': 'Default value',
  'eduflow.designer.flowVariables.valuePlaceholder': 'Text shown instead of @key',
  'eduflow.designer.flowVariables.add': 'Add variable',
  'eduflow.designer.flowVariables.delete': 'Remove',
  'eduflow.designer.flowVariables.invalidKey': 'Each key must be set and use only allowed characters.',
  'eduflow.designer.flowVariables.duplicateKey': 'Duplicate keys (case-insensitive) are not allowed.',
  'eduflow.designer.flowVariables.savedToast': 'Variables saved',
  'eduflow.designer.flowVariables.errorToast': 'Could not save variables.',
  'eduflow.designer.flowVariables.saveNow': 'Save now',
  'eduflow.designer.blockFlowVariablesHint':
    'Global variables: use @your_key in the title or description (configure them with the { } icon in the toolbar).',
  'eduflow.designer.blockFlowVariablesPreviewTitle': 'Preview (title)',
  'eduflow.designer.blockFlowVariablesPreviewDescription': 'Preview (description)',
  'eduflow.designer.flowVariables.mentionNoMatch': 'No matching variable.',
  'eduflow.designer.formFieldsSlash.mentionNoMatch': 'No matching field.',
  'eduflow.designer.onlineCount': '{{count}} online',
  'eduflow.designer.editingBlocks_one': '{{count}} block being edited',
  'eduflow.designer.editingBlocks_other': '{{count}} blocks being edited',
  'eduflow.designer.invite': 'Invite',
  'eduflow.designer.inviteTitle': 'Invite a collaborator',
  'eduflow.designer.exportTitle': 'Export path',
  'eduflow.designer.importTitle': 'Import course (file or JSON paste)',
  'eduflow.designer.exportNeedSavedFlow': 'Save the course first to export a .lyrra package.',
  'eduflow.designer.canvasUntitled': 'Untitled',
  'eduflow.designer.selectBlock': 'Select a block',
  'eduflow.designer.selectBlockHint': 'Click a block on the canvas to edit its properties',
  'eduflow.designer.blockPropertiesSubtitle': 'Block properties',
  'eduflow.designer.textVoiceoverHint':
    'The voiceover reads both the block description and main content.',
  'eduflow.designer.blockLockedReadOnly':
    'This block is locked: properties are shown as read-only.',
  'eduflow.designer.blockTitleField': 'Title',
  'eduflow.designer.blockTitlePlaceholder': 'Block title',
  'eduflow.designer.newFlowDefaultTitle': 'New course',
  'eduflow.designer.you': 'You',
  'eduflow.designer.blockInfoPosition': 'ID: {{id}} | Position: ({{x}}, {{y}})',
  'eduflow.designer.toastBlockCopied': 'Block copied',
  'eduflow.designer.toastCollaboratorInvited': 'Collaborator invited',
  'eduflow.designer.toastInviteError': 'Could not invite this collaborator',
  'eduflow.designer.createFlowError': 'Could not create the path',
  'eduflow.designer.loadFlowError': 'Could not load this training.',
  'eduflow.designer.loadFlowForbidden': 'You do not have access to this training.',
  'eduflow.designer.loadFlowNotFound': 'Training not found or removed.',
  'eduflow.duplicateFlowError': 'Could not duplicate the training.',
  'eduflow.designer.editConnection': 'Edit connection',
  'eduflow.designer.deleteConnection': 'Delete connection',

  'eduflow.translation.toolbarTitle': 'Content translation',
  'eduflow.translation.toolbarShort': 'Translate',
  'eduflow.translation.title': 'Multilingual translation',
  'eduflow.translation.subtitle':
    'Translate block text (AI) and optionally regenerate description voiceovers. Results are stored per language.',
  'eduflow.translation.subtitleV2':
    'Pick a Mistral voice per language for description voiceovers in the player (on-demand translation). Batch translation jobs below are optional.',
  'eduflow.translation.voicesSection': 'TTS voices per language',
  'eduflow.translation.voicesSectionHint':
    'Preview uses sample text in that language. With no voice selected, “Marie - sad” is used for French, Italian and Spanish; “Paul - sad” for other languages.',
  'eduflow.translation.voicesLangCount':
    '{{count}} languages (FR, EN, ES, DE, IT, PT, LB, UK, RU, AR): pick a voice, then tap “Play sample”.',
  'eduflow.translation.voicesLoadError':
    'Could not load voices. Check your connection or try again.',
  'eduflow.translation.voicePreview': 'Play sample',
  'eduflow.translation.previewStop': 'Stop',
  'eduflow.translation.voicePlaceholder': '— Choose a voice —',
  'eduflow.translation.voiceSelectAria': 'Voice for {{lang}}',
  'eduflow.translation.voicesSaveError': 'Could not save voices.',
  'eduflow.translation.selectVoiceFirst': 'Choose a voice for this language first.',
  'eduflow.translation.previewError': 'Audio playback failed.',
  'eduflow.translation.batchSection': 'Precomputed translations (optional)',
  'eduflow.translation.batchSectionHint':
    'The player can translate on the fly; run a job only if you want text + audio stored ahead of time.',
  'eduflow.translation.sourceLang': 'Course source language',
  'eduflow.translation.bulkSection': 'All languages',
  'eduflow.translation.includeText': 'Text',
  'eduflow.translation.includeAudio': 'Audio (description voiceover)',
  'eduflow.translation.runAll': 'Translate all languages (except source)',
  'eduflow.translation.runAllHint':
    'Starts one job per target language among the 10 supported languages, excluding the source language.',
  'eduflow.translation.perLanguage': 'Per language',
  'eduflow.translation.sourceBadge': 'Source',
  'eduflow.translation.runOne': 'Translate',
  'eduflow.translation.history': 'Recent jobs',
  'eduflow.translation.noHistory': 'No jobs yet.',
  'eduflow.translation.saveFirst': 'Save the path before starting a translation.',
  'eduflow.translation.prefsSaveError': 'Could not save translation preferences (per-language Text / Audio toggles).',
  'eduflow.translation.jobStarted': 'Translation started for {{lang}}.',
  'eduflow.translation.jobError': 'Could not start translation.',
  'eduflow.translation.bulkStarted': '{{count}} translation job(s) started.',
  'eduflow.translation.bulkNone':
    'No new jobs: translations may already be running for all target languages, or no options were selected.',
  'eduflow.translation.bulkError': 'Could not start bulk translations.',
  'eduflow.translation.statusPending': 'Pending',
  'eduflow.translation.statusProcessing': 'In progress',
  'eduflow.translation.statusCompleted': 'Completed',
  'eduflow.translation.statusFailed': 'Failed',
  'eduflow.translation.missingSourceAudioHint':
    'Audio translation: generate the description voiceover in {{lang}} for every long description first. Block(s) missing audio ({{count}}): {{blocks}}.',
  'eduflow.translation.missingSourceAudioToast':
    '{{count}} block(s) without source-language voiceover: {{blocks}}. Generate audio from the block properties, then try again.',

  'player.complete': 'Complete',
  'player.exit': 'Exit',
  'player.fullscreen': 'Fullscreen',
  'player.exitFullscreen': 'Exit fullscreen',
  'player.webinarFollowBanner':
    'You are following the host — navigation is locked. You can change the display language using the globe button.',
  'player.textBlockTipsHeading': 'Tips',
  'player.textBlockReadDone': 'Reading complete',
  'player.institutionLogo': 'Organization logo',
  'player.exitConfirmTitle': 'Exit training?',
  'player.exitConfirmMessage': 'Your progress will be saved. Are you sure you want to exit?',
  'player.focusLostTitle': 'Training paused',
  'player.focusLostMessage': 'You left the training window. Your time is paused.',
  'player.focusLostResume': 'Click to resume',
  'player.focusLostRemaining': 'Exits remaining before lockout: {{remaining}}',
  'player.focusBlockedTitle': 'Training locked',
  'player.focusBlockedMessage':
    'You exceeded the allowed number of exits ({{max}}). You can no longer continue this training.',
  'player.unblockRequestButton': 'Request unlock from trainer',
  'player.unblockRequestAgain': 'Request again',
  'player.unblockPending': 'Request sent — waiting for your trainer’s response.',
  'player.unblockRejected': 'Your unlock request was declined.',
  'player.unblockLoginRequired':
    'Sign in to your learner account (or use the personal link from your email) to request an unlock.',
  'player.unblockPublicLoginRequired':
    'In public mode without registration, unlock requests are not available. Complete the training registration form or sign in to LyrraStudio.',
  'player.unblockRequestSent': 'Request sent to your trainer.',
  'player.unblockRequestError': 'Could not send the request. Please try again.',
  'player.unblockApprovedToast': 'Your trainer unlocked the training. You can continue!',
  'player.registrationFormHint':
    'This form creates your learner account for this training (email, first name and last name required).',
  'player.registrationFormSuccess': 'Registration saved. You can continue the training.',
  'player.registrationFormError': 'Could not save registration.',
  'notifications.unblock.title': 'Unlock request',
  'notifications.unblock.message': '{{name}} — {{flow}} ({{count}} exit(s))',
  'notifications.unblock.unknownLearner': 'A learner',
  'notifications.unblock.unknownFlow': 'Training',
  'notifications.unblock.approve': 'Unlock',
  'notifications.unblock.reject': 'Decline',
  'notifications.unblock.approvedToast': 'Training unlocked for this learner.',
  'notifications.unblock.rejectedToast': 'Unlock request declined.',
  'notifications.unblock.resolveError': 'Could not process the request. Please try again.',
  'player.cancel': 'Cancel',
  'player.summary': 'Summary',
  'player.summary.badge': 'Recap',
  'player.summary.noItems': 'No summary points were defined for this block.',
  'player.summary.blockTranslating': 'Translating…',
  'player.summary.blockSimplifying': 'Simplifying text…',
  'player.simplifiedTextLoadingTitle': 'Preparing simplified text…',
  'player.simplifiedTextLoadingHint':
    'We simplify the visible block first, then the rest of the course in the background.',
  'player.contents': 'Content',
  'player.progress': 'Progress',
  'player.block': 'Block',
  'player.parallelBranches': 'Parallel branches',
  'player.parallelBranchesTitle': 'What’s next?',
  'player.parallelBranchesHint': 'The other options will be offered later.',
  'player.parallelBranchShowFull': 'Show more',
  'player.parallelBranchShowLess': 'Show less',
  'player.parallelBranchChoose': 'Open',
  'player.parallelBranchDismiss': 'Close',
  'player.parallelBranchDismissHint':
    'Return to the block. Use “Next” to choose a branch.',
  'player.parallelBranchesPickTitle': 'Choose your next path',
  'player.parallelBranchesPickHint':
    'Completed paths stay clickable to review. Finish each branch at least once before the shared section.',
  'player.parallelBranchActionReplay': 'Retake this path',
  'player.parallelBranchFromBlock': 'From: {{title}}',
  'player.parallelBranchesProgress': '{{done}} / {{total}} paths completed',
  'player.parallelBranchesOverview': 'Progress',
  'player.parallelBranchStatusDone': 'Completed',
  'player.parallelBranchStatusTodo': 'Pending',
  'player.parallelBranchStatusNext': 'Up next',
  'player.parallelBranchAction': 'Take this path',
  'player.parallelBranchesGridLabel': 'Parallel branch grid',
  'player.parallelBranchesScrollHint':
    'Scroll right (or use the arrows) to see more paths.',
  'player.parallelBranchesPageNav': 'Path pages',
  'player.parallelBranchesPageIndicator': 'Page {{current}} / {{total}}',
  'player.parallelBranchesPrevPage': 'Previous paths',
  'player.parallelBranchesNextPage': 'Next paths',
  'player.notes': 'Take notes...',
  'player.noBlocks': 'No blocks to display.',
  'player.contentTranslatingTitle': 'Adapting content to your language…',
  'player.contentTranslatingHint': 'Translation in progress. The first load may take a little longer.',
  'player.contentLanguageReloadFailed':
    'Could not reload content in this language (timeout or server error). The previous version is still shown. Try again shortly or precompute translations in the designer.',
  'player.contentLanguageSubflowHint':
    'To update text and audio, finish or leave the current sub-section, then pick the language again.',
  'player.defaultFlowTitle': 'Course',
  'player.docTitle.error': 'Course unavailable',
  'player.docTitle.completed': 'Course completed',
  'player.docTitle.loading': 'Loading course',
  'player.docTitle.fallback': 'Course',
  'player.initError.title': 'The course is temporarily unavailable',
  'player.initError.loadFailed': 'The course could not be loaded right now.',
  'player.initError.loadFailedWithDetail':
    'The course could not be loaded right now.{{detail}} Please try again in a moment.',
  'player.initError.hintsIntro': 'You can:',
  'player.initError.hintRefresh': 'refresh the page;',
  'player.initError.hintConnection': 'check your internet connection;',
  'player.initError.hintRetryLater': 'try again in a few minutes.',
  'api.error.network': 'Unable to reach the server. Check your connection.',
  'api.error.unexpected': 'An unexpected error occurred.',
  'api.error.badRequest': 'Invalid request.',
  'api.error.forbidden': 'Access denied.',
  'api.error.notFound': 'Resource not found.',
  'api.error.conflict': 'Conflict — this resource already exists.',
  'api.error.unprocessable': 'The submitted data is not valid.',
  'api.error.tooManyRequests': 'Too many requests. Please try again shortly.',
  'api.error.server': 'Internal server error.',
  'api.error.badGateway': 'Service temporarily unavailable.',
  'api.error.maintenance': 'Service under maintenance.',
  'api.error.generic': 'Error {{status}}',
  'player.textBlockReadMinutes': '{{count}} min read',
  'player.textBlockWordCount_one': '{{count}} word',
  'player.textBlockWordCount_other': '{{count}} words',
  'player.textBlockNoContent': 'No content available.',
  'player.blockUnknown': 'Unknown block',
  'player.objectivesPanelTitle': 'Learning objectives',
  'player.objectivesOpenLabel': 'View learning objectives',
  'player.objectivesCloseLabel': 'Close objectives panel',
  'player.objectivesAchievementRate': 'Achievement rate',
  'player.objectivesAchievedCount': '{{achieved}}/{{total}} objectives achieved',
  'player.objectivesNoneDefined': 'No objectives defined for this course.',
  'player.objectiveCategory.savoir': 'Knowledge',
  'player.objectiveCategory.savoirFaire': 'Skills',
  'player.objectiveCategory.savoirEtre': 'Attitudes',
  'player.objectiveLevel.connaissance': 'Knowledge',
  'player.objectiveLevel.comprehension': 'Comprehension',
  'player.objectiveLevel.application': 'Application',
  'player.objectiveLevel.analyse': 'Analysis',
  'player.objectiveLevel.synthese': 'Synthesis',
  'player.objectiveLevel.evaluation': 'Evaluation',
  'player.objectiveBloom': 'Bloom · {{level}}',
  'player.objectiveRequired': 'Required',
  'player.objectiveLinkedBlocks': 'Linked blocks',
  'player.objectiveCriterionScore': 'Score ≥ {{min}}%',
  'player.objectiveCriterionCompletion': 'Block completion',
  'player.objectiveNoLinkedBlocks':
    'No linked block — overall progress does not measure this objective.',
  'player.completion.congratulations': '🎉 Congratulations!',
  'player.completion.finished': '📋 Course completed',
  'player.completion.youCompleted': 'You have completed',
  'player.completion.passed': 'Passed ✓',
  'player.completion.failed': 'Not validated ✗',
  'player.completion.requiredScore': 'Required score: {{score}}%',
  'player.completion.totalTime': 'Total time',
  'player.completion.blocksCompleted': 'Blocks completed',
  'player.completion.quizPassed': 'Quizzes passed',
  'player.completion.successRate': 'Success rate',
  'player.completion.blockCompleted': 'Completed ✓',
  'player.completion.blockFailed': 'Failed ✗',
  'player.completion.blockSkipped': 'Skipped',
  'player.completion.detail': '📊 Detail by block',
  'player.completion.type': 'Type',
  'player.completion.timeSpent': 'Time spent',
  'player.completion.downloadCertificate': '📄 Download certificate',
  'player.completion.certificateSoon': 'The certificate will be available soon.',
  'player.completion.restart': '🔄 Restart',
  'player.completion.viewDetails': '📊 View details',
  'player.completion.backToDashboard': '← Back to dashboard',
  'player.completion.embedDismissHint':
    "You've finished this module. To leave, use the × at the top of the window or the button below.",
  'player.completion.embedDismissButton': 'Close window',
  'player.previewBanner': "Preview mode — this view reflects your training's real data",
  'player.closePreview': 'Close preview',
  'player.flowPreviewMap': 'Flow map',
  'player.flowPreviewTitle': 'Flow preview',
  'player.flowPreviewHint':
    'Read-only overview (same layout as in the designer). The orange frame shows the block currently shown in the player.',
  'player.flowPreviewLegendCurrent': 'Current block',
  'player.flowPreviewClose': 'Close',
  'player.toggleSidebarShow': 'Show sidebar',
  'player.toggleSidebarHide': 'Hide sidebar',
  'player.mobileSummary': 'Outline',
  'player.mobileObjectives': 'Objectives',
  'player.mobileMoreOptions': 'More options',
  'player.sidebarExpand': 'Expand sidebar',
  'player.sidebarCollapse': 'Collapse sidebar',
  'player.keyboardShortcuts': 'Keyboard shortcuts',
  'player.keyboardShortcutPrev': 'Previous block',
  'player.keyboardShortcutNext': 'Next block',
  'player.keyboardShortcutsHint':
    'Turn on “Keyboard navigation” in the accessibility panel (bottom-right button). Disabled while typing in fields.',
  'player.a11yCenter': 'Accessibility center',
  'player.reviewSummary': 'View summary',
  'player.viewResults': 'View results',
  'player.submitFirst': 'Submit first',
  'player.phraseRevealCompleteFirst': 'View all phrases first',
  'player.phraseReveal.emptyBlock': 'No phrases are configured for this block.',
  'player.phraseReveal.revealNextAria': 'Click or press Space to reveal the next phrase',
  'player.phraseReveal.lastPhraseHint': 'Last phrase — use “Next” to continue the path.',
  'player.phraseReveal.morePhrasesHint': 'Click, Space, or right arrow for the next phrase',
  'player.phraseReveal.previousPhrases': 'Previous phrases ({{count}})',
  'player.certificateUnavailable': 'The certificate will be available soon.',
  'player.browser.embeddedContentTitle': 'Embedded content',
  'player.browser.noValidUrl': 'No valid URL',
  'player.browser.enterHttpsHint':
    'Enter an address starting with https:// (or http://) in the block settings.',
  'player.browser.loadingContent': 'Loading content…',
  'player.browser.defaultPageTitle': 'Embedded web page',
  'player.browser.continue': 'Continue',
  'player.browser.stepSaved': 'Step saved',
  'player.browser.fullscreen': 'Fullscreen',
  'player.browser.exitFullscreen': 'Exit fullscreen',
  'player.podcast.defaultShowTitle': 'Podcast',
  'player.podcast.defaultEpisode': 'Episode',
  'player.podcast.loadError': 'Could not load the podcast.',
  'player.podcast.loadingEpisodes': 'Loading episodes…',
  'player.podcast.playAll': 'Play all',
  'player.podcast.nextEpisode': 'Next episode',
  'player.podcast.episodesHeading': 'Episodes',
  'player.podcast.noEpisodes': 'No episodes.',
  'player.podcast.noAudio': 'No audio',
  'player.podcast.chainPlayback': 'Playlist · {{current}}/{{total}}',
  'player.podcast.selectOrPlayAll': 'Select an episode from the list or tap “Play all”.',
  'player.podcast.audioUnavailable': 'Audio is not available for this episode.',
  'player.podcast.showEpisodeText': 'Show transcript',
  'player.podcast.hideEpisodeText': 'Hide transcript',
  'player.podcast.episodeCount_one': '{{count}} episode',
  'player.podcast.episodeCount_other': '{{count}} episodes',
  'player.podcast.showChapters': 'Show chapters',
  'player.podcast.hideChapters': 'Hide chapters',
  'player.podcast.coverDialogLabel': 'Cover artwork: {{title}}',
  'player.podcast.coverEnlargePodcast': 'Enlarge podcast cover artwork',
  'player.podcast.coverEnlargeEpisode': 'Enlarge episode cover artwork',
  'player.podcast.coverEnlarge': 'Enlarge cover artwork',
  'player.audio.play': 'Play',
  'player.audio.pause': 'Pause',
  'player.audio.mute': 'Mute',
  'player.audio.unmute': 'Unmute',
  'player.audio.seek': 'Audio track position',
  'player.audio.playbackSpeed': 'Playback speed',
  'player.audio.completed': 'Audio track finished',
  'player.audio.subtitlesAria': 'Text synced with playback',
  'player.audio.subtitlesWaiting': 'Text segments will appear here during playback.',
  'player.audio.subtitlesNeedText': 'Add text (TTS or block content) to show segments here.',
  'player.audio.subtitlesHintSr':
    'Approximate sync: text is spread over the audio duration, not word-level timecodes.',
  'player.timeline.defaultTitle': 'Timeline',
  'player.timeline.subtitle': 'Timeline to explore.',
  'player.timeline.eventFallback': 'Event',
  'player.timeline.voiceoverLabel': 'Voice-over',
  'player.timeline.playVoiceover': 'Play voice-over',
  'player.timeline.pauseVoiceover': 'Pause voice-over',
  'player.timeline.generateVoiceover': 'Generate voice-over',
  'player.timeline.showDetail': 'View details',
  'player.timeline.hideDetail': 'Hide details',
  'player.timeline.emptyTitle': 'No events on this timeline',
  'player.timeline.emptyHint': 'Add steps in the block configuration panel.',
  'player.timeline.voiceoverUnavailableTitle': 'Voice-over unavailable',
  'player.timeline.voiceoverUnavailableDesc':
    'Open this path from a connected training to generate audio.',
  'player.timeline.voiceoverTextTooShortTitle': 'Text too short',
  'player.timeline.voiceoverTextTooShortDesc':
    'Add a description (at least 20 characters) or a more detailed title for voice-over.',
  'player.timeline.voiceoverGenerationErrorTitle': 'Voice-over generation failed',
  'player.timeline.voiceoverSlowTitle': 'Voice-over',
  'player.timeline.voiceoverSlowDesc':
    'Generation is taking longer than expected. Try again in a moment.',
  'player.timeline.retryLater': 'Please try again later.',
  'player.timeline.openFullscreen': 'Open fullscreen',
  'player.timeline.openFullscreenAria': 'Open timeline in fullscreen',
  'player.timeline.closeFullscreenAria': 'Close fullscreen',
  'player.timeline.reviewed': 'Timeline reviewed',
  'player.video.seek': 'Video position',
  'player.video.subtitlesShow': 'Show subtitles',
  'player.video.subtitlesHide': 'Hide subtitles',
  'player.video.trackLabel': 'Subtitles',
  'player.video.completed': 'Video completed',
  'player.video.markWatched': 'I have watched the video',
  'player.video.defaultIframeTitle': 'Embedded video',
  'player.podcast.playAllAria': 'Play all “{{title}}” — {{count}} episode(s) with audio',
  'player.podcast.skipNextQueuedAria': 'Skip to next episode in queue',
  'player.podcast.playEpisodeAria': 'Play episode: {{title}}',
  'player.podcast.episodeNoAudioAria': 'Episode with no audio file: {{title}}',
  'player.renderError': 'Error rendering block: {{blockType}}',
  'player.blocks.defaultWelcome': 'Welcome to this training',
  'player.blocks.defaultEndTitle': 'Congratulations!',
  'player.quiz.selectCorrectAnswer': 'Select the correct answer:',
  'player.quiz.correctAnswer': '✓ Correct answer!',
  'player.quiz.incorrectAnswer': '✗ Incorrect answer.',
  'player.blockTimer.aria': 'Time remaining for this block',
  'player.blockTimer.modeShortCountup': 'Elapsed',
  'player.blockTimer.modeShortCountdown': 'Countdown',
  'player.form.successMessage': 'Thank you for your response!',
  'player.form.noFieldsConfigured': 'This form does not have any configured fields yet.',
  'player.selfAssessment.empty': 'No criteria are defined for this self-assessment.',
  'player.selfAssessment.continue': 'Continue',
  'player.selfAssessment.pick': 'Choose a score',
  'player.selfAssessment.rateAria': 'Score {{n}}: {{label}}',
  'player.selfAssessment.criterionFallback': 'criterion',
  'player.selfAssessment.commentLabel': 'Comment (optional)',
  'player.selfAssessment.commentPlaceholder': 'Personal reflections, areas for growth…',
  'player.selfAssessment.submitted': 'Responses saved.',
  'player.selfAssessment.submit': 'Submit self-assessment',
  'player.selfAssessment.textAnswerPlaceholder': 'Your answer…',
  'player.selfAssessment.pickBadges': 'Select one or more options:',
  'player.evaluation.commentsLabel': 'Comments (optional)',
  'player.evaluation.commentsPlaceholder': 'Share your feedback...',
  'player.evaluation.submitted': 'Evaluation submitted',
  'player.evaluation.submit': 'Submit evaluation',
  'player.time.lessThanMinute': 'Less than a minute',
  'player.time.oneMinute': '1 minute',
  'player.time.minutes': '{{count}} minutes',
  'player.time.shortSeconds': '{{count}}s',
  'player.time.shortMinutes': '{{count}}min',
  'player.time.shortMinutesSeconds': '{{minutes}}min {{seconds}}s',
  'player.completion.scoreLabel': 'Score',
  'accessibility.openCenter': 'Open accessibility center',
  'accessibility.center': 'Accessibility',
  'accessibility.modalTitle': 'Adapted reading center',
  'accessibility.modalDescription': 'Adjust the interface for low vision, dyslexia and more comfortable reading.',
  'accessibility.closeCenter': 'Close accessibility center',
  'accessibility.accessPad.connected': 'Lyrra Access Pad connected',
  'accessibility.accessPad.playGuide': 'Play LyrraPad guide',
  'accessibility.accessPad.pauseGuide': 'Pause LyrraPad guide',
  'accessibility.presets': 'Preset profiles',
  'accessibility.preset.standard.label': 'Standard',
  'accessibility.preset.standard.desc': 'Default application rendering.',
  'accessibility.preset.lowVision.label': 'Low vision',
  'accessibility.preset.lowVision.desc': 'Stronger contrast, larger text and clearer visual cues.',
  'accessibility.preset.dys.label': 'Dys',
  'accessibility.preset.dys.desc': 'Easier reading with more spacing and a more legible font.',
  'accessibility.preset.cumulHint': 'Combine several profiles if needed',
  'accessibility.preset.clickToAdd': 'Click to add',
  'accessibility.preset.clickToRemove': 'Click to remove',
  'accessibility.typography': 'Typography settings',
  'accessibility.fontScale': 'Text size',
  'accessibility.lineHeight': 'Line height',
  'accessibility.letterSpacing': 'Letter spacing',
  'accessibility.wordSpacing': 'Word spacing',
  'accessibility.visualAids': 'Visual aids',
  'accessibility.highContrast': 'High contrast',
  'accessibility.highContrastDesc': 'Strengthens reading contrast, borders and visual cues.',
  'accessibility.underlineLinks': 'Underlined links',
  'accessibility.underlineLinksDesc': 'Makes links and interactive areas easier to identify.',
  'accessibility.dyslexiaFont': 'Easier reading font',
  'accessibility.dyslexiaFontDesc': 'Uses a more comfortable font for dyslexic users and tired readers.',
  'accessibility.reducedMotion': 'Reduced motion',
  'accessibility.reducedMotionDesc': 'Reduces animations and transitions to limit visual discomfort.',
  'accessibility.mouseMagnifier': 'Reading magnifier',
  'accessibility.mouseMagnifierDesc':
    'Shows a circular lens that follows the pointer and enlarges course content under it (magnified view for reading only).',
  'accessibility.readingMask': 'Reading mask',
  'accessibility.readingMaskDesc':
    'An opaque band follows the cursor to isolate the current line — especially helpful for dyslexia and ADHD. Tip: Option ⌥ + =/+ or Option ⌥ + ↑/↓ (macOS); Alt + + / Alt − (Windows/Linux) to adjust the height.',
  'accessibility.speakOnSelection': 'Speak selected text',
  'accessibility.speakOnSelectionDesc':
    'Select any text on the page: a button appears to read it aloud on demand (different from automatic audio reading).',
  'accessibility.speakSelection.listen': 'Listen to selection',
  'accessibility.speakSelection.stop': 'Stop reading',
  'accessibility.speakSelection.loading': 'Generating…',
  'accessibility.speakSelection.retry': 'Try again',
  'accessibility.speakSelection.error': 'Read-aloud unavailable, please try again shortly.',
  'accessibility.speakSelection.region': 'Read selection aloud',
  'a11y.voiceGuide.title': 'Accessibility voice assistant',
  'a11y.help.shortcuts': 'Keyboard shortcuts',
  'a11y.help.kbdEsc': 'Close the panel / stop speech synthesis',
  'a11y.help.kbdMask': 'Adjust reading mask height (mask enabled)',
  'a11y.help.kbdMaskMac': 'Use = or + key (above numbers), or ↑ ↓ arrows with Option ⌥',
  'a11y.help.kbdMaskOther': 'Windows / Linux: Alt + + / Alt + −',
  'a11y.help.kbdTab': 'Move between interactive elements',
  'a11y.help.tips': 'Tips',
  'a11y.voiceGuide.subtitle': "Describe your needs and I'll turn on the right options for you.",
  'a11y.voiceGuide.cta.title': 'Describe your needs',
  'a11y.voiceGuide.cta.hint': 'Speak: AI automatically enables the right options for you.',
  'a11y.voiceGuide.cta.openLabel': 'Open the voice assistant to describe your accessibility needs',
  'a11y.voiceGuide.cta.start': 'Start the voice assistant',
  'a11y.voiceGuide.cta.stop': 'Stop listening',
  'a11y.voiceGuide.status': 'Status',
  'a11y.voiceGuide.statusActive': 'In progress',
  'a11y.voiceGuide.youSaid': 'You said',
  'a11y.voiceGuide.reformulation': 'Here is what I understood',
  'a11y.voiceGuide.applied': 'Settings applied',
  'a11y.voiceGuide.resetButton': 'Reset to normal',
  'a11y.voiceGuide.resetExplanation': 'All accessibility settings have been reset to default.',
  'a11y.voiceGuide.problem': 'Problem',
  'a11y.voiceGuide.micReady': 'Mic ready',
  'a11y.voiceGuide.micUnsupported': 'Speech recognition not supported',
  'a11y.voiceGuide.stage.idle': 'Ready to listen',
  'a11y.voiceGuide.stage.intro': 'Setting things up…',
  'a11y.voiceGuide.stage.listening': "I'm listening — speak freely.",
  'a11y.voiceGuide.stage.thinking': 'Analyzing your needs…',
  'a11y.voiceGuide.stage.confirmPlaying': 'Summarising…',
  'a11y.voiceGuide.stage.confirmListening': "Say 'yes' to confirm or 'no' to start over.",
  'a11y.voiceGuide.stage.applying': 'Applying your settings…',
  'a11y.voiceGuide.stage.done': 'Done!',
  'a11y.voiceGuide.stage.error': 'Something went wrong.',
  'a11y.voiceGuide.error.mic': 'Microphone access failed.',
  'a11y.voiceGuide.error.micDenied': 'Microphone permission was denied. Please enable it in your browser settings.',
  'a11y.voiceGuide.error.stt': 'Speech recognition is not available in this browser.',
  'a11y.voiceGuide.error.ai': 'AI analysis unavailable.',
  'a11y.voiceGuide.error.serverUnavailable':
    'The Lyrra server is not responding correctly (often: backend not deployed or build error). Check deployment, then try again.',
  'a11y.voiceGuide.error.generic': 'Something went wrong. Please try again or adjust settings manually.',
  'accessibility.alwaysOn': 'Always-on improvements',
  'accessibility.alwaysOnFocus': 'More visible keyboard focus',
  'accessibility.alwaysOnLinks': 'Links and interactive elements easier to detect',
  'accessibility.alwaysOnSaved': 'Settings automatically saved on this device',
  'accessibility.navigation': 'Navigation',
  'accessibility.keyboardNavigation': 'Keyboard navigation',
  'accessibility.keyboardNavigationDesc':
    'Use Left / Up arrow for the previous block and Right / Down arrow for the next block (same as the bottom buttons).',

  // ── Learner Dashboard ──────────────────────────────────
  'learner.header.workspace': 'Learner workspace',
  'learner.tabs.home': 'Home',
  'learner.tabs.parcours': 'My Paths',
  'learner.tabs.formations': 'My Courses',
  'learner.tabs.publicFormations': 'Public courses',
  'learner.tabs.webinars': 'Webinars',
  'learner.tabs.podcasts': 'Podcasts',
  'learner.tabs.documents': 'Documents',
  'learner.tabs.progress': 'My Progress',
  'learner.tabs.profile': 'Profile',
  'learner.sidebar.tagline': 'Learner',
  'learner.sidebar.webinars': 'Webinars',
  'learner.webinars.title': 'My webinars',
  'learner.webinars.loading': 'Loading your webinars…',
  'learner.webinars.empty': 'No webinars yet. Email invitations and sessions where you present will appear here.',
  'learner.webinars.sectionInvited': 'Invitations received',
  'learner.webinars.sectionPresenting': 'Sessions I present',
  'learner.webinars.emptyInvited': 'No invitations yet.',
  'learner.webinars.emptyPresenting': 'You are not a presenter on any webinar.',
  'learner.webinars.rolePresenter': 'Presenter',
  'learner.webinars.joinAsPresenter': 'Open room (presenter)',
  'learner.webinars.join': 'Join room',
  'learner.webinars.invitedOn': 'Invited on',
  'learner.webinars.scheduledFor': 'Scheduled for',
  'learner.webinars.duration': 'Duration: {{duration}}',
  'learner.webinars.loadError': 'Could not load webinars.',
  'learner.publicFormations.title': 'Public courses',
  'learner.publicFormations.subtitle': 'Publicly accessible courses offered by your organization.',
  'learner.publicFormations.loading': 'Loading public courses…',
  'learner.publicFormations.loadError': 'Could not load public courses.',
  'learner.publicFormations.empty': 'No course matches your search.',
  'learner.publicFormations.emptyNoInstitution':
    'No public courses are available for your organization at the moment.',
  'learner.publicFormations.searchPlaceholder': 'Search for a course…',
  'learner.publicFormations.open': 'Open course',
  'learner.webinars.status.draft': 'Draft',
  'learner.webinars.status.scheduled': 'Scheduled',
  'learner.webinars.status.live': 'Live',
  'learner.webinars.status.ended': 'Ended',
  'learner.sidebar.myProfile': 'My profile',
  'learner.sidebar.logout': 'Log out',
  'learner.sidebar.collapse': 'Collapse menu',
  'learner.sidebar.expandAria': 'Expand sidebar',
  'learner.sidebar.collapseAria': 'Collapse sidebar',
  'learner.sidebar.backToTrainer': 'Trainer dashboard',
  'learner.sidebar.backToTrainerAria': 'Go back to the trainer dashboard',
  'learner.home.defaultName': 'learner',
  'learner.home.greeting': 'Hello {{name}},',
  'learner.home.greetingSubtitle': 'Pick up your learning where you left off.',
  'learner.home.resumeMyFormation': 'Resume my course',
  'learner.home.globalProgress': 'Overall progress',
  'learner.home.learningTime': 'Learning time',
  'learner.home.badges': 'Badges earned',
  'learner.home.calendarKicker': 'My progress',
  'learner.home.quickAccess': 'Quick access',
  'learner.learningPath': 'Learning path',
  'learner.home.resumeTraining': 'Resume your courses',
  'learner.home.viewAll': 'View all →',
  'learner.home.trainingDomains': 'Training domains',
  'learner.home.courses': 'courses',
  'learner.home.episodes': 'episodes',
  'learner.home.recommendedPodcasts': 'Recommended podcasts',
  'learner.home.explore': 'Explore →',
  'learner.formations.title': 'My Courses',
  'learner.formations.searchPlaceholder': 'Search a course…',
  'learner.formations.all': 'All',
  'learner.formations.formation': 'Course',
  'learner.formations.inProgress': 'In progress',
  'learner.formations.completed': 'Completed',
  'learner.formations.notStarted': 'Not started',
  'learner.formations.loading': 'Loading your courses...',
  'learner.formations.empty': 'No course found.',
  'learner.formations.progress': 'Progress',
  'learner.formations.instructor': 'Instructor',
  'learner.formations.opening': 'Opening...',
  'learner.formations.review': 'Review',
  'learner.formations.start': 'Start',
  'learner.formations.continue': 'Continue',
  'learner.podcasts.title': 'Explore Podcasts',
  'learner.podcasts.searchPlaceholder': 'Search a podcast…',
  'learner.podcasts.all': 'All',
  'learner.podcasts.assigned': 'Assigned podcast',
  'learner.podcasts.defaultCategory': 'Podcast',
  'learner.podcasts.loading': 'Loading your podcasts...',
  'learner.podcasts.empty': 'No assigned podcast.',
  'learner.podcasts.episodes': 'episodes',
  'learner.podcasts.opening': 'Opening...',
  'learner.podcasts.listen': 'Listen',
  'learner.documents.title': 'Shared Documents',
  'learner.documents.loading': 'Loading your documents...',
  'learner.documents.view': 'View document',
  'learner.documents.empty': 'No shared document.',
  'learner.documents.personalTitle': 'My personal documents',
  'learner.documents.personalHint': 'Your personal documents are only visible to you. Click on a document to preview it and ask questions to the AI assistant.',
  'learner.documents.personalEmpty': 'No personal document yet. Upload a PDF to get started.',
  'learner.documents.upload': 'Upload a document',
  'learner.documents.delete': 'Delete',
  'learner.documents.deleteConfirmTitle': 'Delete this document?',
  'learner.documents.deleteConfirmDesc': 'This action is permanent. The document and its AI analysis will be deleted.',
  'learner.documents.cancel': 'Cancel',
  'learner.documents.confirmDelete': 'Delete',
  'learner.documents.chatReady': 'Chat ready',
  'learner.documents.processing': 'Processing…',
  'learner.documents.backToList': 'Back to documents',
  'learner.documents.chatTitle': 'Ask about this document',
  'learner.documents.chatPlaceholder': 'Ask a question about this document…',
  'learner.documents.chatPlaceholderHint': 'Ask any question about this document. The assistant will only answer based on its content.',
  'learner.documents.chatError': 'An error occurred.',
  'learner.documents.preview': 'Preview',
  'learner.documents.indexing': 'Analyzing your document…',
  'learner.progress.title': 'My Progress',
  'learner.progress.completedFormations': 'Completed courses',
  'learner.progress.trainingHours': 'Training hours',
  'learner.progress.listenedPodcasts': 'Podcasts listened',
  'learner.progress.earnedBadges': 'Badges earned',
  'learner.progress.weeklyActivity': 'Weekly activity',
  'learner.progress.badges': 'Badges',
  'learner.progress.recentActivity': 'Recent activity',
  'learner.progress.rarity.common': 'Common',
  'learner.progress.rarity.rare': 'Rare',
  'learner.progress.rarity.epic': 'Epic',
  'learner.progress.rarity.legendary': 'Legendary',
  'learner.progress.days.mon': 'Mon',
  'learner.progress.days.tue': 'Tue',
  'learner.progress.days.wed': 'Wed',
  'learner.progress.days.thu': 'Thu',
  'learner.progress.days.fri': 'Fri',
  'learner.progress.days.sat': 'Sat',
  'learner.progress.days.sun': 'Sun',
  'learner.profile.title': 'My Profile',
  'learner.profile.defaultName': 'Learner',
  'learner.profile.defaultEmail': 'learner@example.com',
  'learner.profile.memberSince': 'Member since January 2024',
  'learner.profile.editProfile': 'Edit profile',
  'learner.profile.formations': 'Courses',
  'learner.profile.hours': 'Hours',
  'learner.profile.podcasts': 'Podcasts',
  'learner.profile.badges': 'Badges',
  'learner.profile.preferences': 'Preferences',
  'learner.profile.theme': 'Theme',
  'learner.profile.themeLight': 'Light',
  'learner.profile.themeDark': 'Dark',
  'learner.profile.themeSystem': 'System',
  'learner.profile.language': 'Language',
  'learner.profile.notifications': 'Notifications',
  'learner.profile.fontSize': 'Text size',
  'learner.profile.fontNormal': 'Normal',
  'learner.profile.fontLarge': 'Large',
  'learner.profile.highContrast': 'High contrast',
  'learner.profile.a11ySection': 'Accessibility',
  'learner.profile.dangerZone': 'Danger zone',
  'learner.profile.logoutBtn': 'Log out',
  'learner.parcours.title': 'My Paths',
  'learner.parcours.assigned': 'assigned path(s)',
  'learner.parcours.loading': 'Loading your paths…',
  'learner.parcours.loadError': 'Unable to load your paths.',
  'learner.parcours.noAssigned': 'No assigned path',
  'learner.parcours.noAssignedDesc': 'Your instructor will assign paths here.',
  'learner.parcours.completedBadge': 'Completed ✓',
  'learner.parcours.by': 'By',
  'learner.parcours.contents': 'content(s)',
  'learner.parcours.completedItems': 'completed',
  'learner.parcours.emptyPath': 'No content in this path.',
  'learner.parcours.locked': 'Complete the previous one first',
  'learner.parcours.review': 'Review',
  'learner.parcours.listen': 'Listen',
  'learner.parcours.open': 'Open',
  'learner.modal.loading': 'Loading…',
  'learner.modal.noEpisodes': 'No episode available.',
  'learner.modal.markedDone': '✓ Marked as done',
  'learner.modal.listenThenMark': 'Listen then mark as done',
  'learner.modal.alreadyDone': 'Already done',
  'learner.modal.markDone': 'Mark as done',
  'learner.modal.formationDone': '✓ Course completed',
  'learner.modal.completeThenMark': 'Complete the course then mark it as done',
  'learner.modal.formationAlreadyDone': 'Already completed',
  'learner.modal.markFormationDone': 'Mark as completed',
  'learner.modal.formationInstruction': 'Follow the modules of this course in order, then mark it as completed to progress in your path.',
  'learner.modal.launchFormation': 'Launch course',
  'learner.modal.openFormationHint':
    'Open the course in a new tab, then mark it as completed from your path.',
  'learner.home.loadingHome': 'Loading your home…',
  'learner.home.noFormationsAssigned':
    'No course assigned yet. They will appear here when a trainer assigns one to you.',
  'learner.home.loadingPaths': 'Loading your paths…',
  'learner.home.noPathsAssigned':
    'No path assigned yet. They will appear here when a trainer assigns one to you.',
  'learner.home.byTheme': 'Your content by theme',
  'learner.home.themesHint': 'Themes come from the categories of your assigned courses and podcasts.',
  'learner.home.categoryOther': 'Other',
  'learner.home.contentsCount_one': '{{count}} item',
  'learner.home.contentsCount_other': '{{count}} items',
  'learner.home.viewThemeFormations': 'View all {{count}} courses in this theme',
  'learner.home.viewThemePodcasts': 'View all {{count}} podcasts in this theme',
  'learner.home.resumeSubtitleProgress': 'Progress {{percent}}%',
  'learner.home.resumeSubtitleNotStarted': 'Not started yet',
  'learner.home.podcastsAssigned': 'Assigned podcasts',
  'learner.home.viewAllPodcasts': 'See all →',
  'learner.home.gamificationHomeHint':
    'Badges and full activity are under the « My progress » tab.',
  'learner.home.gamificationHomeCta': 'Open my progress',
  'learner.podcasts.mineTitle': 'My podcasts',
  'learner.podcasts.episodeCount_one': '{{count}} episode',
  'learner.podcasts.episodeCount_other': '{{count}} episodes',
  'learner.progress.loading': 'Loading your progress…',
  'learner.progress.statPodcastsCompleted': 'Podcasts completed',
  'learner.progress.statTimeFormationsHours': 'Time on courses (h)',
  'learner.progress.statAssignedContents': 'Assigned content',
  'learner.progress.weeklyEstimated': 'Last 7 days activity (estimated)',
  'learner.progress.badgesSoon': 'Badges and rewards will be available in your space soon.',
  'learner.progress.gamificationSection': 'Level and experience',
  'learner.progress.gamificationLevelLine': 'Level {{level}} — {{name}}',
  'learner.progress.gamificationXpToNext': '{{xp}} XP until the next level',
  'learner.progress.rewardFormation': 'Course completed',
  'learner.progress.rewardPathItem': 'Learning path step',
  'learner.progress.rewardPathComplete': 'Learning path completed',
  'learner.progress.rewardDesignerBlocks': 'Course points (completed blocks)',
  'learner.progress.rewardDesignerPerfect': 'Perfect quiz score bonus',
  'learner.progress.rewardDesignerBadge': 'Course badge',
  'learner.progress.rewardDesignerBadgeNoXp': 'No XP points (visual reward)',
  'learner.progress.badgesEmpty':
    'Complete a course or a learning path to earn points and see your rewards here.',
  'learner.progress.plusXp': '+{{xp}} XP',
  'learner.progress.noRecentHint':
    'No recent activity yet. Open a course or a podcast to feed this list.',
  'learner.progress.hoursShort': '{{hours}}h',
  'learner.profile.statFormationsRatio': 'Courses (completed / total)',
  'learner.profile.statTimeFormations': 'Course time (h)',
  'learner.profile.statPodcastsAssigned': 'Assigned podcasts',
  'learner.profile.statPathsAssigned': 'Assigned paths',
  'learner.profile.memberSincePrefix': 'Member since {{date}}',
  'learner.parcours.playPath': 'Follow this path',
  'learner.parcours.playPathTitle': 'Opens the full-screen path in a new tab',
  'learner.parcours.steps_zero': 'No steps',
  'learner.parcours.steps_one': '1 step',
  'learner.parcours.steps_other': '{{count}} steps',
  'learner.parcours.doneBadge': 'Done',
  'learner.parcours.completedSingle': '{{done}}/{{total}} completed',
  'learner.parcours.completedPlural': '{{done}}/{{total}} completed',
  'learner.parcours.byAuthor': 'By {{firstName}} {{lastName}}',
  'learner.parcours.pathListAria': 'Your paths list',
  'learner.parcours.assignedCount_one': '{{count}} assigned path',
  'learner.parcours.assignedCount_other': '{{count}} assigned paths',

  'learner.pathPlayer.loading': 'Loading path…',
  'learner.pathPlayer.errorGeneric': 'Error',
  'learner.pathPlayer.pathNotFound': 'Path not found',
  'learner.pathPlayer.loadError': 'Unable to load path.',
  'learner.pathPlayer.backToPaths': 'Back to my paths',
  'learner.pathPlayer.showTocAria': 'Show outline',
  'learner.pathPlayer.prevActivityAria': 'Previous activity',
  'learner.pathPlayer.prev': 'Previous',
  'learner.pathPlayer.nextActivityAria': 'Next activity',
  'learner.pathPlayer.next': 'Next',
  'learner.pathPlayer.closePlayerTitle': 'Close and return to paths',
  'learner.pathPlayer.close': 'Close',
  'learner.pathPlayer.congrats': 'Congratulations',
  'learner.pathPlayer.pathCompleteTitle': 'Path completed',
  'learner.pathPlayer.pathCompleteBody': 'You have completed all activities in the path “{{title}}”.',
  'learner.pathPlayer.activitiesCompleted_one': '{{count}} activity completed',
  'learner.pathPlayer.activitiesCompleted_other': '{{count}} activities completed',
  'learner.pathPlayer.seeMyPaths': 'See my paths',
  'learner.pathPlayer.closeTocAria': 'Close outline',
  'learner.pathPlayer.tocNavAria': 'Path outline',
  'learner.pathPlayer.pathTypeLabel': 'Training path',
  'learner.pathPlayer.noDescription': 'No description.',
  'learner.pathPlayer.activityCount_zero': 'No activities',
  'learner.pathPlayer.activityCount_one': '1 activity',
  'learner.pathPlayer.activityCount_other': '{{count}} activities',
  'learner.pathPlayer.progressPercent': 'Progress {{percent}}%',
  'learner.pathPlayer.quit': 'Leave',
  'learner.pathPlayer.summary': 'Outline',
  'learner.pathPlayer.outsideModule': 'Outside module',
  'learner.pathPlayer.moduleContents': 'Module contents',
  'learner.pathPlayer.step': 'Step',
  'learner.pathPlayer.podcastHint': 'Listen to the episode, then mark the activity as complete.',
  'learner.pathPlayer.showChapters': 'Show chapters',
  'learner.pathPlayer.hideChapters': 'Hide chapters',
  'learner.pathPlayer.showText': 'Show transcript',
  'learner.pathPlayer.hideText': 'Hide transcript',
  'learner.pathPlayer.selectEpisode': 'Select an episode to start.',
  'learner.pathPlayer.loadingShort': 'Loading…',
  'learner.pathPlayer.noEpisodes': 'No episodes available.',
  'learner.pathPlayer.listenThenMark': 'Listen, then mark as complete',
  'learner.pathPlayer.updating': 'Updating…',
  'learner.pathPlayer.alreadyDone': 'Already completed',
  'learner.pathPlayer.markComplete': 'Mark as complete',
  'learner.pathPlayer.nextStepBtn': 'Next step',
  'learner.pathPlayer.embeddedFormationHint':
    'The course appears below in the path. Use the outline on the left for detailed steps.',
  'learner.pathPlayer.loadingFormation': 'Loading course…',
  'learner.pathPlayer.loadingReader': 'Loading player…',
  'learner.pathPlayer.emptyPathTitle': 'This path does not have any activities yet.',
  'learner.pathPlayer.emptyPathDesc':
    'Content will be added by your training organization. You can close this window and come back later.',
  'learner.pathPlayer.pickActivity': 'Choose an activity in the outline to get started.',
  'learner.pathPlayer.footerStep': 'Step {{current}} / {{total}}',
  'learner.pathPlayer.unmute': 'Unmute',
  'learner.pathPlayer.mute': 'Mute',

  'learner.activity.flowCompleted': 'Course “{{title}}” completed',
  'learner.activity.flowProgress': 'Course “{{title}}” — {{progress}}%',
  'learner.activity.podcast': 'Podcast “{{title}}”',
  'learner.webinars.durationMin': '{{m}} min',
  'learner.webinars.durationH': '{{h}} h',
  'learner.webinars.durationHM': '{{h}} h {{m}}',
  'learner.formations.durationHours': '{{hours}} h',
  // ── Injected from locales/en.json ──
  'producerAssignments.createInDesigner': 'Create / open course',
  'producerAssignments.draft': 'Draft',
  'producerAssignments.enterprise': 'Enterprise',
  'producerAssignments.enterprisePaths': 'Enterprise paths',
  'producerAssignments.episodes': 'ep.',
  'producerAssignments.formations': 'Course cards',
  'producerAssignments.markCompleted': 'Mark as completed',
  'producerAssignments.markInProgress': 'In production',
  'producerAssignments.markReady': 'Ready',
  'producerAssignments.modulesCatalog': '{{count}} catalog module(s) · updated {{date}}',
  'producerAssignments.noneEnterprisePaths': 'No enterprise path requests.',
  'producerAssignments.noneFormations': 'No cards assigned.',
  'producerAssignments.nonePodcasts': 'No podcasts assigned.',
  'producerAssignments.openInDesigner': 'Open in designer',
  'producerAssignments.openPodcasts': 'Podcast studio',
  'producerAssignments.planningDeadline': 'Deadline {{date}}',
  'producerAssignments.planningFrom': 'Starting {{date}}',
  'producerAssignments.planningRange': '{{start}} → {{end}}',
  'producerAssignments.podcasts': 'Podcasts to produce',
  'producerAssignments.productionStatus.completed': 'Completed',
  'producerAssignments.productionStatus.in_progress': 'In progress',
  'producerAssignments.productionStatus.not_started': 'Not started',
  'producerAssignments.productionStatus.published': 'Published',
  'producerAssignments.publishedCatalog': 'Published to catalog',
  'producerAssignments.rhPlanning': 'HR planning',
  'producerAssignments.subtitle': 'Courses to create in the designer and podcasts to produce, assigned by HR.',
  'producerAssignments.title': 'My production assignments',
  'podcasts.actions.cancel': 'Cancel',
  'podcasts.actions.close': 'Close',
  'podcasts.actions.create': 'Create',
  'podcasts.actions.edit': 'Edit',
  'podcasts.actions.retry': 'Retry',
  'podcasts.actions.save': 'Save',
  'podcasts.actions.saving': 'Saving…',
  'podcasts.actions.share': 'Share',
  'podcasts.category.ariaGroup': 'Podcast category',
  'podcasts.category.emptyBanner': 'Add categories in the Institution space to better organize podcasts.',
  'podcasts.category.emptyInstitutionHint': 'No institution categories yet — you can publish without one.',
  'podcasts.category.noneChip': 'No category',
  'podcasts.category.sectionTitle': 'Category',
  'podcasts.category.withCategoriesHint': 'Catalog placement and institution-side filters.',
  'podcasts.cover.ariaDropzone': 'Choose or drop a cover image',
  'podcasts.cover.browseFiles': 'browse files',
  'podcasts.cover.cancelNewFile': 'Cancel new file',
  'podcasts.cover.dragDrop': 'Drag and drop an image',
  'podcasts.cover.dropHere': 'Drop image here',
  'podcasts.cover.errorMaxSize': 'Image must not exceed 5 MB.',
  'podcasts.cover.errorUnsupportedFormat': 'Unsupported image format.',
  'podcasts.cover.formatsHint': 'PNG, JPG, WebP, GIF · max 5 MB',
  'podcasts.cover.help': 'Square format recommended. Drag and drop a file or click to browse.',
  'podcasts.cover.or': 'or',
  'podcasts.cover.replaceHint': 'Replace image',
  'podcasts.cover.sectionTitle': 'Cover image',
  'podcasts.deleteDialog.body': '"{{title}}" will be moved to trash. Episodes linked only to this podcast will be deleted (including audio). Episodes shared with other podcasts are kept. An administrator can restore the podcast from the Institution trash.',
  'podcasts.deleteDialog.confirm': 'Move to trash',
  'podcasts.deleteDialog.title': 'Move podcast to trash?',
  'podcasts.editPanel.createdOn': 'Created on ',
  'podcasts.editPanel.headerTitle': 'Edit podcast',
  'podcasts.editPanel.saving': 'Saving…',
  'podcasts.editPanel.statsEpisodes': 'Episodes: ',
  'podcasts.editPanel.statsSubscribers': 'Subscribers: ',
  'podcasts.editPanel.successSaved': 'Changes saved',
  'podcasts.episodeStatus.draft': 'Draft',
  'podcasts.episodeStatus.published': 'Published',
  'podcasts.episodesPanel.addEpisode': 'Add episode',
  'podcasts.episodesPanel.emptySubtitle': 'Add episodes to this podcast',
  'podcasts.episodesPanel.emptyTitle': 'No episodes',
  'podcasts.episodesPanel.episodeCount': '{{count}} episode(s)',
  'podcasts.episodesPanel.noEpisodeAvailable': 'No episode available',
  'podcasts.episodesPanel.remove': 'Remove',
  'podcasts.episodesPanel.searchPlaceholder': 'Search an episode…',
  'podcasts.episodesPanel.title': 'Podcast episodes',
  'podcasts.pdfImport.openButton': 'Import document',
  'podcasts.pdfImport.pickPodcastTitle': 'Choose target podcast',
  'podcasts.pdfImport.noPodcast': 'Create a non-archived podcast before importing a document.',
  'podcasts.pdfImport.noPodcastMatch': 'No podcast matches your search.',
  'podcasts.list.actionImportDocument': 'Import document (PDF or Markdown)',
  'podcasts.pdfImport.title': 'Import document (PDF or Markdown)',
  'podcasts.pdfImport.subtitle': 'Create episodes for « {{podcast}} » from document structure',
  'podcasts.pdfImport.hint':
    'Content is split: # → episode, ## → chapter, paragraphs → blocks. PDFs are converted to Markdown first for review. Accepted: PDF or .md.',
  'podcasts.pdfImport.convertingPdf': 'Converting PDF to Markdown…',
  'podcasts.pdfImport.markdownReviewTitle': 'Review generated Markdown',
  'podcasts.pdfImport.markdownReviewSubtitle':
    'PDF converted for « {{podcast}} ». Edit headings (# / ##) then validate.',
  'podcasts.pdfImport.markdownReviewHint':
    'Each episode should start with # and each chapter with ##. Fix text before continuing.',
  'podcasts.pdfImport.markdownEditorLabel': 'Markdown content',
  'podcasts.pdfImport.markdownEditorHint': '# Episode · ## Chapter · paragraphs = script blocks',
  'podcasts.pdfImport.validateMarkdown': 'Validate and view structure',
  'podcasts.pdfImport.editMarkdown': 'Edit Markdown',
  'podcasts.pdfImport.markdownEmpty': 'Markdown is empty.',
  'podcasts.pdfImport.dropLabel': 'Drop a PDF or .md file, or choose a file',
  'podcasts.pdfImport.chooseFile': 'Choose file',
  'podcasts.pdfImport.analyzing': 'Analyzing document…',
  'podcasts.pdfImport.pageCount': '{{count}} page(s)',
  'podcasts.pdfImport.lineCount': '{{count}} line(s)',
  'podcasts.pdfImport.stats': '{{episodes}} episode(s) · {{chapters}} chapter(s) · {{blocks}} section(s)',
  'podcasts.pdfImport.episodeLabel': 'Episode {{index}} — {{title}}',
  'podcasts.pdfImport.sectionsCount': '{{count}} section(s)',
  'podcasts.pdfImport.confirm': 'Create episodes',
  'podcasts.pdfImport.importing': 'Creating…',
  'podcasts.pdfImport.previewError': 'Could not analyze this document.',
  'podcasts.pdfImport.importError': 'Import failed.',
  'podcasts.pdfImport.invalidFile': 'Only PDF and Markdown (.md) files are accepted.',
  'podcasts.pdfImport.success': 'Created {{episodes}} episode(s), {{chapters}} chapter(s), {{blocks}} section(s).',
  'podcasts.pdfImport.hintEditorPage':
    'After choosing a file, you will open the Markdown editor with a structure assistant (episodes, chapters, alerts).',
  'podcasts.pdfImport.editorPageTitle': 'Prepare document import',
  'podcasts.pdfImport.editorPageSubtitle': 'Podcast « {{podcast}} » — {{file}}',
  'podcasts.pdfImport.editorEmptyHint':
    'Choose a PDF or Markdown file to start. Fix structure before creating episodes.',
  'podcasts.pdfImport.backToPodcasts': 'Back to podcasts',
  'podcasts.pdfImport.replaceFile': 'Replace file',
  'podcasts.pdfImport.documentTitleLabel': 'Document title',
  'podcasts.pdfImport.untitledDocument': 'Untitled',
  'podcasts.pdfImport.invalidRoute': 'Invalid podcast.',
  'podcasts.pdfImport.fixErrorsBeforeImport': 'Fix structure errors (right panel) before creating episodes.',
  'podcasts.pdfImport.structurePanelTitle': 'Structure assistant',
  'podcasts.pdfImport.structurePanelSubtitle': 'Live estimates and alerts while you edit.',
  'podcasts.pdfImport.estimatedStructure': 'Estimated structure',
  'podcasts.pdfImport.statEpisodes': 'Episodes',
  'podcasts.pdfImport.statChapters': 'Chapters',
  'podcasts.pdfImport.statBlocks': 'Blocks',
  'podcasts.pdfImport.noPreviewYet': 'Add or import content to analyze structure.',
  'podcasts.pdfImport.markdownMeta': '{{lines}} lines · {{h1}} # heading(s) · {{h2}} ## heading(s)',
  'podcasts.pdfImport.structureBlocked': 'Errors block import — fix Markdown first.',
  'podcasts.pdfImport.structureReady': 'Structure looks good — you can create episodes.',
  'podcasts.pdfImport.structureIssues': 'Structure alerts',
  'podcasts.pdfImport.goToLine': 'Go to line {{line}} in the editor',
  'podcasts.pdfImport.suggestions': 'Suggestions',
  'podcasts.pdfImport.outlinePreview': 'Outline preview',
  'podcasts.pdfImport.conceptionGuide': 'Structuring guide',
  'podcasts.pdfImport.guideH1': '# Heading 1 = one podcast episode (book chapter, course part, etc.).',
  'podcasts.pdfImport.guideH2': '## Heading 2 = chapter inside the episode (sections, sub-parts).',
  'podcasts.pdfImport.guideBlocks': 'Paragraphs and lists = narration blocks for voice-over.',
  'podcasts.pdfImport.guidePdf':
    'Every PDF is different: always review generated Markdown and adjust # / ## before import.',
  'podcasts.error.retry': 'Retry',
  'podcasts.filters.allCategories': 'All categories',
  'podcasts.filters.categoryLabel': 'Category',
  'podcasts.filters.searchLabel': 'Text search',
  'podcasts.filters.searchPlaceholder': 'Title, description…',
  'podcasts.filters.viewCategories': 'Categories view',
  'podcasts.filters.viewList': 'List view',
  'podcasts.form.descriptionLabel': 'Description',
  'podcasts.form.descriptionPlaceholder': 'Describe your podcast…',
  'podcasts.form.namePlaceholder': 'Podcast name',
  'podcasts.form.titleLabel': 'Title',
  'podcasts.episodeOrder.label': 'Episode order',
  'podcasts.episodeOrder.help': 'Playback order in the podcast (matches document order on import).',
  'podcasts.episodeOrder.ariaGroup': 'Episode order',
  'podcasts.episodeOrder.newestFirst': 'Newest → oldest',
  'podcasts.episodeOrder.oldestFirst': 'Oldest → newest',
  'podcasts.language.ariaGroup': 'Podcast language',
  'podcasts.language.help': 'Among the platform\'s 11 languages — to align the podcast with the same locale as the learner player and interface.',
  'podcasts.language.label': 'Language',
  'podcasts.lightbox.ariaClose': 'Close',
  'podcasts.lightbox.ariaDialog': 'Podcast cover preview',
  'podcasts.list.actionActivate': 'Activate',
  'podcasts.list.actionDeactivate': 'Deactivate',
  'podcasts.list.actionDelete': 'Delete',
  'podcasts.list.filterAll': 'All',
  'podcasts.list.filterArchived': 'Archived',
  'podcasts.list.archivePodcast': 'Archive podcast',
  'podcasts.list.restoreFromArchive': 'Restore from archive',
  'podcasts.list.archiveSuccess': 'Podcast archived',
  'podcasts.list.archiveError': 'Could not archive the podcast.',
  'podcasts.list.restoreSuccess': 'Podcast restored',
  'podcasts.list.restoreError': 'Could not restore the podcast.',
  'podcasts.list.badgeArchived': 'Archived',
  'podcasts.list.emptyArchivedNone': 'No archived podcasts.',
  'podcasts.list.emptyAllArchived': 'All podcasts are archived. Open the « Archived » tab to see them.',
  'podcasts.list.actionManageEpisodes': 'Manage episodes',
  'podcasts.list.actionPreview': 'Preview & listen',
  'podcasts.list.coverPreviewAria': 'Podcast cover preview {{title}}',
  'podcasts.list.createFirst': 'Create a podcast',
  'podcasts.list.emptySubtitle': 'Create your first podcast to get started',
  'podcasts.list.emptyTitle': 'No podcasts',
  'podcasts.list.loadError': 'Unable to load podcasts.',
  'podcasts.list.enterpriseBadge': 'Company',
  'podcasts.list.enterpriseNeedTitle': 'Need defined by your company',
  'podcasts.list.episodesShort': '{{count}} ep.',
  'podcasts.list.groupPodcastCount': '{{count}} podcast(s)',
  'podcasts.list.subscribersShort': '{{count}} sub.',
  'podcasts.list.uncategorized': 'Uncategorized',
  'podcasts.modal.errorRequiredFields': 'Title and description are required.',
  'podcasts.modal.errorSaveFailed': 'Error while saving.',
  'podcasts.modal.subtitleNew': 'Enter title, description and options. You can add episodes afterwards.',
  'podcasts.modal.titleEdit': 'Edit podcast',
  'podcasts.modal.titleNew': 'New podcast',
  'podcasts.page.episodesLink': 'Episodes',
  'podcasts.page.newButton': 'New',
  'podcasts.page.title': 'Podcasts',
  'podcasts.page.totalCount': '{{count}} podcast(s)',
  'podcasts.pagination.pageOf': 'Page {{page}} / {{total}}',
  'podcasts.pagination.totalSummary': '{{count}} podcast(s) in total',
  'podcasts.player.listen': 'Listen',
  'podcasts.player.noAudio': 'No audio',
  'podcasts.player.pause': 'Pause',
  'podcasts.learner.pageTitle': 'Listen to podcast',
  'podcasts.learner.invalidLink': 'Invalid or expired link',
  'podcasts.learner.loadError': 'Unable to load podcast',
  'podcasts.learner.loading': 'Loading podcast…',
  'podcasts.learner.noEpisodes': 'No published episode available.',
  'podcasts.learner.listen': 'Listen',
  'podcasts.learner.pause': 'Pause',
  'podcasts.preview.emptySubtitle': 'This podcast has no episodes yet',
  'podcasts.preview.emptyTitle': 'No episodes',
  'podcasts.preview.episodeCount': '{{count}} episode(s)',
  'podcasts.preview.fallbackDescription': 'Listen to available audio episodes.',
  'podcasts.preview.playAll': 'Play all',
  'podcasts.preview.title': 'Podcast preview',
  'podcasts.public': 'Podcast',
  'podcasts.public.subscribeButton': 'Subscribe for updates',
  'podcasts.public.subscribeModalTitle': 'Get new episodes by email',
  'podcasts.public.subscribeModalIntro': 'Enter your details. We will email you when a new episode is published.',
  'podcasts.public.fieldFirstName': 'First name',
  'podcasts.public.fieldLastName': 'Last name',
  'podcasts.public.fieldEmail': 'Email',
  'podcasts.public.fieldCompany': 'Company (optional)',
  'podcasts.public.submitSubscribe': 'Confirm subscription',
  'podcasts.public.cancel': 'Cancel',
  'podcasts.public.done': 'Done',
  'podcasts.public.subscribeSuccess': 'Thank you! You will be notified for each new episode.',
  'podcasts.public.subscribeError': 'Could not complete subscription. Please try again later.',
  'podcasts.public.subscribeValidationRequired': 'First name, last name and email are required.',
  'podcasts.public.totalDuration': '{{duration}} total',
  'podcasts.public.listenExcerpt': 'Listen to excerpt',
  'podcasts.playback.resumeAt': 'Resume at {{time}}',
  'podcasts.playback.listened': 'Listened',
  'podcasts.playback.listenedCount': '{{count}} listened',
  'podcasts.share.assignedLearnersHeading': '{{count}} learner(s) assigned',
  'podcasts.share.copied': 'Copied',
  'podcasts.share.copyLink': 'Copy link',
  'podcasts.share.emailPlaceholder': 'Email *',
  'podcasts.share.errorCreateLearner': 'Unable to create learner.',
  'podcasts.share.errorPublicLink': 'Unable to generate public link.',
  'podcasts.share.errorRemoveLearner': 'Unable to remove learner.',
  'podcasts.share.errorShare': 'Unable to share this podcast.',
  'podcasts.share.firstNamePlaceholder': 'First name *',
  'podcasts.share.generatingLink': 'Generating link…',
  'podcasts.share.hideQuickCreate': 'Hide quick create',
  'podcasts.share.lastNamePlaceholder': 'Last name *',
  'podcasts.share.loadingAssigned': 'Loading…',
  'podcasts.share.loadingLearners': 'Loading learners...',
  'podcasts.share.messageAccessRemoved': 'Access removed for the learner.',
  'podcasts.share.messageCreatedAndShared': 'Account created/linked, podcast shared and invitation email sent.',
  'podcasts.share.messageShared': 'Podcast shared and invitation sent to the learner.',
  'podcasts.share.noAssignedLearners': 'No learner assigned to this podcast.',
  'podcasts.share.noLearnerAvailable': 'No learner available.',
  'podcasts.share.panelTitle': 'Share podcast',
  'podcasts.share.publicLinkSection': 'Public link (no login)',
  'podcasts.share.removeAccessTitle': 'Remove access',
  'podcasts.share.searchLearnerPlaceholder': 'Search a learner (last name, first name, email)',
  'podcasts.share.selectExistingLearnerLabel': 'Select an existing learner',
  'podcasts.share.showQuickCreate': 'Create new learner',
  'podcasts.share.submitCreateAndInvite': 'Create, share and send invitation',
  'podcasts.share.socialNetworksHeading': 'Share on social',
  'podcasts.share.shareLinkedIn': 'Share on LinkedIn',
  'podcasts.share.shareFacebook': 'Share on Facebook',
  'podcasts.share.shareTwitter': 'Share on X (Twitter)',
  'podcasts.share.publicShareStatsHeading': 'Public page stats',
  'podcasts.share.publicShareStatsVisits': 'Visits (public page)',
  'podcasts.share.publicShareStatsLoading': 'Loading stats…',
  'podcasts.share.publicShareStatsUnavailable': 'Stats unavailable right now.',
  'podcasts.share.publicShareStatsLinkedIn': 'LinkedIn',
  'podcasts.share.publicShareStatsFacebook': 'Facebook',
  'podcasts.share.publicShareStatsTwitter': 'X (Twitter)',
  'podcasts.share.publicSubscribersHeading': 'Subscribers (email notifications)',
  'podcasts.share.publicSubscribersEmpty': 'No subscribers yet.',
  'podcasts.share.publicSubscribersCompany': 'Company',
  'share.publicQrTitle': 'Public link QR code',
  'share.publicQrError': 'Could not generate the QR code.',
  'share.downloadQrPng': 'Download as PNG',
  'podcasts.status.active': 'Active',
  'podcasts.status.inactive': 'Inactive',
  'learningPaths.card.contents': '{{count}} content items',
  'learningPaths.card.delete': 'Delete',
  'learningPaths.card.edit': 'Edit',
  'learningPaths.card.learners': '{{count}} learners',
  'learningPaths.card.publish': 'Publish',
  'learningPaths.card.unpublish': 'Unpublish',
  'learningPaths.card.updatedAt': 'Updated {{date}}',
  'learningPaths.createPanel.cancel': 'Cancel',
  'learningPaths.createPanel.description': 'Name your path; you can add podcasts and courses to it.',
  'learningPaths.createPanel.descriptionLabel': 'Description',
  'learningPaths.createPanel.descriptionPlaceholder': 'Path goal, target audience…',
  'learningPaths.createPanel.nameLabel': 'Path name',
  'learningPaths.createPanel.namePlaceholder': 'e.g. HR onboarding path…',
  'learningPaths.createPanel.submit': 'Create path',
  'learningPaths.createPanel.title': 'New path',
  'learningPaths.deleteDialog.body': 'You are about to delete "{{title}}". This action cannot be undone.',
  'learningPaths.deleteDialog.cancel': 'Cancel',
  'learningPaths.deleteDialog.confirm': 'Delete',
  'learningPaths.deleteDialog.title': 'Delete path',
  'learningPaths.empty.createFirst': 'Create a path',
  'learningPaths.empty.noPathsHint': 'Create your first path by combining podcasts and courses.',
  'learningPaths.empty.noPathsYet': 'No paths yet',
  'learningPaths.empty.noResults': 'No results',
  'learningPaths.empty.noResultsHint': 'Adjust your filters or create a new path.',
  'learningPaths.error.loadFailed': 'Could not load paths',
  'learningPaths.error.retry': 'Retry',
  'learningPaths.filters.allStatuses': 'All statuses',
  'learningPaths.filters.draft': 'Draft',
  'learningPaths.filters.published': 'Published',
  'learningPaths.filters.searchPlaceholder': 'Search for a path…',
  'learningPaths.page.newButton': 'New path',
  'learningPaths.page.subtitle': 'Create ordered training paths',
  'learningPaths.page.title': 'Paths',
  'learningPaths.page.totalCount': '{{count}} paths',
  'learningPaths.pagination.next': 'Next',
  'learningPaths.pagination.pageOf': 'Page {{page}} / {{total}} · {{count}} paths',
  'learningPaths.pagination.previous': 'Previous',
  'learningPaths.status.draft': 'Draft',
  'learningPaths.status.published': 'Published',
  'learningPaths.editor.backToList': 'Back to paths',
  'learningPaths.editor.pathNameLabel': 'Path name *',
  'learningPaths.editor.idCopyHint': 'Path ID (for support)',
  'learningPaths.editor.sectionNavAria': 'Path editor sections',
  'learningPaths.editor.tabContents': 'Contents',
  'learningPaths.editor.tabLearners': 'Learners',
  'webinars.card.code': 'Code {{code}}',
  'webinars.card.copyLink': 'Copy link',
  'webinars.card.exportAttendance': 'Download attendance (CSV)',
  'webinars.card.attendanceExportError': 'Could not download the attendance file.',
  'webinars.card.delete': 'Delete',
  'webinars.card.edit': 'Edit',
  'webinars.card.openRoom': 'Open room',
  'webinars.card.publicSignupBadge': 'Public signup',
  'webinars.deleteDialog.body': 'Delete "{{title}}"? This action cannot be undone.',
  'webinars.deleteDialog.cancel': 'Cancel',
  'webinars.deleteDialog.confirm': 'Delete',
  'webinars.deleteDialog.title': 'Delete webinar',
  'webinars.eligibility.default': 'Webinars are not available for your account (check the API connection or contact your administrator).',
  'webinars.eligibility.moduleDisabled': 'The webinars module is not yet enabled for your institution. An administrator must enable \'Enable webinars\' in Institution → Webinars.',
  'webinars.eligibility.noInstitution': 'No institution is linked to your account.',
  'webinars.eligibility.notAuthorized': 'Your account is not on the list of authorized hosts. Ask your institution to add you (Webinar management → Authorized hosts).',
  'webinars.eligibility.roleNotAllowed': 'Your role does not allow you to host webinars with this account.',
  'webinars.empty.createFirst': 'Create a webinar',
  'webinars.empty.noResults': 'No results',
  'webinars.empty.noResultsHint': 'Change your search.',
  'webinars.empty.noWebinarsHint': 'Create a room and invite your participants from the side panel.',
  'webinars.empty.noWebinarsYet': 'No webinars yet',
  'webinars.externalInvite.closeButton': 'Close',
  'webinars.externalInvite.description': 'People who are not on the learner list: each address receives an email with the room link and a calendar attachment.',
  'webinars.externalInvite.done': 'Done.',
  'webinars.externalInvite.duplicatesCount_one': '{{count}} already invited',
  'webinars.externalInvite.duplicatesCount_other': '{{count}} already invited',
  'webinars.externalInvite.emailsHint': 'One address per line, or separated by commas. Duplicates and invitations already sent are ignored.',
  'webinars.externalInvite.emailsLabel': 'Email addresses',
  'webinars.externalInvite.emailsPlaceholder': 'john.doe@example.com\njane@example.org',
  'webinars.externalInvite.errorMax200': 'Maximum 200 addresses per send.',
  'webinars.externalInvite.errorMinOne': 'Enter at least one valid email address.',
  'webinars.externalInvite.errorsLabel': 'errors: {{errors}}',
  'webinars.externalInvite.sendButton': 'Send invitations',
  'webinars.externalInvite.sendFailed': 'Send failed',
  'webinars.externalInvite.sentCount_one': '{{count}} email sent',
  'webinars.externalInvite.sentCount_other': '{{count}} emails sent',
  'webinars.externalInvite.skippedCount_one': '{{count}} skipped',
  'webinars.externalInvite.skippedCount_other': '{{count}} skipped',
  'webinars.externalInvite.title': 'Email invitations',
  'webinars.invite.duplicatesCount_one': '{{count}} already invited',
  'webinars.invite.duplicatesCount_other': '{{count}} already invited',
  'webinars.invite.errorsLabel': 'errors: {{errors}}',
  'webinars.invite.externalButton': 'Off-list (email)',
  'webinars.invite.failed': 'Failed',
  'webinars.invite.groupSuccess': 'Group invitations processed (emails sent to members).',
  'webinars.invite.inviteButton': 'Invite',
  'webinars.invite.learnersHelp': 'Search for a learner at your institution: an <strong>invitation email</strong> (link + calendar) is sent with each invitation.',
  'webinars.invite.learnersTitle': 'Learners',
  'webinars.invite.noCandidates': 'No learners to show (try another search or use \'Off-list\' for external emails).',
  'webinars.invite.noInvitations': 'No invitations recorded.',
  'webinars.invite.searchLabel': 'Search for a learner',
  'webinars.invite.searchPlaceholder': 'Name or email…',
  'webinars.invite.searching': 'Searching…',
  'webinars.invite.sentCount_one': '{{count}} invitation sent (email)',
  'webinars.invite.sentCount_other': '{{count}} invitations sent (email)',
  'webinars.invite.sentInvitationsToggle': 'Invitations sent',
  'webinars.page.newButton': 'New webinar',
  'webinars.page.searchPlaceholder': 'Search…',
  'webinars.page.subtitle': 'List of your rooms — open the panel to create, edit or invite participants.',
  'webinars.page.title': 'Webinars',
  'webinars.panel.backgroundHint':
    'Image behind shared content for participants: enter a URL (https or http), upload a file (saved to My images), or pick an existing one from your library. Leave empty for the built-in Lyrra visual.',
  'webinars.panel.backgroundLabel': 'Background image (participant view)',
  'webinars.panel.backgroundPlaceholder': 'https://… or My images path',
  'webinars.panel.backgroundUrlLabel': 'Image address (URL)',
  'webinars.panel.backgroundUploadTitle': 'Upload an image (My images)',
  'webinars.panel.backgroundLibraryTitle': 'My images',
  'webinars.panel.backgroundLibraryEmpty': 'No images yet — upload above or from the Images page in your dashboard.',
  'webinars.panel.backgroundOpenImagesPage': 'Open Images page',
  'webinars.panel.backgroundUseDefault': 'Use default Lyrra background',
  'webinars.panel.backgroundUploading': 'Uploading…',
  'webinars.panel.backgroundUploadError': 'Image upload failed.',
  'webinars.panel.copyLink': 'Copy link',
  'webinars.panel.createButton': 'Create',
  'webinars.panel.createDescription': 'Fill in the information then save. You can invite participants afterwards.',
  'webinars.panel.createTitle': 'New webinar',
  'webinars.panel.dateLabel': 'Day (optional)',
  'webinars.panel.deleteButton': 'Delete',
  'webinars.panel.descriptionLabel': 'Description',
  'webinars.panel.durationError': 'Enter a duration in hours:minutes format (e.g. 1:00 or 0:45).',
  'webinars.panel.durationHint': 'Required if a day is set. E.g. 1:30 for 1 h 30, 0:45 for 45 min.',
  'webinars.panel.durationLabel': 'Duration (h:mm)',
  'webinars.panel.editTitle': 'Webinar',
  'webinars.panel.loading': 'Loading…',
  'webinars.panel.openRoom': 'Open room',
  'webinars.panel.presentersLabel': 'Co-presenters',
  'webinars.panel.roomCode': 'Room code:',
  'webinars.panel.timeHint': 'If empty: 9:00.',
  'webinars.panel.timeLabel': 'Start time',
  'webinars.panel.titleLabel': 'Title',
  'webinars.panel.waitingHtmlLabel': 'Waiting room HTML (optional)',
  'webinars.panel.waitingHtmlPlaceholder': '<p>Welcome…</p>',
  'webinars.panel.publicAccessTitle': 'Public signup (form)',
  'webinars.panel.publicAccessHelp':
    'Participants who are not co-presenters fill in a form after signing in to Lyrra: email, password (they choose it) and any extra fields you add below.',
  'webinars.panel.publicFormFixedFieldsHint':
    'Email and password (min. 8 characters) are always required for participants; add optional or required extra fields here.',
  'webinars.panel.publicFormAddFieldButton': 'Add field',
  'webinars.panel.publicFormFieldNumber': 'Field {{n}}',
  'webinars.panel.publicFormFieldLabel': 'Label shown',
  'webinars.panel.publicFormFieldType': 'Type',
  'webinars.panel.publicFormFieldTypeText': 'Short text',
  'webinars.panel.publicFormFieldTypeTextarea': 'Long text',
  'webinars.panel.publicFormFieldRequired': 'Required',
  'webinars.panel.publicFormFieldRemove': 'Remove',
  'webinars.panel.publicFormFieldIdHint': 'Technical id:',
  'webinars.panel.publicFormFieldLabelRequired': 'Each field must have a label.',
  'webinars.panel.publicAccessLoginLabel': 'Signup form login',
  'webinars.panel.publicAccessPasswordLabel': 'Signup form password',
  'webinars.panel.publicAccessPasswordPlaceholderNew': 'At least 8 characters',
  'webinars.panel.publicAccessPasswordPlaceholderKeep': 'Leave blank to keep the current password',
  'webinars.panel.publicAccessPasswordHint':
    'On create or first activation: password required (min. 8). Later: leave blank to keep the current password.',
  'webinars.panel.publicAccessLoginRequired': 'Enter a login for public signup.',
  'webinars.panel.publicAccessPasswordRequired': 'Set a password of at least 8 characters.',
  'webinars.panel.publicAccessPasswordMin': 'The public password must be at least 8 characters.',
  'webinars.status.draft': 'Draft',
  'webinars.status.ended': 'Ended',
  'webinars.status.live': 'Live',
  'webinars.status.scheduled': 'Scheduled',
  'videos.addPanel.cancel': 'Cancel',
  'videos.addPanel.continue': 'Continue',
  'videos.addPanel.description': 'Import MP4, WebM, or MOV to use them in your training.',
  'videos.addPanel.dropzoneFormats': 'MP4, WebM, MOV',
  'videos.addPanel.dropzoneLabel': 'Click to choose a video',
  'videos.addPanel.title': 'Add a video',
  'videos.addPanel.titleLabel': 'Title',
  'videos.addPanel.titlePlaceholder': 'Video title…',
  'videos.card.delete': 'Delete',
  'videos.card.edit': 'Edit',
  'videos.card.noThumbnail': 'No thumbnail',
  'videos.card.publishedStudio': 'Published (editable in Studio)',
  'videos.card.draft': 'Draft',
  'videos.deleteDialog.body': '« {{title}} » will be permanently deleted.',
  'videos.deleteDialog.cancel': 'Cancel',
  'videos.deleteDialog.confirm': 'Delete',
  'videos.deleteDialog.title': 'Delete video?',
  'videos.empty.addButton': 'Add a video',
  'videos.empty.description': 'Import your first video to manage it and use it in your training.',
  'videos.empty.title': 'No videos',
  'videos.page.addButton': 'Add a video',
  'videos.page.count_one': '{{count}} video in your library',
  'videos.page.count_other': '{{count}} videos in your library',
  'videos.page.title': 'Videos',
  'videos.page.studioButton': 'Studio',
  'videos.studio.title': 'Video studio',
  'videos.studio.rail.prep': 'Prep',
  'videos.studio.rail.capture': 'Capture',
  'videos.studio.rail.post': 'Edit',
  'videos.studio.rail.aria': 'Video studio steps',
  'videos.studio.badge.setup': 'Setup',
  'videos.studio.badge.capture': 'Capture',
  'videos.studio.badge.recording': 'Recording',
  'videos.studio.badge.post': 'Edit',
  'videos.studio.cannotLeaveRecord': 'Stop recording before switching step.',
  'videos.studio.railNeedRecording': 'Record a video to open the edit step.',
  'videos.studio.sidebar.captureHelp':
    'Recording captures the entire shared feed (screen, tab, or window). Cropping to the useful area will be done in the Edit step.',
  'videos.studio.sidebar.sources': 'Sources',
  'videos.studio.sidebar.recordingLive': 'Recording in progress',
  'videos.studio.sidebar.finalizeHint': 'Save to your library using the button in the top bar.',
  'videos.studio.canvas.setupEmpty': 'Read the side panel, then go to the Capture step.',
  'videos.studio.stepSetup': 'Setup',
  'videos.studio.stepCapture': 'Capture',
  'videos.studio.stepPost': 'Edit',
  'videos.studio.titleLabel': 'Video title',
  'videos.studio.titlePlaceholder': 'My recording…',
  'videos.studio.saveSetup': 'Continue to capture',
  'videos.studio.fieldDescription': 'Description',
  'videos.studio.fieldCategories': 'Categories',
  'videos.studio.categoriesHint': 'One or more categories from your institution (optional).',
  'videos.studio.categoriesEmptyInstitution': 'No categories are set up for your institution.',
  'videos.studio.audioTrackPlanned': 'Planned audio track',
  'videos.studio.voiceHumanPlan': 'Human voice (recorded microphone)',
  'videos.studio.voiceAiPlan': 'AI voice (choose for the edit step)',
  'videos.studio.postVoiceHumanKeep':
    'You chose human voice: the video keeps the microphone audio. Finalize when you are ready.',
  'videos.studio.titleUntitled': 'Untitled',
  'videos.studio.voiceAiNoVoices': 'No AI voices are available right now.',
  'videos.studio.postVoiceAiPipeline':
    'AI voice: polish the text, generate the audio track, then apply it to the video.',
  'videos.studio.allowMedia': 'Allow screen and microphone',
  'videos.studio.controlsHint':
    'Tip: to avoid recording this bar, share a tab or a window instead of the full screen, or use a second display. Space = pause / resume when this page is focused.',
  'videos.studio.warnTabOnly':
    'You selected a single tab: only that tab will be recorded, even if you open other tabs. To capture multiple tabs or windows, restart capture and choose "Entire Screen".',
  'videos.studio.templateMenuTitle': 'Templates menu',
  'videos.studio.templateEmpty': 'No saved template',
  'videos.studio.templatesHintCompact': 'Click + to save, apply, or delete a template.',
  'videos.studio.overlayAddMusic': 'Background music',
  'videos.studio.musicInvalidType': 'Invalid format. Use an audio file (MP3, WAV, M4A, OGG).',
  'videos.studio.musicTooLarge': 'Audio file must not exceed 50 MB.',
  'videos.studio.musicDefault': 'Music',
  'videos.studio.musicAdded': 'Background music added to timeline.',
  'videos.studio.musicUploadError': 'Unable to add background music.',
  'videos.studio.layerScreenBorder': 'Outer border',
  'videos.studio.layerScreenBorderSize': 'Size',
  'videos.studio.layerScreenBorderColor': 'Color',
  'videos.studio.layerScreenBorderRadius': 'Corner radius',
  'videos.studio.segmentToolsTitle': 'Segment tools',
  'videos.studio.segmentZoom': 'Zoom',
  'videos.studio.segmentResetView': 'Reset view',
  'videos.studio.segmentTransition': 'Transition',
  'videos.studio.transitionNone': 'None',
  'videos.studio.transitionFade': 'Fade',
  'videos.studio.transitionWipeLeft': 'Wipe →',
  'videos.studio.transitionSlideUp': 'Slide ↑',
  'videos.studio.textEditor.toolbar': 'Text formatting',
  'videos.studio.textEditor.bold': 'Bold',
  'videos.studio.textEditor.italic': 'Italic',
  'videos.studio.textEditor.underline': 'Underline',
  'videos.studio.textEditor.textColor': 'Text color (selection)',
  'videos.studio.textEditor.size': 'Size',
  'videos.studio.textEditor.font': 'Font',
  'videos.studio.textEditor.fontFamily.system': 'System',
  'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
  'videos.studio.textEditor.fontFamily.serif': 'Serif',
  'videos.studio.textEditor.fontFamily.mono': 'Mono',
  'videos.studio.textEditor.fontFamily.script': 'Script',
  'videos.studio.controlsHintTab':
    'You are sharing another tab: the recording does not include this Studio page (so these buttons are not in the video). Keep Studio open in another browser window or on a second screen next to your content. For Pause, Stop, or Done, switch to the window where Studio is open (Alt+Tab or Cmd+Tab). Space only works when the Studio page is focused.',
  'videos.studio.controlsHintMonitor':
    'You are sharing the full screen: this bar may appear in the recording. Prefer sharing a Tab or Window, or move the controls to another monitor.',
  'videos.studio.controlsHintWindow':
    'You are sharing one window: the rest of the desktop (and other windows) is usually not recorded. Use the buttons below for Pause, Stop, or Done.',
  'videos.studio.startRecord': 'Record',
  'videos.studio.pause': 'Pause',
  'videos.studio.resume': 'Resume',
  'videos.studio.stop': 'Stop',
  'videos.studio.terminer': 'Done',
  'videos.studio.processingUpload': 'Uploading and transcribing…',
  'videos.studio.timeline': 'Playback',
  'videos.studio.transcript': 'Recognized text',
  'videos.studio.transcriptRefining': 'Refining transcription…',
  'videos.studio.transcriptPlaceholder': 'Recognized text from your recording will appear here…',
  'videos.studio.voiceMode': 'Audio track',
  'videos.studio.voiceOriginal': 'My voice (recorded mic)',
  'videos.studio.voiceAi': 'AI voice (Mistral TTS)',
  'videos.studio.voiceSelect': 'Voice',
  'videos.studio.polish': 'Prepare text for AI voice',
  'videos.studio.generateTts': 'Generate voiceover',
  'videos.studio.applyVoiceover': 'Apply AI voice to video',
  'videos.studio.preview': 'Preview',
  'videos.studio.preview.noFile': 'No video file to preview.',
  'videos.studio.bgImage': 'Background image',
  'videos.studio.bgImageChoose': 'Choose',
  'videos.studio.bgImageInvalidType': 'Invalid format. Use an image (PNG, JPG, WebP).',
  'videos.studio.bgImageTooLarge': 'Image must not exceed 10 MB.',
  'videos.studio.bgImageLoadError': 'Could not load the background image.',
  'videos.studio.bgImageUploading': 'Uploading…',
  'videos.studio.bgImageUploadError': 'Could not upload the image. Try again.',
  'videos.studio.bgImageLibraryTitle': 'Video studio background',
  'videos.studio.postLayoutTitle': 'Frame & background',
  'videos.studio.postLayoutHint':
    'Preview matches the published video: save the draft then publish to apply background, size, and position.',
  'videos.studio.bgImageVideoSize': 'Video size',
  'videos.studio.dragToMove': 'Drag to move',
  'videos.studio.finalize': 'Save to library',
  'videos.studio.saveDraft': 'Draft',
  'videos.studio.publish': 'Publish',
  'videos.studio.successDraft': 'Video saved as draft',
  'videos.studio.successPublish': 'Video published to your library',
  'videos.studio.processingPublish': 'Processing video…',
  'videos.studio.back': 'Back to videos',
  'videos.studio.errorMedia': 'Could not access screen or microphone.',
  'videos.studio.errorInsecureContext':
    'Screen capture requires HTTPS or localhost. Open the app via https:// or http://127.0.0.1 / localhost.',
  'videos.studio.errorNotSupported':
    'This browser or device does not support screen capture here. Use an up-to-date Chrome or Edge on desktop.',
  'videos.studio.errorMicDenied':
    'Microphone blocked: allow microphone for this site (lock or info icon in the address bar), then try again.',
  'videos.studio.errorScreenDenied':
    'Screen sharing denied: pick a screen or tab in the browser dialog and confirm « Share ».',
  'videos.studio.errorMicMissing': 'No microphone found. Check hardware and system settings.',
  'videos.studio.errorScreenMissing': 'Screen sharing is not available on this device or browser.',
  'videos.studio.errorMicBusy': 'Microphone is in use by another app. Close it and try again.',
  'videos.studio.errorScreenBusy': 'Could not capture the screen (resource busy). Retry or restart the browser.',
  'videos.studio.errorScreenCancelled': 'Screen sharing cancelled.',
  'videos.studio.errorMicCancelled': 'Microphone access cancelled.',
  'videos.studio.errorMicGeneric': 'Could not access the microphone.',
  'videos.studio.errorScreenGeneric': 'Could not capture the screen.',
  'videos.studio.errorScreenEnded': 'Screen sharing stopped.',
  'videos.studio.mediaHints':
    'Chrome or Edge on desktop. If you use http:// with a LAN IP (e.g. 192.168.x.x), capture is often blocked: use http://localhost:5173 or valid HTTPS.',
  'videos.studio.errorRecord': 'Recording is not supported in this browser.',
  'videos.studio.errorUpload': 'Upload failed.',
  'videos.studio.errorLoadDraft': 'Could not load draft.',
  'videos.studio.errorTranscribe': 'Transcription unavailable.',
  'videos.studio.errorPolish': 'AI polish failed.',
  'videos.studio.errorTts': 'Text-to-speech failed.',
  'videos.studio.errorMux': 'Audio merge failed.',
  'videos.studio.muxSuccess': 'AI voice applied to the video',
  'videos.studio.successFinal': 'Video saved to your library',
  'videos.studio.generatingTimeline': 'Generating timeline…',
  'videos.studio.crop': 'Crop',
  'videos.studio.cropReset': 'Reset crop',
  'videos.studio.cropActive': 'Crop active — drag or resize the region on the video',
  'videos.studio.cropDone': 'Done',
  'videos.studio.cropApplied': 'Crop applied',
  'videos.studio.cropEdit': 'Edit',
  'videos.studio.cropRemove': 'Remove',
  'videos.studio.trimIn': 'IN',
  'videos.studio.trimOut': 'OUT',
  'videos.studio.trimDuration': 'duration',
  'videos.studio.errorCamDenied': "Camera blocked: click the padlock icon in the address bar and allow the camera for this site.",
  'videos.studio.errorCamMissing': "No camera detected. Plug in a webcam or check your system settings.",
  'videos.studio.errorCamBusy': "The camera is already in use by another application. Close it and try again.",
  'videos.studio.errorCamGeneric': "Unable to access the camera.",
  'videos.studio.leaveWhileRecording': "A recording is in progress and will be lost if you leave. Leave anyway?",
  'videos.studio.leaveUnsaved': "You have unsaved changes. Leave the studio anyway?",
  'videos.studio.unsavedChanges': "Unsaved changes",
  'videos.studio.saveDraftShortcut': "Save draft (Ctrl+S)",
  'videos.studio.pendingRecordingTitle': "An unsent recording was found",
  'videos.studio.pendingRecordingBody': "Its upload was interrupted. You can upload it now to edit it, or delete it.",
  'videos.studio.pendingRecordingResume': "Upload and edit",
  'videos.studio.pendingRecordingDiscard': "Delete",
  'videos.studio.uploadFailedTitle': "The recording upload failed",
  'videos.studio.uploadFailedBody': "Your take is kept in this browser. Check your connection and try again: nothing is lost.",
  'videos.studio.uploadRetry': "Retry upload",
  'videos.studio.captionsInProgress': "Transcription and subtitles in progress… you can keep editing.",
  'videos.studio.muxConfirm': "The video’s microphone track will be permanently replaced by the AI voice. Continue?",
  'videos.studio.play': "Play",
  'videos.studio.back5': "Back 5 s",
  'videos.studio.forward5': "Forward 5 s",
  'videos.studio.keySpace': "Space",
  'videos.studio.mute': "Mute",
  'videos.studio.unmute': "Unmute",
  'videos.studio.fullscreen': "Full screen",
  'videos.studio.showSubtitles': "Show subtitles",
  'videos.studio.hideSubtitles': "Hide subtitles",
  'videos.studio.subtitlesTrackLabel': "Subtitles",
  'videos.studio.seekBar': "Playback position",
  'videos.preview.noFile': 'No file available.',
  'videos.status.draft': 'Draft',
  'videos.status.published': 'Published',
  'sounds.card.delete': 'Delete',
  'sounds.card.download': 'Download',
  'sounds.card.mute': 'Mute',
  'sounds.deleteDialog.body': '« {{title}} » will be permanently removed from the server and your library.',
  'sounds.deleteDialog.cancel': 'Cancel',
  'sounds.deleteDialog.confirm': 'Delete',
  'sounds.deleteDialog.title': 'Delete this sound?',
  'sounds.empty.description': 'Import your files: they are stored in the database and copied to the server so nothing is lost.',
  'sounds.empty.importButton': 'Import a sound',
  'sounds.empty.title': 'No sounds imported',
  'sounds.fileSize.bytes': '{{size}} B',
  'sounds.fileSize.kb': '{{size}} KB',
  'sounds.fileSize.mb': '{{size}} MB',
  'sounds.importPanel.cancel': 'Cancel',
  'sounds.importPanel.changeFile': 'Change file',
  'sounds.importPanel.createTag': 'Create « {{tag}} »',
  'sounds.importPanel.description': 'Add an audio file to your library for your learning paths.',
  'sounds.importPanel.descriptionLabel': 'Description',
  'sounds.importPanel.descriptionPlaceholder': 'Describe this sound…',
  'sounds.importPanel.dropzoneBrowse': 'browse',
  'sounds.importPanel.dropzoneFormats': 'MP3, WAV, OGG, M4A, AAC, FLAC',
  'sounds.importPanel.dropzoneLabel': 'Drop an audio file or',
  'sounds.importPanel.optional': '(optional)',
  'sounds.importPanel.submit': 'Import',
  'sounds.importPanel.submitting': 'Sending…',
  'sounds.importPanel.tagsHint': 'Type and press Enter · New tags are created automatically',
  'sounds.importPanel.tagsLabel': 'Tags',
  'sounds.importPanel.tagsPlaceholder': 'Add tags…',
  'sounds.importPanel.title': 'Import a sound',
  'sounds.importPanel.titleLabel': 'Title',
  'sounds.importPanel.titlePlaceholder': 'Sound name…',
  'sounds.importPanel.titleRequired': 'Title is required',
  'sounds.noResults': 'No sound matches « {{query}} »',
  'sounds.page.count_one': '{{count}} file — saved in the database and on the server',
  'sounds.page.count_other': '{{count}} files — saved in the database and on the server',
  'sounds.page.importButton': 'Import',
  'sounds.page.searchPlaceholder': 'Title, description, tag…',
  'sounds.page.title': 'Sounds',
  'documents.count_one': '{{count}} document',
  'documents.count_other': '{{count}} documents',
  'documents.delete.body': 'The document "{{filename}}" will be removed from your library.',
  'documents.delete.cancel': 'Cancel',
  'documents.delete.confirm': 'Delete',
  'documents.delete.irreversible': 'This action cannot be undone.',
  'documents.delete.title': 'Delete document?',
  'documents.importPanel.cancel': 'Cancel',
  'documents.importPanel.chooseFile': 'Choose a PDF document',
  'documents.importPanel.description': 'The document will be available in your PDF blocks and in this library.',
  'documents.importPanel.descriptionLabel': 'Description',
  'documents.importPanel.descriptionPlaceholder': 'Description (optional)',
  'documents.importPanel.fileHint': 'The document will be available in your PDF block and in this library.',
  'documents.importPanel.submit': 'Import',
  'documents.importPanel.title': 'Import PDF',
  'documents.importPanel.titleLabel': 'Title',
  'documents.importPanel.titlePlaceholder': 'Document title',
  'documents.importPanel.uploading': 'Uploading',
  'documents.importPdf': 'Import PDF',
  'documents.loading': 'Loading library…',
  'documents.newDocument': 'New document',
  'documents.noDocuments': 'No documents',
  'documents.noDocumentsHint': 'Import your PDFs here to reuse them later in the PDF block.',
  'documents.noResults': 'No documents match your search.',
  'documents.pages_one': '{{count}} page',
  'documents.pages_other': '{{count}} pages',
  'documents.previewTitle': 'Document preview',
  'documents.searchPlaceholder': 'Search for a PDF...',
  'documents.share.accessRemoved': 'Access removed for the learner.',
  'documents.share.assignedTitle': 'Learners with access',
  'documents.share.close': 'Close',
  'documents.share.createAndShare': 'Create, share, and send invitation',
  'documents.share.createError': 'Could not create the learner.',
  'documents.share.createLearner': 'Create new learner',
  'documents.share.createdSuccess': 'Account created/linked, document shared, and email sent.',
  'documents.share.creating': 'Sending...',
  'documents.share.emailPlaceholder': 'Email *',
  'documents.share.firstNamePlaceholder': 'First name *',
  'documents.share.hideCreateLearner': 'Hide quick create',
  'documents.share.lastNamePlaceholder': 'Last name *',
  'documents.share.loadingAssigned': 'Loading…',
  'documents.share.loadingLearners': 'Loading learners...',
  'documents.share.noAssigned': 'No learners assigned to this document.',
  'documents.share.noLearners': 'No learners available.',
  'documents.share.removeButton': 'Remove',
  'documents.share.removeError': 'Could not remove access.',
  'documents.share.searchPlaceholder': 'Search for a learner (first name, last name, email)',
  'documents.share.selectLearner': 'Select an existing learner',
  'documents.share.shareButton': 'Share',
  'documents.share.shareError': 'Could not share the document.',
  'documents.share.sharedSuccess': 'Document shared and invitation sent.',
  'documents.share.title': 'Share document',
  'documents.subtitle': 'Reusable PDF document library for the designer.',
  'documents.title': 'Documents',
  'documents.toast.importError': 'Import failed',
  'documents.toast.importErrorDefault': 'The import failed.',
  'documents.toast.importSuccess': 'Document imported',
  'documents.toast.importSuccessDesc': '{{filename}} has been added to your document library.',
  'documents.updating': 'Updating…',
  'help.categories.analytics.description': 'Dashboards, filters and exports to steer the impact of training.',
  'help.categories.analytics.title': 'Analytics & reports',
  'help.categories.apprenants.description': 'Track enrollments, cohorts and participant progress.',
  'help.categories.apprenants.title': 'Learners, groups & tracking',
  'help.categories.demarrage.description': 'Learn the interface, your profile and daily navigation in Lyrra.',
  'help.categories.demarrage.title': 'Getting started & account',
  'help.categories.donnees.description': 'GDPR, retention, data subject rights and security best practices.',
  'help.categories.donnees.title': 'Personal data & security',
  'help.categories.eduflow.description': 'Design learning paths with blocks, connections and navigation logic.',
  'help.categories.eduflow.title': 'Paths & EduFlow designer',
  'help.categories.evaluation.description': 'Quizzes, assessments, surveys and feedback to measure learning outcomes.',
  'help.categories.evaluation.title': 'Assessment & active learning',
  'help.categories.institution.description': 'Configure your organization, collaborators and content structure.',
  'help.categories.institution.title': 'Institution & team',
  'help.categories.marque.description': 'Align colors, labels and the learner experience with your identity.',
  'help.categories.marque.title': 'Branding & user experience',
  'help.categories.medias.description': 'Manage library, imports and integrated audio / video / image tools in Lyrra.',
  'help.categories.medias.title': 'Media & creative studio',
  'help.categories.parcours-v2.description': 'Campaigns, skills, integrations: everything that enriches training management.',
  'help.categories.parcours-v2.title': 'Advanced features & roadmap',
  'help.categories.podcasts.description': 'Create and distribute long-form audio content outside the block-by-block path.',
  'help.categories.podcasts.title': 'Podcasts & audiobooks',
  'help.categories.publication.description': 'Make a path available to learners and share the right access.',
  'help.categories.publication.title': 'Publication & distribution',
  'help.categories.qualite.description': 'French quality frameworks: traceability, objectives and evidence for audits.',
  'help.categories.qualite.title': 'Quality, Qualiopi & pedagogical compliance',
  'help.categories.support.description': 'When facing a technical or functional issue, know where to look and who to contact.',
  'help.categories.support.title': 'Troubleshooting & support',
  'help.configHint': 'Editorial content: connect your URLs, articles or knowledge base when you are ready.',
  'help.description': 'Documentation and support',
  'help.hubIntro': 'Topics covering Lyrra usage: you will soon be able to link each article to your documentation, tutorials or support tickets.',
  'help.modal.expectedAnswer': 'Expected answer:',
  'help.modal.fallbackHighlight1': 'Article',
  'help.modal.fallbackHighlight2': 'Training platform',
  'help.modal.fallbackHighlight3': 'To be completed',
  'help.modal.fallbackSectionHeading': 'Key takeaways',
  'help.modal.fallbackSectionParagraph': 'The detailed content of this article will be enriched or linked to your internal documentation.',
  'help.modal.fallbackStep1Desc': 'Use the sidebar or the dashboard to access the relevant module.',
  'help.modal.fallbackStep1Title': 'Open the feature',
  'help.modal.fallbackStep2Desc': 'The screens guide you: save often and test in preview mode when available.',
  'help.modal.fallbackStep2Title': 'Follow the business flow',
  'help.modal.fallbackStep3Desc': 'Note your institution\'s specifics (HR processes, naming, brand guidelines) for your team.',
  'help.modal.fallbackStep3Title': 'Document internally',
  'help.modal.fallbackTip': 'If in doubt, check your role (trainer, admin, HR): the displayed menus depend on it.',
  'help.modal.quiz': 'Validation quiz',
  'help.modal.related': 'See also',
  'help.modal.resources': 'Resources',
  'help.modal.steps': 'In practice',
  'help.modal.tips': 'Tips',
  'help.noResults': 'No results for this search.',
  'help.onboarding.ai-designer': 'Training — LyrraStudio AI designer assistant (onboarding)',
  'help.onboarding.blocks': 'Training — EduFlow block types (onboarding)',
  'help.onboarding.branches': 'Training — EduFlow branches & conditions (onboarding)',
  'help.onboarding.catalog-visibility': 'Training — LyrraStudio catalog & visibility (onboarding)',
  'help.onboarding.certificates': 'Training — LyrraStudio issued certificates (onboarding)',
  'help.onboarding.dashboard': 'Training — Lyrra dashboard (onboarding)',
  'help.onboarding.designer-overview': 'Training — EduFlow designer overview (onboarding)',
  'help.onboarding.enrollments': 'Training — LyrraStudio enrollments & participants (onboarding)',
  'help.onboarding.groups-cohorts': 'Training — LyrraStudio groups & cohorts (onboarding)',
  'help.onboarding.media-library': 'Training — LyrraStudio media library (onboarding)',
  'help.onboarding.navigation': 'Training — Lyrra navigation & sidebar (onboarding)',
  'help.onboarding.objectives': 'Training — EduFlow learning objectives (onboarding)',
  'help.onboarding.profile': 'Training — Lyrra profile & preferences (onboarding)',
  'help.onboarding.progress': 'Training — LyrraStudio progress & time spent (onboarding)',
  'help.onboarding.publication-links': 'Training — LyrraStudio access links & sharing (onboarding)',
  'help.onboarding.publication-status': 'Training — LyrraStudio draft, publication and updates (onboarding)',
  'help.onboarding.roles': 'Training — Lyrra roles and access rights (onboarding)',
  'help.onboarding.validation': 'Training — EduFlow path validation (onboarding)',
  'help.onboardingFlowMissing': 'Training not found',
  'help.onboardingFlowMissingDesc': 'Run the db:seed:onboarding-eduflow and db:seed:onboarding-navigation-eduflow scripts (backend), or set the VITE_ONBOARDING_*_FLOW_ID variables.',
  'help.openArticle': 'Open article',
  'help.searchPlaceholder': 'Search a topic…',
  'help.title': 'Help Center',
  'help.topics.analytics-exports.description': 'Prepare extractions for internal or regulatory reporting.',
  'help.topics.analytics-exports.title': 'Exports & HR reporting',
  'help.topics.analytics-indicateurs.description': 'Completion rate, engagement, distribution by status and time on blocks.',
  'help.topics.analytics-indicateurs.title': 'Key indicators',
  'help.topics.analytics-vue.description': 'Customize charts, periods and filter by training to analyze data.',
  'help.topics.analytics-vue.title': 'Analytics overview',
  'help.topics.app-certificats.description': 'Check generated certificates and associated completion conditions.',
  'help.topics.app-certificats.title': 'Issued certificates',
  'help.topics.app-groupes.description': 'Use groups to segment audiences and facilitate campaigns or reports.',
  'help.topics.app-groupes.title': 'Groups & cohorts',
  'help.topics.app-inscriptions.description': 'View the list of learners on a path, their statuses and access history.',
  'help.topics.app-inscriptions.title': 'Enrollments & participants',
  'help.topics.app-progression.description': 'Interpret progress, completions and engagement time within blocks.',
  'help.topics.app-progression.title': 'Progress & time spent',
  'help.topics.demarrage-navigation.description': "The left menu, quick search, language, theme and your account menu.",
  'help.topics.demarrage-navigation.title': "Navigation & side menu",
  'help.topics.demarrage-profil.description': "Your details, photo, password, notifications and interface language.",
  'help.topics.demarrage-profil.title': "Profile & preferences",
  'help.topics.demarrage-roles.description': "Teacher, head teacher, school leadership, multi-site and student: who sees what.",
  'help.topics.demarrage-roles.title': "Roles and access rights",
  'help.topics.demarrage-formation-simple.description': 'Create a course in Simple mode: modules, chapters and blocks without the EduFlow graphical designer.',
  'help.topics.demarrage-formation-simple.title': 'Creating a simple course',
  'help.topics.demarrage-tableau-bord.description': "Create a course, pick up your last edit and read your key figures as soon as you sign in.",
  'help.topics.demarrage-tableau-bord.title': "Teaching dashboard",
  'help.topics.donnees-conservation.description': 'Archiving duration principles for progress and training evidence.',
  'help.topics.donnees-conservation.title': 'Retention period',
  'help.topics.donnees-mots-de-passe.description': 'Password best practices, sessions and access delegation.',
  'help.topics.donnees-mots-de-passe.title': 'Accounts & authentication',
  'help.topics.donnees-rgpd.description': 'Purposes of data collected in Lyrra and the training manager\'s responsibility.',
  'help.topics.donnees-rgpd.title': 'GDPR & processing registry',
  'help.topics.eduflow-blocs.description': "The block families in the palette, how to find the right one and how to set it up.",
  'help.topics.eduflow-blocs.title': "Block types (content, quiz, media…)",
  'help.topics.eduflow-branches.description': "Send students down different paths depending on their quiz answer, offer optional routes and bring branches back together.",
  'help.topics.eduflow-branches.title': "Branches & conditions",
  'help.topics.eduflow-concept.description': "Toolbar, block palette, canvas, Properties panel and preview: the path designer at a glance.",
  'help.topics.eduflow-concept.title': "Designer overview",
  'help.topics.eduflow-ia.description': "Ask the AI to write, check or change blocks, and generate a multiple-choice question from your lesson, while staying in control.",
  'help.topics.eduflow-ia.title': "AI assistant in the designer",
  'help.topics.eduflow-objectifs.description': "Write the course objectives, classify them (knowledge, skills, attitudes, Bloom level) and link them to the blocks that work on them.",
  'help.topics.eduflow-objectifs.title': "Learning objectives",
  'help.topics.eduflow-validation.description': "Find and fix what blocks publishing: missing titles, blocks with no next step, branches that never meet.",
  'help.topics.eduflow-validation.title': "Path validation",
  'help.topics.eval-auto-eval.description': 'Offer adaptive paths based on results.',
  'help.topics.eval-auto-eval.title': 'Self-assessment & remediation',
  'help.topics.eval-ouverte.description': 'Organize the collection of free-form answers and manual or semi-automated grading.',
  'help.topics.eval-ouverte.title': 'Open-ended questions & grading',
  'help.topics.eval-quiz.description': 'Configure scoring, feedback and attempts for effective MCQs.',
  'help.topics.eval-quiz.title': 'Quizzes & multiple-choice questions',
  'help.topics.inst-collaborateurs.description': 'Add trainers, administrators or HR staff and manage permissions.',
  'help.topics.inst-collaborateurs.title': 'Users & invitations',
  'help.topics.inst-structure.description': 'Define what is shared at institution level versus personal projects.',
  'help.topics.inst-structure.title': 'Structure & scope',
  'help.topics.inst-trash.description': 'Find deleted training or podcasts and restore them if needed.',
  'help.topics.inst-trash.title': 'Trash & restoration',
  'help.topics.marque-accessibilite.description': 'Contrast, text alternatives and keyboard navigation in content.',
  'help.topics.marque-accessibilite.title': 'Accessibility & readability',
  'help.topics.marque-charte.description': 'How branding is reflected in the player and learner spaces.',
  'help.topics.marque-charte.title': 'Brand & institutional colors',
  'help.topics.marque-landing.description': 'Showcase your training offering to visitors when the module is enabled.',
  'help.topics.marque-landing.title': 'Public pages & catalog',
  'help.topics.medias-bibliotheque.description': 'Import, organize and reuse videos, sounds, images and documents in your blocks.',
  'help.topics.medias-bibliotheque.title': 'Media library',
  'help.topics.medias-documents.description': 'Attach downloadable resources and link documentation to the path.',
  'help.topics.medias-documents.title': 'Documents & PDFs',
  'help.topics.medias-studio-audio.description': 'Record, mix or use text-to-speech to enhance your content.',
  'help.topics.medias-studio-audio.title': 'Audio & voice studio',
  'help.topics.medias-video.description': 'Best practices for format, size and accessibility for embedded players.',
  'help.topics.medias-video.title': 'Videos & integrations',
  'help.topics.podcasts-creation.description': 'Structure episodes, metadata and covers for your audio offering.',
  'help.topics.podcasts-creation.title': 'Creating a podcast',
  'help.topics.podcasts-diffusion.description': 'Listening settings, sharing and usage tracking for the institution.',
  'help.topics.podcasts-diffusion.title': 'Distribution & learners',
  'help.topics.pub-catalogue.description': 'Attach a training to the institutional catalog or an offer when enabled.',
  'help.topics.pub-catalogue.title': 'Catalog & visibility',
  'help.topics.pub-liens.description': 'Generate or copy links for learners, presenters or preview.',
  'help.topics.pub-liens.title': 'Access links & sharing',
  'help.topics.pub-statuts.description': 'Training lifecycle: save, test, publish and version changes.',
  'help.topics.pub-statuts.title': 'Draft, publication and updates',
  'help.topics.qualite-qualiopi.description': 'Use dedicated spaces to prepare criteria and the quality dossier.',
  'help.topics.qualite-qualiopi.title': 'Pre-audit & Qualiopi indicators',
  'help.topics.qualite-tracabilite.description': 'Link objectives, content and assessments to document the certified path.',
  'help.topics.qualite-tracabilite.title': 'Pedagogical traceability',
  'help.topics.support-contact.description': 'Support channel, ticket creation and information to provide for a quick diagnosis.',
  'help.topics.support-contact.title': 'Contact Lyrra support',
  'help.topics.support-faq.description': 'Player not loading, missing media, publication error: first steps.',
  'help.topics.support-faq.title': 'Common issues',
  'help.topics.support-navigateur.description': 'Recommended versions, cookies and extensions that may interfere.',
  'help.topics.support-navigateur.title': 'Browser & compatibility',
  'help.topics.v2-campagnes.description': 'Plan the deployment of paths to targeted audiences over time.',
  'help.topics.v2-campagnes.title': 'Campaigns & training plans',
  'help.topics.v2-competences.description': 'Map blocks or paths to expected skills for business reporting.',
  'help.topics.v2-competences.title': 'Skills frameworks',
  'help.topics.v2-integrations.description': 'Exchanges with your HR IS, external LMS or BI tools when connectors are available.',
  'help.topics.v2-integrations.title': 'Integrations & API',
  'pages.images.cancel': 'Cancel',
  'pages.images.changeFile': 'Change file',
  'pages.images.copyUrl': 'Copy URL',
  'pages.images.copyUrlSuccess': 'URL copied to clipboard.',
  'pages.images.copyUrlError': 'Could not copy the URL.',
  'pages.images.urlLabel': 'Public URL',
  'pages.images.download': 'Download',
  'pages.images.downloadSuccess': 'Download started.',
  'pages.images.downloadError': 'Download failed.',
  'pages.images.count_one': '{{count}} image in your library',
  'pages.images.count_other': '{{count}} images in your library',
  'pages.images.delete': 'Delete',
  'pages.images.deleteAction': 'Delete',
  'pages.images.deleteConfirmHint': 'will be permanently deleted.',
  'pages.images.deleteTitle': 'Delete this image?',
  'pages.images.descriptionLabel': 'Description (optional)',
  'pages.images.descriptionPlaceholder': 'Short description…',
  'pages.images.dropzoneBrowse': 'browse',
  'pages.images.dropzoneFormats': 'PNG, JPG, WEBP, GIF',
  'pages.images.dropzoneLabel': 'Drag an image or',
  'pages.images.fileSize.bytes': '{{size}} B',
  'pages.images.fileSize.kb': '{{size}} KB',
  'pages.images.fileSize.mb': '{{size}} MB',
  'pages.images.fileSizeKb': '{{size}} KB',
  'pages.images.importButton': 'Import',
  'pages.images.importSubtitle': 'Add an image to your library for your training.',
  'pages.images.importTitle': 'Import an image',
  'pages.images.noImages': 'No images imported',
  'pages.images.noResults': 'No results',
  'pages.images.searchPlaceholder': 'Search for an image…',
  'pages.images.submit': 'Import',
  'pages.images.titleLabel': 'Title (optional)',
  'pages.images.titlePlaceholder': 'Image name…',
  'pages.images.viewGrid': 'Grid',
  'pages.images.viewList': 'List',
  'pages.images.editPanelTitle': 'Edit image',
  'pages.images.editPanelSubtitle':
    'Title, description and file name. Replacing the file keeps the same URL (existing references keep working).',
  'pages.images.replaceFileLabel': 'New image file',
  'pages.images.replaceFileHint':
    'The image is converted to match the current file extension. The public URL does not change.',
  'pages.images.fileOriginalNameLabel': 'Original file name',
  'pages.images.saveChanges': 'Save',
  'pages.images.saveSuccess': 'Image updated.',
  'pages.images.saveError': 'Could not save.',
  'pages.images.editAction': 'Edit',
  // ── Auto-injected missing t() fallbacks ──
  'accessibility.simplifiedTextMode': 'Simplified text mode',
  'accessibility.simplifiedTextModeDesc':
    'Simplifies the text on the current page with AI (layout preserved). In a course, course blocks are processed one by one.',
  'accessibility.simplifiedTextPageLoading': 'Simplifying this page with AI…',
  'accessibility.simplifiedTextPageError':
    'Unable to simplify this page right now. Please try again in a moment.',
  'accessibility.imageHoverAudio': 'Low-vision mode: play image audio on hover',
  'accessibility.imageHoverAudioDesc':
    'When an image is hovered, AI analyzes its visual content (base64 upload) and reads the description aloud.',
  'accessibility.imageHoverAudio.loading': 'Loading audio description…',
  'accessibility.imageHoverAudio.loadingTts': 'Generating audio description…',
  'accessibility.imageHoverAudio.loadingVision': 'Analyzing image and generating audio…',
  'accessibility.imageHoverAudio.playing': 'Playing description…',
  'accessibility.imageHoverAudio.error': 'Unable to play audio.',
  'accessibility.imageHoverAudio.unavailable': 'Audio description unavailable for this image.',
  'auth.acceptTerms': 'I accept the',
  'auth.accessDenied': 'Access Denied',
  'auth.and': 'and',
  'auth.backToLogin': 'Back to login',
  'auth.checkInbox': 'Check your inbox for a password reset link. It may take a few minutes.',
  'auth.confirmPassword': 'Confirm password',
  'auth.confirmRequired': 'Please confirm your password',
  'auth.createAccount': 'Create your account',
  'auth.email': 'Email',
  'auth.emailInvalid': 'Invalid email address',
  'auth.emailPassword': 'Email & Password',
  'auth.emailRequired': 'Email is required',
  'auth.emailSent': 'Email sent!',
  'auth.firstName': 'First name',
  'auth.firstNameRequired': 'Required',
  'auth.forgotError': 'Failed to send reset email',
  'auth.forgotPassword': 'Forgot password?',
  'auth.forgotPasswordTitle': 'Forgot password?',
  'auth.goBack': 'Go back',
  'auth.goToLogin': 'Go to login',
  'auth.hasAccount': 'Already have an account?',
  'auth.hidePassword': 'Hide password',
  'auth.institutionRegister': 'Inscription Institution',
  'auth.invalidLink': 'Invalid reset link',
  'auth.invalidLinkDesc': 'This password reset link is invalid or has expired.',
  'auth.invalidToken': 'Invalid or expired reset token',
  'auth.lastName': 'Last name',
  'auth.lastNameRequired': 'Required',
  'auth.loginError': 'Login failed. Please check your credentials.',
  'auth.loginOTP': 'Login with OTP',
  'auth.loginSubtitle': 'Sign in to your LyrraStudio account',
  'auth.newPassword': 'New password',
  'auth.otpSubtitle': 'We sent a 6-digit code to your email',
  'auth.otpTitle': 'Enter verification code',
  'auth.password': 'Password',
  'auth.passwordMin': 'Minimum 8 characters',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordRequired': 'Password is required',
  'auth.passwordReset': 'Password reset!',
  'auth.passwordResetDesc': 'Your password has been successfully updated.',
  'auth.privacy': 'Privacy Policy',
  'auth.registerError': 'Registration failed',
  'auth.registerSubtitle': 'Start creating with LyrraStudio',
  'auth.requestNewLink': 'Request a new link',
  'auth.resendCode': 'Resend code',
  'auth.resendIn': 'Resend code in',
  'auth.resetError': 'Failed to reset password',
  'auth.resetPassword': 'Reset password',
  'auth.resetPasswordSubtitle': 'Choose a strong password for your account',
  'auth.resetPasswordTitle': 'Set new password',
  'auth.sendOTP': 'Send OTP code',
  'auth.sendResetLink': 'Send reset link',
  'auth.showPassword': 'Show password',
  'auth.terms': 'Terms of Service',
  'auth.termsRequired': 'You must accept the terms',
  'auth.welcomeBack': 'Welcome back',
  'campaign.analytics.avgProgress': 'Progression moy.',
  'campaign.analytics.avgProgressChart': 'Progression moyenne',
  'campaign.analytics.completion': 'Taux de complétion',
  'campaign.analytics.completionPct': 'Complétion %',
  'campaign.analytics.completionRate': 'Taux de complétion',
  'campaign.analytics.now': 'Actuel',
  'campaign.analytics.totalParticipants': 'Participants',
  'campaign.budget.none': 'Aucun budget renseigné.',
  'campaign.budget.title': 'Budget prévu',
  'campaign.col.participant': 'Participant',
  'campaign.col.progress': 'Progression',
  'campaign.col.status': 'Statut',
  'campaign.content.placeholderTitle': 'Contenus pédagogiques',
  'campaign.detail.actionError': 'Action impossible.',
  'campaign.detail.back': 'Retour aux campagnes',
  'campaign.detail.cancel': 'Annuler',
  'campaign.detail.complete': 'Clôturer',
  'campaign.detail.description': 'Description',
  'campaign.detail.launch': 'Lancer',
  'campaign.detail.loadError': 'Impossible de charger la campagne.',
  'campaign.detail.missingId': 'Identifiant manquant.',
  'campaign.detail.noDescription': 'Aucune description.',
  'campaign.detail.timeline': 'Calendrier',
  'campaign.participants.empty': 'Aucun participant assigné.',
  'campaign.participants.unknown': 'Inconnu',
  'campaign.stats.completed': 'Terminés',
  'campaign.stats.enrolled': 'Inscrits',
  'campaign.stats.inProgress': 'En cours',
  'campaign.stats.notStarted': 'Non démarrés',
  'campaign.tab.analytics': 'Analytique',
  'campaign.tab.budget': 'Budget',
  'campaign.tab.content': 'Contenu',
  'campaign.tab.overview': 'Vue d’ensemble',
  'campaign.tab.participants': 'Participants',
  'campaign.timeline.end': 'Fin prévue',
  'campaign.timeline.start': 'Début de campagne',
  'campaigns.col.budget': 'Budget',
  'campaigns.col.completion': 'Completion',
  'campaigns.col.enrolled': 'Enrolled',
  'campaigns.col.name': 'Name',
  'campaigns.col.period': 'Period',
  'campaigns.col.status': 'Status',
  'campaigns.create.budget': 'Budget (€)',
  'campaigns.create.budgetPlaceholder': '25000',
  'campaigns.create.departments': 'Departments',
  'campaigns.create.description': 'Description',
  'campaigns.create.endDate': 'End date',
  'campaigns.create.error': 'Could not create campaign.',
  'campaigns.create.individualSearchPlaceholder': 'Search people...',
  'campaigns.create.individuals': 'Individuals',
  'campaigns.create.launch': 'Launch campaign',
  'campaigns.create.launchNote': 'Launching notifies all participants and starts the campaign on the selected start date.',
  'campaigns.create.name': 'Campaign name',
  'campaigns.create.namePlaceholder': 'e.g. Leadership 2025',
  'campaigns.create.review': 'Review',
  'campaigns.create.reviewFieldBudget': 'Budget',
  'campaigns.create.reviewFieldName': 'Name',
  'campaigns.create.reviewFieldPeriod': 'Period',
  'campaigns.create.reviewContentItems': 'Training content',
  'campaigns.create.contentSelected': '{{count}} selected',
  'campaigns.create.selectContent': 'Select training content',
  'campaigns.create.startDate': 'Start date',
  'campaigns.create.steps.audience': 'Target audience',
  'campaigns.create.steps.basic': 'Basic information',
  'campaigns.create.steps.content': 'Content',
  'campaigns.create.steps.review': 'Review & launch',
  'campaigns.create.title': 'Create campaign',
  'campaigns.dept.engineering': 'Engineering',
  'campaigns.dept.product': 'Product',
  'campaigns.dept.sales': 'Sales',
  'campaigns.dept.marketing': 'Marketing',
  'campaigns.dept.finance': 'Finance',
  'campaigns.dept.legal': 'Legal',
  'campaigns.dept.hr': 'Human resources',
  'campaigns.list.create': 'New campaign',
  'campaigns.list.empty': 'No campaigns found.',
  'campaigns.list.loadError': 'Unable to load campaigns.',
  'campaigns.list.results': 'campaigns',
  'campaigns.list.search': 'Search campaigns...',
  'campaigns.list.title': 'Campaigns',
  'campaigns.page.documentTitle': 'Campaigns - LyrraStudio',
  'campaigns.sort.completion': 'Completion',
  'campaigns.sort.enrolled': 'Enrolled',
  'campaigns.sort.name': 'Name',
  'campaigns.sort.startDate': 'Start date',
  'campaigns.status.active': 'Active',
  'campaigns.status.all': 'All',
  'campaigns.status.cancelled': 'Cancelled',
  'campaigns.status.completed': 'Completed',
  'campaigns.status.draft': 'Draft',
  'campaigns.status.paused': 'Paused',
  'campaigns.status.scheduled': 'Scheduled',
  'campaigns.validation.budgetRequired': 'Budget is required.',
  'campaigns.validation.endDateRequired': 'End date is required.',
  'campaigns.validation.nameRequired': 'Campaign name is required.',
  'campaigns.validation.startDateRequired': 'Start date is required.',
  'catalog.backToCatalog': 'Retour au catalogue',
  'catalog.categories': 'Catégories',
  'catalog.category': 'Catégorie',
  'catalog.detailLoadError': 'Impossible de charger la formation.',
  'catalog.enrollError': 'Inscription impossible.',
  'catalog.enrollOk': 'Inscription enregistrée.',
  'catalog.enrollments': 'insc.',
  'catalog.flowFromTeacher': 'Parcours formateur',
  'catalog.format': 'Format',
  'catalog.language': 'Langue',
  'catalog.loadError': 'Impossible de charger le catalogue.',
  'catalog.missingId': 'Identifiant manquant.',
  'catalog.noObjectives': 'Aucune compétence liée.',
  'catalog.noResults': 'Aucune formation ne correspond à vos critères.',
  'catalog.objectives': 'Objectifs / compétences',
  'catalog.prerequisites': 'Prérequis',
  'catalog.prerequisitesPlaceholder': 'Les prérequis détaillés seront affichés lorsque renseignés sur la fiche.',
  'catalog.related': 'Formations populaires',
  'catalog.results': 'formation(s)',
  'catalog.search': 'Rechercher une formation…',
  'catalog.sortDuration': 'Durée',
  'catalog.sortName': 'Nom',
  'catalog.sortPopular': 'Popularité',
  'catalog.sortRating': 'Note',
  'certificate.completed': 'has successfully completed',
  'certificate.date': 'Date',
  'certificate.download': 'Download PDF',
  'certificate.title': 'Certificate of Completion',
  'certificate.verification': 'Verification',
  'certification.completeToUnlock': 'Complétez toutes les étapes pour débloquer votre certificat.',
  'certification.download': 'Télécharger',
  'certification.earned': 'Certification obtenue',
  'certification.locked': 'Verrouillé',
  'certification.milestones': 'étapes',
  'certification.preview': 'Certificat',
  'certification.requirements': 'Prérequis à valider :',
  'certification.subtitle': 'Suivez votre progression vers vos certifications professionnelles.',
  'certification.title': 'Parcours de certification',
  'codir.card.budgetAllocated': 'Budget alloué (année)',
  'codir.card.campaigns': 'Campagnes actives',
  'codir.card.completionYtd': 'Taux complétion (année)',
  'codir.card.used': 'utilisé',
  'codir.dashboard.budgetOverview': 'Budget (année en cours)',
  'codir.dashboard.competencyGaps': 'Écarts compétences critiques',
  'codir.dashboard.completionTrends': 'Achèvements par mois',
  'codir.dashboard.completionsPerMonth': 'Achèvements / mois',
  'codir.dashboard.deptPerformance': 'Budget par direction',
  'codir.dashboard.loadError': 'Impossible de charger le tableau de bord.',
  'codir.dashboard.noBudget': 'Non renseigné',
  'codir.dashboard.noDeptData': 'Aucune ligne budgétaire',
  'codir.dashboard.noGaps': 'Aucun écart critique recensé.',
  'codir.dashboard.remaining': 'Restant',
  'codir.dashboard.spent': 'Consommé',
  'codir.dashboard.strategicKPIs': 'Indicateurs clés',
  'codir.dashboard.subtitle': 'Indicateurs formation et budget',
  'codir.dashboard.title': 'Tableau de bord CODIR',
  'codir.dashboard.totalBudget': 'Budget total',
  'codir.dashboard.utilization': 'Utilisation %',
  'codir.kpi.budget': 'Budget consommé',
  'codir.kpi.completion': 'Complétion',
  'codir.kpi.recentCompletions': 'Achèvements 30 j.',
  'collab.certificates.active': 'actif(s)',
  'collab.certificates.empty': 'Aucun certificat pour ce filtre.',
  'collab.certificates.expires': 'Expire le',
  'collab.certificates.issued': 'Délivré le',
  'collab.certificates.title': 'Mes certificats',
  'collab.certificates.verify': 'Vérifier',
  'collab.dashboard.activeTrainings': 'Formations en cours',
  'collab.dashboard.certificates': 'Certificats',
  'collab.dashboard.completed': 'Terminées',
  'collab.dashboard.level': 'Niveau',
  'collab.dashboard.noActiveTrainings': 'Aucune formation en cours.',
  'collab.dashboard.noCompleted': 'Aucune formation terminée.',
  'collab.dashboard.noUpcoming': 'Aucune formation à venir.',
  'collab.dashboard.title': 'Mes formations',
  'collab.dashboard.totalXp': 'XP Total',
  'collab.dashboard.upcoming': 'À venir',
  'collab.request.history': 'Historique de mes demandes',
  'collab.request.justification': 'Justification',
  'collab.request.justificationPlaceholder': 'Expliquez pourquoi cette formation est nécessaire (min. 10 caractères)…',
  'collab.request.newRequest': 'Nouvelle demande',
  'collab.request.noHistory': 'Aucune demande pour le moment.',
  'collab.request.preferredDate': 'Date souhaitée',
  'collab.request.searchCatalog': 'Rechercher dans le catalogue…',
  'collab.request.submit': 'Envoyer la demande',
  'collab.request.title': 'Demander une formation',
  'collab.request.training': 'Formation',
  'collab.skills.current': 'Niveau actuel',
  'collab.skills.empty': 'Aucune compétence renseignée.',
  'collab.skills.emptyHint': 'Vos compétences apparaîtront ici une fois ajoutées.',
  'collab.skills.radar': 'Radar de compétences',
  'collab.skills.selfAssess': 'Auto-évaluation',
  'collab.skills.target': 'Objectif',
  'collab.skills.title': 'Mes compétences',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.collaborator': 'Collaborateur',
  'common.comingSoon': 'En construction',
  'common.comment': 'Commentaire',
  'common.comments': 'Comments',
  'common.complete': 'Complete',
  'common.confirm': 'Confirmer',
  'common.cost': 'Coût',
  'common.current': 'Current',
  'common.date': 'Date souhaitée',
  'common.delete': 'Supprimer',
  'common.description': 'Description',
  'common.download': 'Télécharger',
  'common.invalidEmail': 'Invalid email',
  'common.justification': 'Justification',
  'common.managerComment': 'Commentaire manager',
  'common.moveDown': 'Descendre',
  'common.moveUp': 'Monter',
  'common.next': 'Suivant',
  'common.noExpiry': 'Sans expiration',
  'common.noResults': 'Aucun résultat',
  'common.overall': 'Global',
  'common.preview': 'Preview',
  'common.previous': 'Précédent',
  'common.publish': 'Publish',
  'common.read': 'read',
  'common.remove': 'Supprimer',
  'common.required': 'Required',
  'common.restore': 'Restore',
  'common.retry': 'Réessayer',
  'common.save': 'Save',
  'common.search': 'Rechercher…',
  'common.select': 'Select...',
  'common.sending': 'Envoi…',

  // Training request panel
  'trainingRequest.panelTitle': 'Training request — educational content',
  'trainingRequest.intro': 'Describe the need. HR teams approve or decline the request; if approved, it moves to content preparation.',
  'trainingRequest.fieldTitle': 'Training title',
  'trainingRequest.fieldDescription': 'Description of the need',
  'trainingRequest.fieldJustification': 'Justification / objectives',
  'trainingRequest.fieldDate': 'Preferred date',
  'trainingRequest.datePlaceholder': 'e.g. Q2 2026',
  'trainingRequest.fieldFormateur': 'Institution trainer',
  'trainingRequest.formateurPlaceholder': 'Choose a trainer…',
  'trainingRequest.formateursEmpty': 'No trainer account listed for your institution.',
  'trainingRequest.titleRequired': 'Please enter a title (minimum 2 characters).',
  'trainingRequest.sent': 'Request submitted. You will be notified by email of the decision.',
  'trainingRequest.sendError': 'Unable to submit the request. Check your connection or permissions.',
  'trainingRequest.submit': 'Submit request',
  'common.share': 'Partager',
  'common.status': 'Statut',
  'common.submit': 'Submit',
  'common.submittedAt': 'Soumis le',
  'common.title': 'Title',
  'common.training': 'Formation',
  'common.view': 'Consulter',
  'common.words': 'words',
  'compliance.download': 'Télécharger',
  'compliance.generate': 'Générer',
  'compliance.generating': 'Génération…',
  'compliance.lastGenerated': 'Généré le',
  'compliance.subtitle': 'Générez et gérez vos documents réglementaires et pédagogiques.',
  'compliance.title': 'Documents de conformité',
  'compliance.uploadEvidence': 'Importer un justificatif',
  'confirmDialog.cancel': 'Cancel',
  'confirmDialog.confirm': 'Confirm',
  'dashboard.collaborators': 'Staff accounts',
  'dysModule.font': 'OpenDyslexic',
  'dysModule.letter': 'Aa',
  'dysModule.ruler': 'Ruler',
  'dysModule.settings': 'Dyslexia settings',
  'dysModule.word': 'W',
  'eduflow.adaptiveReplacement.assist': 'Assisté',
  'eduflow.adaptiveReplacement.auto': 'Auto',
  'eduflow.adaptiveReplacement.force': 'Forcer la version jeu simplifiée pour ce bloc',
  'eduflow.adaptiveReplacement.manual': 'Manuel',
  'eduflow.adaptiveReplacement.mode': 'Mode',
  'eduflow.adaptiveReplacement.title': 'Remplacement adaptatif',
  'eduflow.answerSubmitted': '✓ Answer submitted successfully',
  'eduflow.collaborativeBadge': "Collaborative",
  'eduflow.contents': 'Contents',
  'eduflow.correct': '✓ Correct!',
  'eduflow.designer.descriptionPlaceholder': "Optional description...",
  'eduflow.designer.gridToggle': "Grid: {{state}}",
  'eduflow.designer.conditionalLogic': "Conditional logic",
  'eduflow.designer.conditionalLogicSoon': "Branching conditions will be available soon.",
  'eduflow.designer.descriptionLabel': "Description",
  'eduflow.designer.contentLabel': "Content",
  'eduflow.designer.autoLayoutDone': "Auto layout applied",
  'eduflow.designer.autoLayoutFailed': "Could not compute the layout.",
  'eduflow.designer.autoLayoutNoBlocks': "Add at least one block to run the auto layout.",
  'eduflow.designer.paletteCategory.institution': "Institution blocks",
  'eduflow.duration': 'Duration (seconds)',
  'eduflow.evaluation': 'Evaluation',
  'eduflow.evaluationSubmitted': 'Evaluation submitted',
  'eduflow.exportFlow': 'Export Flow',
  'eduflow.flowSettings': 'Flow Settings',
  'eduflow.form': 'Form',
  'eduflow.formSubmitted': 'Form submitted',
  'eduflow.incorrect': '✗ Incorrect. Try again.',
  'eduflow.list.coverLabel': 'Image de couverture',
  'eduflow.list.coverRemove': 'Retirer la couverture',
  'eduflow.list.coverUpload': 'Choisir une image',
  'eduflow.list.flowProperties': 'Title, description & cover',
  'eduflow.list.propertiesPanelTitle': 'Training properties',
  'eduflow.list.duplicatePanelTitle': 'Duplicate training',
  'eduflow.list.duplicatePanelSubtitle': 'Original training: {{name}}',
  'eduflow.list.duplicateTitleSuffix': 'copy',
  'eduflow.list.duplicateSuccess': 'Training duplicated.',
  'eduflow.list.duplicateCoverHint':
    'Cover image and learning content from the original training will be copied.',
  'eduflow.list.propertiesSaveError': 'Could not save properties.',
  'eduflow.list.propertiesSaved': 'Properties saved.',
  'eduflow.list.propertiesUploadError': 'Image upload failed.',
  'eduflow.mindmap': 'Mindmap',
  'eduflow.next': 'Suivant',
  'eduflow.noExtraConfig': 'No additional configuration for this block type.',
  'eduflow.openQuestion': 'Open Question',
  'eduflow.phraseReveal.addPhrase': 'Ajouter une phrase',
  'eduflow.phraseReveal.autoScroll': 'Défilement automatique',
  'eduflow.phraseReveal.displayDuration': 'Durée par phrase (s)',
  'eduflow.phraseReveal.emptyPhrase': 'Phrase vide',
  'eduflow.phraseReveal.noPhrases': 'Aucune phrase pour l’instant.',
  'eduflow.phraseReveal.phraseContent': 'Contenu de la phrase',
  'eduflow.phraseReveal.phrasePlaceholder': 'Saisissez le texte de cette étape…',
  'eduflow.phraseReveal.showHistory': 'Afficher l’historique des phrases',
  'eduflow.quiz': 'Quiz',
  'eduflow.readComplete': 'Read complete',
  'eduflow.summary.addItem': 'Ajouter un point',
  'eduflow.summary.emptyList': 'Aucun point pour l’instant. Ajoutez-en un.',
  'eduflow.summary.itemPlaceholder': 'Ex. : Les risques incendie en entreprise',
  'eduflow.takeNotes': 'Take notes...',
  'eduflow.timeline.addEvent': 'Événement',
  'eduflow.timeline.alternatingSides': 'Côtés alternés',
  'eduflow.timeline.animatedReveal': 'Révélation animée',
  'eduflow.timeline.colorHint': 'Couleur du point (optionnel)',
  'eduflow.timeline.colorLabel': 'Couleur',
  'eduflow.timeline.datePlaceholder': 'Date ou période (ex. 1789, janv. 2024)',
  'eduflow.timeline.descPlaceholder': 'Description (affichée au développement)',
  'eduflow.timeline.events': 'Événements',
  'eduflow.timeline.horizontal': 'Horizontale',
  'eduflow.timeline.orientation': 'Orientation',
  'eduflow.timeline.settings': 'Paramètres',
  'eduflow.timeline.showConnectors': 'Lignes de connexion',
  'eduflow.timeline.untitled': 'Sans titre',
  'eduflow.timeline.vertical': 'Verticale',
  'eduflow.typeAnswer': 'Type your answer...',
  'eduflow.typeConfig': 'Type Configuration',
  'eduflow.unsaved': 'Unsaved changes',
  'eduflow.versionHistory.activateSuccess': 'Version {{label}} activated (public)',
  'eduflow.versionHistory.activateTitle': 'Activate {{label}}?',
  'eduflow.versionHistory.confirmActivate': 'Activate',
  'eduflow.versionHistory.copyFailed': 'Copy failed',
  'eduflow.versionHistory.copyHtml': 'Copy HTML',
  'eduflow.versionHistory.copyPlain': 'Copy plain text',
  'eduflow.versionHistory.createMajor': 'Major version',
  'eduflow.versionHistory.createMajorSuccess': 'Major version created',
  'eduflow.versionHistory.createMinor': 'Minor version',
  'eduflow.versionHistory.createMinorSuccess': 'Minor version created',
  'eduflow.versionHistory.deleteError': 'Error deleting version',
  'eduflow.versionHistory.deleteSuccess': 'Version deleted',
  'eduflow.versionHistory.deleteTitle': 'Delete {{label}}?',
  'eduflow.versionHistory.deleteWarning': 'This action is irreversible.',
  'eduflow.versionHistory.duplicateConfirm': 'Duplicate',
  'eduflow.versionHistory.duplicateError': 'Error duplicating version',
  'eduflow.versionHistory.duplicateMinor': 'Duplicate (minor)',
  'eduflow.versionHistory.duplicateSuccess': 'Version dupliquée — contenu restauré dans l’éditeur',
  'eduflow.versionHistory.duplicateTitle': 'Duplicate {{label}}?',
  'eduflow.versionHistory.newFromCurrent': 'New version from current content',
  'eduflow.versionHistory.preview': 'Preview',
  'eduflow.versionHistory.previewBlocks': '{{count}} blocks',
  'eduflow.versionHistory.previewEdges': '{{count}} connections',
  'eduflow.versionHistory.previewError': 'Impossible de charger l’aperçu',
  'eduflow.versionHistory.previewTitle': 'Preview {{label}}',
  'eduflow.versionHistory.setPublic': 'Activate',
  'eduflow.versionHistory.startDescModalTitle': 'Description — Start block ({{version}})',
  'eduflow.videoCompleted': 'Video watched',
  'gamification.active': 'actives',
  'gamification.badges': 'Badges',
  'gamification.bestStreak': 'Meilleur streak',
  'gamification.claim': 'Récupérer',
  'gamification.completed': 'Terminées',
  'gamification.days': 'jours',
  'gamification.leaderboard': 'Classement',
  'gamification.level': 'Niv.',
  'gamification.maxLevel': '✨ Niveau max atteint !',
  'gamification.missions': 'Missions',
  'gamification.name': 'Nom',
  'gamification.streakActive': 'Streak en cours 🔥',
  'gamification.streakInactive': 'Pas de streak actif',
  'gamification.xpRemaining': '${formatXp(xpForNextLevel - currentXp)} XP restants',
  'header.language': 'Language',
  'institution.categories.color': 'Couleur',
  'institution.categories.coverLabel': 'Image de couverture',
  'institution.categories.coverUploadError': 'Échec du téléversement de la couverture.',
  'institution.categories.removeCover': 'Retirer l’image',
  'institution.categories.descShortPh': 'Décrivez l’usage de la catégorie',
  'institution.categories.edit': 'Modifier la catégorie',
  'institution.categories.episodes': 'épisode(s)',
  'institution.categories.learners': 'apprenant(s)',
  'institution.categories.namePlaceholder': 'Nom de la catégorie',
  'institution.categories.new': 'Nouvelle catégorie',
  'institution.categories.preview': 'Aperçu',
  'institution.categories.previewApiModeOff': 'Prévisualisation indisponible (mode local).',
  'institution.categories.previewDesc': 'Podcasts et parcours reliés à cette catégorie.',
  'institution.categories.previewError': 'Erreur lors du chargement de l’aperçu.',
  'institution.categories.previewFormations': 'Parcours / formations',
  'institution.categories.previewLoading': 'Chargement de l’aperçu…',
  'institution.categories.previewNoFlows': 'Aucune formation rattachée à cette catégorie.',
  'institution.categories.previewNoPodcasts': 'Aucun podcast rattaché à cette catégorie.',
  'institution.categories.previewPodcasts': 'Podcasts',
  'institution.categories.saveError': 'Erreur lors de la sauvegarde.',
  'institution.categories.subscribers': 'abonné(s)',
  'institution.categories.usageItems': 'élément(s)',
  'institution.common.add': 'Ajouter',
  'institution.common.cancel': 'Annuler',
  'institution.common.close': 'Fermer',
  'institution.common.delete': 'Supprimer',
  'institution.common.description': 'Description',
  'institution.common.email': 'Email',
  'institution.common.loading': 'Chargement…',
  'institution.common.name': 'Nom',
  'institution.common.refresh': 'Actualiser',
  'institution.common.required': 'Requis',
  'institution.common.retry': 'Réessayer',
  'institution.common.save': 'Enregistré',
  'institution.contentPipeline.confirmDeletePath': 'Supprimer ce parcours ?',
  'institution.contentPipeline.createPath': 'Créer le parcours',
  'institution.contentPipeline.createPodcast': 'Créer le podcast',
  'institution.contentPipeline.createShell': 'Créer la fiche',
  'institution.contentPipeline.descPh': 'Description / objectifs',
  'institution.contentPipeline.newPath': 'Nouveau parcours',
  'institution.contentPipeline.newPodcastShell': 'Pré-créer un podcast',
  'institution.contentPipeline.newShell': 'Nouvelle fiche formation',
  'institution.contentPipeline.openDesigner': 'Ouvrir dans le designer',
  'institution.contentPipeline.openStudio': 'Épisodes',
  'institution.contentPipeline.pathDescLabel': 'Description',
  'institution.contentPipeline.pathPlannedLaunch': 'Date cible de lancement',
  'institution.contentPipeline.pathTitlePh': 'Titre du parcours',
  'institution.contentPipeline.pickProducer': 'Assigner producteur…',
  'institution.contentPipeline.pickTeacher': 'Assigner…',
  'institution.contentPipeline.plannedDeadline': 'Échéance',
  'institution.contentPipeline.plannedStart': 'Début prévu',
  'institution.contentPipeline.planningColumn': 'Planning création',
  'institution.contentPipeline.planningOptional': 'Planning de création (optionnel)',
  'institution.contentPipeline.podDescPh': 'Brief / description pour le formateur',
  'institution.contentPipeline.savePlanning': 'Enregistrer le planning',
  'institution.contentPipeline.statFormations': 'Fiches catalogue',
  'institution.contentPipeline.statPaths': 'Parcours certifiants',
  'institution.contentPipeline.statPodcasts': 'Podcasts',
  'institution.contentPipeline.tabFormations': 'Fiches formations',
  'institution.contentPipeline.tabPaths': 'Parcours',
  'institution.contentPipeline.tabPodcasts': 'Podcasts',
  'institution.contentPipeline.title': 'Préparation du contenu',
  'institution.contentPipeline.titlePh': 'Titre',
  'institution.learnerAnalytics.chartActivity': 'Activité et complétions (8 dernières semaines)',
  'institution.learnerAnalytics.chartProgress': 'Progression globale (par apprenant)',
  'institution.learnerAnalytics.chartStatus': 'Répartition par statut',
  'institution.learnerAnalytics.chartTopFlows': 'Formations les plus suivies',
  'institution.learnerAnalytics.colDone': 'Terminé',
  'institution.learnerAnalytics.colFlow': 'Formation',
  'institution.learnerAnalytics.colLastAccess': 'Dernier accès',
  'institution.learnerAnalytics.colName': 'Apprenant',
  'institution.learnerAnalytics.colTime': 'Temps',
  'institution.learnerAnalytics.count': 'Effectif',
  'institution.learnerAnalytics.done': 'Terminés',
  'institution.learnerAnalytics.enrolled': 'Inscrits',
  'institution.learnerAnalytics.kpiEnrollments': 'Inscriptions',
  'institution.learnerAnalytics.kpiLearners': 'Apprenants',
  'institution.learnerAnalytics.kpiLearnersSub': 'Participants enregistrés',
  'institution.learnerAnalytics.kpiRate': 'Taux de complétion',
  'institution.learnerAnalytics.kpiRateSub': 'Sur l’ensemble des inscriptions',
  'institution.learnerAnalytics.kpiTime': 'Temps cumulé',
  'institution.learnerAnalytics.kpiTimeSub': 'Temps passé sur les parcours',
  'institution.learnerAnalytics.loadError': 'Impossible de charger les analyses.',
  'institution.learnerAnalytics.noData': 'Pas encore de données.',
  'institution.learnerAnalytics.seriesActivity': 'Accès (inscriptions)',
  'institution.learnerAnalytics.seriesCompletions': 'Complétions',
  'institution.learnerAnalytics.tableEmpty': 'Aucune ligne à afficher.',
  'institution.learnerAnalytics.tableSearch': 'Rechercher nom, email, formation…',
  'institution.learnerAnalytics.tableTitle': 'Détail des inscriptions',
  'institution.learnerAnalytics.tableTruncated': 'Affichage limité à 500 lignes — utilisez l’export pour la liste complète.',
  'institution.learnerAnalytics.title': 'Analyse & suivi des apprenants',
  'institution.learnerAnalytics.xapiDataSourceTitle': 'Source des données & xAPI',
  'institution.learners.flowDone': 'Terminé',
  'institution.learners.inProgress': 'En cours',
  'institution.menuBlocked.title': 'Fonctionnalité non disponible',
  'institution.rhModule.pickFromMenu': 'Choisissez un outil dans le menu Formation & RH.',
  'institution.sirh.apiKey': 'Clé API',
  'institution.sirh.apiKeyStored': 'déjà enregistrée — laissez vide pour ne pas changer',
  'institution.sirh.apiSecret': 'Secret / second jeton (optionnel)',
  'institution.sirh.apiSecretHint': 'Envoyé en en-tête X-API-Secret si renseigné.',
  'institution.sirh.baseUrl': 'URL de base de l’API',
  'institution.sirh.baseUrlHint': 'Lyrra appellera GET {URL}/employees avec Authorization: Bearer &lt;clé API&gt;.',
  'institution.sirh.enabled': 'Synchronisation activée',
  'institution.sirh.formatTitle': 'Format attendu pour GET /employees',
  'institution.sirh.loadError': 'Impossible de charger la configuration SIRH.',
  'institution.sirh.mappingJob': 'Poste / fonction (jobTitle ou ex. position)',
  'institution.sirh.mappingTitle': 'Correspondance des champs (JSON SIRH)',
  'institution.sirh.name': 'Libellé interne',
  'institution.sirh.saveError': 'Enregistrement impossible.',
  'institution.sirh.saved': 'Configuration enregistrée.',
  'institution.sirh.secretStored': 'déjà enregistré',
  'institution.sirh.syncDone': 'Synchronisation : ${s.created} créés, ${s.updated} mis à jour, ${s.errors} erreurs, ${s.skipped} ignorés (autre établissement).',
  'institution.sirh.syncError': 'Synchronisation impossible.',
  'institution.sirh.syncNow': 'Synchroniser les collaborateurs',
  'institution.sirh.syncOk': 'Synchronisation terminée.',
  'institution.sirh.test': 'Tester la connexion',
  'institution.sirh.testError': 'Test impossible.',
  'institution.sirh.title': 'SIRH — connexion API',
  'institution.sirh.type': 'Type / famille',
  'institution.sirh.typeCustom': 'API HTTP personnalisée',
  'institution.templates.blockType': 'Type de bloc',
  'institution.templates.create': 'Créer un modèle',
  'institution.templates.createDrawerTitle': 'Créer un modèle de bloc',
  'institution.templates.createNameRequired': 'Veuillez renseigner le titre du modèle.',
  'institution.templates.editDrawerTitle': 'Modifier le modèle',
  'institution.templates.modelDescPh': 'Description du modèle',
  'institution.templates.modelNamePh': 'Titre du modèle',
  'institution.templates.previewDrawerTitle': 'Aperçu du modèle',
  'institution.templates.publish': 'Publier',
  'institution.templates.published': 'Publié',
  'institution.templates.typeConfig': 'Configuration du bloc',
  'institution.templates.unpublish': 'Dépublier',
  'institution.xapi.accessToken': 'Jeton d’accès (Bearer)',
  'institution.xapi.actorField': 'Champ acteur (référence)',
  'institution.xapi.authBasic': 'HTTP Basic (utilisateur / mot de passe)',
  'institution.xapi.authBearer': 'Bearer / jeton OAuth',
  'institution.xapi.authType': 'Authentification LRS',
  'institution.xapi.enable': 'Activer l’envoi des statements vers le LRS',
  'institution.xapi.formTitle': 'Paramètres LRS',
  'institution.xapi.loadError': 'Impossible de charger la configuration xAPI.',
  'institution.xapi.loadStatements': 'Afficher 15 statements (JSON)',
  'institution.xapi.lrsReadTitle': 'Lecture distante (diagnostic)',
  'institution.xapi.lrsUrl': 'URL de base du LRS',
  'institution.xapi.optionalKeep': 'optionnel pour conserver l’actuel',
  'institution.xapi.password': 'Mot de passe / secret',
  'institution.xapi.payloadOptions': 'Données incluses dans les statements (parcours)',
  'institution.xapi.profile': 'Profil / usage',
  'institution.xapi.save': 'Enregistrer',
  'institution.xapi.saveError': 'Enregistrement impossible.',
  'institution.xapi.saveSuccess': 'Configuration xAPI enregistrée.',
  'institution.xapi.sendCompletion': 'Envoyer complétion',
  'institution.xapi.sendDuration': 'Envoyer durée',
  'institution.xapi.sendScore': 'Envoyer score',
  'institution.xapi.statementsError': 'Impossible de charger les statements.',
  'institution.xapi.sync': 'Lire un lot (aperçu)',
  'institution.xapi.syncError': 'Lecture LRS impossible.',
  'institution.xapi.test': 'Tester le LRS',
  'institution.xapi.testError': 'Test impossible.',
  'institution.xapi.testFail': 'Test LRS échoué.',
  'institution.xapi.testOk': 'LRS joignable.',
  'institution.xapi.title': 'xAPI — Learning Record Store (LRS)',
  'institution.xapi.username': 'Utilisateur (Basic Auth)',
  'loading.label': 'Loading…',
  'manager.dashboard.loadError': 'Chargement impossible.',
  'manager.dashboard.noReports': 'Aucun collaborateur rattaché comme manager dans le SIRH.',
  'manager.dashboard.pendingCount': 'en attente',
  'manager.dashboard.pendingWorkflowTitle': 'Demandes en circuit',
  'manager.dashboard.title': 'Manager dashboard',
  'manager.dashboard.workflowBox': 'Validation',
  'manager.kpi.activeTrainings': 'Active trainings',
  'manager.kpi.completionRate': 'Completion rate',
  'manager.kpi.pendingWorkflow': 'Pending workflows',
  'manager.nav.requests': 'Training requests',
  'manager.nav.reviews': 'Reviews',
  'manager.requests.commentPlaceholder': 'Add a comment…',
  'manager.requests.empty': 'No requests for this filter.',
  'manager.requests.title': 'Training requests',
  'manager.reviews.commentPlaceholder': 'Commentaire sur cette compétence…',
  'manager.reviews.lastAssessment': 'Dernière éval.',
  'manager.reviews.selectMember': 'Sélectionnez un collaborateur pour évaluer',
  'manager.reviews.submitted': 'Évaluation soumise',
  'manager.reviews.target': 'Cible',
  'manager.reviews.title': 'Évaluations de compétences',
  'manager.team.loadError': 'Chargement impossible.',
  'notifications.empty': 'No notifications yet',
  'notifications.label': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.markRead': 'Mark as read',
  'notifications.title': 'Notifications',
  'notifications.viewAll': 'View all notifications',
  'pages.branding.comingSoonDesc': 'Configurez les couleurs, logos et la charte graphique de votre espace de formation.',
  'pages.branding.title': 'Identité visuelle',
  'pages.budget.comingSoonDesc': 'Planifiez et suivez votre budget de formation, gérez les enveloppes et les dépenses par département.',
  'pages.budget.description': 'Gestion du budget de formation',
  'pages.budget.title': 'Budget',
  'pages.campaigns.description': 'Gestion des campagnes de formation',
  'pages.campaigns.title': 'Campagnes',
  'pages.catalog.comingSoonDesc': 'Parcourez le catalogue de formations disponibles, recherchez par compétence et inscrivez-vous aux sessions.',
  'pages.catalog.description': 'Catalogue des formations',
  'pages.catalog.title': 'Catalogue',
  'pages.certificates.comingSoonDesc': 'Consultez et téléchargez vos certificats de formation, attestations et badges obtenus.',
  'pages.certificates.description': 'Vos certifications et attestations',
  'pages.certificates.title': 'Certificats',
  'pages.competencies.comingSoonDesc': 'Gérez votre référentiel de compétences, cartographiez les savoir-faire et identifiez les besoins en formation.',
  'pages.competencies.description': 'Référentiel de compétences',
  'pages.competencies.title': 'Compétences',
  'pages.compliance.comingSoonDesc': 'Suivez les formations obligatoires, les certifications et la conformité réglementaire de votre organisation.',
  'pages.compliance.description': 'Suivi de la conformité réglementaire',
  'pages.compliance.title': 'Conformité',
  'pages.integrations.comingSoonDesc': 'Connectez votre plateforme à vos outils RH, SIRH, LMS et autres services pour synchroniser vos données.',
  'pages.integrations.description': 'Connexions aux services externes',
  'pages.integrations.title': 'Intégrations',
  'pages.learners.statsAdaptiveChoice': 'Choix assistes - acceptes: {{accepted}} · refuses: {{declined}}',
  'pages.learners.statsAdaptiveEvaluated': 'Adaptations evaluees: {{count}}',
  'pages.learners.statsAdaptiveReplaced': 'Versions simplifiees utilisees: {{count}}',
  'pages.reports.description': 'Rapports et bilans de formation',
  'pages.reports.title': 'Rapports',
  'pages.requests.description': 'Gestion des demandes de formation',
  'pages.requests.title': 'Demandes',
  'pages.reviews.comingSoonDesc': 'Gérez les entretiens annuels, les évaluations de compétences et le suivi de progression de votre équipe.',
  'pages.reviews.description': 'Entretiens et évaluations',
  'pages.reviews.title': 'Évaluations',
  'pages.settings.description': 'Configuration de votre compte et préférences',
  'pages.settings.title': 'Paramètres',
  'pages.skills.description': 'Votre profil de compétences',
  'pages.skills.title': 'Mes compétences',
  'pages.team.comingSoonDesc': 'Visualisez votre équipe, suivez les compétences et les formations de chaque collaborateur.',
  'pages.team.description': 'Gestion de votre équipe',
  'pages.team.title': 'Mon équipe',
  'pages.training.comingSoonDesc': 'Accédez à vos formations en cours, suivez votre progression et reprenez là où vous vous êtes arrêté.',
  'pages.training.description': 'Vos parcours de formation en cours',
  'pages.training.title': 'Mes formations',
  'pages.webhooks.comingSoonDesc': 'Configurez des webhooks pour connecter votre plateforme à des services externes et automatiser vos flux.',
  'pages.webhooks.description': 'Configuration des webhooks et intégrations',
  'pages.webhooks.title': 'Webhooks',
  'player.adaptiveGame.done': 'Objectif valide en mode simplifie',
  'player.adaptiveGame.next': 'Etape suivante',
  'player.adaptiveGame.step': 'Etape {{index}} / {{total}}',
  'player.adaptiveGame.subtitle': 'Une alternative plus accessible pour valider cet objectif.',
  'player.adaptiveGame.title': 'Version simplifiee interactive',
  'player.adaptiveGame.validate': 'Valider',
  'player.adaptivePrompt.keepOriginal': 'Garder le bloc standard',
  'player.adaptivePrompt.subtitle': 'Souhaitez-vous utiliser une version interactive plus accessible ?',
  'player.adaptivePrompt.title': 'Version simplifiee disponible pour ce bloc',
  'player.adaptivePrompt.useSimplified': 'Utiliser la version simplifiee',
  'player.descriptionTeleprompter': 'Mode téléprompteur',
  'player.descriptionTeleprompterOff': 'Afficher le bloc',
  'player.descriptionVoiceover': 'Voix off — description du bloc',
  'player.subflow.blockTitle': 'Sous-formation',
  'player.subflow.defaultTitle': 'Sous-formation',
  'player.subflow.loadError': 'La sous-formation n’a pas pu être chargée.',
  'player.subflow.loading': 'Chargement…',
  'player.subflow.missingId': 'Aucune sous-formation n’est liée à ce bloc.',
  'player.subflow.noContext': 'Impossible de charger la sous-formation (contexte lecteur manquant).',
  'player.subflow.start': 'Commencer la sous-formation',
  'player.teleprompterNextParagraph': 'Paragraphe suivant',
  'player.teleprompterPrevParagraph': 'Paragraphe précédent',
  'podcast.episodes.chapters': 'Chapitres',
  'podcast.episodes.hideText': 'Masquer le texte',
  'podcast.episodes.noChapters': 'Aucun chapitre disponible.',
  'podcast.episodes.previewEpisodeTitle': 'Épisode',
  'podcast.episodes.showText': 'Afficher le texte',
  'qualiopi.actions': 'Actions',
  'qualiopi.completion': 'Conformité',
  'qualiopi.evidence': 'Documents justificatifs',
  'qualiopi.noDocuments': 'Aucun document',
  'qualiopi.notes': 'Notes',
  'qualiopi.notesPlaceholder': 'Ajouter une note…',
  'quickActions.hint': 'Press',
  'quickActions.hintToggle': 'to toggle',
  'quickActions.title': 'Quick Actions',
  'rh.achievements': 'Achievements',
  'rh.addComment': 'Add a comment...',
  'rh.addCompetency': 'Add Competency',
  'rh.afterApproval': 'After Approval',
  'rh.alert.forecast': 'Prévision fin d’année',
  'rh.alert.workflow': 'Workflows',
  'rh.all': 'All',
  'rh.allCompetencies': 'All Competencies',
  'rh.allDepartments': 'All Departments',
  'rh.allocated': 'Alloué',
  'rh.approvalHistory': 'Approval History',
  'rh.approve': 'Approve',
  'rh.approveConfirm': 'Are you sure you want to approve this training request?',
  'rh.approveRequest': 'Approve Request',
  'rh.approveSelected': 'Approve',
  'rh.approved': 'Approved',
  'rh.auditOverdue': 'Audit overdue',
  'rh.avgGap': 'Avg Gap Size',
  'rh.avgLevel': 'Avg Level',
  'rh.avgOverall': 'Avg Overall',
  'rh.budget.subtitle': 'Budget tracking and cost per completion',
  'rh.budget.title': 'Budget formation',
  'rh.budgetAlerts': 'Alertes',
  'rh.budgetDashboard': 'Budget formation',
  'rh.budgetLoadError': 'Impossible de charger le budget.',
  'rh.budgetRemaining': 'Budget Remaining',
  'rh.byDepartment': 'Budget par direction',
  'rh.competencies': 'competencies',
  'rh.competencyMap': 'Competency Heatmap',
  'rh.competencyRadar': 'Competency Radar',
  'rh.completion': 'Completion',
  'rh.complianceCritical': 'Critical: Several criteria are below acceptable thresholds.',
  'rh.complianceGood': 'Your organization meets Qualiopi standards. Keep up the good work.',
  'rh.complianceWarning': 'Some criteria need attention before the next audit.',
  'rh.compliant': 'compliant',
  'rh.confirm': 'Confirm Approval',
  'rh.confirmReject': 'Confirm Rejection',
  'rh.consumed': 'Consommé',
  'rh.costInfo': 'Cost Info',
  'rh.critical': 'critical',
  'rh.criticalGaps': 'Critical (High)',
  'rh.current': 'Current',
  'rh.currentLevel': 'Current Level',
  'rh.date': 'Date',
  'rh.daysRemaining': 'days remaining',
  'rh.department': 'Department',
  'rh.departmentBreakdown': 'Détail par direction',
  'rh.departments': 'departments',
  'rh.description': 'Description',
  'rh.edit': 'Edit',
  'rh.employee': 'Employee',
  'rh.employeeInfo': 'Employee Info',
  'rh.evolution': 'Evolution',
  'rh.evolutionTimeline': 'Evolution Timeline',
  'rh.exportReport': 'Exporter',
  'rh.gapAnalysis': 'Gap Analysis',
  'rh.growth': 'Growth this year',
  'rh.lastUpdated': 'Last updated',
  'rh.level': 'Level',
  'rh.manager': 'Manager',
  'rh.needsWork': 'needs work',
  'rh.nextAudit': 'Next Audit',
  'rh.noAlerts': 'Aucune alerte.',
  'rh.noAllocations': 'Aucune ligne budgétaire pour cette année.',
  'rh.noDeptData': 'Aucune donnée',
  'rh.noGaps': 'No gaps found for this filter.',
  'rh.of': 'of',
  'rh.ofTotal': 'du total',
  'rh.overallCompliance': 'Overall Compliance Score',
  'rh.pending': 'Pending',
  'rh.provider': 'Provider',
  'rh.qualiopiCompliance': 'Qualiopi Compliance',
  'rh.radar': 'Radar',
  'rh.reject': 'Reject',
  'rh.rejectConfirm': 'Are you sure you want to reject this training request?',
  'rh.rejectRequest': 'Reject Request',
  'rh.rejectSelected': 'Reject',
  'rh.rejected': 'Rejected',
  'rh.remaining': 'Restant',
  'rh.requestInfo': 'Request Info',
  'rh.requests.subtitle': 'Request workflows and approvals',
  'rh.requests.title': 'Training requests',
  'rh.required': 'Required',
  'rh.rh': 'RH',
  'rh.saveAssessment': 'Save Assessment',
  'rh.searchCompetencies': 'Search competencies…',
  'rh.selfAssess': 'Self',
  'rh.showing': 'Showing',
  'rh.skillsSelfAssessment': 'Skills & Self-Assessment',
  'rh.spending': 'Dépenses',
  'rh.spendingTimeline': 'Dépenses par année',
  'rh.status': 'Status',
  'rh.targetLevel': 'Target Level',
  'rh.totalBudget': 'Budget total',
  'rh.totalGaps': 'Total Gaps',
  'rh.training': 'Training',
  'rh.trainingCost': 'Training Cost',
  'rh.trainingRequests': 'Training Requests',
  'search.close': 'to close',
  'search.input': 'Search input',
  'search.navigate': 'to navigate',
  'search.placeholder': 'Search pages, projects, flows...',
  'search.recent': 'Recent',
  'search.select': 'to select',
  'search.title': 'Search',
  'status.completed': 'Terminé',
  'status.inProgress': 'En cours',
  'status.notStarted': 'Non commencé',
  'webinar.room.closeParticipantCamerasView': 'Fermer',
  'webinar.room.hideParticipantCameras': 'Fermer la vue caméras',
  'webinar.room.participantCamerasViewTitle': 'Caméras des participants',
  'webinar.room.showParticipantCameras': 'Voir les caméras des participants',
  // ── Auto-injected missing t() fallbacks ──
  'designer.ctx.addBlock': "Add a block",
  'designer.ctx.blockInfo': "Block info",
  'designer.ctx.changeColor': "Change colour",
  'designer.ctx.copy': "Copy",
  'designer.ctx.delete': "Delete",
  'designer.ctx.duplicate': "Duplicate",
  'designer.ctx.edit': "Edit",
  'designer.ctx.fitView': "Fit view",
  'designer.ctx.lock': "Lock",
  'designer.ctx.paste': "Paste",
  'designer.ctx.toggleGrid': "Grid ON/OFF",
  'designer.ctx.toggleMinimap': 'Minimap ON/OFF',
  'designer.ctx.unlock': "Unlock",
  'designer.edge.cancel': "Cancel",
  'designer.edge.color': "Colour",
  'designer.edge.name': "Name",
  'designer.edge.namePlaceholder': "e.g. Passed, Branch A…",
  'designer.edge.save': "Save",
  'designer.edge.title': "Connection",
  'designer.gamification.badgeName': "Badge name",
  'designer.gamification.badges': 'Badges',
  'designer.gamification.blocksGte': "Blocks ≥",
  'designer.gamification.bonusPerfect': "Perfect score bonus (quiz)",
  'designer.gamification.condition': 'Condition',
  'designer.gamification.conditionPlaceholder': "e.g. quiz_completed >= 3",
  'designer.gamification.description': 'Description',
  'designer.gamification.enable': "Enable gamification",
  'designer.gamification.enableLeaderboard': "Enable the leaderboard",
  'designer.gamification.icon': "Icon",
  'designer.gamification.leaderboard': "Leaderboard",
  'designer.gamification.missionDesc': "Description (e.g. Finish 3 quizzes)",
  'designer.gamification.missionTitle': "Mission title",
  'designer.gamification.missions': 'Missions',
  'designer.gamification.noBadges': "No badges yet",
  'designer.gamification.noMissions': "No missions yet",
  'designer.gamification.points': 'Points',
  'designer.gamification.pointsPerBlock': "Points per completed block",
  'designer.gamification.pointsPerSecond': "Points per second saved",
  'designer.gamification.publicLeaderboard': "Public leaderboard",
  'designer.gamification.reward': "Reward",
  'designer.gamification.scoreGte': 'Score ≥',
  'designer.gamification.timeBonus': "Speed bonus",
  'designer.gamification.title': 'Gamification',
  'designer.gamification.topN': "Show the top N",
  'designer.objectives.add': "Add",
  'designer.objectives.addTitle': "Add an objective",
  'designer.objectives.cancel': "Cancel",
  'designer.objectives.category': "Category",
  'designer.objectives.completed': "Completed",
  'designer.objectives.empty': "No objectives yet.",
  'designer.objectives.emptyHint': "Add your first learning objectives.",
  'designer.objectives.errorToast': "Could not save",
  'designer.objectives.level': "Level (Bloom)",
  'designer.objectives.linkedBlocks': "Linked blocks",
  'designer.objectives.noContentBlocks': "No content block available.",
  'designer.objectives.required': "Required",
  'designer.objectives.save': "Save",
  'designer.objectives.savedToast': "Objectives saved",
  'designer.objectives.scoreGte': "Score ≥",
  'designer.objectives.title': "Learning objectives",
  'designer.objectives.lvl.evaluation': "Evaluate",
  'designer.objectives.lvl.synthese': "Create",
  'designer.objectives.lvl.analyse': "Analyse",
  'designer.objectives.lvl.application': "Apply",
  'designer.objectives.lvl.comprehension': "Understand",
  'designer.objectives.lvl.connaissance': "Remember",
  'designer.objectives.cat.savoirEtre': "Attitudes",
  'designer.objectives.cat.savoirFaire': "Skills",
  'designer.objectives.cat.savoir': "Knowledge",
  'designer.objectives.linkedCount_other': "{{count}} blocks",
  'designer.objectives.linkedCount_one': "{{count}} block",
  'designer.objectives.defined_other': "{{count}} objectives defined",
  'designer.objectives.defined_one': "{{count}} objective defined",
  'designer.objectives.placeholder': "The learner will be able to…",
  'designer.objectives.update': "Update",
  'designer.validation.circularPath': 'Circular path detected',
  'designer.validation.duplicateStart': "Duplicate Start block",
  'designer.validation.invalid': "Invalid",
  'designer.validation.noEnd': "No End block found. There must be at least one.",
  'designer.validation.noIssues': "No issues found",
  'designer.validation.noStart': "There must be exactly one Start block (none found).",
  'designer.validation.title': 'Validation',
  'designer.validation.untitled': "Untitled",
  'designer.validation.valid': "Valid",
  'designer.validation.errorCount': '{{count}} error(s)',
  'designer.validation.warningCount': '{{count}} warning(s)',
  'designer.validation.titleRequired': "The “{{type}}” block must have a title.",
  'designer.validation.summary': 'Flow: {{blocks}} block(s), {{edges}} connection(s).',
  'designer.validation.multipleStart': 'Multiple Start blocks ({{count}}): only one is allowed.',
  'designer.validation.noOutgoing': "The “{{type}}” block has no outgoing connection to a next block.",
  'designer.validation.tooManyOutputs': '« {{label}} »: too many outgoing connections ({{count}}, maximum {{max}}).',
  'designer.validation.deadEndBranch': 'Branch with no path to an End block (from « {{label}} »).',
  'designer.validation.moreDeadEnds': '… and {{count}} more branch(es) with no path to the end.',
  'designer.validation.noConvergence': 'Branches from « {{label}} » do not converge to a common block.',
  'designer.validation.orphanBlock': 'Block isolated from the main graph: « {{title}} ».',
  'designer.validation.endHasOutgoing': 'The End block « {{title}} » still has an outgoing connection.',
  'designer.validation.noScoring': 'Scoring settings not defined for « {{title}} ».',
  'designer.validation.noCorrectAnswer': 'No correct answer selected for “{{title}}”.',
  'designer.validation.emptyText': 'The text block « {{title}} » is empty.',
  'designer.validation.textTooLong':
    'Text block « {{title}} » is estimated at {{lines}} lines. Recommended: {{min}} to {{max}} lines.',
  'designer.validation.splitTextBlock': 'Split',
  'designer.validation.textSplitNotNeeded':
    'This block does not exceed the recommended reading threshold.',
  'designer.validation.textSplitUnable':
    'Unable to provide a reliable automatic split for this block.',
  'designer.validation.textSplitComplexFlow':
    'Automatic split is unavailable for this block (multiple/conditional branches).',
  'designer.validation.textSplitDone': '{{count}} text blocks created',
  'designer.validation.textSplitConfirmTitle': 'Automatically split this text block?',
  'designer.validation.textSplitConfirmMessage':
    'Block « {{title}} » is estimated at {{lines}} lines. We will split it into {{parts}} blocks (sentence-based on punctuation) and place them just below, in order.',
  'designer.validation.textSplitConfirmAction': 'Split block',
  'designer.validation.formFieldRefsMissing':
    '« {{title}} »: reference(s) to missing form field(s): {{refs}}.',
  'designer.validation.formFieldRefsOrder':
    '« {{title}} »: invalid form field variable(s) {{refs}} — the form block must be reached before this block in the flow.',
  'designer.confirmDeleteForm.title': 'Delete this form block?',
  'designer.confirmDeleteForm.message':
    'Other blocks may reference its fields as /fieldId/. Those references will break. You can undo with ⌘Z or Ctrl+Z.',
  'designer.confirmDeleteForm.batchTitle': 'Delete selection?',
  'designer.confirmDeleteForm.batchMessage':
    'The selection includes at least one form block. Slash references elsewhere may break. You can undo with ⌘Z or Ctrl+Z.',
  'designer.variables.camelCaseError': "The name must be in camelCase (e.g. myVariable)",
  'designer.variables.copy': "Copy",
  'designer.variables.custom': "Custom variables",
  'designer.variables.defaultValue': "Default value",
  'designer.variables.howToTitle': "How to use variables:",
  'designer.variables.noCustom': "No custom variables",
  'designer.variables.system': "System variables",
  'designer.variables.systemHint': "Read-only — available in every block",
  'designer.variables.title': 'Variables',
  'designer.variables.type': 'Type',
  'voicePanel.title': 'Default voice',
  'voicePanel.provider': 'Provider: Mistral AI',
  'voicePanel.hint': 'Select the voice used for block narration.',
  'voicePanel.multiLangHint':
    'For one Mistral voice per UI language (all 10), open the Translation panel (languages icon in the designer toolbar).',
  'voicePanel.translationFrenchFallback':
    'Translated audio: with no voice saved for a language, the engine uses “Marie - sad” (Mistral) for French, Italian and Spanish, and “Paul - sad” for other languages.',
  'voicePanel.search': 'Search…',
  'voicePanel.allLanguages': 'All languages',
  'voicePanel.loading': 'Loading voices…',
  'voicePanel.loadError': 'Unable to load voices',
  'voicePanel.empty': 'No voices available.',
  'voicePanel.emptyHint': 'Check the Mistral API key in Administration → Integrations.',
  'voicePanel.noResults': 'No voices match your search.',
  'voicePanel.saved': 'Default voice saved',
  'voicePanel.saveError': 'Error saving voice',
  'voicePanel.default': 'Default',
  'voicePanel.female': 'Female',
  'voicePanel.male': 'Male',
  'voicePanel.playPreview': 'Preview voice',
  'voicePanel.stopPreview': 'Stop',
  'voicePanel.noPreview': 'Preview not available',
  'voicePanel.previewLoading': 'Generating…',
  'voicePanel.previewGenerateError': 'Could not generate the audio preview.',
  'voicePanel.previewPlayError': 'Audio playback failed.',
  'voicePanel.filterNoVoicesForLang':
    'No catalog voice matches this language — try “All languages”.',
  'episodeEditor.voicePicker.blockVoice': 'Voice for this block',
  'episodeEditor.voicePicker.defaultVoice': 'Default voice',
  'episodeEditor.voicePicker.voices': 'voices',
  'episodeEditor.voicePicker.searchPlaceholder': 'Search a voice…',
  'episodeEditor.voicePicker.noVoiceFound': 'No voice found',
  'episodeEditor.voicePicker.female': 'Female',
  'episodeEditor.voicePicker.male': 'Male',
  'episodeEditor.voicePicker.stop': 'Stop',
  'episodeEditor.voicePicker.preview': 'Preview voice',
  'episodeEditor.voicePicker.stopPlayback': 'Stop playback',
  'episodeEditor.errors.notFound': 'Episode not found',
  'episodeEditor.errors.titleRequired': 'Title is required.',
  'episodeEditor.errors.saveFailed': 'Error saving.',
  'episodeEditor.errors.noVoicesAvailable': 'No voices available. Enter the API key in Administration → Platform integrations, then reload the page.',
  'episodeEditor.errors.selectVoiceFirst': 'Select a voice in the "Voice" tab before generating voiceover.',
  'episodeEditor.errors.invalidBlockVoice': 'One or more blocks have an invalid voice. Open the per-block voice selector or reload the voice list.',
  'episodeEditor.errors.addTextFirst': 'Add text before generating audio.',
  'episodeEditor.errors.audioGenerationFailedApi': 'Audio generation failed. {{message}} (displayed duration = estimate.)',
  'episodeEditor.errors.audioGenerationFailed': 'Error during audio generation. Displayed duration = estimate.',
  'episodeEditor.success.published': 'Episode published!',
  'episodeEditor.success.draftSaved': 'Draft saved',
  'episodeEditor.success.audioGenerated': 'Audio generated with {{count}} block(s)',
  'episodeEditor.tabs.edit': 'Edit',
  'episodeEditor.tabs.chapters': 'Chapters',
  'episodeEditor.tabs.voice': 'Voice',
  'episodeEditor.tabs.podcasts': 'Podcasts',
  'episodeEditor.toolbar.undo': 'Undo (Ctrl+Z)',
  'episodeEditor.toolbar.redo': 'Redo (Ctrl+Y)',
  'episodeEditor.toolbar.generating': 'Generating…',
  'episodeEditor.toolbar.generateAudio': 'Generate audio',
  'episodeEditor.toolbar.save': 'Save',
  'episodeEditor.toolbar.publish': 'Publish',
  'episodeEditor.titlePlaceholder': 'Episode title…',
  'episodeEditor.status.published': 'Published',
  'episodeEditor.status.draft': 'Draft',
  'episodeEditor.warnings.mistralNotConfigured': 'Episode voiceover uses Mistral Voxtral TTS. Add the Mistral key in Administration → Platform integrations, then reload this page.',
  'episodeEditor.warnings.mistralNoVoices': 'The Mistral key is recognized but the API returned no voices. Check backend logs.',
  'episodeEditor.warnings.voiceLoadFailed': 'Unable to load voice list. Check Mistral configuration in Administration → Platform integrations.',
  'episodeEditor.warnings.voiceOffDisabled': 'Without a real voice, voiceover generation remains disabled.',
  'episodeEditor.edit.voiceOff': 'Voiceover',
  'episodeEditor.edit.voiceDescription': 'Synthesis via Mistral Voxtral TTS. Choose the voice in the "Voice" tab or per block.',
  'episodeEditor.edit.coverImage': 'Cover image',
  'episodeEditor.edit.coverAlt': 'Episode cover',
  'episodeEditor.edit.deleteCover': 'Delete',
  'episodeEditor.edit.dropOrClickImage': 'Click or drop an image',
  'episodeEditor.edit.selectedVoice': 'Selected voice',
  'episodeEditor.edit.selectVoice': 'Select a voice',
  'episodeEditor.edit.defaultVoiceByLang': 'Default voice (podcast language)',
  'episodeEditor.edit.lastAudioGenerated': 'Last generated audio',
  'episodeEditor.edit.audioNotFound': 'Audio file not found — regenerate the voiceover.',
  'episodeEditor.edit.generatingInProgress': 'Generating…',
  'episodeEditor.edit.generateVoiceOff': 'Generate voiceover',
  'episodeEditor.chapters.title': 'Chapters',
  'episodeEditor.chapters.add': 'Add',
  'episodeEditor.chapters.defaultChapterTitle': 'Introduction',
  'episodeEditor.chapters.newChapterTitle': 'Chapter {{n}}',
  'episodeEditor.chapters.blockCount': '{{count}} block(s)',
  'episodeEditor.chapters.readByAi': 'Read chapter title',
  'episodeEditor.chapters.readByAiHint':
    'When off, the AI does not read this chapter title aloud. The chapter blocks stay included in the voiceover.',
  'episodeEditor.voiceTab.availableVoices': 'Available voices',
  'episodeEditor.podcasts.linked': 'Linked podcasts',
  'episodeEditor.podcasts.assign': 'Assign',
  'episodeEditor.podcasts.noneAvailable': 'No podcast available',
  'episodeEditor.podcasts.close': 'Close',
  'episodeEditor.podcasts.noneLinked': 'No linked podcast',
  'episodeEditor.podcasts.assignHint': 'Assign this episode to one or more podcasts',
  'episodeEditor.podcasts.remove': 'Remove',
  'episodeEditor.editor.addTextBlock': 'Add a text block',
  'episodeEditor.editor.scriptPlaceholder': 'Start writing your script…',
  'episodeEditor.editor.addBlock': 'Add a block',
  'episodeEditor.player.loop': 'Loop',
  'episodeEditor.player.playbackSpeed': 'Playback speed',
  'episodeEditor.player.collapse': 'Collapse',
  'episodeEditor.player.expand': 'Expand',
  'episodeEditor.player.waveformHidden': 'Waveform hidden',
  'podcast.episodes.listen': 'Listen',
  'podcast.episodes.noAudioShort': 'No audio available',
  'podcast.episodes.edit': 'Edit',
  'podcast.episodes.delete': 'Delete',
  'podcast.episodes.unmute': 'Unmute',
  'podcast.episodes.mute': 'Mute',
  'podcast.episodes.totalEpisodes': '{{count}} episode(s) total',
  'eduflow.designer.toast.blockCopied': 'Block copied',
  'eduflow.designer.toast.blockPasted': 'Block pasted',
  'eduflow.designer.toast.blockDuplicated': 'Block duplicated',
  'eduflow.designer.toast.connectionUpdated': 'Connection updated',
  'eduflow.designer.toast.flowExported': 'Course exported',
  'eduflow.designer.toast.flowImported': 'Course imported',
  'eduflow.designer.toast.flowPastedFromClipboard': 'Course created from clipboard',
  'eduflow.designer.toast.invalidFile': 'Invalid file',
  'eduflow.designer.toast.exportFailed': 'Export failed.',
  'eduflow.designer.toast.importPackageFailed': 'Could not import the .lyrra package.',
  'eduflow.designer.untitled': 'Untitled',
  'eduflow.designer.importedFlow': 'Imported course',
  'eduflow.designer.collaborator.online': 'Online',
  'eduflow.designer.collaborator.offline': 'Offline',
  'eduflow.designer.collaborator.defaultName': 'User',
  'eduflow.designer.collaborator.editor': 'Editor',
  'eduflow.designer.collaborator.viewer': 'Viewer',
  'eduflow.designer.resizePanel': 'Resize panel',
  'eduflow.designer.published': 'Published',
  'eduflow.designer.draft': 'Draft',
  'eduflow.designer.quizOutput.success': 'Passed',
  'eduflow.designer.quizOutput.failure': 'Failed',
  'eduflow.designer.loopHandle.loop': 'Loop',
  'eduflow.designer.loopHandle.end': 'End',
  'eduflow.designer.handle.default': 'Default',
  'eduflow.designer.genError': 'Error during generation. Please try again.',
  'trainerTour.welcome.title': 'Welcome to your trainer workspace',
  'trainerTour.welcome.description':
    'This short tour introduces the dashboard and menu. Then, the first time you open each page, a tip explains the essentials. You can close a step with the X — the tour will not start again.',
  'trainerTour.dashboard.header.title': 'Your dashboard',
  'trainerTour.dashboard.header.description':
    'Overview of your activity: key numbers and shortcuts to create content quickly.',
  'trainerTour.dashboard.actions.title': 'Create content',
  'trainerTour.dashboard.actions.description':
    'Shortcuts to a new EduFlow course, a podcast, or a learning path. Use them to start a project without going through the menu.',
  'trainerTour.dashboard.stats.title': 'Metrics',
  'trainerTour.dashboard.stats.description':
    'Courses, published paths, learners reached, and podcasts: see the scope of your catalog at a glance.',
  'trainerTour.dashboard.activity.title': 'Recent activity',
  'trainerTour.dashboard.activity.description':
    'Latest updated content. Click a row to reopen the matching course, podcast, or path.',
  'trainerTour.sidebar.title': 'Side menu',
  'trainerTour.sidebar.description':
    'Overview (home, learners, analytics, missions), creation (courses, podcasts, paths, webinars if enabled), media library, and help. Open each section — a contextual tip appears the first time on every page.',
  'trainerTour.routes.learners.title': 'Learners',
  'trainerTour.routes.learners.description':
    'People enrolled in your content, progress, exports — your hub for teaching follow-up.',
  'trainerTour.routes.analytics.title': 'Analytics',
  'trainerTour.routes.analytics.description':
    'Charts on engagement and completion. Customize the layout to track what matters to you.',
  'trainerTour.routes.eduflow.title': 'Courses (EduFlow)',
  'trainerTour.routes.eduflow.description':
    'Create and manage interactive courses, versions, publishing, and sharing — the core of instructional design.',
  'trainerTour.routes.podcasts.title': 'Podcasts',
  'trainerTour.routes.podcasts.description':
    'Shows, episodes, visibility, and sharing with your organization’s groups or learners.',
  'trainerTour.routes.parcours.title': 'Learning paths',
  'trainerTour.routes.parcours.description':
    'Sequences of courses and resources: structure long journeys and assign them to your audiences.',
  'trainerTour.routes.videos.title': 'Videos',
  'trainerTour.routes.videos.description':
    'Reusable video library for your courses and documents.',
  'trainerTour.routes.sounds.title': 'Audio',
  'trainerTour.routes.sounds.description':
    'Audio files and tags for podcasts and sound blocks in courses.',
  'trainerTour.routes.images.title': 'Images',
  'trainerTour.routes.images.description':
    'Centralized visuals: import, folders, and reuse across content.',
  'trainerTour.routes.documents.title': 'Documents',
  'trainerTour.routes.documents.description':
    'PDFs and attachments, with optional targeted sharing to learners.',
  'trainerTour.routes.help.title': 'Help',
  'trainerTour.routes.help.description':
    'Articles, tutorials, and walkthroughs about LyrraStudio. Come back when you are stuck on a feature.',
  'trainerTour.routes.producerAssignments.title': 'Production missions',
  'trainerTour.routes.producerAssignments.description':
    'When HR assigns catalog items for you to produce, they show up here with progress.',
  'trainerTour.routes.webinars.title': 'Webinars',
  'trainerTour.routes.webinars.description':
    'Schedule live sessions, invitations, and broadcast links for your groups.',
  'trainerTour.driver.progress': '{{current}} of {{total}}',
  'trainerTour.driver.next': 'Next',
  'trainerTour.driver.prev': 'Previous',
  'trainerTour.driver.done': 'Done',
  'trainerTour.driver.gotIt': 'Got it',
  'player.lightMode': 'Light mode',
  'player.darkMode': 'Dark mode',
  'a11y.skipToContent': 'Skip to main content',
  'a11y.skipToNav': 'Skip to navigation',
  'a11y.skipToPlayerMain': 'Skip to course content',
  'a11y.playerMainLandmark': 'Course content',
  'a11y.dialog': 'Dialog',
  'a11y.closeDialog': 'Close dialog',
  'a11y.dismissNotification': 'Dismiss notification',
  'a11y.notificationsRegion': 'Notifications',
  'a11y.skipLinksLandmark': 'Quick access links',

  // ── Parity with fr (dashboard / roles / projects / audio / notifications / settings / analytics / gamification / Qualiopi / training / player) ──
  'role.institution_admin': 'Organization administrator',
  'role.resp_formation': 'Training manager',
  'dashboard.recent_projects': 'Recent projects',
  'dashboard.recent_activity': 'Recent activity',
  'dashboard.quick_actions': 'Quick actions',
  'dashboard.stats': 'Statistics',
  'dashboard.total_projects': 'Total projects',
  'dashboard.total_learners': 'Total learners',
  'dashboard.total_eduflows': 'Total courses',
  'dashboard.completion_rate': 'Completion rate',
  'dashboard.active_users': 'Active users',
  'dashboard.new_registrations': 'New registrations',
  'dashboard.xp_earned': 'XP earned',
  'dashboard.streak': 'Current streak',
  'dashboard.level': 'Level {{level}}',
  'dashboard.badges_earned': 'Badges earned',
  'dashboard.next_level': 'Next level',
  'dashboard.no_projects': 'No projects yet',
  'dashboard.create_first': 'Create your first project',
  'dashboard.pending_reviews': 'Pending reviews',
  'dashboard.upcoming_deadlines': 'Upcoming deadlines',
  'eduflow.summary.editorHint':
    'List the concepts or steps covered in the course. Each line appears as a numbered card in the player.',
  'project.title': 'Projects',
  'project.new': 'New project',
  'project.name': 'Project name',
  'project.description': 'Project description',
  'project.status.in_review': 'In review',
  'project.visibility.public': 'Public',
  'project.visibility.private': 'Private',
  'project.visibility.institution': 'Organization',
  'project.no_projects': 'No projects found',
  'project.delete_confirm': 'Are you sure you want to delete this project?',
  'audio.title': 'Audio Studio',
  'audio.audiobooks': 'Audiobooks',
  'audio.new_audiobook': 'New audiobook',
  'audio.record': 'Record',
  'audio.stop': 'Stop',
  'audio.play': 'Play',
  'audio.pause': 'Pause',
  'audio.volume': 'Volume',
  'audio.tracks': 'Tracks',
  'audio.add_track': 'Add track',
  'audio.remove_track': 'Remove track',
  'audio.effects': 'Effects',
  'audio.export': 'Export',
  'audio.export_format': 'Export format',
  'audio.duration': 'Duration',
  'audio.narrator': 'Narrator',
  'audio.chapter': 'Chapter',
  'notification.title': 'Notifications',
  'notification.mark_all_read': 'Mark all as read',
  'notification.no_notifications': 'No notifications',
  'notification.new': 'New notification',
  'notification.type.info': 'Information',
  'notification.type.success': 'Success',
  'notification.type.warning': 'Warning',
  'notification.type.error': 'Error',
  'settings.title': 'Settings',
  'settings.general': 'General',
  'settings.account': 'Account',
  'settings.security': 'Security',
  'settings.appearance': 'Appearance',
  'settings.notifications': 'Notifications',
  'settings.language': 'Language',
  'settings.theme': 'Theme',
  'settings.theme.light': 'Light',
  'settings.theme.dark': 'Dark',
  'settings.theme.system': 'System',
  'settings.accessibility': 'Accessibility',
  'settings.font_size': 'Font size',
  'settings.high_contrast': 'High contrast',
  'settings.reduced_motion': 'Reduced motion',
  'settings.dyslexia_mode': 'Dyslexia mode',
  'analytics.title': 'Analytics',
  'analytics.overview': 'Overview',
  'analytics.learner_progress': 'Learner progress',
  'analytics.engagement': 'Engagement',
  'analytics.completion': 'Completion',
  'analytics.time_spent': 'Time spent',
  'analytics.average_score': 'Average score',
  'analytics.export_report': 'Export report',
  'gamification.xp': 'XP',
  'gamification.streak': 'Streak',
  'gamification.achievements': 'Achievements',
  'gamification.daily_goal': 'Daily goal',
  'gamification.congratulations': 'Congratulations!',
  'gamification.new_badge': 'New badge unlocked!',
  'gamification.level_up': 'Level up!',
  'qualiopi.title': 'Qualiopi compliance',
  'qualiopi.dashboard': 'Qualiopi dashboard',
  'qualiopi.criteria': 'Criterion {{number}}',
  'qualiopi.indicator': 'Indicator {{number}}',
  'qualiopi.compliant': 'Compliant',
  'qualiopi.non_compliant': 'Non-compliant',
  'qualiopi.partial': 'Partially compliant',
  'qualiopi.not_applicable': 'Not applicable',
  'qualiopi.upload_evidence': 'Upload evidence',
  'qualiopi.audit_date': 'Audit date',
  'qualiopi.next_audit': 'Next audit',
  'qualiopi.compliance_score': 'Compliance score',
  'qualiopi.action_plan': 'Action plan',
  'training.plan': 'Training plan',
  'training.request': 'Training request',
  'training.requests': 'Training requests',
  'training.catalog': 'Training catalog',
  'training.budget': 'Training budget',
  'training.campaign': 'Interview campaign',
  'training.competency': 'Skills framework',
  'training.gap_analysis': 'Gap analysis',
  'training.roi': 'Return on investment',
  ...eduflowBlockEditorsEn,
  ...institutionMcpEn,
};

const SUPPORTED_LNGS = ['fr', 'en', 'es', 'de', 'it', 'pt', 'lb', 'uk', 'ru', 'ar', 'tr'] as const;

function readStoredLanguageCode(): string {
  if (typeof window === 'undefined') return 'en';
  const code = resolveInitialLanguage();
  return (SUPPORTED_LNGS as readonly string[]).includes(code) ? code : 'en';
}

function persistLanguageChange(lng: string): void {
  const code = lng.split('-')[0];
  if (!(SUPPORTED_LNGS as readonly string[]).includes(code)) return;
  try {
    localStorage.setItem('locale', code);
    localStorage.setItem('app_language', code);
    applyDocumentLocale(code);
    useLanguageStore.setState({ language: code as Language });
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Langues chargées à la demande : seuls le français (défaut) et l'anglais (repli) sont dans le bundle
 * initial. Les autres sont téléchargées au premier choix de la langue (gain important en bas débit).
 */
type LazyBundleModule = {
  default: Record<string, string>;
  institution?: Record<string, string>;
  override?: Record<string, string>;
};

const LAZY_BUNDLES: Record<string, () => Promise<LazyBundleModule>> = {
  es: () => import('./i18n-bundles/es'),
  de: () => import('./i18n-bundles/de'),
  it: () => import('./i18n-bundles/it'),
  pt: () => import('./i18n-bundles/pt'),
  lb: () => import('./i18n-bundles/lb'),
  uk: () => import('./i18n-bundles/uk'),
  ru: () => import('./i18n-bundles/ru'),
  ar: () => import('./i18n-bundles/ar'),
  tr: () => import('./i18n-bundles/tr'),
};

const lazySlices: Record<string, Record<string, string>> = {
  es: { ...videosStudioLocales.es, ...playerVisualParallelLocales.es, ...educationSegmentLocales.es, ...classesEvaluationsLocales.es, ...schoolExperienceLocales.es, ...rhReportsLocales.es, ...studentLoginLocales.es },
  de: { ...videosStudioLocales.de, ...playerVisualParallelLocales.de, ...educationSegmentLocales.de, ...classesEvaluationsLocales.de, ...schoolExperienceLocales.de, ...rhReportsLocales.de, ...studentLoginLocales.de },
  it: { ...videosStudioLocales.it, ...playerVisualParallelLocales.it, ...educationSegmentLocales.it, ...classesEvaluationsLocales.it, ...schoolExperienceLocales.it, ...rhReportsLocales.it, ...studentLoginLocales.it },
  pt: { ...videosStudioLocales.pt, ...playerVisualParallelLocales.pt, ...educationSegmentLocales.pt, ...classesEvaluationsLocales.pt, ...schoolExperienceLocales.pt, ...rhReportsLocales.pt, ...studentLoginLocales.pt },
  lb: { ...videosStudioLocales.lb, ...playerVisualParallelLocales.lb, ...educationSegmentLocales.lb, ...classesEvaluationsLocales.lb, ...schoolExperienceLocales.lb, ...rhReportsLocales.lb, ...studentLoginLocales.lb },
  uk: { ...videosStudioLocales.uk, ...playerVisualParallelLocales.uk, ...educationSegmentLocales.uk, ...classesEvaluationsLocales.uk, ...schoolExperienceLocales.uk, ...rhReportsLocales.uk, ...studentLoginLocales.uk },
  ru: { ...videosStudioLocales.ru, ...playerVisualParallelLocales.ru, ...educationSegmentLocales.ru, ...classesEvaluationsLocales.ru, ...schoolExperienceLocales.ru, ...rhReportsLocales.ru, ...studentLoginLocales.ru },
  ar: { ...videosStudioLocales.ar, ...playerVisualParallelLocales.ar, ...educationSegmentLocales.ar, ...classesEvaluationsLocales.ar, ...schoolExperienceLocales.ar, ...rhReportsLocales.ar, ...studentLoginLocales.ar },
  tr: { ...videosStudioLocales.tr, ...playerVisualParallelLocales.tr, ...educationSegmentLocales.tr, ...classesEvaluationsLocales.tr, ...schoolExperienceLocales.tr, ...rhReportsLocales.tr, ...studentLoginLocales.tr },
};

const pendingBundles = new Map<string, Promise<boolean>>();

/**
 * Télécharge et enregistre le bundle d'une langue (sans effet pour fr / en, déjà présents).
 * Résout `true` si la langue est utilisable, `false` si le téléchargement a échoué (réseau coupé, déploiement).
 */
export function ensureLanguageLoaded(lng: string | undefined | null): Promise<boolean> {
  const code = String(lng ?? '').split('-')[0].toLowerCase();
  const loader = LAZY_BUNDLES[code];
  if (!loader || i18n.hasResourceBundle(code, 'translation')) return Promise.resolve(true);
  const pending = pendingBundles.get(code);
  if (pending) return pending;
  const task = loader()
    .then((mod) => {
      const translation = {
        ...withInstitution(withEnglishFallback({ ...mod.default, ...(lazySlices[code] ?? {}) }), code, mod.institution),
        ...(mod.override ?? {}),
      };
      i18n.addResourceBundle(code, 'translation', translation, true, true);
      return true;
    })
    .catch((err) => {
      pendingBundles.delete(code);
      // Réseau coupé : on garde la langue actuelle ; nouvelle tentative au prochain choix ou au retour du réseau.
      console.warn(`[i18n] chargement de la langue « ${code} » impossible`, err);
      return false;
    });
  pendingBundles.set(code, task);
  return task;
}

i18n.use(initReactI18next).init({
  resources: {
    fr: {
      translation: withInstitution(
        { ...frTranslations, ...playerVisualParallelLocales.fr, ...webhooksLocales.fr, ...educationSegmentLocales.fr, ...classesEvaluationsLocales.fr, ...schoolExperienceLocales.fr, ...rhReportsLocales.fr, ...studentLoginLocales.fr },
        'fr',
      ),
    },
    en: {
      translation: withInstitution(
        { ...enTranslations, ...playerVisualParallelLocales.en, ...webhooksLocales.en, ...educationSegmentLocales.en, ...classesEvaluationsLocales.en, ...schoolExperienceLocales.en, ...rhReportsLocales.en, ...studentLoginLocales.en },
        'en',
      ),
    },
  },
  partialBundledLanguages: true,
  lng: readStoredLanguageCode(),
  fallbackLng: {
    ar: ['en'],
    default: ['en'],
  },
  supportedLngs: [...SUPPORTED_LNGS],
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

i18n.on('languageChanged', (lng) => {
  persistLanguageChange(lng);
});

/**
 * Toute bascule de langue attend d'abord son bundle (pas d'affichage en anglais puis dans la langue choisie).
 * Seule la dernière demande s'applique (choix rapides successifs) ; un échec de téléchargement laisse la langue actuelle.
 */
const baseChangeLanguage = i18n.changeLanguage.bind(i18n);
let languageRequest = 0;
let wantedLanguage: string | null = null;
i18n.changeLanguage = ((lng?: string, callback?: Parameters<typeof baseChangeLanguage>[1]) => {
  const ticket = ++languageRequest;
  wantedLanguage = lng ?? null;
  return ensureLanguageLoaded(lng).then((ok) => {
    if (ticket !== languageRequest || !ok) return i18n.t;
    wantedLanguage = null;
    return baseChangeLanguage(lng, callback);
  });
}) as typeof i18n.changeLanguage;

/** Langue mémorisée hors fr / en : la charger (à attendre avant le premier rendu, voir main.tsx). */
export function prepareInitialLanguage(): Promise<boolean> {
  const code = (i18n.language || '').split('-')[0];
  if (!LAZY_BUNDLES[code]) return Promise.resolve(true);
  return ensureLanguageLoaded(code).then((ok) => {
    if (ok) void baseChangeLanguage(code);
    else wantedLanguage = code;
    return ok;
  });
}

if (typeof window !== 'undefined') {
  // Retour du réseau : réessayer la langue demandée qui n'avait pas pu être téléchargée.
  window.addEventListener('online', () => {
    if (wantedLanguage) void i18n.changeLanguage(wantedLanguage);
  });
}

if (typeof document !== 'undefined') {
  applyDocumentLocale(i18n.language);
}

if (typeof window !== 'undefined') {
  (window as unknown as { __i18n: typeof i18n }).__i18n = i18n;
  /** Aligne i18next sur la même résolution que le store (évite un premier rendu FR puis AR). */
  queueMicrotask(() => {
    try {
      const want = resolveInitialLanguage();
      const have = (i18n.language || '').split('-')[0].toLowerCase();
      if (want && have !== want && (SUPPORTED_LNGS as readonly string[]).includes(want)) {
        void i18n.changeLanguage(want);
      }
    } catch {
      /* noop */
    }
  });
}

export default i18n;
